// ╔══════════════════════════════════════════════════════════════════════════╗
// ║  CrickitCoach v4.997  —  by ArcGain LLC                                        ║
// ║  Multi-class, multi-tenant, persistent cricket coaching platform        ║
// ║  © 2026 ArcGain LLC  |  support@crickitcoach.com  |  com.arcgain.crickit      ║
// ╚══════════════════════════════════════════════════════════════════════════╝
import React, { useState, useEffect, useRef, useCallback } from "react";

// ─── Font injection (safe for artifact runner) ────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("crickit-fonts")) {
  const _fl = document.createElement("link");
  _fl.id = "crickit-fonts";
  _fl.rel = "stylesheet";
  _fl.href = "https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=JetBrains+Mono:wght@400;600&display=swap";
  document.head.appendChild(_fl);
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════════
const CLASSES    = ["Introductory","Foundational","Intermediate","Advanced"];
const CLASS_COLOR= { Introductory:"#0284c7", Foundational:"#16a34a", Intermediate:"#d97706", Advanced:"#dc2626" };
const CLASS_ICON = { Introductory:"🌱", Foundational:"⚡", Intermediate:"🔥", Advanced:"🏆" };
const CATEGORIES = ["Batting","Bowling","Fielding","Mental","Physical"];
const CAT_COLOR  = { Batting:"#0284c7", Bowling:"#ea580c", Fielding:"#16a34a", Mental:"#7c3aed", Physical:"#b45309" };
const CAT_ICON   = { Batting:"🏏", Bowling:"⚾", Fielding:"🧤", Mental:"🧠", Physical:"💪" };
const RATING_COLOR = r => r>=4.5?"#00e676":r>=3.5?"#69f0ae":r>=2.5?"#ffeb3b":r>=1.5?"#ff9800":"#f44336";
const RATING_LABEL = r => r>=4.5?"Elite":r>=3.5?"Proficient":r>=2.5?"Competent":r>=1.5?"Developing":"Beginner";
const dateStr      = d => new Date(d).toLocaleDateString("en-AU",{day:"numeric",month:"short",year:"numeric"});

// Player tags — full taxonomy
const PLAYER_TAGS = [
  { id:"Bowler-PaceRH",  label:"Bowler — Pace RH",     group:"Bowling",    icon:"⚾", color:"#ff6b35" },
  { id:"Bowler-PaceLH",  label:"Bowler — Pace LH",     group:"Bowling",    icon:"⚾", color:"#ff8c00" },
  { id:"Bowler-SpinOff", label:"Bowler — Spin Off",    group:"Bowling",    icon:"🌀", color:"var(--gold)" },
  { id:"Bowler-SpinLeg", label:"Bowler — Spin Leg",    group:"Bowling",    icon:"🌀", color:"#ffc107" },
  { id:"Bowler-SpinLA",  label:"Bowler — Spin LA",     group:"Bowling",    icon:"🌀", color:"#ffab00" },
  { id:"Batter-RH",      label:"Batter — Right Hand",  group:"Batting",    icon:"🏏", color:"#00d4ff" },
  { id:"Batter-LH",      label:"Batter — Left Hand",   group:"Batting",    icon:"🏏", color:"#00b4ff" },
  { id:"AllRounder",     label:"All Rounder",           group:"AllRounder", icon:"⭐", color:"#00e676" },
  { id:"Wicketkeeper",   label:"Wicketkeeper",          group:"WK",         icon:"🧤", color:"#a855f7" },
];
const TAG_GROUPS = {
  "All Bowlers":    ["Bowler-PaceRH","Bowler-PaceLH","Bowler-SpinOff","Bowler-SpinLeg","Bowler-SpinLA"],
  "Pace Bowlers":   ["Bowler-PaceRH","Bowler-PaceLH"],
  "Spin Bowlers":   ["Bowler-SpinOff","Bowler-SpinLeg","Bowler-SpinLA"],
  "All Batters":    ["Batter-RH","Batter-LH"],
  "All Rounders":   ["AllRounder"],
  "Wicketkeepers":  ["Wicketkeeper"],
};
const GOAL_CATS = ["Batting","Bowling","Fielding","Mental","Physical","General"];

// ═══════════════════════════════════════════════════════════════════════════════
// DEFAULT SKILL LIBRARY  (117 skills from Excel — isDefault:true, cannot delete)
// ═══════════════════════════════════════════════════════════════════════════════
const DEFAULT_SKILL_LIBRARY = {
  "Introductory": {
    "Batting": [
      { id:"def_int_bat_001", label:"Grip and Stance", description:"Top-hand / bottom-hand placement, balanced stance, head position", isDefault:true, active:true },
      { id:"def_int_bat_002", label:"Front Foot Defence", description:"Forward controlled movement, weight transfer, bat position", isDefault:true, active:true },
      { id:"def_int_bat_003", label:"Back Foot Defence", description:"Back foot weight transfer, bat position", isDefault:true, active:true },
      { id:"def_int_bat_004", label:"Forward Push", description:"Forward controlled movement, weight transfer, bat position", isDefault:true, active:true },
      { id:"def_int_bat_005", label:"Watching ball from hand", description:"Watching the ball from hand/machine, getting into line - throwdowns", isDefault:true, active:true },
      { id:"def_int_bat_006", label:"Running Between Wickets — Calling basics", description:"Calling (Yes/No/Wait), grounding the bat, turning correctly", isDefault:true, active:true },
    ],
    "Bowling": [
      { id:"def_int_bow_025", label:"Basic Grip, Seam Up", description:"Holding the ball with fingers on either side of the seam, seam upright", isDefault:true, active:true },
      { id:"def_int_bow_026", label:"Run-up Basics, Delivery Stride", description:"Smooth, rhythmic approach, momentum, body, arms and feet position during delivery stride", isDefault:true, active:true },
      { id:"def_int_bow_027", label:"Length and Line basics", description:"Legal delivery, length and line for pace and spin", isDefault:true, active:true },
      { id:"def_int_bow_028", label:"Follow Through", description:"Completing the action after release and recovery with balance", isDefault:true, active:true },
      { id:"def_int_bow_029", label:"Bowling at the Stumps", description:"Accuracy drill, consistently hitting the stumps", isDefault:true, active:true },
      { id:"def_int_bow_030", label:"Basic Over-the-Wicket / Around-the-Wicket Awareness", description:"Understanding angles created by different sides of the wicket", isDefault:true, active:true },
    ],
    "Fielding": [
      { id:"def_int_fie_048", label:"Ready Position", description:"Low, balanced athletic stance on the balls of the feet, hands ready", isDefault:true, active:true },
      { id:"def_int_fie_049", label:"Ground Fielding, Body Barrier", description:"Getting side-on, one knee down to form a safe barrier behind the ball", isDefault:true, active:true },
      { id:"def_int_fie_050", label:"Ground Fielding, Attack the Ball", description:"Moving forward to collect cleanly and quickly on the move", isDefault:true, active:true },
      { id:"def_int_fie_051", label:"Catching, Basic High Catch", description:"Eyes on the ball, fingers pointing up, cushioning on contact", isDefault:true, active:true },
      { id:"def_int_fie_052", label:"Catching, Basic Low Catch", description:"Fingers pointing down, cupping under the ball close to the ground", isDefault:true, active:true },
      { id:"def_int_fie_053", label:"Overarm Throw", description:"Side-on position, high elbow, throwing accurately overarm at a target", isDefault:true, active:true },
      { id:"def_int_fie_054", label:"Wicketkeeping Stance and Position", description:"Low crouch, gloves together, fingers down, eyes level with bails, feet position", isDefault:true, active:true },
      { id:"def_int_fie_055", label:"Wicketkeeping movements and gathering", description:"Sideways feet movement, anticipating ball movement, gathering ball cleanly two hands", isDefault:true, active:true },
    ],
    "Mental": [
      { id:"def_int_men_075", label:"Enjoyment of the Game", description:"Finding fun in cricket, Celebrating effort, not just results", isDefault:true, active:true },
      { id:"def_int_men_076", label:"Basic Sportsmanship", description:"Walking when out, applauding opponents, respecting the umpire", isDefault:true, active:true },
      { id:"def_int_men_077", label:"Listening to Coaches", description:"Being attentive, absorbing feedback, applying instructions", isDefault:true, active:true },
      { id:"def_int_men_078", label:"Teamwork Basics", description:"Encouraging teammates, sharing, playing for the team", isDefault:true, active:true },
      { id:"def_int_men_079", label:"Positive Attitude and Energy", description:"Approaching practice and games with enthusiasm and energy", isDefault:true, active:true },
      { id:"def_int_men_080", label:"Handling Winning and Losing", description:"Grace in victory and defeat, Learning from both outcomes", isDefault:true, active:true },
    ],
    "Physical": [
      { id:"def_int_phy_100", label:"Basic Warm-Up, Stretching and Cooldown Routines", description:"Jogging, Stretching, Joint Mobilisation before play and Cool Down stretching after play", isDefault:true, active:true },
      { id:"def_int_phy_101", label:"Hand-Eye Coordination Drills", description:"Catching tennis balls, reaction ball exercises, throwing and ball judgement", isDefault:true, active:true },
      { id:"def_int_phy_102", label:"Basic Running Technique", description:"Proper arm swing, posture, and foot strike when sprinting", isDefault:true, active:true },
      { id:"def_int_phy_103", label:"Flexibility Basics", description:"Age-appropriate stretching to maintain a full range of motion", isDefault:true, active:true },
      { id:"def_int_phy_104", label:"Diet and fluid intake for match / practice", description:"Importance of fluid and correct diet intake (complex carbs) to maintain energy levels", isDefault:true, active:true },
    ],
  },
  "Foundational": {
    "Batting": [
      { id:"def_fou_bat_007", label:"Drives — off, cover, straight, on drive", description:"Feet position, controlled movement, weight transfer, bat position and flow, bat finish, balance", isDefault:true, active:true },
      { id:"def_fou_bat_008", label:"Leg Glance / Flick", description:"Deflecting the ball off the pads through the leg side using wrist work", isDefault:true, active:true },
      { id:"def_fou_bat_009", label:"Square Cut", description:"Weight transfer, feet position, balance, timing and bat position, finish", isDefault:true, active:true },
      { id:"def_fou_bat_010", label:"Ball length and line judgement", description:"Recognising ball line and length, getting in position quickly", isDefault:true, active:true },
      { id:"def_fou_bat_011", label:"Playing Spin Bowling", description:"Reading hand cues, watching ball from the hand, recognizing turn", isDefault:true, active:true },
      { id:"def_fou_bat_012", label:"Running Between Wickets — Taking starts, Turning at crease", description:"Taking a start, early call/response, Efficient turns using the bat, calling for second runs", isDefault:true, active:true },
    ],
    "Bowling": [
      { id:"def_fou_bow_031", label:"Seam Position for pace", description:"Various seam positions and their effect", isDefault:true, active:true },
      { id:"def_fou_bow_032", label:"Seam position, grip and finger/wrist action for spin", description:"Various seam positions, grips, finger/wrist action and their effect", isDefault:true, active:true },
      { id:"def_fou_bow_033", label:"Bowling Good Length Consistently", description:"Hitting the 6 to 8 metre zone repeatedly with control", isDefault:true, active:true },
      { id:"def_fou_bow_033b", label:"Spin Ball flight and Landing areas", description:"Hitting the right lengths and line consistently for ball control", isDefault:true, active:true },
      { id:"def_fou_bow_034", label:"Yorker basics", description:"Grip, wrist and release for bowling yorkers", isDefault:true, active:true },
      { id:"def_fou_bow_035", label:"Bouncer basics", description:"Grip, wrist and release for bowling bouncers", isDefault:true, active:true },
    ],
    "Fielding": [
      { id:"def_fou_fie_056", label:"Backing Up (Behind the Stumps)", description:"Good Positioning behind the stumps to stop overthrows", isDefault:true, active:true },
      { id:"def_fou_fie_057", label:"Catching at Various Heights", description:"Adjusting technique for catches above head, at chest, and below knee", isDefault:true, active:true },
      { id:"def_fou_fie_058", label:"Throwing Accuracy, throwing into wicketkeeper hands", description:"Targeting above stumps into hands - consistently from various distances", isDefault:true, active:true },
      { id:"def_fou_fie_059", label:"Fielding in Ring Positions", description:"Covering ground at mid-off, mid-on, point, square leg", isDefault:true, active:true },
      { id:"def_fou_fie_060", label:"Wicketkeeping Stumpings — offside and legside", description:"Gather and break wickets - body/hands position, ball and batter anticipation", isDefault:true, active:true },
      { id:"def_fou_fie_061", label:"Wicketkeeping Runouts", description:"Calling for ball, fielder awareness, ball anticipation and clean gather, break stumps", isDefault:true, active:true },
    ],
    "Mental": [
      { id:"def_fou_men_081", label:"Concentration, Focus and Intent", description:"Maintaining focus and Intent for the duration of an innings or spell, learning how to switch on/off", isDefault:true, active:true },
      { id:"def_fou_men_082", label:"Simple Goal Setting", description:"Setting small, achievable personal goals for each session or match", isDefault:true, active:true },
      { id:"def_fou_men_083", label:"Dealing With Mistakes", description:"Letting go of errors, refocusing on the next ball", isDefault:true, active:true },
      { id:"def_fou_men_084", label:"Pre-Match Routine", description:"Consistent warm-up, kit preparation, and mental readiness habits", isDefault:true, active:true },
      { id:"def_fou_men_085", label:"Confidence Building", description:"Drawing confidence from training, preparation, and past successes", isDefault:true, active:true },
      { id:"def_fou_men_086", label:"Understanding Fair Play", description:"Appreciating the spirit of cricket beyond just the laws", isDefault:true, active:true },
    ],
    "Physical": [
      { id:"def_fou_phy_105", label:"Core Strength Basics", description:"Planks, bridges, dead bugs, foundational core stability exercises", isDefault:true, active:true },
      { id:"def_fou_phy_106", label:"Upper Body Basics", description:"Push-ups, light resistance bands, building shoulder and arm strength", isDefault:true, active:true },
      { id:"def_fou_phy_107", label:"Lower Body Basics", description:"Squats, lunges, calf raises, building leg strength for running and bowling", isDefault:true, active:true },
      { id:"def_fou_phy_108", label:"Balance Exercises", description:"Single-leg stands, wobble board, improving balance", isDefault:true, active:true },
      { id:"def_fou_phy_109", label:"Injury Prevention Basics", description:"Understanding the importance of warm-up, cool-down, and rest days", isDefault:true, active:true },
    ],
  },
  "Intermediate": {
    "Batting": [
      { id:"def_int2_bat_013", label:"Trigger Movement", description:"Feet positioning prior to bowler delivery - head still, body in balance", isDefault:true, active:true },
      { id:"def_int2_bat_014", label:"Ball length and line judgement", description:"Early ball recognition, feet and body positioning for shot", isDefault:true, active:true },
      { id:"def_int2_bat_015", label:"Pull Shot", description:"Hands position, feet position, weight transfer, balance and timing", isDefault:true, active:true },
      { id:"def_int2_bat_016", label:"Batting with intent", description:"Maintain strike rate, rotating strike, calling ones and twos, counting balls and runs", isDefault:true, active:true },
      { id:"def_int2_bat_017", label:"Footwork Against Spin", description:"Using feet to advance or going deep in the crease for shot making", isDefault:true, active:true },
      { id:"def_int2_bat_018", label:"Finding Field Gaps", description:"Reading the Field, anticipating bowling length and line, working ball into gaps", isDefault:true, active:true },
    ],
    "Bowling": [
      { id:"def_int2_bow_036", label:"Changing Pace", description:"Varying ball speed - length and line consistency", isDefault:true, active:true },
      { id:"def_int2_bow_037", label:"Bowling to Right-Handers and Left-Handers", description:"Adjusting line and angle for different-handed batters", isDefault:true, active:true },
      { id:"def_int2_bow_038", label:"Over the Wicket / Around the Wicket", description:"Coming over or around the wicket - effect on line and length, angle of delivery", isDefault:true, active:true },
      { id:"def_int2_bow_039", label:"Seam Position Variations", description:"Upright and scrambled seam effects. Shining ball and using shiny side", isDefault:true, active:true },
      { id:"def_int2_bow_040", label:"Older Ball medium pace", description:"Length and line, bowling Cutters and slower deliveries", isDefault:true, active:true },
      { id:"def_int2_bow_041", label:"Spin - Variations", description:"Stock ball versus Googly, Top Spin, Arm Ball, Straighter/Faster ball", isDefault:true, active:true },
    ],
    "Fielding": [
      { id:"def_int2_fie_062", label:"Intensity and pressuring batter", description:"Attacking the ball, encouraging bowler and fielders, quick gather and release", isDefault:true, active:true },
      { id:"def_int2_fie_063", label:"Batter position awareness, run anticipation", description:"Awareness of batter intent, throwing to correct end for run out, anticipating and restricting ones/twos", isDefault:true, active:true },
      { id:"def_int2_fie_064", label:"Wicketkeeping Batter Analysis and Adjustments", description:"Batter analysis, awareness of batter movements, ball and shot anticipation", isDefault:true, active:true },
      { id:"def_int2_fie_065", label:"Wicketkeeping Running to wicket, live ball v dead ball", description:"Ball anticipation, batter/non-striker position awareness, glove off for ball throw to wicket", isDefault:true, active:true },
      { id:"def_int2_fie_066", label:"Wicketkeeping appealing — judging LBW and Catches", description:"Judging Ball line and height for LBW, appealing and supporting bowler for clarity", isDefault:true, active:true },
      { id:"def_int2_fie_067", label:"Wicketkeeping Diving and Rolling", description:"Hands under ball, rolling to break dives, clean catching two hands", isDefault:true, active:true },
    ],
    "Mental": [
      { id:"def_int2_men_087", label:"Positive Self-Talk, Encouraging teammates", description:"Using internal dialogue to maintain confidence and focus under stress", isDefault:true, active:true },
      { id:"def_int2_men_088", label:"Match Awareness", description:"Understanding the state of the game, required rate, overs left, match dynamics", isDefault:true, active:true },
      { id:"def_int2_men_089", label:"Emotional Control", description:"Managing frustration, excitement, and anger during a match", isDefault:true, active:true },
      { id:"def_int2_men_090", label:"Resilience and Bouncing Back", description:"Recovering from a poor performance or a run of low scores", isDefault:true, active:true },
      { id:"def_int2_men_091", label:"Competition Mindset", description:"Embracing competitive situations as opportunities rather than threats", isDefault:true, active:true },
      { id:"def_int2_men_092", label:"Focus on Strengths not on Outcomes", description:"Focusing on execution of skills rather than worrying about results", isDefault:true, active:true },
      { id:"def_int2_men_093", label:"Managing Nerves", description:"Simple breathing techniques to calm pre-match or pre-innings anxiety", isDefault:true, active:true },
      { id:"def_int2_men_094", label:"Handling Setbacks (Injury / Non-Selection)", description:"Coping strategies for extended periods out of the team or injured", isDefault:true, active:true },
    ],
    "Physical": [
      { id:"def_int2_phy_110", label:"Nutrition Basics", description:"Understanding macronutrients, hydration, and pre/post-match nutrition", isDefault:true, active:true },
      { id:"def_int2_phy_111", label:"Recovery Techniques", description:"Foam rolling, stretching, sleep hygiene, and active recovery", isDefault:true, active:true },
      { id:"def_int2_phy_112", label:"Hydration and Wellness", description:"Understanding fluid intake needs during training and matches", isDefault:true, active:true },
      { id:"def_int2_phy_113", label:"Throwing Arm Conditioning", description:"Shoulder strengthening and rotator cuff exercises for injury prevention", isDefault:true, active:true },
    ],
  },
  "Advanced": {
    "Batting": [
      { id:"def_adv_bat_019", label:"Power Hitting, Clean Striking", description:"Generating maximum bat speed through the ball for six-hitting", isDefault:true, active:true },
      { id:"def_adv_bat_020", label:"Scoring Against Defensive Fields", description:"Manufacturing runs through innovation when fields are spread", isDefault:true, active:true },
      { id:"def_adv_bat_021", label:"Advanced Shot Placement", description:"Deliberately targeting field areas with placement and timing", isDefault:true, active:true },
      { id:"def_adv_bat_022", label:"Batting With the Tail", description:"Shielding lower-order batters, farming the strike", isDefault:true, active:true },
      { id:"def_adv_bat_023", label:"Situation-Specific Batting", description:"Powerplay aggression, middle-overs rotation, death-overs finishing", isDefault:true, active:true },
      { id:"def_adv_bat_024", label:"Batting in Various Positions (Opener / Middle / Finisher)", description:"Adapting technique and tempo to batting role", isDefault:true, active:true },
    ],
    "Bowling": [
      { id:"def_adv_bow_042", label:"Powerplay Bowling", description:"Bowling with the new ball and fielding restrictions versus attacking batter", isDefault:true, active:true },
      { id:"def_adv_bow_043", label:"Spin Flight and Loop", description:"Ball Flight with spin and length to land for confusing batters", isDefault:true, active:true },
      { id:"def_adv_bow_044", label:"Death Bowling", description:"Executing yorkers, slower balls, and wide bouncers in the final overs", isDefault:true, active:true },
      { id:"def_adv_bow_045", label:"Reading batter strength and weakness", description:"Bowling deliveries to restrict or attack", isDefault:true, active:true },
      { id:"def_adv_bow_046", label:"Setting Fields and Bowling to Field Placements", description:"Setting Fields to batter and situation, Adjusting line and length to match field placement", isDefault:true, active:true },
      { id:"def_adv_bow_047", label:"Bowling with Intent", description:"Executing under pressure, targeting specific areas, key innings phases", isDefault:true, active:true },
    ],
    "Fielding": [
      { id:"def_adv_fie_068", label:"Intercepting and Throwing in One Motion", description:"Attacking the ball and releasing the throw in a single fluid action", isDefault:true, active:true },
      { id:"def_adv_fie_069", label:"Hard Catching — inside ring, on the ring and out field", description:"Anticipation, batter awareness of shot (based on ball length/line), take catch, release ball to stumps", isDefault:true, active:true },
      { id:"def_adv_fie_070", label:"Direct Hit, Run Out", description:"Hitting the stumps directly from various angles and distances", isDefault:true, active:true },
      { id:"def_adv_fie_071", label:"Wicketkeeping, Standing Up to Medium Pace", description:"Keeping up to the stumps for medium pacers, quick reaction", isDefault:true, active:true },
      { id:"def_adv_fie_072", label:"Wicketkeeping, Runout versus Stumping for No-Balls", description:"Awareness of Runout versus Stumping for No-Balls", isDefault:true, active:true },
      { id:"def_adv_fie_073", label:"Wicketkeeping Runouts after stumps displaced", description:"Uprooting stumps, replacing stump and uprooting for runouts", isDefault:true, active:true },
      { id:"def_adv_fie_074", label:"Wicketkeeping Field awareness vs batter, one glove off", description:"Field position awareness, field adjustments for each batter, one glove off for effecting runouts", isDefault:true, active:true },
    ],
    "Mental": [
      { id:"def_adv_men_095", label:"Reading Game Situations", description:"Sensing momentum shifts and responding appropriately", isDefault:true, active:true },
      { id:"def_adv_men_096", label:"Visualisation Techniques", description:"Mentally rehearsing shots, deliveries, or match scenarios before performing", isDefault:true, active:true },
      { id:"def_adv_men_097", label:"Strategic Thinking", description:"Thinking multiple overs ahead, anticipating opposition moves", isDefault:true, active:true },
      { id:"def_adv_men_098", label:"Managing External Expectations", description:"Handling pressure from parents, peers, coaches and friends", isDefault:true, active:true },
      { id:"def_adv_men_099", label:"Focus and Refocus Techniques", description:"Using trigger words, routines, or cues to regain concentration", isDefault:true, active:true },
    ],
    "Physical": [
      { id:"def_adv_phy_114", label:"Speed and Endurance advanced drills", description:"Sprints, HIIT, developing acceleration and top-end speed, Long distance endurance", isDefault:true, active:true },
      { id:"def_adv_phy_115", label:"Periodised Training Programs", description:"Structured pre-season, in-season, and off-season training plans", isDefault:true, active:true },
      { id:"def_adv_phy_116", label:"Sport-Specific Strength and Conditioning", description:"Gym programs designed specifically for cricket demands (bowling, batting, fielding)", isDefault:true, active:true },
      { id:"def_adv_phy_117", label:"Injury Prevention and Management", description:"Prehab exercises, understanding common cricket injuries, managing workload", isDefault:true, active:true },
    ],
  },
};

// ═══════════════════════════════════════════════════════════════════════════════
// INITIAL SEED DATA
// ═══════════════════════════════════════════════════════════════════════════════
// ── Empty DB template — used to initialise a brand new school registration ──
function emptySchoolDb(orgId) {
  return {
    coaches: [],
    students: [],
    skillLibraries: { [orgId]: JSON.parse(JSON.stringify(DEFAULT_SKILL_LIBRARY)) },
    assessments: {},
    goals: {},
    sessions: {},
    coachingNotes: {},
    skillNotes: {},
    camps: {},
  };
}

// INITIAL_DATA is no longer used at runtime — kept only as a reference shape.
// New schools are created via the Register School flow.
const INITIAL_DATA = null;

// ═══════════════════════════════════════════════════════════════════════════════
// SUPERADMIN — ArcGain LLC owner credentials (never shown in UI, hardcoded only)
// Change these values before deploying to production
// ═══════════════════════════════════════════════════════════════════════════════
const SUPERADMIN_EMAIL    = "superadmin@arcgain.com";
const SUPERADMIN_PASSWORD = "ArcGain@CricKit#2024!SA";

// ═══════════════════════════════════════════════════════════════════════════════
// STORAGE — DynamoDB via Lambda API
// Set REACT_APP_API_URL and REACT_APP_API_SECRET in your Amplify environment variables
// REACT_APP_ORG_ID is NO LONGER NEEDED — orgId is resolved dynamically by email at login
// ═══════════════════════════════════════════════════════════════════════════════
const API_URL    = "https://45ai3y9e12.execute-api.us-east-1.amazonaws.com";
const API_SECRET = "CricKitSecure2024";
const STORE_KEY  = "crickit_appdata";    // FIXED key — never changes between versions
// CrickitCoach logo — embedded as base64 for offline/PWA compatibility
const LOGO_B64 = "data:image/jpeg;base64,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";
// Billing API helpers — SuperAdmin only
async function fetchBillingAll(){
  if(!API_URL||!API_SECRET) return [];
  try{
    const r=await fetch(`${API_URL}/billing?all=1`,{headers:{"Authorization":`Bearer ${API_SECRET}`}});
    if(!r.ok) return [];
    return await r.json();
  }catch(e){ console.warn("fetchBillingAll failed",e.message); return []; }
}
async function fetchBillingOne(orgId){
  if(!API_URL||!API_SECRET||!orgId) return null;
  try{
    const r=await fetch(`${API_URL}/billing?orgId=${encodeURIComponent(orgId)}`,{headers:{"Authorization":`Bearer ${API_SECRET}`}});
    if(!r.ok) return null;
    return await r.json();
  }catch(e){ return null; }
}
async function saveBilling(orgId,data){
  if(!API_URL||!API_SECRET||!orgId) return false;
  try{
    const r=await fetch(`${API_URL}/billing?orgId=${encodeURIComponent(orgId)}`,{
      method:"PUT",
      headers:{"Authorization":`Bearer ${API_SECRET}`,"Content-Type":"application/json"},
      body:JSON.stringify(data),
    });
    return r.ok;
  }catch(e){ return false; }
}
// NOTE: ORG_ID env var is gone. Each login resolves its own orgId via /db?lookup=email
// The active orgId is stored in localStorage under STORE_KEY alongside the db.

// ── Schema migration — runs on every load to backfill fields added in later versions ──
// This makes v3.7 fully backwards compatible with data saved by v3.1–v3.6.
// Safe to call repeatedly — only adds missing fields, never overwrites existing data.
function migrateDb(db) {
  if (!db) return db;

  // v3.5: student status field (inactive/active)
  if (db.students) {
    db.students = db.students.map(s => ({
      status: "active",          // default: active
      naSkills: [],              // v3.7: no N/A skills by default
      ...s,                      // existing fields override defaults above
    }));
  }

  // v3.8: coach role field — backfill existing coaches as "coach"
  // (first coach created via Register School gets role:"admin" explicitly)
  if (db.coaches) {
    db.coaches = db.coaches.map(c => ({ role:"coach", status:"active", ...c }));
  }

  // v3.13: reportHistory — backfill empty arrays for all existing students
  if (db.students) {
    db.students = db.students.map(s => ({ reportHistory: [], ...s }));
  }

  // v3.19: schoolStatus — backfill "active" for all existing schools
  if (!db.schoolStatus)     db.schoolStatus     = "active";
  if (!db.squadVisibility)  db.squadVisibility  = "private"; // "private" | "shared"
  // Ensure all students have linkedPlayerIds array (for parent/sibling accounts)
  if(db.students) db.students=db.students.map(s=>({linkedPlayerIds:[],...s}));

  // v3.7: coachingNotes table — backfill empty arrays for all existing students
  if (!db.coachingNotes) db.coachingNotes = {};
  if (!db.skillNotes) db.skillNotes = {};
  if (!db.coachLastViewedAt) db.coachLastViewedAt = {};
  if (!db.studentLastViewedAt) db.studentLastViewedAt = {};
  if (!db.camps) db.camps = {};
  if (db.students) {
    db.students.forEach(s => {
      if (!db.coachingNotes[s.id]) db.coachingNotes[s.id] = [];
    });
  }

  // v3.1: skillLibraries — backfill if missing (very old data)
  if (!db.skillLibraries) {
    db.skillLibraries = {
      org1: JSON.parse(JSON.stringify(DEFAULT_SKILL_LIBRARY)),
      org2: JSON.parse(JSON.stringify(DEFAULT_SKILL_LIBRARY)),
    };
  }

  // Ensure goals/sessions/assessments exist for every student
  if (db.students) {
    db.students.forEach(s => {
      if (!db.assessments[s.id]) db.assessments[s.id] = [];
      if (!db.goals[s.id])       db.goals[s.id]       = [];
      if (!db.sessions[s.id])    db.sessions[s.id]    = [];
    });
  }

  return db;
}

// Legacy localStorage keys from old versioned releases — checked as fallback
const LEGACY_STORE_KEYS = [
  "crickit_v311_cache","crickit_v310_cache","crickit_v39_cache",
  "crickit_v38_cache","crickit_v37_cache",
];

// ── Get the active orgId from localStorage (set after login) ─────────────────
function getStoredOrgId() {
  try { return localStorage.getItem("crickit_active_orgId") || ""; } catch(e) { return ""; }
}
function setStoredOrgId(orgId) {
  try { localStorage.setItem("crickit_active_orgId", orgId); } catch(e) {}
}

// ── Step 1 of login: look up which orgId belongs to this email ────────────────
// Uses GET /db?lookup=<email> — the SAME /db route already in API Gateway.
// No new API Gateway route or Lambda path needed.
// Returns { orgId, role } or null if email not found.
async function lookupEmailOrgId(email) {
  if (!API_URL || !API_SECRET) return null;
  try {
    const res = await fetch(
      `${API_URL}/db?lookup=${encodeURIComponent(email.toLowerCase().trim())}`,
      { headers: { "Authorization": `Bearer ${API_SECRET}` } }
    );
    if (res.status === 404) return null;
    if (!res.ok) return null;
    const data = await res.json();
    return data && data.orgId ? data : null;
  } catch(e) {
    console.warn("Email lookup failed:", e.message);
    return null;
  }
}

// ── Load school data from DynamoDB by orgId ───────────────────────────────────
async function loadDbByOrgId(orgId) {
  if (!API_URL || !API_SECRET || !orgId) return null;
  try {
    const res = await fetch(
      `${API_URL}/db?orgId=${encodeURIComponent(orgId)}`,
      { headers: { "Authorization": `Bearer ${API_SECRET}` } }
    );
    if (!res.ok) return null;
    const data = await res.json();
    if (data && data.coaches) {
      const migrated = migrateDb(data);
      try { localStorage.setItem(STORE_KEY, JSON.stringify(migrated)); } catch(e) {}
      setStoredOrgId(orgId);
      return migrated;
    }
    return null;
  } catch(e) {
    console.warn("loadDbByOrgId failed:", e.message);
    return null;
  }
}

// ── Initial load on app start: use cached orgId + localStorage ────────────────
// On a fresh device this returns null (user must log in to fetch their school data).
// On a returning device this loads the cached data and refreshes from DynamoDB.
async function loadFromStorage() {
  // 1. Try DynamoDB using the orgId cached from the last login on this device
  const cachedOrgId = getStoredOrgId();
  if (cachedOrgId && API_URL && API_SECRET) {
    const fresh = await loadDbByOrgId(cachedOrgId);
    if (fresh) return fresh;
  }
  // 2. Try localStorage cache (offline fallback / no API configured)
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.coaches) return migrateDb(parsed);
    }
  } catch(e) {}
  // 3. Try legacy version-numbered keys (upgrade path from very old versions)
  for (const legacyKey of LEGACY_STORE_KEYS) {
    try {
      const raw = localStorage.getItem(legacyKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.coaches) {
          const migrated = migrateDb(parsed);
          try { localStorage.setItem(STORE_KEY, JSON.stringify(migrated)); localStorage.removeItem(legacyKey); } catch(e) {}
          return migrated;
        }
      }
    } catch(e) {}
  }
  return null;
}

// ── Save school data to DynamoDB + localStorage ───────────────────────────────
async function saveToStorage(db, orgId) {
  if (!db || !db.coaches || !Array.isArray(db.coaches)) {
    console.warn("CricKit: saveToStorage blocked — db is null or invalid");
    return;
  }
  const payload = JSON.stringify(db);
  try { localStorage.setItem(STORE_KEY, payload); } catch(e) {}
  if (API_URL && API_SECRET && orgId) {
    try {
      await fetch(`${API_URL}/db?orgId=${encodeURIComponent(orgId)}`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${API_SECRET}`, "Content-Type": "application/json" },
        body: payload,
      });
    } catch(e) {
      console.warn("API save failed (data safe in local cache):", e.message);
    }
  }
}

// ── Record student last login directly to DynamoDB (bypasses state system) ────
async function recordStudentLogin(orgId, studentId) {
  if (!API_URL || !API_SECRET || !orgId || !studentId) return;
  try {
    // Load current db, patch the student, save back
    const r = await fetch(`${API_URL}/db?orgId=${encodeURIComponent(orgId)}`, {
      headers: { "Authorization": `Bearer ${API_SECRET}` }
    });
    if (!r.ok) return;
    const current = await r.json();
    if (!current || !current.students) return;
    current.students = current.students.map(s =>
      s.id === studentId ? { ...s, lastLoginAt: new Date().toISOString() } : s
    );
    await fetch(`${API_URL}/db?orgId=${encodeURIComponent(orgId)}`, {
      method: "PUT",
      headers: { "Authorization": `Bearer ${API_SECRET}`, "Content-Type": "application/json" },
      body: JSON.stringify(current),
    });
  } catch(e) {
    console.warn("recordStudentLogin failed:", e.message);
  }
}


const ratingAvg = scores => {
  const vals = Object.values(scores||{}).filter(v=>typeof v==="number"&&v>0);
  return vals.length ? +(vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(1) : 0;
};
const catAvgFromAssessment = (assessment, cat) => ratingAvg(assessment.scores?.[cat]||{});
const overallFromAssessment = a => {
  const catAvgs = CATEGORIES.map(c=>catAvgFromAssessment(a,c)).filter(v=>v>0);
  return catAvgs.length ? +(catAvgs.reduce((x,y)=>x+y,0)/catAvgs.length).toFixed(1) : 0;
};
const getActiveSkills = (lib, cls, cat) => (lib?.[cls]?.[cat]||[]).filter(s=>s.active);
const getOrgLibrary   = (db, orgId)     => db.skillLibraries?.[orgId] || DEFAULT_SKILL_LIBRARY;

// ── Build a report snapshot for a student from current live db data ─────────
// Mirrors the shape ProgressReportOverlay.handleSave produces, so it renders
// identically whether generated interactively or in bulk (e.g. camp close).
function buildReportSnapshot(student, db){
  const finalAssessments = (db.assessments[student.id]||[]).sort((a,b)=>new Date(a.date)-new Date(b.date));
  const finalSessions    = (db.sessions[student.id]||[]).sort((a,b)=>new Date(b.date)-new Date(a.date));
  const finalGoals       = (db.goals[student.id]||[]);
  const coach = db.coaches.find(c=>c.id===student.coachId);
  const schoolName = coach?.schoolName||"";
  const lt=finalAssessments[finalAssessments.length-1];
  return {
    id:"rpt_"+Date.now()+"_"+student.id,
    generatedAt:new Date().toISOString(),
    label:`Report — ${dateStr(new Date().toISOString().split('T')[0])} at ${new Date().toTimeString().slice(0,5)}`,
    sessionCount:finalSessions.length,
    assessmentCount:finalAssessments.length,
    goalCount:finalGoals.length,
    overallScore:lt?(lt.overall||overallFromAssessment(lt)):null,
    studentName:student.name,
    coachName:coach?.name||"",
    schoolName,
    frozenSessions:JSON.parse(JSON.stringify(finalSessions)),
    frozenAssessments:JSON.parse(JSON.stringify(finalAssessments)),
    frozenGoals:JSON.parse(JSON.stringify(finalGoals)),
    frozenStudent:JSON.parse(JSON.stringify({...student,name:student.name,cricketClass:student.cricketClass,tags:student.tags,avatar:student.avatar})),
    frozenCoach:{name:coach?.name||"",schoolName:schoolName},
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// CSS
// ═══════════════════════════════════════════════════════════════════════════════
const CSS = `
  *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
  :root{
    --bg:#f0f4f8;--surface:#ffffff;--surface2:#e8edf5;--surface3:#d8e2ef;
    --border:#94a3b8;--accent:#0369a1;--accent2:#c2410c;--accent3:#15803d;
    --text:#0a0f1a;--text2:#1e293b;--text3:#475569;
    --danger:#b91c1c;--warn:#c2410c;--gold:#92400e;--purple:#6d28d9;
    --font-display:'Bebas Neue',sans-serif;
    --font-body:'DM Sans',sans-serif;
    --font-mono:'JetBrains Mono',monospace;
    --radius:16px;--radius-sm:10px;--radius-xs:6px;
    --glow:0 2px 20px rgba(3,105,161,0.22);
    --shadow:0 4px 20px rgba(0,0,0,0.13);
  }
  html,body{background:#f0f4f8 !important;margin:0;padding:0}
  body{color:var(--text);font-family:var(--font-body);overflow:hidden}
  #app-shell{height:100svh;display:flex;flex-direction:column;max-width:440px;margin:0 auto;background:var(--bg);position:relative;padding-top:env(safe-area-inset-top,0px);}
  ::-webkit-scrollbar{width:3px;height:3px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:var(--border);border-radius:99px}
  input,textarea,select{background:var(--surface2);border:1.5px solid var(--border);color:var(--text);font-family:var(--font-body);font-size:14px;border-radius:var(--radius-sm);padding:11px 14px;width:100%;outline:none;transition:border-color .2s}
  input:focus,textarea:focus,select:focus{border-color:var(--accent);box-shadow:0 0 0 3px rgba(3,105,161,0.15)}
  button{cursor:pointer;font-family:var(--font-body);border:none;outline:none;transition:all .15s}
  button:active{transform:scale(0.96)}
  .screen{flex:1;overflow-y:auto;overflow-x:hidden;-webkit-overflow-scrolling:touch;padding-bottom:120px;padding-left:8px;padding-right:8px;padding-top:4px}
  .screen-hdr{padding:20px 16px 10px;background:linear-gradient(180deg,var(--bg) 85%,transparent)}
  .card{background:var(--surface);border:1.5px solid var(--border);border-radius:var(--radius);padding:14px;margin-bottom:10px}
  .card-hl{background:linear-gradient(135deg,var(--surface),var(--surface2));border-color:var(--accent);border-width:2px;box-shadow:var(--glow)}
  .btn-primary{background:linear-gradient(135deg,var(--accent),#0c4a7a);color:#fff;font-weight:700;font-size:14px;border-radius:var(--radius-sm);padding:13px 20px;width:100%;letter-spacing:.3px}
  .btn-secondary{background:var(--surface);color:var(--text);font-weight:600;font-size:13px;border-radius:var(--radius-sm);padding:10px 16px;border:1.5px solid var(--border)}
  .btn-danger{background:linear-gradient(135deg,#c62828,#b71c1c);color:#fff;font-weight:600;font-size:13px;border-radius:var(--radius-sm);padding:10px 16px}
  .btn-ghost{background:transparent;color:var(--text2);font-size:11px;padding:5px 9px;border-radius:var(--radius-xs);border:1.5px solid var(--border)}
  .btn-sm{font-size:12px;padding:6px 12px;border-radius:var(--radius-xs)}
  .badge{display:inline-flex;align-items:center;gap:3px;padding:2px 9px;border-radius:99px;font-size:10px;font-weight:700;letter-spacing:.5px;text-transform:uppercase}
  .top-bar{position:relative;z-index:90;background:var(--navy,#0B1F3A);display:flex;align-items:center;justify-content:space-between;padding:0 14px;height:48px;flex-shrink:0;}
  .top-bar-school{font-size:11px;font-weight:700;color:rgba(255,255,255,0.55);letter-spacing:.5px;text-transform:uppercase;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:55%;}
  .top-bar-tab{font-size:14px;font-weight:900;color:#ffffff;letter-spacing:1px;text-transform:uppercase;font-family:var(--font-display);}
  .tab-bar{position:fixed;bottom:0;left:0;right:0;max-width:440px;margin:0 auto;background:rgba(255,255,255,0.98);border-top:2px solid var(--border);display:flex;padding:12px 8px env(safe-area-inset-bottom,16px);z-index:100;backdrop-filter:blur(20px)}
  .tab-item{flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;padding:10px 4px;cursor:pointer;transition:all .2s;border:none;background:transparent;color:var(--text3)}
  .tab-item.active{color:var(--accent);font-weight:700}
  .tab-icon{font-size:24px;line-height:1}.tab-label{font-size:10px;font-weight:700;letter-spacing:.5px;text-transform:uppercase}
  .stat-row{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-bottom:10px}
  .stat-box{background:var(--surface2);border:1px solid var(--border);border-radius:var(--radius-sm);padding:10px 6px;text-align:center}
  .stat-val{font-family:var(--font-display);font-size:26px;color:var(--accent);line-height:1}
  .stat-lbl{font-size:9px;font-weight:700;color:var(--text2);text-transform:uppercase;letter-spacing:.5px;margin-top:2px}
  .pb-track{height:6px;background:var(--surface3);border-radius:99px;overflow:hidden}
  .pb-fill{height:100%;border-radius:99px;transition:width .5s ease}
  .sec-title{font-family:var(--font-display);font-size:21px;letter-spacing:1px;margin-bottom:10px;color:var(--text)}
  .lbl{font-size:12px;font-weight:700;color:var(--text);text-transform:uppercase;letter-spacing:.6px;margin-bottom:6px}
  .avatar{display:flex;align-items:center;justify-content:center;border-radius:50%;font-family:var(--font-display);font-weight:700;flex-shrink:0}
  .chip{display:inline-flex;align-items:center;padding:3px 10px;border-radius:99px;font-size:11px;font-weight:600;border:1.5px solid}
  .divider{height:1.5px;background:var(--border);margin:12px 0}
  .list-item{display:flex;align-items:center;gap:10px;padding:12px 0;border-bottom:1px solid var(--border)}
  .list-item:last-child{border-bottom:none}
  .overlay{position:fixed;inset:0;background:rgba(0,0,0,0.82);z-index:200;display:flex;align-items:flex-end;animation:fadeIn .2s;touch-action:none;overscrollBehavior:contain}
  .sheet{background:var(--surface);border-radius:22px 22px 0 0;padding:20px 16px;max-height:82svh;overflow-y:auto;width:100%;animation:slideUp .3s ease}
  .handle{width:36px;height:4px;background:var(--border);border-radius:99px;margin:0 auto 18px}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes slideUp{from{transform:translateY(100%)}to{transform:translateY(0)}}
  @keyframes slideIn{from{transform:translateX(24px);opacity:0}to{transform:translateX(0);opacity:1}}
  .slide-in{animation:slideIn .2s ease}
  .notif{position:absolute;top:70px;left:12px;right:12px;background:var(--surface);border:2px solid var(--accent3);border-radius:var(--radius-sm);padding:12px 14px;z-index:300;animation:slideDown .3s ease;font-size:13px;font-weight:700;color:var(--text);box-shadow:var(--shadow)}
  .notif-err{border-color:var(--danger)}
  @keyframes slideDown{from{transform:translateY(-50px);opacity:0}to{transform:translateY(0);opacity:1}}
  .star{font-size:20px;cursor:pointer;transition:transform .15s;line-height:1}.star:hover{transform:scale(1.25)}
  .scroll-x{display:flex;gap:6px;overflow-x:auto;padding-bottom:4px;scrollbar-width:none}.scroll-x::-webkit-scrollbar{display:none}
  select option{background:var(--surface)}
  textarea{resize:none;min-height:70px}
  .empty{text-align:center;padding:36px 16px;color:var(--text2)}
  .empty-icon{font-size:44px;margin-bottom:10px}
  .pill-row{display:flex;gap:5px;flex-wrap:wrap;margin-bottom:10px}
  .pill{padding:5px 12px;border-radius:99px;font-size:11px;font-weight:700;cursor:pointer;transition:all .2s;border:1.5px solid var(--border);background:transparent;color:var(--text)}
  .pill.active{background:var(--accent);border-color:var(--accent);color:#fff}
  .class-badge{padding:2px 10px;border-radius:99px;font-size:10px;font-weight:800;letter-spacing:.5px;text-transform:uppercase;display:inline-block}
  /* skill lib rows */
  .sk-row{display:flex;align-items:flex-start;gap:8px;padding:10px 4px;border-bottom:1px solid var(--border);transition:background .15s}
  .sk-row:last-child{border-bottom:none}
  .sk-inactive{opacity:.4}
  .toggle{width:38px;height:21px;border-radius:99px;position:relative;cursor:pointer;transition:background .2s;flex-shrink:0}
  .toggle-thumb{width:17px;height:17px;border-radius:50%;background:#fff;position:absolute;top:2px;transition:left .2s}
  .drag{cursor:grab;color:var(--text3);font-size:16px;padding:2px 4px;flex-shrink:0;user-select:none;line-height:1}
  .drag:active{cursor:grabbing}
  /* tag chips */
  .tag-chip{display:inline-flex;align-items:center;gap:3px;padding:2px 8px;border-radius:99px;font-size:10px;font-weight:700;white-space:nowrap;border:1px solid transparent}
  /* squad intel */
  .si-group-hdr{background:var(--surface2);border:1.5px solid var(--border);border-radius:var(--radius-sm);padding:10px 14px;display:flex;align-items:center;gap:10px;cursor:pointer;margin-bottom:4px}
`;

// ═══════════════════════════════════════════════════════════════════════════════
// UI PRIMITIVES
// ═══════════════════════════════════════════════════════════════════════════════
function Toggle({on,onChange}){
  return <div className="toggle" style={{background:on?"var(--accent3)":"var(--border)"}} onClick={()=>onChange(!on)}><div className="toggle-thumb" style={{left:on?"19px":"2px"}}/></div>;
}
function StarInput({value,onChange,label,desc}){
  return (
    <div style={{marginBottom:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:3}}>
        <div className="lbl" style={{marginBottom:0}}>{label}</div>
        {desc&&<div style={{fontSize:9,color:"var(--text3)",maxWidth:"50%",textAlign:"right",lineHeight:1.3}}>{desc}</div>}
      </div>
      <div style={{display:"flex",gap:3,alignItems:"center"}}>
        {[1,2,3,4,5].map(s=><span key={s} className="star" onClick={()=>onChange(s)} style={{color:s<=value?"var(--gold)":"var(--surface3)"}}>★</span>)}
        <span style={{fontSize:11,color:"var(--text2)",marginLeft:4,fontFamily:"var(--font-mono)"}}>{value>0?`${value} — ${RATING_LABEL(value)}`:"tap"}</span>
      </div>
    </div>
  );
}
function ScoreRing({value,size=72,label}){
  const r=(size/2)-6,circ=2*Math.PI*r,pct=Math.min(value/5,1),c=RATING_COLOR(value);
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth="5"/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={c} strokeWidth="5" strokeDasharray={`${circ*pct} ${circ}`} strokeLinecap="round" transform={`rotate(-90 ${size/2} ${size/2})`}/>
        <text x={size/2} y={size/2+1} textAnchor="middle" dominantBaseline="middle" fontSize={size>64?14:11} fontWeight="700" fill={c} fontFamily="Bebas Neue">{Number(value).toFixed(1)}</text>
      </svg>
      {label&&<div style={{fontSize:9,color:"var(--text2)",textTransform:"uppercase",letterSpacing:".5px",textAlign:"center"}}>{label}</div>}
    </div>
  );
}
function RadarChart({scores,skills,color="#00d4ff"}){
  const keys=skills.map(s=>s.id);
  if(!keys.length) return null;  // correct guard: check actual skill count
  const n=Math.max(keys.length,3);
  const cx=110,cy=100,r=70;
  const angle=i=>(Math.PI*2*i/n)-Math.PI/2;
  const pt=(val,i)=>{const a=angle(i),v=(Math.max(val||0,0)/5)*r;return[cx+v*Math.cos(a),cy+v*Math.sin(a)];};
  const gp=lvl=>keys.map((_,i)=>{const a=angle(i),v=(lvl/5)*r;return`${cx+v*Math.cos(a)},${cy+v*Math.sin(a)}`;}).join(" ");
  const dps=keys.map((k,i)=>pt(scores[k]||0,i));
  const dp=dps.map((p,i)=>(i===0?"M":"L")+p[0].toFixed(1)+","+p[1].toFixed(1)).join(" ")+"Z";
  return(
    <div style={{position:"relative",width:"100%",paddingBottom:"82%",overflow:"hidden"}}>
      <svg viewBox="0 0 220 200" xmlns="http://www.w3.org/2000/svg" style={{position:"absolute",inset:0,width:"100%",height:"100%"}}>
        {[1,2,3,4,5].map(l=><polygon key={l} points={gp(l)} fill="none" stroke="rgba(0,0,0,0.07)" strokeWidth="1"/>)}
        {keys.map((_,i)=>{const[x,y]=pt(5,i);return<line key={i} x1={cx} y1={cy} x2={x.toFixed(1)} y2={y.toFixed(1)} stroke="rgba(0,0,0,0.07)" strokeWidth="1"/>;})}
        <path d={dp} fill={color+"28"} stroke={color} strokeWidth="2" strokeLinejoin="round"/>
        {dps.map((p,i)=><circle key={i} cx={p[0].toFixed(1)} cy={p[1].toFixed(1)} r="3.5" fill={color}/>)}
        {keys.map((k,i)=>{const[x,y]=pt(5.85,i);const lbl=(skills[i]?.label||"").split(" ")[0].substring(0,10);return<text key={k} x={x.toFixed(1)} y={y.toFixed(1)} textAnchor="middle" dominantBaseline="middle" fontSize="7.5" fill="rgba(0,0,0,0.45)" fontFamily="DM Sans">{lbl}</text>;})}
      </svg>
    </div>
  );
}
function MiniLine({data,color="#00d4ff"}){
  if(!data||data.length<2) return null;
  const vs=data.map(d=>d.y),mn=Math.min(...vs)-.5,mx=Math.max(...vs)+.5,W=240,H=54;
  const range=mx-mn||0.1;  // prevent divide-by-zero when all values identical
  const x=i=>(i/(data.length-1))*W, y=v=>H-((v-mn)/range)*H;
  const path=data.map((d,i)=>`${i===0?"M":"L"}${x(i)},${y(d.y)}`).join(" ");
  return(
    <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:54,display:"block"}}>
      <defs><linearGradient id={`lg${color.slice(1)}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity=".3"/><stop offset="100%" stopColor={color} stopOpacity="0"/></linearGradient></defs>
      <path d={`${path} L${W},${H} L0,${H} Z`} fill={`url(#lg${color.slice(1)})`}/>
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      {data.map((d,i)=>(
        <g key={i}>
          <circle cx={x(i)} cy={y(d.y)} r="3.5" fill={color}/>
          <text x={x(i)} y={H-1} textAnchor="middle" fontSize="6" fill="rgba(0,0,0,0.35)" fontFamily="DM Sans">{d.label}</text>
          <text x={x(i)} y={y(d.y)-6} textAnchor="middle" fontSize="7" fill={color} fontWeight="700" fontFamily="Bebas Neue">{d.y.toFixed(1)}</text>
        </g>
      ))}
    </svg>
  );
}
function GoalCard({goal,onUpdate,canEdit}){
  const cc={Batting:"#0284c7",Bowling:"#ea580c",Fielding:"#16a34a",Mental:"#7c3aed",Physical:"#b45309",General:"#64748b"};
  const c=cc[goal.category]||"#64748b",done=goal.status==="completed";
  return(
    <div className="card" style={{borderColor:done?"var(--accent3)":"var(--border)",marginBottom:8}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
        <div style={{flex:1,marginRight:6}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
            <span className="badge" style={{background:c+"18",color:c,border:`1.5px solid ${c}`}}>{goal.category}</span>
            {done&&<span className="badge" style={{background:"#dcfce7",color:"var(--accent3)",border:"1.5px solid var(--accent3)"}}>✓ Done</span>}
            {goal.isGroupGoal&&<span className="badge" style={{background:"#dbeafe",color:"var(--accent)",border:"1.5px solid var(--accent)"}}>👥 Squad</span>}
            {goal.createdBy==="coach"&&!goal.isGroupGoal&&<span style={{fontSize:9,color:"var(--text3)",fontWeight:600}}>📋 Coach</span>}
          </div>
          <div style={{fontSize:13,fontWeight:600,lineHeight:1.4,color:"var(--text)"}}>{goal.title}</div>
          <div style={{fontSize:10,color:"var(--text3)",marginTop:3}}>🎯 {dateStr(goal.targetDate)}</div>
        </div>
        <div style={{fontSize:20,fontWeight:700,color:done?"var(--accent3)":c,fontFamily:"var(--font-display)",minWidth:34,textAlign:"right"}}>{goal.progress}%</div>
      </div>
      <div className="pb-track"><div className="pb-fill" style={{width:`${goal.progress}%`,background:done?"var(--accent3)":c}}/></div>
      {canEdit&&!done&&(
        <div style={{marginTop:10,display:"flex",gap:5}}>
          {[25,50,75,100].map(v=><button key={v} className="btn-secondary btn-sm" style={{flex:1,minWidth:0,opacity:goal.progress>=v?0.4:1}} onClick={()=>onUpdate(goal.id,v)}>{v===100?"✓":"+"+v+"%"}</button>)}
        </div>
      )}
    </div>
  );
}

function ClassBadge({cls,size=10}){
  const c=CLASS_COLOR[cls]||"#888";
  return <span className="class-badge" style={{background:c+"22",color:c,border:`1.5px solid ${c}`,fontSize:size}}>{CLASS_ICON[cls]} {cls}</span>;
}
function TagChip({tagId}){
  const t=PLAYER_TAGS.find(x=>x.id===tagId);
  if(!t) return null;
  return <span className="tag-chip" style={{background:t.color+"20",color:t.color,borderColor:t.color+"40"}}>{t.icon} {t.id}</span>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// SKILL LIBRARY MANAGER  (per-org, per-class, per-category)
// ═══════════════════════════════════════════════════════════════════════════════
function SkillLibraryManager({db,orgId,updateLib,onBack,showNotif}){
  const lib=getOrgLibrary(db,orgId);
  const [cls,setCls]=useState("Introductory");
  const [cat,setCat]=useState("Batting");
  const [editing,setEditing]=useState(null);
  const [form,setForm]=useState({label:"",description:""});
  const [confirmDel,setConfirmDel]=useState(null);
  const [dragOver,setDragOver]=useState(null);
  const dragId=useRef(null);

  const skills=lib[cls]?.[cat]||[];
  const activeCount=(cl,ca)=>(lib[cl]?.[ca]||[]).filter(s=>s.active).length;
  const totalCount=(cl,ca)=>(lib[cl]?.[ca]||[]).length;

  const commitLib=(newLib)=>updateLib(orgId,newLib);

  const toggleSkill=(skillId)=>{
    const newLib=JSON.parse(JSON.stringify(lib));
    const idx=newLib[cls][cat].findIndex(s=>s.id===skillId);
    if(idx>=0) newLib[cls][cat][idx].active=!newLib[cls][cat][idx].active;
    commitLib(newLib);
  };
  const editSkill=(sk)=>{
    setForm({label:sk.label,description:sk.description||""});
    setEditing(sk);
  };
  const openNew=()=>{ setForm({label:"",description:""}); setEditing("new"); };
  const saveSkill=()=>{
    if(!form.label.trim()){showNotif("Skill name required","error");return;}
    const newLib=JSON.parse(JSON.stringify(lib));
    if(editing==="new"){
      newLib[cls][cat].push({
        id:`custom_${Date.now()}`,
        label:form.label.trim(),
        description:form.description.trim(),
        isDefault:false,
        active:true,
      });
      showNotif(`"${form.label.trim()}" added ✅`);
    } else {
      const idx=newLib[cls][cat].findIndex(s=>s.id===editing.id);
      if(idx>=0){ newLib[cls][cat][idx].label=form.label.trim(); newLib[cls][cat][idx].description=form.description.trim(); }
      showNotif("Skill updated ✅");
    }
    commitLib(newLib); setEditing(null);
  };
  const deleteSkill=(skillId)=>{
    const newLib=JSON.parse(JSON.stringify(lib));
    newLib[cls][cat]=newLib[cls][cat].filter(s=>s.id!==skillId);
    commitLib(newLib); setConfirmDel(null); showNotif("Custom skill removed");
  };

  const onDragStart=(e,sk)=>{dragId.current=sk.id;e.dataTransfer.effectAllowed="move";};
  const onDragOver=(e,sk)=>{e.preventDefault();setDragOver(sk.id);};
  const onDrop=(e,target)=>{
    e.preventDefault();
    const from=dragId.current; if(!from||from===target.id){setDragOver(null);return;}
    const newLib=JSON.parse(JSON.stringify(lib));
    const arr=newLib[cls][cat];
    const fi=arr.findIndex(s=>s.id===from),ti=arr.findIndex(s=>s.id===target.id);
    const[moved]=arr.splice(fi,1);arr.splice(ti,0,moved);
    commitLib(newLib);setDragOver(null);dragId.current=null;
  };

  const CTAB=CAT_COLOR;
  const CLTAB=CLASS_COLOR;

  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}} className="slide-in">
      {/* back + title */}
      <div style={{padding:"14px 16px 0",flexShrink:0}}>
        <button onClick={onBack} style={{background:"transparent",color:"var(--accent)",fontSize:13,fontWeight:700,padding:0,letterSpacing:.5,marginBottom:12}}>← Settings</button>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
          <div>
            <div style={{fontFamily:"var(--font-display)",fontSize:26,letterSpacing:1}}>Skill Library</div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Org-level · Changes apply to this school only</div>
          </div>
          <button className="btn-primary btn-sm" style={{minWidth:84}} onClick={openNew}>+ Add</button>
        </div>
        {/* Class tabs */}
        <div style={{display:"flex",gap:4,marginBottom:8}}>
          {CLASSES.map(cl=>(
            <button key={cl} onClick={()=>setCls(cl)} style={{flex:1,padding:"6px 2px",borderRadius:8,fontWeight:700,fontSize:10,letterSpacing:.3,
              background:cls===cl?CLTAB[cl]:"var(--surface2)",color:cls===cl?"#000":"var(--text2)",
              border:`1.5px solid ${cls===cl?CLTAB[cl]:"var(--border)"}`}}>
              {CLASS_ICON[cl]}<br/>{cl.substring(0,5)}
            </button>
          ))}
        </div>
        {/* Category tabs */}
        <div className="scroll-x" style={{marginBottom:0,gap:4}}>
          {CATEGORIES.map(ca=>(
            <button key={ca} onClick={()=>setCat(ca)} style={{flexShrink:0,padding:"5px 10px",borderRadius:8,fontWeight:700,fontSize:11,
              background:cat===ca?CTAB[ca]:"var(--surface2)",color:cat===ca?"#000":"var(--text2)",
              border:`1.5px solid ${cat===ca?CTAB[ca]:"var(--border)"}`}}>
              {CAT_ICON[ca]} {ca} <span style={{fontSize:9,opacity:.7}}>{activeCount(cls,ca)}/{totalCount(cls,ca)}</span>
            </button>
          ))}
        </div>
        <div className="divider" style={{margin:"8px 0 0"}}/>
      </div>

      {/* List */}
      <div className="screen" style={{padding:"6px 12px"}}>
        <div style={{fontSize:10,color:"var(--text3)",marginBottom:8}}>
          ⠿ drag to reorder · toggle to show/hide · default skills cannot be deleted
        </div>
        {skills.length===0&&(
          <div className="empty">
            <div className="empty-icon">{CAT_ICON[cat]}</div>
            <div style={{marginBottom:12}}>No {cat} skills in {cls} yet</div>
            <button className="btn-primary" style={{width:"auto",padding:"9px 20px"}} onClick={openNew}>Add First Skill</button>
          </div>
        )}
        {skills.map(sk=>(
          <div key={sk.id}
            className={`sk-row${!sk.active?" sk-inactive":""}`}
            style={{background:dragOver===sk.id?"var(--surface3)":"transparent",borderRadius:6,padding:"8px 4px"}}
            draggable onDragStart={e=>onDragStart(e,sk)} onDragOver={e=>onDragOver(e,sk)} onDrop={e=>onDrop(e,sk)} onDragLeave={()=>setDragOver(null)}>
            <span className="drag">⠿</span>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                <div style={{fontWeight:700,fontSize:13}}>{sk.label}</div>
                {sk.isDefault&&<span style={{fontSize:9,color:"var(--text3)",background:"var(--surface3)",padding:"1px 5px",borderRadius:4}}>default</span>}
                {!sk.active&&<span style={{fontSize:9,color:"var(--text3)",fontStyle:"italic"}}>off</span>}
              </div>
              {sk.description&&<div style={{fontSize:10,color:"var(--text3)",lineHeight:1.3,marginBottom:4}}>{sk.description}</div>}
              <div style={{display:"flex",gap:5}}>
                <button className="btn-ghost btn-sm" onClick={()=>editSkill(sk)}>✏️ Edit</button>
                {!sk.isDefault&&<button className="btn-ghost btn-sm" style={{color:"var(--danger)",borderColor:"var(--danger)40"}} onClick={()=>setConfirmDel(sk)}>🗑</button>}
              </div>
            </div>
            <Toggle on={sk.active} onChange={()=>toggleSkill(sk.id)}/>
          </div>
        ))}
        <div style={{background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,padding:"10px 12px",marginTop:14,fontSize:11,color:"var(--text3)",lineHeight:1.7}}>
          <strong style={{color:"var(--accent)"}}>ℹ️ Library Rules</strong><br/>
          Default skills can be toggled off but never deleted. Custom skills can be freely added, edited, reordered, or deleted. All changes apply only to this school — other schools are unaffected.
        </div>
      </div>

      {/* Edit / New modal */}
      {editing!==null&&(
        <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)setEditing(null);}}>
          <div className="sheet">
            <div className="handle"/>
            <div style={{fontFamily:"var(--font-display)",fontSize:24,color:"var(--accent)",marginBottom:14}}>
              {editing==="new"?"➕ New Skill":"✏️ Edit Skill"}
            </div>
            <div style={{marginBottom:12}}><div className="lbl">Skill Name *</div><input value={form.label} onChange={e=>setForm(f=>({...f,label:e.target.value}))} placeholder={`e.g. Reverse Sweep`}/></div>
            <div style={{marginBottom:18}}><div className="lbl">Coaching Notes (optional)</div>
              <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="What does this skill involve?"/>
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn-secondary" style={{flex:1}} onClick={()=>setEditing(null)}>Cancel</button>
              <button className="btn-primary" style={{flex:2}} onClick={saveSkill}>{editing==="new"?"Add Skill":"Save"}</button>
            </div>
          </div>
        </div>
      )}
      {confirmDel&&(
        <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)setConfirmDel(null);}}>
          <div className="sheet">
            <div className="handle"/>
            <div style={{fontFamily:"var(--font-display)",fontSize:22,color:"var(--danger)",marginBottom:8}}>Delete Skill?</div>
            <div style={{fontSize:13,color:"var(--text2)",marginBottom:18,lineHeight:1.5}}>Delete <strong style={{color:"var(--text)"}}>{confirmDel.label}</strong>? Historical assessment data is preserved.</div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn-secondary" style={{flex:1}} onClick={()=>setConfirmDel(null)}>Keep</button>
              <button className="btn-danger" style={{flex:1}} onClick={()=>deleteSkill(confirmDel.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SCHOOL VIEW  — cross-coach org-wide visibility
// Shows ALL students in the org, grouped by coach / class / specialty
// Accessible via the "School" tab (coach only)
// ═══════════════════════════════════════════════════════════════════════════════
function SchoolView({allStudents,allCoaches,db,setSubView}){
  // Views: "coach" | "class" | "specialty" | "search"
  const [view,setView]=useState("coach");
  // Filters applied across views
  const [classFilter,setClassFilter]=useState("All");
  const [tagFilter,setTagFilter]=useState(null);
  const [coachFilter,setCoachFilter]=useState("All");
  const [searchInput,setSearchInput]=useState("");
  const [expandedGroups,setExpandedGroups]=useState({});
  const toggleGroup=k=>setExpandedGroups(e=>({...e,[k]:!e[k]}));
  const [sortAZ,setSortAZ]=useState(true); // true=A→Z (default), false=age order

  // ── Cross-filter logic ──────────────────────────────────────────────────────
  const baseFiltered=[...allStudents
    .filter(s=>classFilter==="All"||s.cricketClass===classFilter)
    .filter(s=>coachFilter==="All"||s.coachId===coachFilter)
    .filter(s=>{
      if(!tagFilter) return true;
      const ids=TAG_GROUPS[tagFilter];
      return ids?(s.tags||[]).some(t=>ids.includes(t)):(s.tags||[]).includes(tagFilter);
    })
    .filter(s=>!searchInput.trim()||
      s.name.toLowerCase().includes(searchInput.toLowerCase())||
      (s.tags||[]).some(t=>t.toLowerCase().includes(searchInput.toLowerCase())))
  ].sort((a,b)=>sortAZ
      ? a.name.localeCompare(b.name)
      : (a.age||99)-(b.age||99));

  const getCoachName=id=>allCoaches.find(c=>c.id===id)?.name||"Unknown Coach";

  const VIEW_TABS=[
    ["coach","👨‍🏫","By Coach"],
    ["class","🎓","By Class"],
    ["specialty","🏷️","By Specialty"],
    ["search","🔍","Search"],
  ];

  // Reset coachFilter (search-only filter) when leaving search view
  const switchView=(v)=>{ setView(v); if(v!=="search") setCoachFilter("All"); };

  return(
    <div>
      <div className="screen-hdr">
        {/* Title row with A→Z sort button in top right */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:2}}>
          <div style={{display:"flex",alignItems:"baseline",gap:10}}>
            <div className="sec-title" style={{margin:0}}>School View</div>
            <div style={{fontSize:11,color:"var(--text3)"}}>{allStudents.length} players · {allCoaches.length} coaches</div>
          </div>
          <button
            onClick={()=>setSortAZ(s=>!s)}
            style={{flexShrink:0,padding:"5px 12px",borderRadius:99,fontSize:11,fontWeight:700,
              background:sortAZ?"var(--accent)":"var(--surface2)",
              color:sortAZ?"#fff":"var(--text3)",
              border:`1.5px solid ${sortAZ?"var(--accent)":"var(--border)"}`,
              cursor:"pointer",whiteSpace:"nowrap"}}>
            {sortAZ?"By Age":"A→Z ✓"}
          </button>
        </div>
        <div style={{fontSize:11,color:"var(--accent)",marginBottom:10}}>
          All students across your school
        </div>

        {/* View mode tabs — full width now sort is moved out */}
        <div className="scroll-x" style={{marginBottom:8,gap:4}}>
          {VIEW_TABS.map(([v,ic,lb])=>(
            <button key={v} className={`pill${view===v?" active":""}`}
              onClick={()=>switchView(v)} style={{flexShrink:0}}>
              {ic} {lb}
            </button>
          ))}
        </div>

        {/* Universal filters (shown in all views) */}
        {(view==="coach"||view==="class"||view==="specialty")&&(
          <div className="scroll-x" style={{gap:4,marginBottom:4}}>
            {/* Class filter */}
            <button className={`pill${classFilter==="All"?" active":""}`}
              onClick={()=>setClassFilter("All")} style={{flexShrink:0,fontSize:10}}>
              All Classes
            </button>
            {CLASSES.map(cl=>{
              const cnt=allStudents.filter(s=>s.cricketClass===cl).length;
              return cnt?(
                <button key={cl} className={`pill${classFilter===cl?" active":""}`}
                  onClick={()=>setClassFilter(f=>f===cl?"All":cl)}
                  style={{flexShrink:0,fontSize:10,
                    color:classFilter===cl?"#000":CLASS_COLOR[cl],
                    borderColor:CLASS_COLOR[cl]+"60",
                    background:classFilter===cl?CLASS_COLOR[cl]:"transparent"}}>
                  {CLASS_ICON[cl]} {cl} ({cnt})
                </button>
              ):null;
            })}
          </div>
        )}
        <div className="divider" style={{margin:"6px 0 0"}}/>
      </div>

      <div style={{padding:"0 8px"}}>

        {/* ── BY COACH ───────────────────────────────────────────────────────── */}
        {view==="coach"&&(
          <>
            {allCoaches.map(coach=>{
              const coachStudents=baseFiltered.filter(s=>s.coachId===coach.id);
              if(!coachStudents.length) return null;
              const expanded=expandedGroups[`cch_${coach.id}`]!==false;
              const clsBreak=CLASSES.map(cl=>coachStudents.filter(s=>s.cricketClass===cl).length);
              return(
                <div key={coach.id} style={{marginBottom:12}}>
                  {/* Coach header */}
                  <div className="si-group-hdr" onClick={()=>toggleGroup(`cch_${coach.id}`)}
                    style={{borderLeft:`3px solid var(--accent)`,paddingLeft:12}}>
                    <div className="avatar" style={{width:34,height:34,background:"linear-gradient(135deg,var(--accent2),#8a3000)",fontSize:12,color:"#fff",fontFamily:"var(--font-display)",flexShrink:0}}>
                      {coach.avatar}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:13}}>{coach.name}</div>
                      <div style={{fontSize:10,color:"var(--text3)"}}>{coach.team}</div>
                      {/* Mini class breakdown bar */}
                      <div style={{display:"flex",gap:3,marginTop:4,flexWrap:"wrap"}}>
                        {CLASSES.map((cl,i)=>clsBreak[i]>0?(
                          <span key={cl} style={{fontSize:9,padding:"1px 6px",borderRadius:99,
                            background:CLASS_COLOR[cl]+"22",color:CLASS_COLOR[cl],
                            border:`1px solid ${CLASS_COLOR[cl]}44`,fontWeight:700}}>
                            {CLASS_ICON[cl]}{clsBreak[i]}
                          </span>
                        ):null)}
                      </div>
                    </div>
                    <div style={{fontSize:12,color:"var(--text2)",fontFamily:"var(--font-mono)",flexShrink:0}}>
                      {coachStudents.length} player{coachStudents.length!==1?"s":""}
                    </div>
                    <span style={{color:"var(--text3)",marginLeft:6}}>{expanded?"▾":"▸"}</span>
                  </div>
                  {expanded&&coachStudents.map(s=><StudentRow key={s.id} s={s} db={db} setSubView={setSubView} fromTab="school"/>)}
                </div>
              );
            })}
            {baseFiltered.length===0&&<div className="empty"><div className="empty-icon">👨‍🏫</div><div>No players match the selected filters</div></div>}
          </>
        )}

        {/* ── BY CLASS ───────────────────────────────────────────────────────── */}
        {view==="class"&&(
          <>
            {CLASSES.map(cl=>{
              const clsStudents=baseFiltered.filter(s=>s.cricketClass===cl);
              if(!clsStudents.length) return null;
              const expanded=expandedGroups[`sv_cls_${cl}`]!==false;
              const c=CLASS_COLOR[cl];
              // Sub-group by coach within the class
              const coachGroups=allCoaches
                .map(coach=>({coach,students:clsStudents.filter(s=>s.coachId===coach.id)}))
                .filter(g=>g.students.length>0);

              return(
                <div key={cl} style={{marginBottom:14}}>
                  {/* Class header */}
                  <div className="si-group-hdr" onClick={()=>toggleGroup(`sv_cls_${cl}`)}
                    style={{background:`linear-gradient(135deg,var(--surface2),${c}12)`,
                            borderLeft:`3px solid ${c}`,paddingLeft:12}}>
                    <span style={{fontSize:22}}>{CLASS_ICON[cl]}</span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:800,fontSize:14,color:c}}>{cl}</div>
                      <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>
                        {clsStudents.length} player{clsStudents.length!==1?"s":""} across {coachGroups.length} coach{coachGroups.length!==1?"es":""}
                      </div>
                    </div>
                    <div style={{fontSize:12,color:c,fontFamily:"var(--font-mono)",fontWeight:700}}>
                      {clsStudents.length}
                    </div>
                    <span style={{color:"var(--text3)",marginLeft:8}}>{expanded?"▾":"▸"}</span>
                  </div>

                  {expanded&&(
                    <>
                      {/* Within the class, show sub-grouped by coach */}
                      {coachGroups.map(({coach,students:cStds})=>(
                        <div key={coach.id} style={{marginLeft:8,marginBottom:6}}>
                          <div style={{fontSize:11,color:"var(--text3)",fontWeight:600,
                                       padding:"4px 10px",marginBottom:4,
                                       borderLeft:`2px solid ${c}40`}}>
                            👨‍🏫 {coach.name} — {cStds.length} player{cStds.length!==1?"s":""}
                          </div>
                          {cStds.map(s=><StudentRow key={s.id} s={s} db={db} setSubView={setSubView} fromTab="school"/>)}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
            {baseFiltered.length===0&&<div className="empty"><div className="empty-icon">🎓</div><div>No players match the selected filters</div></div>}
          </>
        )}

        {/* ── BY SPECIALTY / TAG ─────────────────────────────────────────────── */}
        {view==="specialty"&&(
          <>
            {Object.entries(TAG_GROUPS).map(([grp,ids])=>{
              const grpStudents=baseFiltered.filter(s=>(s.tags||[]).some(t=>ids.includes(t)));
              if(!grpStudents.length) return null;
              const expanded=expandedGroups[`sv_tag_${grp}`]!==false;
              const repTag=PLAYER_TAGS.find(t=>ids.includes(t.id));
              const c=repTag?.color||"#888";

              // Sub-group by class within specialty
              const clsGroups=CLASSES
                .map(cl=>({cl,students:grpStudents.filter(s=>s.cricketClass===cl)}))
                .filter(g=>g.students.length>0);

              return(
                <div key={grp} style={{marginBottom:14}}>
                  <div className="si-group-hdr" onClick={()=>toggleGroup(`sv_tag_${grp}`)}
                    style={{background:`linear-gradient(135deg,var(--surface2),${c}12)`,
                            borderLeft:`3px solid ${c}`,paddingLeft:12}}>
                    <span style={{fontSize:22}}>{repTag?.icon||"🏏"}</span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:800,fontSize:14,color:c}}>{grp}</div>
                      <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>
                        Across {clsGroups.length} class{clsGroups.length!==1?"es":""}
                      </div>
                    </div>
                    <div style={{fontSize:14,color:c,fontFamily:"var(--font-mono)",fontWeight:800}}>
                      {grpStudents.length}
                    </div>
                    <span style={{color:"var(--text3)",marginLeft:8}}>{expanded?"▾":"▸"}</span>
                  </div>

                  {expanded&&(
                    <>
                      {clsGroups.map(({cl,students:cStds})=>(
                        <div key={cl} style={{marginLeft:8,marginBottom:6}}>
                          <div style={{display:"flex",alignItems:"center",gap:6,
                                       padding:"4px 10px",marginBottom:4,
                                       borderLeft:`2px solid ${CLASS_COLOR[cl]}50`}}>
                            <ClassBadge cls={cl} size={9}/>
                            <span style={{fontSize:11,color:"var(--text3)",fontWeight:600}}>
                              {cStds.length} player{cStds.length!==1?"s":""}
                            </span>
                          </div>
                          {cStds.map(s=><StudentRow key={s.id} s={s} db={db} setSubView={setSubView} fromTab="school"/>)}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              );
            })}
            {baseFiltered.filter(s=>(s.tags||[]).length>0).length===0&&(
              <div className="empty"><div className="empty-icon">🏷️</div><div>No tagged players match the selected filters</div></div>
            )}
          </>
        )}

        {/* ── SEARCH ─────────────────────────────────────────────────────────── */}
        {view==="search"&&(
          <>
            <input value={searchInput} onChange={e=>setSearchInput(e.target.value)}
              placeholder="🔍 Name, tag (e.g. Bowler-Pace, Batter-LH)…"
              style={{marginBottom:10}}/>

            {/* Combined filter row */}
            <div style={{marginBottom:8}}>
              <div className="lbl" style={{marginBottom:4}}>Filter by Class</div>
              <div className="pill-row">
                <button className={`pill${classFilter==="All"?" active":""}`} onClick={()=>setClassFilter("All")}>All Classes</button>
                {CLASSES.map(cl=>(
                  <button key={cl} className={`pill${classFilter===cl?" active":""}`}
                    onClick={()=>setClassFilter(f=>f===cl?"All":cl)}
                    style={{color:classFilter===cl?"#000":CLASS_COLOR[cl],
                            borderColor:CLASS_COLOR[cl]+"60",
                            background:classFilter===cl?CLASS_COLOR[cl]:"transparent"}}>
                    {CLASS_ICON[cl]} {cl}
                  </button>
                ))}
              </div>
            </div>

            <div style={{marginBottom:8}}>
              <div className="lbl" style={{marginBottom:4}}>Filter by Specialty</div>
              <div className="pill-row">
                <button className={`pill${!tagFilter?" active":""}`} onClick={()=>setTagFilter(null)}>All Roles</button>
                {Object.keys(TAG_GROUPS).map(grp=>(
                  <button key={grp} className={`pill${tagFilter===grp?" active":""}`}
                    onClick={()=>setTagFilter(g=>g===grp?null:grp)}>{grp}</button>
                ))}
              </div>
            </div>

            <div style={{marginBottom:8}}>
              <div className="lbl" style={{marginBottom:4}}>Filter by Coach</div>
              <div className="pill-row">
                <button className={`pill${coachFilter==="All"?" active":""}`} onClick={()=>setCoachFilter("All")}>All Coaches</button>
                {allCoaches.map(c=>(
                  <button key={c.id} className={`pill${coachFilter===c.id?" active":""}`}
                    onClick={()=>setCoachFilter(f=>f===c.id?"All":c.id)}>{c.name}</button>
                ))}
              </div>
            </div>

            <div className="divider" style={{margin:"4px 0 10px"}}/>

            <div style={{fontSize:11,color:"var(--text3)",marginBottom:8}}>
              {baseFiltered.length} player{baseFiltered.length!==1?"s":""} found
            </div>

            {baseFiltered.length===0
              ?<div className="empty"><div className="empty-icon">🔍</div><div>No players match your search</div></div>
              :baseFiltered.map(s=>(
                <div key={s.id}>
                  {/* Show coach label above each student in search view */}
                  <StudentRow s={s} db={db} setSubView={setSubView} showCoach={true} coachName={getCoachName(s.coachId)} fromTab="school"/>
                </div>
              ))
            }
          </>
        )}

      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SQUAD INTELLIGENCE  (tag-based search & grouping)
// ═══════════════════════════════════════════════════════════════════════════════
function SquadIntelligence({students,db,setSubView}){
  const [searchTag,setSearchTag]=useState(null);
  const [groupBy,setGroupBy]=useState("class"); // "class" | "tag"
  const [searchInput,setSearchInput]=useState("");

  // Determine which students match the filter
  const filterStudents=(stds,tag)=>{
    if(!tag) return stds;
    // tag can be a specific tag ID or a group label
    const groupIds=TAG_GROUPS[tag];
    if(groupIds) return stds.filter(s=>s.tags&&groupIds.some(t=>s.tags.includes(t)));
    return stds.filter(s=>s.tags&&s.tags.includes(tag));
  };
  const searchFiltered=searchInput.trim()
    ?students.filter(s=>s.name.toLowerCase().includes(searchInput.toLowerCase())||(s.tags||[]).some(t=>t.toLowerCase().includes(searchInput.toLowerCase())))
    :students;
  const displayed=filterStudents(searchFiltered,searchTag);

  // Group by class
  const byClass=CLASSES.reduce((acc,cl)=>{
    acc[cl]=displayed.filter(s=>s.cricketClass===cl);
    return acc;
  },{});

  // Group by tag group
  const byTagGroup=Object.entries(TAG_GROUPS).reduce((acc,[grp,ids])=>{
    acc[grp]=displayed.filter(s=>(s.tags||[]).some(t=>ids.includes(t)));
    return acc;
  },{});

  const [expandedGroups,setExpandedGroups]=useState({});
  const toggleGroup=k=>setExpandedGroups(e=>({...e,[k]:!e[k]}));
  const [sortAZ,setSortAZ]=useState(true); // true=A→Z (default), false=age order

  return(
    <div>
      <div className="screen-hdr">
        <div className="sec-title">Squad Intelligence</div>
        <input value={searchInput} onChange={e=>setSearchInput(e.target.value)} placeholder="🔍 Search by name or tag…" style={{marginBottom:8}}/>
        {/* Quick filter buttons */}
        <div className="scroll-x" style={{marginBottom:6}}>
          <button className={`pill${!searchTag?" active":""}`} onClick={()=>setSearchTag(null)}>All ({students.length})</button>
          {Object.entries(TAG_GROUPS).map(([grp,ids])=>{
            const cnt=students.filter(s=>(s.tags||[]).some(t=>ids.includes(t))).length;
            if(!cnt) return null;
            return <button key={grp} className={`pill${searchTag===grp?" active":""}`} onClick={()=>setSearchTag(g=>g===grp?null:grp)}>{grp} ({cnt})</button>;
          })}
        </div>
        <div style={{display:"flex",gap:6,marginBottom:4}}>
          <button className={`pill${groupBy==="class"?" active":""}`} onClick={()=>setGroupBy("class")}>Group by Class</button>
          <button className={`pill${groupBy==="tag"?" active":""}`} onClick={()=>setGroupBy("tag")}>Group by Specialty</button>
        </div>
        <div className="divider" style={{margin:"8px 0 0"}}/>
      </div>
      <div style={{padding:"0 8px"}}>
        {displayed.length===0&&<div className="empty"><div className="empty-icon">🏏</div><div>No players match this filter</div></div>}

        {groupBy==="class"&&CLASSES.map(cl=>{
          const grpStudents=byClass[cl];
          if(!grpStudents.length) return null;
          const expanded=expandedGroups[`cls_${cl}`]!==false; // default open
          return(
            <div key={cl} style={{marginBottom:12}}>
              <div className="si-group-hdr" onClick={()=>toggleGroup(`cls_${cl}`)}>
                <ClassBadge cls={cl} size={11}/>
                <div style={{flex:1,fontWeight:700,fontSize:13}}>{cl}</div>
                <div style={{fontSize:12,color:"var(--text2)",fontFamily:"var(--font-mono)"}}>{grpStudents.length} player{grpStudents.length!==1?"s":""}</div>
                <span style={{color:"var(--text3)"}}>{expanded?"▾":"▸"}</span>
              </div>
              {expanded&&grpStudents.map(s=><StudentRow key={s.id} s={s} db={db} setSubView={setSubView} fromTab="squad"/>)}
            </div>
          );
        })}

        {groupBy==="tag"&&Object.entries(TAG_GROUPS).map(([grp,ids])=>{
          const grpStudents=byTagGroup[grp].filter(s=>displayed.includes(s));
          if(!grpStudents.length) return null;
          const expanded=expandedGroups[`tag_${grp}`]!==false;
          const representativeTag=PLAYER_TAGS.find(t=>ids.includes(t.id));
          const c=representativeTag?.color||"#888";
          return(
            <div key={grp} style={{marginBottom:12}}>
              <div className="si-group-hdr" onClick={()=>toggleGroup(`tag_${grp}`)}>
                <span style={{fontSize:18}}>{representativeTag?.icon||"🏏"}</span>
                <div style={{flex:1,fontWeight:700,fontSize:13,color:c}}>{grp}</div>
                <div style={{fontSize:12,color:"var(--text2)",fontFamily:"var(--font-mono)"}}>{grpStudents.length} player{grpStudents.length!==1?"s":""}</div>
                <span style={{color:"var(--text3)"}}>{expanded?"▾":"▸"}</span>
              </div>
              {expanded&&grpStudents.map(s=><StudentRow key={s.id} s={s} db={db} setSubView={setSubView} fromTab="squad"/>)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StudentRow({s,db,setSubView,showCoach,coachName,fromTab}){
  const assessments=(db.assessments[s.id]||[]).sort((a,b)=>new Date(a.date)-new Date(b.date));
  const lt=assessments[assessments.length-1];

  // Engagement dot
  const engDot=(()=>{
    if(!s.lastLoginAt) return {color:"#94a3b8",title:"Never logged in"};
    const days=Math.floor((Date.now()-new Date(s.lastLoginAt))/(1000*60*60*24));
    if(days<=7)  return {color:"#22c55e",title:`Active — logged in ${days===0?"today":days===1?"yesterday":`${days}d ago`}`};
    if(days<=30) return {color:"#f59e0b",title:`Last login ${days}d ago`};
    return {color:"#94a3b8",title:`Last login ${days}d ago`};
  })();

  // Unread badge — new notes or goals since coach last viewed
  const lastViewed=db.coachLastViewedAt?.[s.id]?new Date(db.coachLastViewedAt[s.id]):null;
  const newNotes=(db.coachingNotes?.[s.id]||[]).filter(n=>
    n.createdBy!=="coach"&&(!lastViewed||new Date(n.date)>lastViewed)
  ).length;
  const newGoals=(db.goals?.[s.id]||[]).filter(g=>
    g.createdBy==="student"&&g.createdAt&&(!lastViewed||new Date(g.createdAt)>lastViewed)
  ).length;
  const unreadCount=newNotes+newGoals;

  return(
    <div className="card" style={{cursor:"pointer",marginBottom:6,padding:"10px 12px"}} onClick={()=>setSubView&&setSubView({type:"profile",data:s,fromTab:fromTab||"squad"})}>
      {showCoach&&coachName&&(
        <div style={{fontSize:10,color:"var(--text3)",marginBottom:5,fontWeight:600}}>
          👨‍🏫 {coachName}
        </div>
      )}
      <div style={{display:"flex",alignItems:"center",gap:10}}>
        <div style={{position:"relative",flexShrink:0}}>
          <div className="avatar" style={{width:40,height:40,background:"linear-gradient(135deg,var(--accent),#005577)",fontSize:14,color:"#000",fontFamily:"var(--font-display)"}}>{s.avatar}</div>
          <div title={engDot.title} style={{position:"absolute",bottom:1,right:1,width:10,height:10,borderRadius:"50%",background:engDot.color,border:"2px solid var(--bg)",boxSizing:"border-box"}}/>
        </div>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,fontSize:13,display:"flex",alignItems:"center",gap:6}}>
            {s.name}
            {unreadCount>0&&(
              <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",
                minWidth:16,height:16,borderRadius:99,fontSize:9,fontWeight:800,
                background:"var(--danger)",color:"#fff",padding:"0 4px",lineHeight:1}}>
                {unreadCount}
              </span>
            )}
          </div>
          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:3}}>
            <ClassBadge cls={s.cricketClass} size={9}/>
            {(s.tags||[]).map(t=><TagChip key={t} tagId={t}/>)}
          </div>
        </div>
        {lt&&<ScoreRing value={lt.overall||overallFromAssessment(lt)} size={46}/>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ASSESSMENT WIZARD  (class-aware, all 5 categories, N/A skills supported)
// ═══════════════════════════════════════════════════════════════════════════════
function AssessmentWizard({student,db,addAssessment,onDone,showNotif,coachId="c1"}){
  const lib=getOrgLibrary(db,student.orgId);
  const cls=student.cricketClass;
  const naSkills=student.naSkills||[]; // skills marked not-applicable for this student

  // Only include categories that have at least one APPLICABLE active skill
  const steps=CATEGORIES.map(cat=>{
    const allActive=getActiveSkills(lib,cls,cat);
    const applicable=allActive.filter(sk=>!naSkills.includes(sk.id));
    const naInCat=allActive.filter(sk=>naSkills.includes(sk.id));
    return {cat, skills:applicable, naSkills:naInCat};
  }).filter(s=>s.skills.length>0||s.naSkills.length>0); // show cat if it has any skills at all
  steps.push({cat:"notes",skills:[],naSkills:[]});

  const [step,setStep]=useState(0);
  const [scores,setScores]=useState({});
  const [notes,setNotes]=useState("");
  const [sectionNotes,setSectionNotes]=useState({});
  const [week,setWeek]=useState("1");

  const setScore=(skillId,val)=>setScores(s=>({...s,[skillId]:val}));
  // Average only over applicable (non-N/A) skills that were rated
  const catAvg=skills=>{ const vs=skills.map(s=>scores[s.id]||0).filter(v=>v>0); return vs.length?(vs.reduce((a,b)=>a+b,0)/vs.length).toFixed(1):"—"; };

  const submit=()=>{
    const catScores={};
    CATEGORIES.forEach(cat=>{
      const applicable=getActiveSkills(lib,cls,cat).filter(sk=>!naSkills.includes(sk.id));
      if(applicable.length){
        const obj={};
        applicable.forEach(sk=>{ if(scores[sk.id]) obj[sk.id]=scores[sk.id]; });
        catScores[cat]=obj;
      }
    });
    const overall=overallFromAssessment({scores:catScores});
    addAssessment(student.id,{
      date:new Date().toISOString().split("T")[0],
      week:parseInt(week),
      coachId:coachId,
      cricketClass:cls,
      scores:catScores,
      naSkills:[...naSkills], // snapshot which skills were N/A at time of assessment
      notes,
      sectionNotes,
      overall,
      skillSnapshot:Object.fromEntries(CATEGORIES.map(cat=>[cat,
        (lib[cls]?.[cat]||[]).filter(s=>s.active&&!naSkills.includes(s.id)).map(s=>({id:s.id,label:s.label}))
      ]))
    });
    onDone();
  };

  const cur=steps[step];
  const isNotesStep=cur?.cat==="notes";

  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}} className="slide-in">
      <div style={{padding:"14px 16px 0",flexShrink:0}}>
        <button onClick={onDone} style={{background:"transparent",color:"var(--accent)",fontSize:13,fontWeight:700,padding:0,marginBottom:12}}>← Cancel</button>
        <div style={{fontFamily:"var(--font-display)",fontSize:22,marginBottom:4}}>
          Assess: {student.name}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
          <ClassBadge cls={cls}/>
          <select value={week} onChange={e=>setWeek(e.target.value)} style={{width:100,padding:"4px 8px",fontSize:12}}>
            {[1,2,3,4,5,6,7,8].map(w=><option key={w} value={w}>Week {w}</option>)}
          </select>
          {naSkills.length>0&&(
            <div style={{fontSize:10,color:"var(--accent2)",fontWeight:600,marginLeft:4}}>
              {naSkills.length} N/A
            </div>
          )}
        </div>
        <div style={{display:"flex",gap:3,marginBottom:0}}>
          {steps.map((_,i)=><div key={i} style={{flex:1,height:3,borderRadius:99,background:i<=step?"var(--accent)":"var(--border)",transition:"background .3s"}}/>)}
        </div>
        <div className="divider" style={{margin:"8px 0 0"}}/>
      </div>
      <div className="screen" style={{padding:"10px 14px"}}>
        {!isNotesStep&&(
          <div className="card card-hl">
            <div style={{fontFamily:"var(--font-display)",fontSize:20,color:CAT_COLOR[cur.cat],marginBottom:4}}>
              {CAT_ICON[cur.cat]} {cur.cat} Skills
            </div>
            <div style={{fontSize:11,color:"var(--text3)",marginBottom:14}}>
              {cur.skills.length} applicable skill{cur.skills.length!==1?"s":""} · {cls}
              {cur.naSkills.length>0&&<span style={{color:"var(--accent2)",marginLeft:6}}>· {cur.naSkills.length} N/A</span>}
            </div>

            {/* Applicable skills — fully scored */}
            {cur.skills.length===0&&cur.naSkills.length===0&&(
              <div style={{color:"var(--text3)",fontSize:12,fontStyle:"italic"}}>No active {cur.cat} skills in {cls}. Manage in Skill Library.</div>
            )}
            {cur.skills.map(sk=>(
              <StarInput key={sk.id} label={sk.label} desc={sk.description} value={scores[sk.id]||0} onChange={v=>setScore(sk.id,v)}/>
            ))}

            {/* N/A skills — shown greyed out, not scored */}
            {cur.naSkills.length>0&&(
              <>
                <div style={{display:"flex",alignItems:"center",gap:8,margin:"12px 0 8px",opacity:.6}}>
                  <div style={{flex:1,height:1,background:"var(--border)"}}/>
                  <div style={{fontSize:10,color:"var(--text3)",fontWeight:600,letterSpacing:.5}}>NOT APPLICABLE — excluded from score</div>
                  <div style={{flex:1,height:1,background:"var(--border)"}}/>
                </div>
                {cur.naSkills.map(sk=>(
                  <div key={sk.id} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",opacity:.45,borderBottom:"1px solid var(--border)"}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600,textDecoration:"line-through",color:"var(--text3)"}}>{sk.label}</div>
                      {sk.description&&<div style={{fontSize:10,color:"var(--text3)"}}>{sk.description}</div>}
                    </div>
                    <div style={{padding:"2px 10px",borderRadius:99,fontSize:11,fontWeight:800,background:"#fef2f2",color:"var(--danger)",border:"1.5px solid var(--danger)"}}>N/A</div>
                  </div>
                ))}
              </>
            )}

            {/* Section notes — optional per-category coach note */}
            <div style={{marginTop:16,paddingTop:12,borderTop:"1px solid var(--border)"}}>
              <div className="lbl" style={{marginBottom:4}}>{cur.cat} Notes <span style={{fontWeight:400,color:"var(--text3)"}}>(optional)</span></div>
              <textarea
                value={sectionNotes[cur.cat]||""}
                onChange={e=>setSectionNotes(sn=>({...sn,[cur.cat]:e.target.value}))}
                placeholder={`Any specific ${cur.cat.toLowerCase()} observations…`}
                style={{minHeight:52,fontSize:12}}/>
            </div>
          </div>
        )}
        {isNotesStep&&(
          <div className="card card-hl">
            <div style={{fontFamily:"var(--font-display)",fontSize:20,color:"var(--accent3)",marginBottom:14}}>📝 Summary & Notes</div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:14}}>
              {CATEGORIES.map(cat=>{
                const applicable=getActiveSkills(lib,cls,cat).filter(sk=>!naSkills.includes(sk.id));
                if(!applicable.length) return null;
                const avg=catAvg(applicable);
                return <div key={cat} className="stat-box"><div className="stat-val" style={{color:CAT_COLOR[cat],fontSize:20}}>{avg}</div><div className="stat-lbl" style={{fontSize:9}}>{CAT_ICON[cat]}</div></div>;
              })}
            </div>
            {naSkills.length>0&&(
              <div style={{fontSize:11,color:"var(--text3)",marginBottom:12,padding:"6px 10px",background:"#fef2f2",borderRadius:6}}>
                ℹ️ {naSkills.length} skill{naSkills.length!==1?"s":""} excluded from scoring (marked N/A on this player's profile)
              </div>
            )}
            <div className="lbl">Coach Notes</div>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Key observations, areas of improvement…"/>
          </div>
        )}
        <div style={{display:"flex",gap:8,marginTop:10}}>
          {step>0&&<button className="btn-secondary" style={{flex:1}} onClick={()=>setStep(s=>s-1)}>← Back</button>}
          {!isNotesStep
            ?<button className="btn-primary" style={{flex:2}} onClick={()=>setStep(s=>s+1)}>Next →</button>
            :<button className="btn-primary" style={{flex:2,background:"linear-gradient(135deg,var(--accent3),#00a152)"}} onClick={submit}>💾 Save Assessment</button>
          }
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STUDENT CLASS & TAG EDITOR (modal) — now includes N/A Skills
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// PROGRESS REPORT OVERLAY — generates + prints a formal PDF progress report
// Triggered by: 4-session milestone banner, or 📄 Reports tab inside student profile
// Save separates from PDF download — PDF generated as blob, stays inside the app
// ═══════════════════════════════════════════════════════════════════════════════
function ProgressReportOverlay({student,db,onClose,saveReportSnapshot,showNotif,readOnly=false,viewSnapshot=null}){
  const isHistorical=!!viewSnapshot;
  // Historical reports MUST use frozen data. If frozen data is missing (old report
  // generated before v3.17), show unavailable message — never show wrong data.
  const hasFrozen=isHistorical ? !!(viewSnapshot.frozenSessions&&viewSnapshot.frozenAssessments) : false;

  // Data source: frozen snapshot (historical) or live db (new report)
  const finalAssessments = isHistorical
    ? (hasFrozen ? [...viewSnapshot.frozenAssessments].sort((a,b)=>new Date(a.date)-new Date(b.date)) : [])
    : (db.assessments[student.id]||[]).sort((a,b)=>new Date(a.date)-new Date(b.date));
  const finalSessions = isHistorical
    ? (hasFrozen ? [...viewSnapshot.frozenSessions].sort((a,b)=>new Date(b.date)-new Date(a.date)) : [])
    : (db.sessions[student.id]||[]).sort((a,b)=>new Date(b.date)-new Date(a.date));
  const finalGoals = isHistorical
    ? (hasFrozen ? (viewSnapshot.frozenGoals||[]) : [])
    : (db.goals[student.id]||[]);

  const liveCoach=db.coaches.find(c=>c.id===student.coachId);
  const coach = (isHistorical&&viewSnapshot.frozenCoach)
    ? viewSnapshot.frozenCoach
    : liveCoach;
  const schoolName = coach?.schoolName||"";
  const lt=finalAssessments[finalAssessments.length-1];
  const first=finalAssessments[0];
  const lib=getOrgLibrary(db,student.orgId);
  const [saved,setSaved]=useState(false);
  const [pdfLoading,setPdfLoading]=useState(false);

  // Old report without frozen data — show clear message rather than wrong data
  if(isHistorical && !hasFrozen){
    return(
      <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>
        <div id="report-toolbar" style={{padding:"12px 14px",background:"var(--surface)",borderBottom:"1.5px solid var(--border)",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          <button onClick={onClose} style={{background:"transparent",color:"var(--accent)",fontWeight:700,fontSize:13,padding:0,border:"none"}}>← Back</button>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:13}}>{student.name} — Historical Report</div>
            <div style={{fontSize:11,color:"var(--text3)"}}>{viewSnapshot.label||dateStr(viewSnapshot.generatedAt?.split("T")[0])}</div>
          </div>
        </div>
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
          <div style={{textAlign:"center",maxWidth:280}}>
            <div style={{fontSize:40,marginBottom:12}}>📄</div>
            <div style={{fontWeight:700,fontSize:16,marginBottom:8}}>Historical data unavailable</div>
            <div style={{fontSize:13,color:"var(--text2)",lineHeight:1.6,marginBottom:16}}>
              This report was generated before v3.17 and does not contain a frozen data snapshot. Only reports generated from v3.17 onwards retain full historical content.
            </div>
            <div style={{fontSize:12,color:"var(--text3)",marginBottom:16}}>
              Summary on record: {viewSnapshot.sessionCount} sessions · {viewSnapshot.assessmentCount} assessments · Overall: {viewSnapshot.overallScore||"—"}/5
            </div>
            <button className="btn-primary" onClick={onClose}>← Back</button>
          </div>
        </div>
      </div>
    );
  }

  // Stat helpers — use final (frozen or live) data
  const totalMins=finalSessions.reduce((acc,s)=>acc+(s.duration||0),0);
  const groupSessions=finalSessions.filter(s=>s.isGroupSession).length;
  const goalsCompleted=finalGoals.filter(g=>g.status==="completed").length;

  // Category progress: first vs latest
  // Use frozen assessments for category progress
  const catProgress=CATEGORIES.map(cat=>{
    const cur=lt?catAvgFromAssessment(lt,cat):0;
    const ini=first?catAvgFromAssessment(first,cat):0;
    const delta=cur&&ini?+(cur-ini).toFixed(1):null;
    return {cat,cur,ini,delta};
  }).filter(x=>x.cur>0);

  // Skill detail from latest assessment
  const skillDetail=CATEGORIES.map(cat=>{
    if(!lt?.scores?.[cat]) return null;
    const sks=lt.skillSnapshot?.[cat]||(lib[lt.cricketClass||student.cricketClass]?.[cat]||[]).filter(s=>s.active);
    const sc=lt.scores[cat];
    const rated=sks.filter(sk=>sc[sk.id]>0);
    if(!rated.length) return null;
    return {cat, skills:rated.map(sk=>({label:sk.label,score:sc[sk.id]}))};
  }).filter(Boolean);

  // Rating colour as hex for print
  const ratingHex=v=>v>=4.5?"#16a34a":v>=3.5?"#2563eb":v>=2.5?"#d97706":v>=1.5?"#ea580c":"#dc2626";

  // ── Save Report to DynamoDB (makes it visible to student) ────────────────
  const handleSave=()=>{
    if(saved||isHistorical||!saveReportSnapshot) return;
    const snapshot={
      id:"rpt_"+Date.now(),
      generatedAt:new Date().toISOString(),
      label:`Report — ${dateStr(new Date().toISOString().split('T')[0])} at ${new Date().toTimeString().slice(0,5)}`,
      sessionCount:finalSessions.length,
      assessmentCount:finalAssessments.length,
      goalCount:finalGoals.length,
      overallScore:lt?(lt.overall||overallFromAssessment(lt)):null,
      studentName:student.name,
      coachName:coach?.name||"",
      schoolName,
      frozenSessions:JSON.parse(JSON.stringify(finalSessions)),
      frozenAssessments:JSON.parse(JSON.stringify(finalAssessments)),
      frozenGoals:JSON.parse(JSON.stringify(finalGoals)),
      frozenStudent:JSON.parse(JSON.stringify({...student,name:student.name,cricketClass:student.cricketClass,tags:student.tags,avatar:student.avatar})),
      frozenCoach:{name:coach?.name||"",schoolName:schoolName},
    };
    saveReportSnapshot(student.id,snapshot);
    setSaved(true);
    showNotif("✅ Report saved — now visible to student");
  };

  // ── Download PDF blob — stays inside the app, no window.print() ───────────
  const handleDownloadPDF=()=>{
    setPdfLoading(true);
    try{
      // Build a self-contained HTML string of the report body only
      const reportEl=document.getElementById("report-body");
      if(!reportEl){ setPdfLoading(false); return; }
      const reportHTML=reportEl.innerHTML;
      const filename=`${student.name.replace(/\s+/g,"_")}_Progress_Report_${new Date().toISOString().split("T")[0]}.html`;

      // Wrap in a minimal HTML document styled for print
      const fullHTML=`<!DOCTYPE html>
<html><head><meta charset="UTF-8"/>
<title>${student.name} — Progress Report</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;font-size:14px;color:#0a0f1a;background:#fff;padding:20px}
  @page{margin:18mm 14mm}
  @media print{body{padding:0}}
</style>
</head><body>${reportHTML}</body></html>`;

      // Create blob and trigger download
      const blob=new Blob([fullHTML],{type:"text/html;charset=utf-8"});
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");
      a.href=url;
      a.download=filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(()=>URL.revokeObjectURL(url),2000);
      showNotif("📄 Report downloaded — open in Files to print as PDF");
    } catch(e){
      showNotif("Download failed — try again");
    }
    setPdfLoading(false);
  };

  const today=dateStr(new Date().toISOString().split("T")[0]);
  const overallScore=lt?(lt.overall||overallFromAssessment(lt)):null;

  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden",background:"var(--bg)"}}>

      {/* ── Screen styles for report body ── */}
      <style>{`
        @media screen {
          #report-body { max-width:680px; margin:0 auto; padding:16px; }
        }
      `}</style>

      {/* ── Toolbar (screen only, hidden on print) ── */}
      <div id="report-toolbar" style={{padding:"12px 14px",background:"var(--surface)",borderBottom:"1.5px solid var(--border)",display:"flex",alignItems:"center",gap:8,flexShrink:0,flexWrap:"wrap"}}>
        <button onClick={onClose} style={{background:"transparent",color:"var(--accent)",fontWeight:700,fontSize:13,padding:0,border:"none",flexShrink:0}}>← Back</button>
        <div style={{flex:1,minWidth:80}}>
          <div style={{fontWeight:700,fontSize:13}}>{student.name} — Progress Report</div>
          <div style={{fontSize:11,color:"var(--text3)"}}>
            {isHistorical
              ? `Historical report — ${dateStr(viewSnapshot.generatedAt)}`
              : saved
                ? "✅ Saved — visible to student"
                : readOnly
                  ? "View and download this report"
                  : "Save to share with student · Download as PDF"}
          </div>
        </div>
        <div style={{display:"flex",gap:6,flexShrink:0}}>
          {/* Save Report — only show for coach on new (unsaved) report */}
          {!readOnly&&!isHistorical&&(
            <button
              onClick={handleSave}
              disabled={saved}
              style={{
                background:saved?"#e2e8f0":"linear-gradient(135deg,var(--accent3),#0a5c2a)",
                color:saved?"var(--text3)":"#fff",
                fontWeight:700,fontSize:12,
                padding:"9px 12px",borderRadius:8,border:"none",
                whiteSpace:"nowrap",cursor:saved?"default":"pointer",
                opacity:saved?0.7:1
              }}>
              {saved?"✅ Saved":"💾 Save Report"}
            </button>
          )}
          {/* Download PDF — always available */}
          <button
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
            style={{
              background:"linear-gradient(135deg,var(--accent),#003d5c)",
              color:"#fff",fontWeight:700,fontSize:12,
              padding:"9px 12px",borderRadius:8,border:"none",
              whiteSpace:"nowrap",cursor:pdfLoading?"wait":"pointer"
            }}>
            {pdfLoading?"⏳ Preparing…":"📄 Download PDF"}
          </button>
        </div>
      </div>

      {/* ── Report body (screen + print) ── */}
      <div className="screen" style={{padding:0}}>
        <div id="report-body">

          {/* PAGE 1 — HEADER ─────────────────────────────────────────────── */}
          <div style={{borderBottom:"3px solid #0369a1",paddingBottom:16,marginBottom:20}}>
            {/* School & CricKit branding */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
              <div>
                {schoolName&&<div style={{fontSize:18,fontWeight:900,color:"#0369a1",letterSpacing:1}}>{schoolName}</div>}
                <div style={{fontSize:11,color:"#64748b",marginTop:2}}>Powered by CrickitCoach · ArcGain LLC</div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:22,lineHeight:1}}>🏏</div>
                <div style={{fontWeight:900,fontSize:16,letterSpacing:2,color:"#0369a1"}}>CrickitCoach</div>
              </div>
            </div>

            {/* Report title */}
            <div style={{background:"#f0f9ff",border:"1.5px solid #0369a1",borderRadius:10,padding:"14px 16px"}}>
              <div style={{fontSize:22,fontWeight:900,color:"#0a0f1a",marginBottom:4}}>Player Progress Report</div>
              <div style={{display:"flex",gap:20,flexWrap:"wrap"}}>
                <div><span style={{fontSize:11,color:"#64748b",fontWeight:700}}>PLAYER </span><span style={{fontSize:13,fontWeight:700}}>{student.name}</span></div>
                <div><span style={{fontSize:11,color:"#64748b",fontWeight:700}}>CLASS </span><span style={{fontSize:13,fontWeight:700}}>{student.cricketClass}</span></div>
                <div><span style={{fontSize:11,color:"#64748b",fontWeight:700}}>COACH </span><span style={{fontSize:13,fontWeight:700}}>{coach?.name||"—"}</span></div>
                <div><span style={{fontSize:11,color:"#64748b",fontWeight:700}}>DATE </span><span style={{fontSize:13,fontWeight:700}}>{today}</span></div>
              </div>
              {(student.tags||[]).length>0&&(
                <div style={{marginTop:8,display:"flex",gap:4,flexWrap:"wrap"}}>
                  {(student.tags||[]).map(t=>{
                    const tag=PLAYER_TAGS.find(x=>x.id===t);
                    return tag?<span key={t} style={{fontSize:11,padding:"2px 8px",borderRadius:99,fontWeight:700,background:tag.color+"22",color:tag.color,border:`1px solid ${tag.color}44`}}>{tag.icon} {t}</span>:null;
                  })}
                </div>
              )}
            </div>
          </div>

          {/* SUMMARY STATS ───────────────────────────────────────────────── */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8,marginBottom:20}}>
            {[
              {val:finalSessions.length,lbl:"Sessions",color:"#0369a1"},
              {val:finalSessions.length>0?`${Math.round(totalMins/60)}h`:"0h",lbl:"Training Time",color:"#0369a1"},
              {val:goalsCompleted,lbl:"Goals Hit",color:"#15803d"},
              {val:overallScore?`${overallScore}/5`:"—",lbl:"Overall Rating",color:overallScore?ratingHex(overallScore):"#64748b"},
            ].map(({val,lbl,color})=>(
              <div key={lbl} style={{border:"1.5px solid #e2e8f0",borderRadius:8,padding:"10px 8px",textAlign:"center"}}>
                <div style={{fontSize:22,fontWeight:900,color,fontFamily:"monospace"}}>{val}</div>
                <div style={{fontSize:9,fontWeight:700,color:"#64748b",textTransform:"uppercase",letterSpacing:.5,marginTop:2}}>{lbl}</div>
              </div>
            ))}
          </div>

          {/* OVERALL RATING ─────────────────────────────────────────────── */}
          {overallScore&&(
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:800,color:"#0369a1",textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Overall Performance Rating</div>
              <div style={{display:"flex",alignItems:"center",gap:14,padding:"12px 14px",background:"#f8fafc",borderRadius:10,border:"1.5px solid #e2e8f0"}}>
                <div style={{fontSize:36,fontWeight:900,color:ratingHex(overallScore),fontFamily:"monospace",minWidth:52}}>{overallScore}</div>
                <div>
                  <div style={{fontSize:18,fontWeight:900,color:ratingHex(overallScore)}}>{RATING_LABEL(overallScore)}</div>
                  <div style={{fontSize:11,color:"#64748b",marginTop:2}}>
                    {lt?`Latest assessment: Week ${lt.week} · ${dateStr(lt.date)}`:""}
                    {finalAssessments.length>=2&&first?` · Started: ${dateStr(first.date)}`:""}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* CATEGORY SCORES ─────────────────────────────────────────────── */}
          {catProgress.length>0&&(
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:800,color:"#0369a1",textTransform:"uppercase",letterSpacing:.5,marginBottom:10}}>Category Assessment Scores</div>
              {catProgress.map(({cat,cur,ini,delta})=>(
                <div key={cat} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                    <div style={{fontSize:13,fontWeight:700}}>{CAT_ICON[cat]} {cat}</div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      {delta!==null&&finalAssessments.length>=2&&(
                        <span style={{fontSize:11,fontWeight:700,color:delta>=0?"#15803d":"#dc2626"}}>
                          {delta>=0?`▲ +${delta}`:`▼ ${delta}`}
                        </span>
                      )}
                      <span style={{fontSize:13,fontWeight:900,color:ratingHex(cur),fontFamily:"monospace"}}>{cur}/5</span>
                    </div>
                  </div>
                  <div style={{height:8,background:"#e2e8f0",borderRadius:99,overflow:"hidden"}}>
                    <div style={{height:"100%",borderRadius:99,background:ratingHex(cur),width:`${(cur/5)*100}%`}}/>
                  </div>
                  <div style={{fontSize:10,color:"#94a3b8",marginTop:2}}>{RATING_LABEL(cur)}</div>
                </div>
              ))}
            </div>
          )}

          {/* SKILL DETAIL ────────────────────────────────────────────────── */}
          {skillDetail.length>0&&(
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:800,color:"#0369a1",textTransform:"uppercase",letterSpacing:.5,marginBottom:10}}>Individual Skill Breakdown</div>
              {skillDetail.map(({cat,skills})=>(
                <div key={cat} style={{marginBottom:14}}>
                  <div style={{fontSize:11,fontWeight:800,color:CAT_COLOR[cat],textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>{CAT_ICON[cat]} {cat}</div>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <tbody>
                      {skills.map(({label,score})=>(
                        <tr key={label} style={{borderBottom:"1px solid #f1f5f9"}}>
                          <td style={{padding:"4px 0",fontSize:12,color:"#1e293b"}}>{label}</td>
                          <td style={{padding:"4px 0",textAlign:"right",width:80}}>
                            <span style={{fontSize:11,fontWeight:700,color:ratingHex(score)}}>{score}/5 — {RATING_LABEL(score)}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {lt?.sectionNotes?.[cat]&&(
                    <div style={{marginTop:6,padding:"6px 10px",background:"#f0f9ff",borderLeft:"3px solid #0369a1",borderRadius:"0 6px 6px 0",fontSize:11,color:"#1e293b",fontStyle:"italic",lineHeight:1.5}}>
                      {lt.sectionNotes[cat]}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* COACH NOTES ─────────────────────────────────────────────────── */}
          {lt?.notes&&(
            <div style={{marginBottom:20,padding:"12px 14px",background:"#eff6ff",border:"1.5px solid #bfdbfe",borderRadius:10}}>
              <div style={{fontSize:11,fontWeight:800,color:"#0369a1",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>Latest Coach Notes</div>
              <div style={{fontSize:13,fontStyle:"italic",color:"#1e293b",lineHeight:1.7}}>"{lt.notes}"</div>
              <div style={{fontSize:10,color:"#64748b",marginTop:6}}>— {coach?.name||"Coach"} · Week {lt.week} · {dateStr(lt.date)}</div>
            </div>
          )}

          {/* COACHING TIPS ──────────────────────────────────────────────── */}
          {(()=>{
            const sNotes=db?.skillNotes?.[student.id]||{};
            const lib=getOrgLibrary(db,student.orgId);
            const cls=student.cricketClass;
            const coachId=sNotes._coachId||student.coachId;
            const coach=db?.coaches?.find(c=>c.id===coachId);
            const updatedAt=sNotes._updatedAt;
            const sections=CATEGORIES.map(cat=>{
              const catSkills=(lib[cls]?.[cat]||[]).filter(sk=>sk.active);
              const catNotes=sNotes[cat]||{};
              const items=catSkills.filter(sk=>{
                const raw=catNotes[sk.id];
                return raw&&(Array.isArray(raw)?raw.length>0:raw.trim());
              }).map(sk=>{
                const raw=catNotes[sk.id];
                const note=Array.isArray(raw)?raw[0]?.note:raw;
                return{label:sk.label,note};
              });
              return{cat,items};
            }).filter(s=>s.items.length>0);
            if(!sections.length) return null;
            return(
              <div style={{marginBottom:20}}>
                <div style={{fontSize:12,fontWeight:800,color:"#0369a1",textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Coaching Tips</div>
                {coach&&<div style={{fontSize:10,color:"#64748b",marginBottom:10}}>From {coach.name}{updatedAt?` · Updated ${dateStr(updatedAt)}`:""}</div>}
                {sections.map(({cat,items})=>(
                  <div key={cat} style={{marginBottom:14}}>
                    <div style={{fontSize:11,fontWeight:800,color:CAT_COLOR[cat],textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>{CAT_ICON[cat]} {cat}</div>
                    {items.map(({label,note})=>(
                      <div key={label} style={{marginBottom:8}}>
                        <div style={{fontSize:12,fontWeight:700,color:"#1e293b",marginBottom:3}}>{label}</div>
                        <div style={{fontSize:11,color:"#334155",lineHeight:1.6,padding:"6px 10px",background:"#f0f9ff",borderLeft:"3px solid "+CAT_COLOR[cat],borderRadius:"0 6px 6px 0"}}>{note}</div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* GOALS SUMMARY ───────────────────────────────────────────────── */}
          {finalGoals.length>0&&(
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:800,color:"#0369a1",textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Goals Summary</div>
              {[...finalGoals].sort((a,b)=>{
                if(a.status==="completed"&&b.status!=="completed") return 1;
                if(b.status==="completed"&&a.status!=="completed") return -1;
                return new Date(a.targetDate)-new Date(b.targetDate);
              }).map(g=>(
                <div key={g.id} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 10px",marginBottom:4,borderRadius:8,background:"#f8fafc",border:"1px solid #e2e8f0"}}>
                  <span style={{fontSize:14,flexShrink:0}}>{g.status==="completed"?"✅":"🎯"}</span>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:600,color:"#0a0f1a"}}>{g.title}</div>
                    <div style={{fontSize:10,color:"#64748b"}}>{g.category} · {g.status==="completed"?"Completed":"In Progress — "+g.progress+"%"}{g.isGroupGoal?" · 👥 Squad":""}</div>
                  </div>
                  {g.targetDate&&<div style={{fontSize:10,color:"#94a3b8",flexShrink:0}}>🎯 {dateStr(g.targetDate)}</div>}
                </div>
              ))}
            </div>
          )}

          {/* SESSIONS SUMMARY ────────────────────────────────────────────── */}
          {finalSessions.length>0&&(
            <div style={{marginBottom:20}}>
              <div style={{fontSize:12,fontWeight:800,color:"#0369a1",textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Recent Sessions ({finalSessions.length} total · {groupSessions} group)</div>
              {finalSessions.slice(0,8).map(se=>(
                <div key={se.id} style={{display:"flex",alignItems:"flex-start",gap:10,padding:"7px 10px",marginBottom:4,borderRadius:8,background:"#f8fafc",border:`1px solid ${se.isGroupSession?"#fed7aa":"#e2e8f0"}`}}>
                  <div style={{flexShrink:0,width:22,textAlign:"center",fontSize:14}}>
                    {se.type==="Batting"?"🏏":se.type==="Bowling"?"⚾":se.type==="Fielding"?"🧤":se.type==="Match"?"🏆":se.type==="Fitness"?"💪":"📋"}
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:12,fontWeight:600}}>{se.type}{se.isGroupSession?" (Group)":""} · {se.duration}min</div>
                    <div style={{fontSize:10,color:"#64748b"}}>{dateStr(se.date)}</div>
                    {se.coachNote&&<div style={{fontSize:11,color:"#475569",fontStyle:"italic",marginTop:2}}>"{se.coachNote}"</div>}
                  </div>
                </div>
              ))}
              {finalSessions.length>8&&<div style={{fontSize:11,color:"#64748b",textAlign:"center",padding:"4px 0"}}>+ {finalSessions.length-8} more sessions not shown</div>}
            </div>
          )}

          {/* FOOTER ──────────────────────────────────────────────────────── */}
          <div style={{borderTop:"2px solid #e2e8f0",paddingTop:12,marginTop:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontSize:10,color:"#94a3b8"}}>Generated by CrickitCoach · ArcGain LLC · support@crickitcoach.com</div>
            <div style={{fontSize:10,color:"#94a3b8"}}>{today}</div>
          </div>

        </div>
      </div>
    </div>
  );
}

function StudentEditor({student,onSave,onClose,db,coaches,isAdmin,currentCoachId}){
  const [cls,setCls]=useState(student.cricketClass||"Introductory");
  const [tags,setTags]=useState([...(student.tags||[])]);
  const [naSkills,setNaSkills]=useState([...(student.naSkills||[])]);
  const [naTab,setNaTab]=useState("Bowling");
  const _firstRealCoach=(coaches||[]).find(c=>c.role!=="admin"&&c.status!=="inactive");
  const _initialCoachId=(()=>{
    const cur=student.coachId||"";
    const curIsAdmin=(coaches||[]).find(c=>c.id===cur&&c.role==="admin");
    return curIsAdmin ? (_firstRealCoach?.id||"") : cur;
  })();
  const [assignedCoachId,setAssignedCoachId]=useState(_initialCoachId);
  const [editName,setEditName]=useState(student.name||"");
  const [editEmail,setEditEmail]=useState(student.email||"");
  const [editAge,setEditAge]=useState(student.age||"");
  const [changePw,setChangePw]=useState(false);
  const [newPw,setNewPw]=useState("");
  const [editErr,setEditErr]=useState("");
  const [linkAsSibling,setLinkAsSibling]=useState(false);
  const toggleTag=t=>setTags(ts=>ts.includes(t)?ts.filter(x=>x!==t):[...ts,t]);
  const toggleNA=id=>setNaSkills(ns=>ns.includes(id)?ns.filter(x=>x!==id):[...ns,id]);
  const lib=getOrgLibrary(db,student.orgId);
  const activeCoaches=(coaches||[]).filter(c=>c.role!=="admin"&&c.status!=="inactive");
  const canReassign = isAdmin || (currentCoachId && student.coachId===currentCoachId && activeCoaches.length>1);

  // Detect if edited email matches another existing student — offer sibling link
  const emailMatch=(db?.students||[]).find(s=>
    s.id!==student.id &&
    s.email===editEmail.trim().toLowerCase() &&
    s.status!=="inactive"
  );
  // Already linked?
  const alreadyLinked=emailMatch&&(student.linkedPlayerIds||[]).includes(emailMatch.id);

  const handleSave=()=>{
    setEditErr("");
    if(!editName.trim()){setEditErr("Name cannot be empty");return;}
    if(!editEmail.trim()||!editEmail.includes("@")){setEditErr("Valid email required");return;}
    if(changePw&&newPw&&newPw.length<6){setEditErr("Password must be at least 6 characters");return;}
    const nameChanged  = editName.trim()!==student.name;
    const emailChanged = editEmail.trim().toLowerCase()!==student.email;
    const pwChanged    = changePw&&newPw.length>=6;
    if(isAdmin&&!assignedCoachId){setEditErr("Please assign this player to a coach");return;}
    const isTransfer = canReassign && !isAdmin && assignedCoachId && assignedCoachId !== student.coachId;
    onSave(
      student.id, cls, tags, naSkills,
      canReassign ? assignedCoachId : undefined,
      nameChanged  ? editName.trim()          : undefined,
      emailChanged ? editEmail.trim()         : undefined,
      pwChanged    ? newPw                    : undefined,
      editAge      ? parseInt(editAge)        : student.age,
      isTransfer   ? assignedCoachId          : undefined,
      // Pass sibling link info if coach chose to link
      (linkAsSibling&&emailMatch&&!alreadyLinked) ? emailMatch.id : undefined,
    );
  };

  return(
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="sheet" style={{maxHeight:"92%",overflowY:"auto"}}>
        <div className="handle"/>
        <div style={{fontFamily:"var(--font-display)",fontSize:22,marginBottom:4}}>✏️ Edit Player</div>
        <div style={{fontSize:12,color:"var(--text3)",marginBottom:14}}>Update details for {student.name}</div>

        {/* ── Identity fields ── */}
        <div style={{marginBottom:12}}>
          <div className="lbl">Full Name *</div>
          <input value={editName} onChange={e=>setEditName(e.target.value)} placeholder="Player full name"/>
        </div>
        <div style={{marginBottom:12}}>
          <div className="lbl">Email Address *</div>
          <input type="email" value={editEmail} onChange={e=>{setEditEmail(e.target.value);setLinkAsSibling(false);}} placeholder="player@email.com"/>
          {emailMatch&&!alreadyLinked&&(
            <div style={{marginTop:8,background:"#f0f9ff",border:"1.5px solid #bae0fd",borderRadius:8,padding:"10px 12px"}}>
              <div style={{fontSize:12,fontWeight:700,color:"#0369a1",marginBottom:4}}>👥 This email belongs to {emailMatch.name}</div>
              <div style={{fontSize:11,color:"#0369a1",marginBottom:8}}>Do you want to link them as siblings? They will share this login email and a parent can switch between both profiles.</div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setLinkAsSibling(true)}
                  style={{flex:1,padding:"7px",borderRadius:6,fontWeight:700,fontSize:11,
                    background:linkAsSibling?"#0369a1":"transparent",
                    color:linkAsSibling?"#fff":"#0369a1",
                    border:"1.5px solid #0369a1",cursor:"pointer"}}>
                  {linkAsSibling?"✅ Will link as siblings":"Yes, link as siblings"}
                </button>
                <button onClick={()=>setLinkAsSibling(false)}
                  style={{flex:1,padding:"7px",borderRadius:6,fontWeight:700,fontSize:11,
                    background:"transparent",color:"var(--text3)",
                    border:"1.5px solid var(--border)",cursor:"pointer"}}>
                  No, keep separate
                </button>
              </div>
            </div>
          )}
          {emailMatch&&alreadyLinked&&(
            <div style={{marginTop:6,fontSize:11,color:"var(--accent3)",fontWeight:600}}>✅ Already linked as siblings with {emailMatch.name}</div>
          )}
          {!emailMatch&&<div style={{fontSize:11,color:"var(--text3)",marginTop:4}}>Used to log in — must be unique or shared with a sibling</div>}
        </div>

        <div style={{marginBottom:12}}>
          <div className="lbl">Age</div>
          <select value={editAge} onChange={e=>setEditAge(e.target.value)} style={{width:"100%"}}>
            <option value="">Select age…</option>
            {[6,7,8,9,10,11,12,13,14,15,16,17,18].map(a=><option key={a} value={a}>{a} years old</option>)}
          </select>
        </div>

        <div style={{marginBottom:16,padding:"12px 14px",background:"var(--surface2)",borderRadius:10,border:"1px solid var(--border)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:changePw?10:0}}>
            <div style={{flex:1,fontSize:13,fontWeight:600}}>Reset Password</div>
            <button className="btn-ghost btn-sm" onClick={()=>{setChangePw(!changePw);setNewPw("");}}
              style={{color:changePw?"var(--danger)":"var(--accent)",borderColor:changePw?"var(--danger)":"var(--accent)"}}>
              {changePw?"Cancel":"Change"}
            </button>
          </div>
          {changePw&&<input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="New password (min 6 characters)"/>}
        </div>
        {editErr&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"9px 12px",fontSize:12,color:"var(--danger)",marginBottom:12}}>{editErr}</div>}

        <div className="divider" style={{marginBottom:14}}/>

        {/* ── Reassign coach — admin always, coach only for own players ── */}
        {canReassign&&activeCoaches.length>0&&(
          <div style={{marginBottom:16,padding:"12px 14px",
            background:isAdmin?"#fef9e7":"#f0f4ff",
            border:`2px solid ${isAdmin?"var(--gold)":"var(--accent)"}`,borderRadius:10}}>
            <div style={{fontSize:12,fontWeight:800,
              color:isAdmin?"var(--gold)":"var(--accent)",
              textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>
              {isAdmin?"👑 Reassign to Different Coach":"🔄 Transfer to Another Coach"}
            </div>
            <select value={assignedCoachId} onChange={e=>setAssignedCoachId(e.target.value)}
              style={{border:`2px solid ${isAdmin?"var(--gold)":"var(--accent)"}`}}>
              {activeCoaches.map(c=>(
                <option key={c.id} value={c.id}>{c.name}{c.team?` — ${c.team}`:""}{c.id===student.coachId?" ✓ current":""}</option>
              ))}
            </select>
            <div style={{fontSize:11,color:"var(--text2)",marginTop:6,lineHeight:1.5}}>
              {isAdmin
                ?"As School Admin you can move any player between coaches."
                :"You can transfer players from your squad to another coach. A transfer note will be added to the player's record."}
            </div>
          </div>
        )}

        {/* Class */}
        <div className="lbl">Cricket Class Level</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:6,marginBottom:16}}>
          {CLASSES.map(cl=>(
            <button key={cl} onClick={()=>setCls(cl)} style={{padding:"9px",borderRadius:10,fontWeight:700,fontSize:12,
              background:cls===cl?CLASS_COLOR[cl]:"var(--surface2)",color:cls===cl?"#fff":"var(--text2)",
              border:`1.5px solid ${cls===cl?CLASS_COLOR[cl]:"var(--border)"}`}}>
              {CLASS_ICON[cl]} {cl}
            </button>
          ))}
        </div>

        {/* Tags */}
        <div className="lbl">Player Specialisation Tags</div>
        <div className="pill-row" style={{marginBottom:18}}>
          {PLAYER_TAGS.map(t=>(
            <button key={t.id} onClick={()=>toggleTag(t.id)} style={{
              padding:"5px 10px",borderRadius:99,fontSize:11,fontWeight:700,cursor:"pointer",
              background:tags.includes(t.id)?t.color+"30":"transparent",
              color:tags.includes(t.id)?t.color:"var(--text2)",
              border:`1.5px solid ${tags.includes(t.id)?t.color:"var(--border)"}`,
              marginBottom:4
            }}>{t.icon} {t.id}</button>
          ))}
        </div>

        {/* N/A Skills */}
        <div className="divider" style={{marginBottom:14}}/>
        <div className="lbl" style={{marginBottom:2}}>Not Applicable Skills</div>
        <div style={{fontSize:11,color:"var(--text3)",marginBottom:10,lineHeight:1.5}}>
          Mark skills that are not relevant for this player. Marked skills are shown as N/A during assessments and excluded from their scores.
        </div>
        {naSkills.length>0&&(
          <div style={{marginBottom:10,padding:"8px 10px",background:"#fff0ea",border:"1.5px solid var(--accent2)",borderRadius:8,fontSize:11,color:"var(--accent2)",fontWeight:600}}>
            ⚠️ {naSkills.length} skill{naSkills.length!==1?"s":""} marked as N/A
          </div>
        )}
        <div className="scroll-x" style={{marginBottom:8,gap:4}}>
          {CATEGORIES.map(ca=>{
            const skList=(lib[cls]?.[ca]||[]).filter(s=>s.active);
            const naCount=skList.filter(s=>naSkills.includes(s.id)).length;
            return(
              <button key={ca} onClick={()=>setNaTab(ca)} style={{flexShrink:0,padding:"5px 10px",borderRadius:8,fontWeight:700,fontSize:11,
                background:naTab===ca?CAT_COLOR[ca]:"var(--surface2)",color:naTab===ca?"#fff":"var(--text2)",
                border:`1.5px solid ${naTab===ca?CAT_COLOR[ca]:"var(--border)"}`}}>
                {CAT_ICON[ca]} {ca} {naCount>0&&<span style={{fontSize:9,opacity:.8}}>({naCount})</span>}
              </button>
            );
          })}
        </div>
        {(lib[cls]?.[naTab]||[]).filter(s=>s.active).length===0
          ?<div style={{fontSize:12,color:"var(--text3)",fontStyle:"italic",padding:"8px 0",marginBottom:10}}>No active {naTab} skills in {cls}</div>
          :(lib[cls]?.[naTab]||[]).filter(s=>s.active).map(sk=>{
            const isNA=naSkills.includes(sk.id);
            return(
              <div key={sk.id} onClick={()=>toggleNA(sk.id)} style={{
                display:"flex",alignItems:"center",gap:10,padding:"9px 12px",
                borderRadius:8,marginBottom:4,cursor:"pointer",
                background:isNA?"#fef2f2":"var(--surface2)",
                border:`1.5px solid ${isNA?"var(--danger)":"var(--border)"}`,
                transition:"all .15s"
              }}>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:isNA?"var(--text3)":"var(--text)",textDecoration:isNA?"line-through":"none"}}>{sk.label}</div>
                  {sk.description&&<div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>{sk.description}</div>}
                </div>
                <div style={{
                  padding:"3px 10px",borderRadius:99,fontSize:11,fontWeight:800,
                  background:isNA?"#fef2f2":"var(--surface3)",
                  color:isNA?"var(--danger)":"var(--text3)",
                  border:`1.5px solid ${isNA?"var(--danger)":"var(--border)"}`
                }}>{isNA?"N/A":"—"}</div>
              </div>
            );
          })
        }

        <div style={{display:"flex",gap:8,marginTop:18}}>
          <button className="btn-secondary" style={{flex:1}} onClick={onClose}>Cancel</button>
          <button className="btn-primary" style={{flex:2}} onClick={handleSave}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COACH SUB VIEW  (full student profile)
// ═══════════════════════════════════════════════════════════════════════════════
function CoachSubView({subView,setSubView,db,addAssessment,addGoal,addSession,deleteSession,updGoal,updStudent,updAssessmentNotes,deactivateStudent,reactivateStudent,deleteCoachingNote,saveSkillNote,markCoachViewed,showNotif,isAdmin,onGenerateReport,saveReportSnapshot}){
  const s = db.students.find(st=>st.id===subView.data.id) || subView.data;
  const [tab,setTab]=useState("overview");
  const [showEdit,setShowEdit]=useState(false);
  const [showAssess,setShowAssess]=useState(false);
  const [showDeactivate,setShowDeactivate]=useState(false);
  const [editingNoteId,setEditingNoteId]=useState(null);
  const [editNoteDraft,setEditNoteDraft]=useState("");
  const [skillNoteEditingKey,setSkillNoteEditingKey]=useState(null);
  const [skillNoteDraft,setSkillNoteDraft]=useState("");
  const [skillHistoryExpanded,setSkillHistoryExpanded]=useState({});
  // Mark coach as having viewed this student — clears unread badge
  useEffect(()=>{ if(s?.id&&markCoachViewed) markCoachViewed(s.id); },[s?.id]);
  // Session player picker state — must be at top level (Rules of Hooks)
  const [addToSession,setAddToSession]=useState(null);
  const [pickerSelected,setPickerSelected]=useState(new Set());
  const lib=getOrgLibrary(db,s.orgId);
  const assessments=(db.assessments[s.id]||[]).sort((a,b)=>new Date(a.date)-new Date(b.date));
  const goals=db.goals[s.id]||[];
  const sessions=(db.sessions[s.id]||[]).sort((a,b)=>new Date(b.date)-new Date(a.date));
  const lt=assessments[assessments.length-1];
  const isInactive=s.status==="inactive";
  const allOrgCoaches=db.coaches.filter(c=>c.orgId===s.orgId);
  const reportHistory=(s.reportHistory||[]).slice().reverse();

  if(showAssess&&!isInactive) return(
    <AssessmentWizard student={s} db={db} addAssessment={addAssessment} onDone={()=>setShowAssess(false)} showNotif={showNotif} coachId={subView.coachId||"c1"}/>
  );

  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      <div style={{padding:"14px 14px 0",flexShrink:0,background:`linear-gradient(180deg,${isInactive?"var(--surface3)":"var(--surface)"} 0%,var(--bg) 100%)`}}>
        <button onClick={()=>setSubView(null)} style={{background:"transparent",color:"var(--accent)",fontSize:13,fontWeight:700,marginBottom:10,padding:0}}>← {subView.fromTab==="home"?"Home":subView.fromTab==="school"?"School":"Squad"}</button>

        {isInactive&&(
          <div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:10,padding:"10px 14px",marginBottom:10,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:20}}>⏸️</span>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:13,color:"var(--danger)"}}>Inactive Player</div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                {s.statusReason?`Reason: ${s.statusReason} · `:""}{s.statusDate?`Since ${dateStr(s.statusDate)}`:""}
              </div>
            </div>
            <button className="btn-secondary btn-sm" style={{borderColor:"var(--accent3)",color:"var(--accent3)"}}
              onClick={()=>{reactivateStudent(s.id);showNotif(`${s.name} reactivated ✅`);}}>
              ▶ Reactivate
            </button>
          </div>
        )}

        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
          <div className="avatar" style={{width:52,height:52,
            background:isInactive?"linear-gradient(135deg,#bbb,#999)":"linear-gradient(135deg,var(--accent),#003d5c)",
            fontSize:18,color:"#fff",fontFamily:"var(--font-display)",opacity:isInactive?0.7:1}}>{s.avatar}</div>
          <div style={{flex:1}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:22,letterSpacing:.5,color:"var(--text)",opacity:isInactive?0.7:1}}>{s.name}</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:4}}>
              <ClassBadge cls={s.cricketClass}/>
              {(s.tags||[]).map(t=><TagChip key={t} tagId={t}/>)}
            </div>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:4,alignItems:"flex-end"}}>
            {lt&&<ScoreRing value={lt.overall||overallFromAssessment(lt)} size={52} label="Overall"/>}
            <div style={{display:"flex",gap:4}}>
              {!isInactive&&<button className="btn-ghost btn-sm" onClick={()=>setShowEdit(true)}>✏️ Edit</button>}
              {!isInactive&&<button className="btn-ghost btn-sm" style={{color:"var(--danger)",borderColor:"var(--danger)"}} onClick={()=>setShowDeactivate(true)}>⏸</button>}
            </div>
          </div>
        </div>
        <div className="scroll-x" style={{marginBottom:0}}>
          {[["overview","📊"],["progress","📈"],["goals","🎯"],["sessions","📋"],["skillnotes","📝"],["reports","📄"]].map(([t,i])=>(
            <button key={t} className={`pill${tab===t?" active":""}`} onClick={()=>setTab(t)} style={{textTransform:"capitalize"}}>{i} {t==="skillnotes"?"Skill Notes":t}</button>
          ))}
          {!isInactive&&<button className="pill" style={{background:"var(--accent2)",borderColor:"var(--accent2)",color:"#fff",fontWeight:700}} onClick={()=>setShowAssess(true)}>📊 Assess</button>}
        </div>
        <div className="divider" style={{margin:"8px 0 0"}}/>
      </div>
      <div className="screen" style={{padding:"10px 12px"}}>

        {tab==="overview"&&(<>
          {!lt&&<div className="empty"><div className="empty-icon">📊</div><div>No assessments yet</div><button className="btn-primary" style={{marginTop:12,width:"auto",padding:"10px 20px"}} onClick={()=>setShowAssess(true)}>Run First Assessment</button></div>}
          {lt&&(<>
            <div style={{display:"flex",gap:6,justifyContent:"space-around",marginBottom:12}}>
              {CATEGORIES.map(cat=>{
                const avg=catAvgFromAssessment(lt,cat);
                if(!avg) return null;
                return <ScoreRing key={cat} value={avg} size={54} label={cat.substring(0,4)}/>;
              })}
            </div>
            {CATEGORIES.map(cat=>{
              const sks=lt.skillSnapshot?.[cat]||(lib[lt.cricketClass||s.cricketClass]?.[cat]||[]).filter(s=>s.active);
              const sc=lt.scores?.[cat]||{};
              if(!sks.length||!Object.keys(sc).length) return null;
              return(
                <div key={cat} className="card">
                  <div style={{fontWeight:700,fontSize:11,color:CAT_COLOR[cat],textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>{CAT_ICON[cat]} {cat}</div>
                  <RadarChart scores={sc} skills={sks} color={CAT_COLOR[cat]}/>
                </div>
              );
            })}
            {lt.notes&&<div className="card" style={{borderColor:"var(--accent)"}}><div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Latest Notes</div><div style={{fontSize:13,color:"var(--text2)",fontStyle:"italic",lineHeight:1.5}}>"{lt.notes}"</div><div style={{fontSize:10,color:"var(--text3)",marginTop:4}}>{dateStr(lt.date)} · Week {lt.week}</div></div>}
          </>)}
        </>)}

        {tab==="progress"&&(<>
          {assessments.length<2?<div className="empty"><div className="empty-icon">📈</div><div>Need 2+ assessments for progress charts</div></div>
          :CATEGORIES.map(cat=>{
            const data=assessments.filter(a=>(a.scores?.[cat]&&Object.keys(a.scores[cat]).length)).map(a=>({y:catAvgFromAssessment(a,cat),label:`W${a.week}`}));
            if(data.length<2) return null;
            return(
              <div key={cat} className="card">
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <div style={{fontWeight:700,fontSize:13,color:CAT_COLOR[cat]}}>{CAT_ICON[cat]} {cat}</div>
                  <div style={{fontFamily:"var(--font-mono)",fontSize:12,color:CAT_COLOR[cat]}}>{data[0].y}→{data[data.length-1].y}</div>
                </div>
                <MiniLine data={data} color={CAT_COLOR[cat]}/>
              </div>
            );
          })}
          {assessments.length>0&&(<>
            <div className="sec-title" style={{marginTop:8}}>All Assessments</div>
            {[...assessments].reverse().map((a,idx)=>(
              <div key={a.id} className="card">
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:13}}>Week {a.week}</div>
                    <div style={{fontSize:11,color:"var(--text3)"}}>{dateStr(a.date)}</div>
                    <ClassBadge cls={a.cricketClass||s.cricketClass} size={9}/>
                  </div>
                  <ScoreRing value={a.overall||overallFromAssessment(a)} size={46}/>
                </div>
                {/* Q5: editable notes — only on most recent assessment (idx===0 because reversed) */}
                {editingNoteId===a.id?(
                  <div style={{marginTop:8}}>
                    <textarea value={editNoteDraft} onChange={e=>setEditNoteDraft(e.target.value)}
                      style={{marginBottom:8,minHeight:60}} placeholder="Add or correct your coaching notes…"/>
                    <div style={{display:"flex",gap:6}}>
                      <button className="btn-secondary btn-sm" style={{flex:1}} onClick={()=>setEditingNoteId(null)}>Cancel</button>
                      <button className="btn-primary btn-sm" style={{flex:2}} onClick={()=>{
                        updAssessmentNotes(s.id,a.id,editNoteDraft.trim());
                        setEditingNoteId(null);
                        showNotif("Notes updated ✅");
                      }}>Save Notes</button>
                    </div>
                  </div>
                ):(
                  <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:8,borderTop:a.notes||idx===0?"1px solid var(--border)":"none",paddingTop:a.notes||idx===0?6:0,marginTop:a.notes||idx===0?2:0}}>
                    {a.notes&&<div style={{flex:1,fontSize:11,color:"var(--text2)",fontStyle:"italic",lineHeight:1.5}}>"{a.notes}"</div>}
                    {idx===0&&(
                      <button className="btn-ghost btn-sm" style={{flexShrink:0,fontSize:10,borderColor:"var(--accent)",color:"var(--accent)"}}
                        onClick={()=>{setEditingNoteId(a.id);setEditNoteDraft(a.notes||"");}}>
                        ✏️ {a.notes?"Edit Notes":"Add Notes"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </>)}
        </>)}

        {tab==="goals"&&(()=>{
          const coachingNotes=(db.coachingNotes?.[s.id]||[]).slice().reverse();
          return(
            <>
              {coachingNotes.length>0&&(
                <div style={{marginBottom:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                    <span style={{fontSize:14}}>💬</span>
                    <div style={{fontWeight:700,fontSize:12,color:"var(--purple)"}}>Notes from {s.name}</div>
                    <div style={{fontSize:10,color:"var(--text3)"}}>({coachingNotes.length})</div>
                  </div>
                  {coachingNotes.map(n=>(
                    <div key={n.id} className="card" style={{borderColor:"var(--purple)",marginBottom:6}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                        <div style={{flex:1}}>
                          <div style={{fontSize:13,color:"var(--text)",lineHeight:1.5}}>"{n.text}"</div>
                          <div style={{fontSize:10,color:"var(--text3)",marginTop:5}}>{dateStr(n.date)}</div>
                        </div>
                        {deleteCoachingNote&&(
                          <button className="btn-ghost btn-sm" style={{color:"var(--text3)",flexShrink:0}}
                            onClick={()=>deleteCoachingNote(s.id,n.id)}>✕</button>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="divider" style={{margin:"12px 0"}}/>
                </div>
              )}
              {goals.length===0
                ?<div className="empty"><div className="empty-icon">🎯</div><div>No goals set</div></div>
                :(()=>{
                  const active=[...goals].filter(g=>g.status!=="completed").sort((a,b)=>new Date(a.targetDate)-new Date(b.targetDate));
                  const done=[...goals].filter(g=>g.status==="completed");
                  return(<>
                    {active.map(g=><GoalCard key={g.id} goal={g} onUpdate={(gid,v)=>updGoal(s.id,gid,v)} canEdit/>)}
                    {done.length>0&&<>
                      <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:1,margin:"12px 0 8px"}}>🏆 Completed</div>
                      {done.map(g=><GoalCard key={g.id} goal={g} onUpdate={()=>{}} canEdit={false}/>)}
                    </>}
                  </>);
                })()
              }
            </>
          );
        })()}

        {tab==="sessions"&&(()=>{
          // Player pool for picker — all active players in school except current player
          const pickerPool=(db.students||[])
            .filter(x=>x.status!=="inactive"&&x.id!==subView.id&&x.orgId===s.orgId)
            .sort((a,b)=>a.name.localeCompare(b.name));

          const confirmAddPlayers=()=>{
            if(!pickerSelected.size||!addToSession) return;
            pickerSelected.forEach(pid=>{
              // addSession(studentId, sessionObject) — sid is the KEY in db.sessions
              addSession(pid,{
                type:addToSession.type,
                date:addToSession.date,
                duration:addToSession.duration,
                coachNote:addToSession.coachNote||"",
                isGroupSession:true,
                attended:true,
                addedViaPickerFrom:s.id,
                loggedBy:subView.coachId,
              });
            });
            showNotif(pickerSelected.size+" player"+
              (pickerSelected.size!==1?"s":"")+" added to session ✅");
            setAddToSession(null);
            setPickerSelected(new Set());
          };

          return(<>
            {/* Player picker modal */}
            {addToSession&&(
              <div className="overlay" onClick={e=>{if(e.target===e.currentTarget){setAddToSession(null);setPickerSelected(new Set());}}}>
                <div className="sheet" style={{maxHeight:"80%",overflowY:"auto"}}>
                  <div className="handle"/>
                  <div style={{fontFamily:"var(--font-display)",fontSize:20,marginBottom:4}}>
                    Add Missed Players
                  </div>
                  <div style={{fontSize:12,color:"var(--text3)",marginBottom:4}}>
                    {addToSession.type} Session · {addToSession.duration}min · {dateStr(addToSession.date)}
                  </div>
                  <div style={{fontSize:12,color:"var(--text2)",fontStyle:"italic",marginBottom:14}}>
                    "{addToSession.coachNote}"
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div className="lbl" style={{marginBottom:0}}>Select players to add</div>
                    <div style={{display:"flex",gap:8}}>
                      <button className="btn-ghost btn-sm"
                        onClick={()=>setPickerSelected(new Set(pickerPool.map(p=>p.id)))}>
                        All
                      </button>
                      <button className="btn-ghost btn-sm"
                        onClick={()=>setPickerSelected(new Set())}>
                        Clear
                      </button>
                    </div>
                  </div>
                  {pickerPool.length===0?(
                    <div style={{fontSize:13,color:"var(--text3)",padding:"12px 0",textAlign:"center"}}>
                      No other players in this school
                    </div>
                  ):(
                    <div style={{borderRadius:10,border:"1px solid var(--border)",overflow:"hidden",marginBottom:14}}>
                      {pickerPool.map((p,i)=>{
                        const checked=pickerSelected.has(p.id);
                        return(
                          <div key={p.id}
                            onClick={()=>setPickerSelected(prev=>{
                              const n=new Set(prev);
                              checked?n.delete(p.id):n.add(p.id);
                              return n;
                            })}
                            style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",
                              cursor:"pointer",
                              borderBottom:i<pickerPool.length-1?"1px solid var(--border-lt,#f0f0f0)":"none",
                              background:checked?"#eff6ff":"transparent"}}>
                            <div style={{width:20,height:20,borderRadius:4,flexShrink:0,
                              background:checked?"var(--accent)":"transparent",
                              border:"2px solid "+(checked?"var(--accent)":"var(--border)"),
                              display:"flex",alignItems:"center",justifyContent:"center",
                              fontSize:12,color:"#fff",fontWeight:900}}>
                              {checked?"✓":""}
                            </div>
                            <div className="avatar" style={{width:30,height:30,flexShrink:0,
                              background:"linear-gradient(135deg,var(--accent),#003d5c)",
                              fontSize:11,color:"#fff",fontFamily:"var(--font-display)"}}>
                              {p.avatar}
                            </div>
                            <div style={{flex:1}}>
                              <div style={{fontWeight:700,fontSize:13,
                                color:checked?"var(--accent)":"var(--text)"}}>{p.name}</div>
                              <div style={{fontSize:10,color:"var(--text3)"}}>{p.cricketClass||"No class assigned"}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {pickerSelected.size>0&&(
                    <button className="btn-primary" style={{width:"100%",marginBottom:8}}
                      onClick={confirmAddPlayers}>
                      Add {pickerSelected.size} Player{pickerSelected.size!==1?"s":""} to Session
                    </button>
                  )}
                  <button className="btn-secondary" style={{width:"100%"}}
                    onClick={()=>{setAddToSession(null);setPickerSelected(new Set());}}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Session list */}
            {sessions.length===0
              ?<div className="empty"><div className="empty-icon">📋</div><div>No sessions logged</div></div>
              :sessions.map(se=>(
                <div key={se.id} className="card">
                  <div style={{display:"flex",justifyContent:"space-between",gap:8,alignItems:"flex-start"}}>
                    <div style={{flex:1}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
                        <span className="badge" style={{background:"#dbeafe",color:"var(--accent)",border:"1.5px solid var(--accent)"}}>{se.type}</span>
                        {se.isGroupSession&&<span className="badge" style={{background:"#fff0ea",color:"var(--accent2)",border:"1.5px solid var(--accent2)"}}>👥 Group</span>}
                        <span style={{fontSize:11,color:"var(--text3)"}}>{se.duration}min</span>
                      </div>
                      <div style={{fontSize:11,color:"var(--text3)",margin:"3px 0"}}>{dateStr(se.date)}</div>
                      <div style={{fontSize:12,color:"var(--text2)",fontStyle:"italic"}}>"{se.coachNote}"</div>
                      {se.addedViaPickerFrom&&(()=>{
                        const src=db.students.find(x=>x.id===se.addedViaPickerFrom);
                        return src?<div style={{fontSize:10,color:"var(--accent2)",marginTop:3}}>
                          👥 Added from {src.name}'s session
                        </div>:null;
                      })()}
                    </div>
                    <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6,flexShrink:0}}>
                      <div style={{fontSize:22}}>{se.attended?"✅":"❌"}</div>
                      {pickerPool.length>0&&(
                        <button
                          onClick={()=>{setAddToSession(se);setPickerSelected(new Set());}}
                          style={{padding:"3px 8px",borderRadius:8,fontSize:10,fontWeight:700,
                            color:"var(--accent)",background:"#EFF6FF",
                            border:"1px solid var(--accent)",cursor:"pointer",whiteSpace:"nowrap"}}>
                          + Player
                        </button>
                      )}
                      <button
                        onClick={()=>{
                          if(window.confirm("Remove this session from "+s.name+"?")){
                            deleteSession(s.id,se.id);
                          }
                        }}
                        title="Remove this session"
                        style={{padding:"2px 7px",borderRadius:6,fontSize:11,
                          color:"var(--text4)",background:"transparent",
                          border:"1px solid var(--border)",cursor:"pointer",
                          lineHeight:1.2,opacity:0.6}}>
                        ✕
                      </button>
                    </div>
                  </div>
                </div>
              ))
            }
          </>);
        })()}

        {/* ── Reports tab ── */}
        {tab==="skillnotes"&&(()=>{
          const cls=s.cricketClass;
          const lib=getOrgLibrary(db,s.orgId);
          const sNotes=db.skillNotes?.[s.id]||{};
          const updatedAt=sNotes._updatedAt;
          const editingKey=skillNoteEditingKey;
          const setEditingKey=setSkillNoteEditingKey;
          const draft=skillNoteDraft;
          const setDraft=setSkillNoteDraft;
          // Helper: get notes array for a skill (handles legacy string migration)
          const getNotes=(cat,skillId)=>{
            const raw=(sNotes[cat]||{})[skillId];
            if(!raw) return [];
            if(Array.isArray(raw)) return raw;
            return [{note:raw,date:sNotes._updatedAt||"",coachId:sNotes._coachId||""}];
          };
          return(
            <div>
              {updatedAt&&<div style={{fontSize:11,color:"var(--text3)",marginBottom:10}}>Last updated {dateStr(updatedAt)}</div>}
              {CATEGORIES.map(cat=>{
                const catSkills=(lib[cls]?.[cat]||[]).filter(sk=>sk.active);
                if(!catSkills.length) return null;
                return(
                  <div key={cat} className="card" style={{marginBottom:10,padding:0,overflow:"hidden"}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"10px 12px",borderBottom:"1px solid var(--border)",background:"var(--surface)"}}>
                      <span style={{fontSize:16}}>{CAT_ICON[cat]}</span>
                      <span style={{fontWeight:700,fontSize:13,color:CAT_COLOR[cat],textTransform:"uppercase",letterSpacing:.5}}>{cat}</span>
                      <span style={{fontSize:10,color:"var(--text3)",marginLeft:"auto"}}>{catSkills.filter(sk=>getNotes(cat,sk.id).length>0).length}/{catSkills.length} tips added</span>
                    </div>
                    {catSkills.map(sk=>{
                      const key=cat+"__"+sk.id;
                      const isEditing=editingKey===key;
                      const notes=getNotes(cat,sk.id);
                      const latestNote=notes[0]||null;
                      const historyNotes=notes.slice(1);
                      const histKey=cat+"__"+sk.id;
                      const histExpanded=skillHistoryExpanded[histKey];
                      return(
                        <div key={sk.id} style={{padding:"10px 12px",borderBottom:"1px solid var(--border)"}}>
                          <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:6}}>{sk.label}</div>
                          {/* Latest note display */}
                          {!isEditing&&(
                            <div>
                              <div style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:latestNote?4:0}}>
                                <div style={{flex:1,padding:"7px 10px",borderRadius:8,
                                  background:latestNote?"#f0f9ff":"var(--surface2)",
                                  border:`1px solid ${latestNote?"#bae0fd":"var(--border)"}`,
                                  fontSize:12,color:latestNote?"#0369a1":"var(--text3)",
                                  lineHeight:1.5,fontStyle:latestNote?"normal":"italic",
                                  cursor:"pointer",minHeight:34}}
                                  onClick={()=>{setEditingKey(key);setDraft("");}}>
                                  {latestNote?latestNote.note:"Tap to add coaching tip…"}
                                </div>
                                <button
                                  title="Add new tip"
                                  onClick={()=>{setEditingKey(key);setDraft("");}}
                                  style={{background:"var(--accent)",border:"none",borderRadius:6,padding:"6px 8px",cursor:"pointer",color:"#fff",fontSize:11,fontWeight:700,flexShrink:0}}>
                                  + Add
                                </button>
                              </div>
                              {latestNote&&<div style={{fontSize:10,color:"var(--text3)",marginBottom:4}}>{dateStr(latestNote.date)}</div>}
                              {/* History */}
                              {historyNotes.length>0&&(
                                <div>
                                  <button
                                    onClick={()=>setSkillHistoryExpanded(h=>({...h,[histKey]:!h[histKey]}))}
                                    style={{background:"none",border:"none",padding:"3px 0",fontSize:11,color:"var(--text3)",cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                                    <span style={{fontSize:10}}>{histExpanded?"▾":"▸"}</span>
                                    {historyNotes.length} earlier tip{historyNotes.length!==1?"s":""}
                                  </button>
                                  {histExpanded&&historyNotes.map((n,i)=>(
                                    <div key={i} style={{marginTop:4,padding:"6px 10px",borderRadius:8,background:"var(--surface2)",border:"1px solid var(--border)",opacity:.75}}>
                                      <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.5}}>{n.note}</div>
                                      <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>{n.date?dateStr(n.date):""}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {/* Add new note form */}
                          {isEditing&&(
                            <div>
                              {latestNote&&(
                                <div style={{marginBottom:8,padding:"6px 10px",borderRadius:8,background:"var(--surface2)",border:"1px solid var(--border)",opacity:.7}}>
                                  <div style={{fontSize:10,color:"var(--text3)",marginBottom:2}}>Previous tip — {latestNote.date?dateStr(latestNote.date):""}</div>
                                  <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.5}}>{latestNote.note}</div>
                                </div>
                              )}
                              <textarea value={draft} onChange={e=>setDraft(e.target.value)}
                                placeholder={`New coaching tip for ${sk.label}…`}
                                style={{minHeight:64,fontSize:12,marginBottom:6}}
                                autoFocus/>
                              <div style={{display:"flex",gap:6}}>
                                <button className="btn-secondary btn-sm" onClick={()=>{setEditingKey(null);setDraft("");}}>Cancel</button>
                                <button className="btn-primary btn-sm" disabled={!draft.trim()} onClick={()=>{
                                  if(!draft.trim()) return;
                                  saveSkillNote(s.id,cat,sk.id,draft);
                                  setEditingKey(null);setDraft("");
                                  showNotif("Tip saved ✅");
                                }}>Save</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {tab==="reports"&&(
          <div>
            {/* Generate new report button */}
            <div className="card card-hl" style={{marginBottom:12}}>
              <div style={{fontFamily:"var(--font-display)",fontSize:18,color:"var(--accent3)",marginBottom:6}}>📄 Progress Report</div>
              <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.6,marginBottom:12}}>
                Generate a formal PDF progress report for {s.name}. The report covers all assessments, sessions, and goals. It can be printed or saved as PDF and shared with parents.
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:4}}>
                <div style={{fontSize:11,color:"var(--text3)"}}>{assessments.length} assessment{assessments.length!==1?"s":""}</div>
                <div style={{fontSize:11,color:"var(--text3)"}}>·</div>
                <div style={{fontSize:11,color:"var(--text3)"}}>{sessions.length} session{sessions.length!==1?"s":""}</div>
                <div style={{fontSize:11,color:"var(--text3)"}}>·</div>
                <div style={{fontSize:11,color:"var(--text3)"}}>{goals.length} goal{goals.length!==1?"s":""}</div>
              </div>
              {assessments.length===0?(
                <div style={{fontSize:12,color:"var(--text3)",fontStyle:"italic",padding:"8px 0"}}>Complete at least one assessment before generating a report.</div>
              ):(
                <button className="btn-primary" style={{background:"linear-gradient(135deg,var(--accent3),#0a5c2a)",marginTop:4}}
                  onClick={()=>onGenerateReport&&onGenerateReport(s.id)}>
                  📄 Generate Report
                </button>
              )}
            </div>

            {/* Past reports archive */}
            {reportHistory.length>0&&(<>
              <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Past Reports ({reportHistory.length})</div>
              {reportHistory.map((r,i)=>(
                <div key={r.id||i} className="card" style={{marginBottom:6}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:22,flexShrink:0}}>📄</span>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:13}}>{r.label||`Report — ${dateStr(r.generatedAt)}`}</div>
                      <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                        {r.sessionCount} sessions · {r.assessmentCount} assessments · Generated {dateStr(r.generatedAt)}
                      </div>
                    </div>
                    <button className="btn-ghost btn-sm" style={{color:"var(--accent)",borderColor:"var(--accent)",flexShrink:0}}
                      onClick={()=>onGenerateReport&&onGenerateReport(s.id,r)}>
                      📄 View
                    </button>
                  </div>
                </div>
              ))}
            </>)}
            {reportHistory.length===0&&assessments.length>0&&(
              <div className="empty" style={{padding:"20px 0"}}>
                <div className="empty-icon">📄</div>
                <div style={{fontWeight:700,marginBottom:4}}>No reports generated yet</div>
                <div style={{fontSize:12}}>Generate your first report above to share with parents</div>
              </div>
            )}
          </div>
        )}
      </div>

      {showEdit&&!isInactive&&<StudentEditor student={s} db={db} coaches={allOrgCoaches} isAdmin={isAdmin} currentCoachId={subView.coachId} onClose={()=>setShowEdit(false)} onSave={(id,cls,tags,naSkills,newCoachId,newName,newEmail,newPw,newAge,transferCoachId,siblingLinkId)=>{updStudent(id,cls,tags,naSkills,newCoachId,newName,newEmail,newPw,newAge,transferCoachId,siblingLinkId);setShowEdit(false);showNotif(siblingLinkId?"Players linked as siblings ✅":"Player updated ✅");}}/>}
      {showDeactivate&&<DeactivateModal student={s} onClose={()=>setShowDeactivate(false)}
        onConfirm={(reason)=>{deactivateStudent(s.id,reason);setShowDeactivate(false);showNotif(`${s.name} deactivated`,"success");}}/>}
    </div>
  );
}

function DeactivateModal({student,onConfirm,onClose}){
  const [reason,setReason]=useState("");
  const REASONS=["Graduated / Aged Out","Dropped Out","Taking a Season Off","Moved Away","Other"];
  return(
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="sheet">
        <div className="handle"/>
        <div style={{fontFamily:"var(--font-display)",fontSize:22,color:"var(--danger)",marginBottom:4}}>
          ⏸️ Deactivate Player
        </div>
        <div style={{fontSize:12,color:"var(--text3)",marginBottom:18,lineHeight:1.6}}>
          <strong style={{color:"var(--text)"}}>{student.name}</strong> will be hidden from your squad, assessments, and school view. Their full history is preserved and they can be reactivated at any time.
        </div>
        <div style={{marginBottom:6}}><div className="lbl">Reason (optional)</div></div>
        <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:14}}>
          {REASONS.map(r=>(
            <button key={r} onClick={()=>setReason(r)} style={{
              padding:"10px 14px",borderRadius:10,fontWeight:600,fontSize:13,textAlign:"left",
              background:reason===r?"#f4433618":"var(--surface2)",
              color:reason===r?"#f44336":"var(--text2)",
              border:`1.5px solid ${reason===r?"#f4433650":"var(--border)"}`
            }}>{r}</button>
          ))}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button className="btn-secondary" style={{flex:1}} onClick={onClose}>Cancel</button>
          <button className="btn-danger" style={{flex:2}} onClick={()=>onConfirm(reason)}>
            ⏸️ Deactivate
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COACH SCREENS
// ═══════════════════════════════════════════════════════════════════════════════
function CoachHome({user,students,db,getSA,setSubView,activeTab}){
  const [drillClass,setDrillClass]=useState(null); // null = home, else class name

  // Reset drill-down when the Home tab is re-activated from another tab
  useEffect(()=>{ setDrillClass(null); },[activeTab]);

  // If a class tile was clicked, show that class drill-down
  if(drillClass) return(
    <ClassDrillDown
      cls={drillClass}
      students={students.filter(s=>s.cricketClass===drillClass)}
      db={db}
      setSubView={setSubView}
      onBack={()=>setDrillClass(null)}
    />
  );

  const allA=students.flatMap(s=>getSA(s.id));
  const avgO=allA.length?(allA.reduce((t,a)=>t+(a.overall||overallFromAssessment(a)),0)/allA.length).toFixed(1):"—";
  const clsDist=CLASSES.map(cl=>({cl,cnt:students.filter(s=>s.cricketClass===cl).length}));

  return(
    <div>
      <div className="screen-hdr">
        {user.schoolName&&<div style={{fontSize:11,color:"var(--accent)",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:2}}>🏫 {user.schoolName}</div>}
        <div style={{fontSize:10,color:"var(--text2)",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:3}}>Good session,</div>
        <div style={{fontFamily:"var(--font-display)",fontSize:28,letterSpacing:1,marginBottom:2,color:"var(--text)",fontWeight:900}}>{user.name}</div>
        {user.team&&<div style={{fontSize:12,color:"var(--text2)",fontWeight:600}}>{user.team}</div>}
      </div>
      <div style={{padding:"0 8px"}}>
        <div className="stat-row">
          <div className="stat-box card-hl"><div className="stat-val">{students.length}</div><div className="stat-lbl">Players</div></div>
          <div className="stat-box"><div className="stat-val" style={{color:"var(--accent3)"}}>{avgO}</div><div className="stat-lbl">Avg Score</div></div>
          <div className="stat-box"><div className="stat-val" style={{color:"var(--gold)"}}>{allA.length}</div><div className="stat-lbl">Assessments</div></div>
        </div>

        {/* ── Class tiles — tap to drill in ── */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div className="sec-title" style={{margin:0}}>Classes</div>
          <div style={{fontSize:10,color:"var(--text3)"}}>Tap a class to view players</div>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:6,marginBottom:14}}>
          {clsDist.map(({cl,cnt})=>(
            <div key={cl} className="card" onClick={()=>setDrillClass(cl)}
              style={{borderColor:CLASS_COLOR[cl]+"50",padding:"12px",cursor:"pointer",
                      background:`linear-gradient(135deg,var(--surface),${CLASS_COLOR[cl]}10)`,
                      transition:"all .18s",boxShadow:cnt>0?`0 0 12px ${CLASS_COLOR[cl]}18`:"none"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <ClassBadge cls={cl}/>
                <div style={{fontFamily:"var(--font-display)",fontSize:32,color:CLASS_COLOR[cl],lineHeight:1}}>{cnt}</div>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div style={{fontSize:10,color:"var(--text3)"}}>{cnt} player{cnt!==1?"s":""}</div>
                {cnt>0&&<div style={{fontSize:10,color:CLASS_COLOR[cl],fontWeight:700}}>View →</div>}
              </div>
            </div>
          ))}
        </div>

        <div className="sec-title">Recent Players</div>
        {students.slice(0,5).map(s=><StudentRow key={s.id} s={s} db={db} setSubView={setSubView} fromTab="home"/>)}
      </div>
    </div>
  );
}

// ─── Class Drill-Down — shown when a Home class tile is tapped ────────────────
function ClassDrillDown({cls,students,db,setSubView,onBack}){
  const [tagFilter,setTagFilter]=useState(null);
  const c=CLASS_COLOR[cls];

  const filtered=tagFilter
    ? students.filter(s=>{
        const groupIds=TAG_GROUPS[tagFilter];
        return groupIds?(s.tags||[]).some(t=>groupIds.includes(t)):(s.tags||[]).includes(tagFilter);
      })
    : students;

  // Tag counts within this class
  const tagCounts=Object.entries(TAG_GROUPS).map(([grp,ids])=>({
    grp,
    cnt:students.filter(s=>(s.tags||[]).some(t=>ids.includes(t))).length
  })).filter(x=>x.cnt>0);

  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}} className="slide-in">
      {/* Header */}
      <div style={{padding:"14px 14px 0",flexShrink:0,
                   background:`linear-gradient(180deg,${c}18 0%,var(--bg) 100%)`}}>
        <button onClick={onBack}
          style={{background:"transparent",color:c,fontSize:13,fontWeight:700,padding:0,marginBottom:12}}>
          ← Home
        </button>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:12}}>
          <div style={{fontSize:36,lineHeight:1}}>{CLASS_ICON[cls]}</div>
          <div>
            <div style={{fontFamily:"var(--font-display)",fontSize:26,color:c,letterSpacing:1}}>{cls}</div>
            <div style={{fontSize:12,color:"var(--text3)"}}>
              {students.length} player{students.length!==1?"s":""} in this class
            </div>
          </div>
        </div>

        {/* Tag filter pills — only show tags that exist in this class */}
        {tagCounts.length>0&&(
          <div className="scroll-x" style={{marginBottom:4}}>
            <button className={`pill${!tagFilter?" active":""}`}
              style={{borderColor:c+"60",color:tagFilter?c:"#000",background:tagFilter?"transparent":c}}
              onClick={()=>setTagFilter(null)}>All ({students.length})</button>
            {tagCounts.map(({grp,cnt})=>(
              <button key={grp} className={`pill${tagFilter===grp?" active":""}`}
                onClick={()=>setTagFilter(g=>g===grp?null:grp)}>
                {grp} ({cnt})
              </button>
            ))}
          </div>
        )}
        <div className="divider" style={{margin:"8px 0 0"}}/>
      </div>

      {/* Student list */}
      <div className="screen" style={{padding:"8px 12px"}}>
        {students.length===0?(
          <div className="empty">
            <div className="empty-icon">{CLASS_ICON[cls]}</div>
            <div style={{fontWeight:700,marginBottom:6}}>No players in {cls} yet</div>
            <div style={{fontSize:12}}>Assign players to this class via their profile</div>
          </div>
        ):filtered.length===0?(
          <div className="empty">
            <div className="empty-icon">{CLASS_ICON[cls]}</div>
            <div>No players match this filter</div>
          </div>
        ):filtered.map(s=><StudentRow key={s.id} s={s} db={db} setSubView={setSubView} fromTab="home"/>)}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD STUDENT MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function AddStudentScreen({coaches,orgId,onSave,onClose,existingStudents}){
  const [name,setName]   = useState("");
  const [email,setEmail] = useState("");
  const [pw,setPw]       = useState("");
  const [age,setAge]     = useState("");
  const [cls,setCls]     = useState("Introductory");
  const [tags,setTags]   = useState([]);
  const [coachId,setCoachId] = useState(coaches[0]?.id||"");
  const [err,setErr]     = useState("");
  const noCoaches = coaches.length === 0;
  const [linkSibling,setLinkSibling]=useState(false);
  // When email is entered, check if it already belongs to a player in this school
  const existingMatch=(existingStudents||[]).find(s=>s.email===email.trim().toLowerCase());
  const toggleTag = t => setTags(ts=>ts.includes(t)?ts.filter(x=>x!==t):[...ts,t]);

  const submit = () => {
    setErr("");
    if(!name.trim())           { setErr("Name is required");            return; }
    if(!email.trim()||!email.includes("@")) { setErr("Valid email required"); return; }
    if(!coachId&&!noCoaches)   { setErr("Please assign this player to a coach"); return; }
    // If linking to an existing player email, don't require a new password
    if(linkSibling&&existingMatch){
      // Save new player and link both as siblings
      onSave({
        name:name.trim(), email:email.trim().toLowerCase(),
        password:existingMatch.password, // same password as sibling
        age:parseInt(age)||12, cricketClass:cls, tags, coachId, orgId,
        linkToPlayerId:existingMatch.id, // flag for addStudent to create the link
      });
      return;
    }
    if(!pw.trim()||pw.length<6){ setErr("Password must be 6+ characters"); return; }
    onSave({
      name:name.trim(), email:email.trim().toLowerCase(), password:pw,
      age:parseInt(age)||12, cricketClass:cls, tags, coachId, orgId,
    });
  };

  return(
    <div>
      <div className="screen-hdr">
        <button onClick={onClose} style={{background:"transparent",color:"var(--accent)",fontSize:13,fontWeight:700,padding:0,marginBottom:12}}>← Squad</button>
        <div style={{fontFamily:"var(--font-display)",fontSize:26,color:"var(--accent)",marginBottom:2}}>➕ Add New Player</div>
        <div style={{fontSize:12,color:"var(--text3)"}}>Player can log in with their email and password</div>
        <div className="divider" style={{margin:"10px 0 0"}}/>
      </div>
      <div style={{padding:"0 8px 24px"}}>
        {noCoaches&&(
          <div style={{background:"#fef9c3",border:"2px solid #d97706",borderRadius:10,padding:"14px 16px",marginBottom:16}}>
            <div style={{fontWeight:700,fontSize:14,color:"#92400e",marginBottom:6}}>⚠️ No coaches set up yet</div>
            <div style={{fontSize:13,color:"#78350f",lineHeight:1.6}}>Please add at least one coach before adding players. Go to Settings → Manage Coaches first.</div>
          </div>
        )}
        <div style={{marginBottom:12}}><div className="lbl">Full Name *</div><input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Sam Williams"/></div>
        <div style={{marginBottom:12}}><div className="lbl">Email Address *</div><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="sam@school.edu"/></div>
        {/* Sibling detection */}
        {existingMatch&&!linkSibling&&(
          <div style={{background:"#eff6ff",border:"1.5px solid var(--accent)",borderRadius:10,
            padding:"12px 14px",marginBottom:12}}>
            <div style={{fontWeight:700,fontSize:13,color:"var(--accent)",marginBottom:4}}>
              👥 This email already belongs to {existingMatch.name}
            </div>
            <div style={{fontSize:12,color:"var(--text2)",marginBottom:10,lineHeight:1.5}}>
              Are you adding a sibling? They can share the same login and switch between profiles.
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setLinkSibling(true)}
                style={{flex:1,padding:"8px",borderRadius:8,fontWeight:700,fontSize:12,
                  background:"var(--accent)",color:"#fff",border:"none",cursor:"pointer"}}>
                Yes, link as sibling
              </button>
              <button onClick={()=>setEmail("")}
                style={{flex:1,padding:"8px",borderRadius:8,fontWeight:700,fontSize:12,
                  background:"transparent",border:"1.5px solid var(--border)",cursor:"pointer"}}>
                No, use different email
              </button>
            </div>
          </div>
        )}
        {linkSibling&&existingMatch&&(
          <div style={{background:"#f0fdf4",border:"1.5px solid var(--accent3,#15803d)",borderRadius:10,
            padding:"10px 14px",marginBottom:12}}>
            <div style={{fontSize:12,color:"var(--accent3,#15803d)",fontWeight:700}}>
              ✅ Will be linked as sibling of {existingMatch.name}
            </div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
              Both players will share the same login email and can switch between profiles.
            </div>
          </div>
        )}
        {!linkSibling&&<div style={{marginBottom:12}}><div className="lbl">Password * (min 6 characters)</div><input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="Temporary password"/></div>}
        <div style={{marginBottom:14}}>
          <div className="lbl">Age</div>
          <select value={age} onChange={e=>setAge(e.target.value)} style={{width:"100%"}}>
            <option value="">Select age…</option>
            {[6,7,8,9,10,11,12,13,14,15,16,17,18].map(a=><option key={a} value={a}>{a} years old</option>)}
          </select>
        </div>
        {coaches.length>=1&&(
          <div style={{marginBottom:14}}>
            <div className="lbl">Assign to Coach *</div>
            <select value={coachId} onChange={e=>setCoachId(e.target.value)} style={{width:"100%"}}>
              {coaches.map(c=><option key={c.id} value={c.id}>{c.name}{c.team?` — ${c.team}`:""}</option>)}
            </select>
          </div>
        )}
        <div style={{marginBottom:14}}>
          <div className="lbl">Cricket Class Level</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:6}}>
            {CLASSES.map(cl=>(
              <button key={cl} onClick={()=>setCls(cl)} style={{padding:"9px",borderRadius:10,fontWeight:700,fontSize:12,
                background:cls===cl?CLASS_COLOR[cl]:"var(--surface2)",color:cls===cl?"#fff":"var(--text2)",
                border:`1.5px solid ${cls===cl?CLASS_COLOR[cl]:"var(--border)"}`}}>
                {CLASS_ICON[cl]} {cl}
              </button>
            ))}
          </div>
        </div>
        <div style={{marginBottom:20}}>
          <div className="lbl">Specialisation Tags (optional)</div>
          <div className="pill-row">
            {PLAYER_TAGS.map(t=>(
              <button key={t.id} onClick={()=>toggleTag(t.id)} style={{
                padding:"5px 10px",borderRadius:99,fontSize:11,fontWeight:700,cursor:"pointer",
                background:tags.includes(t.id)?t.color+"30":"transparent",
                color:tags.includes(t.id)?t.color:"var(--text2)",
                border:`1.5px solid ${tags.includes(t.id)?t.color:"var(--border)"}`,
                marginBottom:4
              }}>{t.icon} {t.id}</button>
            ))}
          </div>
        </div>
        {err&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"9px 12px",fontSize:12,color:"var(--danger)",marginBottom:14}}>{err}</div>}
        <div style={{display:"flex",gap:8}}>
          <button className="btn-secondary" style={{flex:1}} onClick={onClose}>Cancel</button>
          <button className="btn-primary" style={{flex:2,opacity:noCoaches?0.4:1}} onClick={noCoaches?undefined:submit} disabled={noCoaches}>✅ Add Player</button>
        </div>
        <div style={{fontSize:10,color:"var(--text3)",marginTop:12,textAlign:"center",lineHeight:1.6}}>Player can log in at any time using their email and password.</div>
      </div>
    </div>
  );
}


function CoachSquad({students,allStudents,db,getSA,setSubView,onAddStudent,allCoaches,orgId}){
  const [clsFilter,setClsFilter]=useState("All");
  const [tagFilter,setTagFilter]=useState(null);
  const [showAdd,setShowAdd]=useState(false);
  const [showInactive,setShowInactive]=useState(false);

  const [sortAZ,setSortAZ]=useState(true);
  const inactiveCount=(allStudents||students).filter(s=>s.status==="inactive").length;
  const sourceStudents=showInactive?(allStudents||students):students;

  const filtered=[...sourceStudents
    .filter(s=>clsFilter==="All"||s.cricketClass===clsFilter)
    .filter(s=>{
      if(!tagFilter) return true;
      const groupIds=TAG_GROUPS[tagFilter];
      return groupIds?(s.tags||[]).some(t=>groupIds.includes(t)):(s.tags||[]).includes(tagFilter);
    })].sort((a,b)=>sortAZ
      ? a.name.localeCompare(b.name)
      : (a.age||99)-(b.age||99));
  // Show Add Player as a full screen instead of overlay
  if(showAdd) return(
    <AddStudentScreen
      coaches={allCoaches.filter(c=>c.role!=="admin"&&c.status!=="inactive")}
      orgId={orgId}
      onClose={()=>setShowAdd(false)}
      onSave={data=>{onAddStudent(data);setShowAdd(false);}}
      existingStudents={allStudents||[]}
    />
  );

  return(
    <div>
      <div className="screen-hdr">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:6,marginBottom:6}}>
          <div className="sec-title" style={{margin:0,flexShrink:0}}>Squad</div>
          <div style={{display:"flex",gap:6,alignItems:"center",flexShrink:0}}>
            <button className="btn-primary btn-sm" onClick={()=>setShowAdd(true)}
              style={{whiteSpace:"nowrap"}}>Add Player+</button>
            <button
              onClick={()=>setSortAZ(s=>!s)}
              style={{padding:"5px 10px",borderRadius:99,fontSize:11,fontWeight:700,
                background:sortAZ?"var(--accent)":"var(--surface2)",
                color:sortAZ?"#fff":"var(--text3)",
                border:`1.5px solid ${sortAZ?"var(--accent)":"var(--border)"}`,
                cursor:"pointer",whiteSpace:"nowrap"}}>
              {sortAZ?"By Age":"A→Z ✓"}
            </button>
          </div>
        </div>
        <div className="scroll-x" style={{marginBottom:6}}>
          <button className={`pill${clsFilter==="All"?" active":""}`} onClick={()=>setClsFilter("All")}>All</button>
          {CLASSES.map(cl=>{const cnt=sourceStudents.filter(s=>s.cricketClass===cl).length;return cnt?<button key={cl} className={`pill${clsFilter===cl?" active":""}`} onClick={()=>setClsFilter(cl)} style={{color:clsFilter===cl?"#000":CLASS_COLOR[cl],borderColor:CLASS_COLOR[cl]+"60"}}>{CLASS_ICON[cl]} {cl} ({cnt})</button>:null;})}
        </div>
        <div className="scroll-x" style={{marginBottom:4}}>
          <button className={`pill${!tagFilter?" active":""}`} onClick={()=>setTagFilter(null)}>All Roles</button>
          {Object.keys(TAG_GROUPS).map(grp=>{const cnt=sourceStudents.filter(s=>(s.tags||[]).some(t=>TAG_GROUPS[grp].includes(t))).length;return cnt?<button key={grp} className={`pill${tagFilter===grp?" active":""}`} onClick={()=>setTagFilter(g=>g===grp?null:grp)}>{grp} ({cnt})</button>:null;})}
        </div>
        {inactiveCount>0&&(
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"6px 0",marginBottom:2}}>
            <Toggle on={showInactive} onChange={setShowInactive}/>
            <span style={{fontSize:11,color:"var(--text3)"}}>
              Show inactive players ({inactiveCount})
            </span>
          </div>
        )}
        <div className="divider" style={{margin:"4px 0 0"}}/>
      </div>
      <div style={{padding:"0 8px"}}>
        {students.length===0&&!showInactive&&(
          <div className="empty">
            <div className="empty-icon">👥</div>
            <div style={{fontWeight:700,marginBottom:6}}>No players yet</div>
            <div style={{fontSize:12,color:"var(--text3)",marginBottom:16}}>Add your first player to get started</div>
            <button className="btn-primary" style={{width:"auto",padding:"10px 24px"}} onClick={()=>setShowAdd(true)}>+ Add First Player</button>
          </div>
        )}
        {sourceStudents.length>0&&filtered.length===0&&<div className="empty"><div className="empty-icon">🏏</div><div>No players match filter</div></div>}
        {filtered.map(s=>{
          const inactive=s.status==="inactive";
          return(
            <div key={s.id} className="card" style={{cursor:"pointer",opacity:inactive?0.6:1,borderColor:inactive?"var(--border)":"var(--border)"}} onClick={()=>setSubView({type:"profile",data:s,fromTab:"squad"})}>
              {inactive&&(
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6,padding:"4px 8px",background:"#fef2f2",borderRadius:6}}>
                  <span style={{fontSize:11}}>⏸️</span>
                  <span style={{fontSize:11,color:"var(--danger)",fontWeight:600}}>Inactive{s.statusReason?` — ${s.statusReason}`:""}</span>
                </div>
              )}
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <div className="avatar" style={{width:46,height:46,background:inactive?"linear-gradient(135deg,#444,#222)":"linear-gradient(135deg,var(--accent),#005577)",fontSize:15,color:inactive?"#666":"#000",fontFamily:"var(--font-display)"}}>{s.avatar}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:14}}>{s.name}</div>
                  <div style={{fontSize:11,color:"var(--text3)"}}>{s.age}y</div>
                  <div style={{display:"flex",gap:3,flexWrap:"wrap",marginTop:4}}>
                    <ClassBadge cls={s.cricketClass} size={9}/>
                    {(s.tags||[]).map(t=><TagChip key={t} tagId={t}/>)}
                  </div>
                </div>
                {(()=>{const lt=(db.assessments[s.id]||[]).slice(-1)[0];return lt?<ScoreRing value={lt.overall||overallFromAssessment(lt)} size={46}/>:null;})()}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CoachAssessTab({students,allOrgStudents,allOrgCoaches,db,addSession,addGroupSession,addGoal,addGroupGoal,showNotif,isAdmin,isShared,onGenerateReport}){
  const [mode,setMode]=useState("session");
  const [sid,setSid]=useState(students[0]?.id||"");
  // In shared mode ensure sid is always a valid player id
  const sessionPool = isShared ? (allOrgStudents||students) : students;
  // Individual session
  const [st,setSt]=useState("Batting");const [sdur,setSdur]=useState("15");const [snote,setSnote]=useState("");const [sdate,setSdate]=useState(new Date().toISOString().split("T")[0]);
  // Group session
  const [gst,setGst]=useState("Match");const [gsdur,setGsdur]=useState("15");const [gsnote,setGsnote]=useState("");const [gsdate,setGsdate]=useState(new Date().toISOString().split("T")[0]);
  const [selected,setSelected]=useState(new Set());
  // Group session history
  const [histCoach,setHistCoach]=useState("all");const [histExpanded,setHistExpanded]=useState({});
  // Individual goal
  const [gtitle,setGtitle]=useState("");const [gcat,setGcat]=useState("Batting");const [gdate,setGdate]=useState("");
  // Group/squad goal
  const [ggTitle,setGgTitle]=useState("");const [ggCat,setGgCat]=useState("Batting");const [ggDate,setGgDate]=useState("");
  const [ggSel,setGgSel]=useState(new Set());

  const toggleSel=id=>setSelected(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n;});
  const toggleGgSel=id=>setGgSel(s=>{const n=new Set(s);n.has(id)?n.delete(id):n.add(id);return n;});

  if(!students.length) return(
    <div>
    <div className="empty"><div className="empty-icon">👥</div><div>No players in your squad yet</div></div></div>
  );

  const submitS=()=>{
    if(!snote.trim()){showNotif("Add a note","error");return;}
    addSession(sid,{date:sdate||new Date().toISOString().split("T")[0],type:st,duration:parseInt(sdur),coachNote:snote,attended:true});
    setSnote("");setSdate(new Date().toISOString().split("T")[0]);showNotif("Session logged 📋");
  };
  const submitG=()=>{
    if(!gtitle.trim()||!gdate){showNotif("Fill all fields","error");return;}
    addGoal(sid,{title:gtitle,category:gcat,targetDate:gdate,status:"active",createdBy:"coach",progress:0});
    setGtitle("");setGdate("");showNotif("Goal set 🎯");
  };
  const submitGroup=()=>{
    if(selected.size===0){showNotif("Select at least one player","error");return;}
    if(!gsnote.trim()){showNotif("Add session notes","error");return;}
    addGroupSession([...selected],{date:gsdate||new Date().toISOString().split("T")[0],type:gst,duration:parseInt(gsdur),coachNote:gsnote,attended:true,isGroupSession:true});
    showNotif(`Group session logged for ${selected.size} player${selected.size!==1?"s":""} 📋`);
    setGsnote("");setSelected(new Set());setGsdate(new Date().toISOString().split("T")[0]);
  };
  const submitGroupGoal=()=>{
    if(ggSel.size===0){showNotif("Select at least one player","error");return;}
    if(!ggTitle.trim()||!ggDate){showNotif("Fill all fields","error");return;}
    addGroupGoal([...ggSel],{title:ggTitle,category:ggCat,targetDate:ggDate,status:"active",createdBy:"coach",progress:0});
    showNotif(`Squad goal set for ${ggSel.size} player${ggSel.size!==1?"s":""} 🎯`);
    setGgTitle("");setGgDate("");setGgSel(new Set());
  };

  // ── Build group session history (Q3) ─────────────────────────────────────
  const histStudents = isAdmin ? (allOrgStudents||students) : students;
  const sessMap={};
  histStudents.forEach(stu=>{
    (db.sessions[stu.id]||[]).filter(se=>se.isGroupSession).forEach(se=>{
      const key=`${se.date}||${se.type}||${se.coachNote}`;
      if(!sessMap[key]) sessMap[key]={...se,playerNames:[],playerIds:[],coachId:stu.coachId};
      sessMap[key].playerNames.push(stu.name);
      sessMap[key].playerIds.push(stu.id);
    });
  });
  const allGroupSessions=Object.values(sessMap).sort((a,b)=>new Date(b.date)-new Date(a.date));
  const filteredGroupSessions=histCoach==="all"?allGroupSessions:allGroupSessions.filter(gs=>{
    const stu=histStudents.find(s=>gs.playerIds.includes(s.id));
    return stu&&stu.coachId===histCoach;
  });
  const getCoachName=cid=>(allOrgCoaches||[]).find(c=>c.id===cid)?.name||"";

  return(
    <div>
      <div className="screen-hdr">
        <div className="sec-title">Input Tools</div>
        <div className="scroll-x" style={{marginBottom:4}}>
          {[["session","📋","Session"],["group","👥","Group Session"],["history","📅","Session Log"],["goal","🎯","Goal"],["groupGoal","🎯👥","Squad Goal"],["goalLog","📅🎯","Goal Log"],["reports","📄","Reports"]].map(([t,ic,lb])=>(
            <button key={t} className={`pill${mode===t?" active":""}`} onClick={()=>setMode(t)}>{ic} {lb}</button>
          ))}
        </div>
      </div>
      <div style={{padding:"0 8px"}}>

        {/* ── Individual Session ── */}
        {mode==="session"&&(<>
          <div style={{marginBottom:12}}><div className="lbl">Player {isShared&&<span style={{fontWeight:400,color:"var(--text3)",fontSize:10}}>— all school</span>}</div>
            <select value={sid} onChange={e=>setSid(e.target.value)}>
              {(isShared?(allOrgStudents||students):students)
                .slice().sort((a,b)=>a.name.localeCompare(b.name))
                .map(s=><option key={s.id} value={s.id}>{s.name} — {s.cricketClass}</option>)}
            </select>
          </div>
          <div className="card card-hl">
            <div style={{fontFamily:"var(--font-display)",fontSize:20,color:"var(--accent)",marginBottom:12}}>📋 Log Session</div>
            <div style={{marginBottom:10}}><div className="lbl">Type</div>
              <div className="scroll-x" style={{marginBottom:4}}>{["Batting","Bowling","Fielding","Match","Fitness","Other"].map(t=><button key={t} className={`pill${st===t?" active":""}`} onClick={()=>setSt(t)}>{t}</button>)}</div>
            </div>
            <div style={{marginBottom:10}}><div className="lbl">Duration</div>
              <select value={sdur} onChange={e=>setSdur(e.target.value)}>{[5,10,15,20,30,45,60,90,120].map(d=><option key={d} value={d}>{d} min</option>)}</select>
            </div>
            <div style={{marginBottom:12}}><div className="lbl">Session Date</div>
              <input type="date" value={sdate} onChange={e=>setSdate(e.target.value)} max={new Date().toISOString().split("T")[0]}/>
              {sdate!==new Date().toISOString().split("T")[0]&&<div style={{fontSize:11,color:"var(--accent2)",marginTop:4}}>📅 Logging for a past session</div>}
            </div>
            <div style={{marginBottom:12}}><div className="lbl">Notes</div><textarea value={snote} onChange={e=>setSnote(e.target.value)} placeholder="What did you work on?"/></div>
            <button className="btn-primary" onClick={submitS}>📋 Log Session</button>
          </div>
        </>)}

        {/* ── Group Session (log new) ── */}
        {mode==="group"&&(
          <div className="card card-hl">
            <div style={{fontFamily:"var(--font-display)",fontSize:20,color:"var(--accent2)",marginBottom:4}}>👥 Group Session</div>
            <div style={{fontSize:11,color:"var(--text2)",marginBottom:14}}>Select players, enter shared notes. Same session logged for every selected player — visible in each player's own login.</div>
            <div style={{marginBottom:10}}><div className="lbl">Session Type</div>
              <div className="scroll-x" style={{marginBottom:4}}>{["Batting","Bowling","Fielding","Match","Fitness","Other"].map(t=><button key={t} className={`pill${gst===t?" active":""}`} onClick={()=>setGst(t)}>{t}</button>)}</div>
            </div>
            <div style={{marginBottom:12}}><div className="lbl">Duration</div>
              <select value={gsdur} onChange={e=>setGsdur(e.target.value)}>{[5,10,15,20,30,45,60,90,120].map(d=><option key={d} value={d}>{d} min</option>)}</select>
            </div>
            {/* ── Player pool: own squad (private) or all school students grouped by coach (shared) ── */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
              <div className="lbl" style={{marginBottom:0}}>Select Players {isShared&&<span style={{fontWeight:400,color:"var(--accent2)",fontSize:10}}> — All school students</span>}</div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <button className="btn-ghost btn-sm" onClick={()=>setSelected(new Set((isShared?(allOrgStudents||students):students).map(s=>s.id)))}>Select All</button>
                <button className="btn-ghost btn-sm" onClick={()=>setSelected(new Set())}>Clear</button>
                {selected.size>0&&<span style={{fontSize:11,color:"var(--accent2)",fontWeight:700}}>{selected.size}</span>}
              </div>
            </div>
            {(()=>{
              const pool = isShared ? (allOrgStudents||students) : students;
              if(!isShared){
                // Private mode: flat list (existing behaviour)
                return pool.map(s=>(
                  <div key={s.id} onClick={()=>toggleSel(s.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:8,marginBottom:5,cursor:"pointer",background:selected.has(s.id)?"#fff0ea":"var(--surface2)",border:`1.5px solid ${selected.has(s.id)?"var(--accent2)":"var(--border)"}`,transition:"all .15s"}}>
                    <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${selected.has(s.id)?"var(--accent2)":"var(--text3)"}`,background:selected.has(s.id)?"var(--accent2)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:13,color:"#fff"}}>{selected.has(s.id)?"✓":""}</div>
                    <div className="avatar" style={{width:28,height:28,background:"linear-gradient(135deg,var(--accent),#003d5c)",fontSize:10,color:"#fff",fontFamily:"var(--font-display)",flexShrink:0}}>{s.avatar}</div>
                    <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600}}>{s.name}</div><div style={{fontSize:10,color:"var(--text3)"}}>{s.cricketClass}</div></div>
                  </div>
                ));
              }
              // Shared mode: group by assigned coach
              const coaches = (allOrgCoaches||[]).filter(c=>c.role!=="admin"&&c.status!=="inactive");
              // Include unassigned students too
              const groups = coaches.map(coach=>({
                coach,
                players:[...pool].filter(s=>s.coachId===coach.id).sort((a,b)=>a.name.localeCompare(b.name))
              })).filter(g=>g.players.length>0);
              const unassigned=pool.filter(s=>!coaches.find(c=>c.id===s.coachId)).sort((a,b)=>a.name.localeCompare(b.name));
              if(unassigned.length>0) groups.push({coach:{id:"none",name:"Unassigned",avatar:"?"},players:unassigned});

              return groups.map(({coach,players})=>{
                const groupSelected=players.filter(s=>selected.has(s.id)).length;
                const allGroupSel=groupSelected===players.length;
                const toggleGroup=()=>{
                  setSelected(prev=>{
                    const n=new Set(prev);
                    if(allGroupSel) players.forEach(s=>n.delete(s.id));
                    else players.forEach(s=>n.add(s.id));
                    return n;
                  });
                };
                return(
                  <div key={coach.id} style={{marginBottom:10}}>
                    {/* Coach group header */}
                    <div onClick={toggleGroup} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:"var(--surface2)",borderRadius:8,marginBottom:4,cursor:"pointer",border:"1.5px solid var(--border)"}}>
                      <div className="avatar" style={{width:26,height:26,background:"linear-gradient(135deg,var(--accent),#003d5c)",fontSize:9,color:"#fff",fontFamily:"var(--font-display)",flexShrink:0}}>{coach.name.split(" ").map(w=>w[0]).join("").slice(0,2)}</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:700,color:"var(--text2)"}}>{coach.name}'s squad</div>
                        <div style={{fontSize:10,color:"var(--text3)"}}>{players.length} player{players.length!==1?"s":""}</div>
                      </div>
                      <div style={{fontSize:11,fontWeight:700,color:"var(--accent2)"}}>{groupSelected>0?`${groupSelected} selected`:""}</div>
                      <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${allGroupSel?"var(--accent2)":"var(--text3)"}`,background:allGroupSel?"var(--accent2)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:11,color:"#fff"}}>{allGroupSel?"✓":groupSelected>0?"–":""}</div>
                    </div>
                    {/* Players in this group */}
                    {players.map(s=>(
                      <div key={s.id} onClick={()=>toggleSel(s.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px 8px 18px",borderRadius:8,marginBottom:4,cursor:"pointer",background:selected.has(s.id)?"#fff0ea":"transparent",border:`1.5px solid ${selected.has(s.id)?"var(--accent2)":"var(--border)"}`,transition:"all .15s"}}>
                        <div style={{width:18,height:18,borderRadius:4,border:`2px solid ${selected.has(s.id)?"var(--accent2)":"var(--text3)"}`,background:selected.has(s.id)?"var(--accent2)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:11,color:"#fff"}}>{selected.has(s.id)?"✓":""}</div>
                        <div className="avatar" style={{width:26,height:26,background:"linear-gradient(135deg,var(--accent),#003d5c)",fontSize:9,color:"#fff",fontFamily:"var(--font-display)",flexShrink:0}}>{s.avatar}</div>
                        <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600}}>{s.name}</div><div style={{fontSize:10,color:"var(--text3)"}}>{s.cricketClass}</div></div>
                      </div>
                    ))}
                  </div>
                );
              });
            })()}
            <div style={{marginTop:12,marginBottom:12}}>
              <div className="lbl">Session Date</div>
              <input type="date" value={gsdate} onChange={e=>setGsdate(e.target.value)} max={new Date().toISOString().split("T")[0]}/>
              {gsdate!==new Date().toISOString().split("T")[0]&&<div style={{fontSize:11,color:"var(--accent2)",marginTop:4}}>📅 Logging for a past session</div>}
            </div>
            <div style={{marginTop:0,marginBottom:12}}>
              <div className="lbl">Session Notes (shared with all selected players)</div>
              <textarea value={gsnote} onChange={e=>setGsnote(e.target.value)} placeholder="e.g. Full squad match. Focus on running between wickets and field placements."/>
            </div>
            <button className="btn-primary" style={{background:"linear-gradient(135deg,var(--accent2),#922c00)"}} onClick={submitGroup}>
              👥 Log Group Session {selected.size>0?`(${selected.size} players)`:""}
            </button>
          </div>
        )}

        {/* ── Group Session History (Q3) ── */}
        {mode==="history"&&(<>
          <div style={{marginBottom:10}}>
            <div style={{fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:2}}>
              Group Session Log — {isAdmin?"All coaches":"Your sessions"} · most recent first
            </div>
            <div style={{fontSize:11,color:"var(--text3)",marginBottom:10}}>
              {allGroupSessions.length} group session{allGroupSessions.length!==1?"s":""} recorded
            </div>
            {isAdmin&&allOrgCoaches&&allOrgCoaches.filter(c=>c.role!=="admin"&&c.status!=="inactive").length>0&&(
              <div style={{marginBottom:12}}>
                <div className="lbl" style={{marginBottom:6}}>Filter by Coach</div>
                <div className="scroll-x" style={{gap:4}}>
                  <button className={`pill${histCoach==="all"?" active":""}`} onClick={()=>setHistCoach("all")}>All Coaches</button>
                  {allOrgCoaches.filter(c=>c.role!=="admin"&&c.status!=="inactive").map(c=>(
                    <button key={c.id} className={`pill${histCoach===c.id?" active":""}`} onClick={()=>setHistCoach(c.id)}>{c.name}</button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {filteredGroupSessions.length===0&&(
            <div className="empty"><div className="empty-icon">📅</div>
              <div>{allGroupSessions.length===0?"No group sessions recorded yet":"No sessions match this filter"}</div>
              {allGroupSessions.length===0&&<div style={{fontSize:12,marginTop:6}}>Log group sessions from the 👥 Group Session tab</div>}
            </div>
          )}

          {filteredGroupSessions.map((gs,i)=>{
            const exp=histExpanded[i]!==false;
            const coachName=getCoachName(gs.coachId);
            return(
              <div key={i} className="card" style={{marginBottom:8}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",cursor:"pointer"}}
                  onClick={()=>setHistExpanded(e=>({...e,[i]:!exp}))}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:4,flexWrap:"wrap"}}>
                      <span className="badge" style={{background:"#fff0ea",color:"var(--accent2)",border:"1.5px solid var(--accent2)"}}>{gs.type}</span>
                      <span className="badge" style={{background:"#dbeafe",color:"var(--accent)",border:"1.5px solid var(--accent)"}}>👥 Group</span>
                      <span style={{fontSize:11,color:"var(--text3)",fontFamily:"var(--font-mono)"}}>{gs.duration}m</span>
                    </div>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--text)"}}>{dateStr(gs.date)}</div>
                    <div style={{fontSize:11,color:"var(--text2)",marginTop:2}}>
                      {gs.playerIds.length} player{gs.playerIds.length!==1?"s":""}
                      {coachName&&isAdmin&&<span style={{color:"var(--text3)"}}> · {coachName}</span>}
                    </div>
                  </div>
                  <span style={{fontSize:14,color:"var(--text3)",marginLeft:8,flexShrink:0}}>{exp?"▾":"▸"}</span>
                </div>
                {exp&&(
                  <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid var(--border)"}}>
                    <div style={{fontSize:13,color:"var(--text)",fontStyle:"italic",lineHeight:1.6,marginBottom:10}}>
                      "{gs.coachNote}"
                    </div>
                    <div style={{fontSize:11,fontWeight:700,color:"var(--text2)",marginBottom:6}}>Players in this session:</div>
                    <div style={{display:"flex",flexWrap:"wrap",gap:4}}>
                      {gs.playerNames.map((name,ni)=>(
                        <span key={ni} style={{fontSize:11,padding:"3px 10px",borderRadius:99,background:"var(--surface2)",border:"1.5px solid var(--border)",fontWeight:600,color:"var(--text)"}}>{name}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </>)}

        {/* ── Individual Goal ── */}
        {mode==="goal"&&(<>
          <div style={{marginBottom:12}}><div className="lbl">Player {isShared&&<span style={{fontWeight:400,color:"var(--text3)",fontSize:10}}>— all school</span>}</div>
            <select value={sid} onChange={e=>setSid(e.target.value)}>
              {(isShared?(allOrgStudents||students):students)
                .slice().sort((a,b)=>a.name.localeCompare(b.name))
                .map(s=><option key={s.id} value={s.id}>{s.name} — {s.cricketClass}</option>)}
            </select>
          </div>
          <div className="card card-hl">
            <div style={{fontFamily:"var(--font-display)",fontSize:20,color:"var(--gold)",marginBottom:12}}>🎯 Set Goal</div>
            <div style={{marginBottom:10}}><div className="lbl">Goal</div><input value={gtitle} onChange={e=>setGtitle(e.target.value)} placeholder="e.g. Bowl 3 consecutive maidens"/></div>
            <div style={{marginBottom:10}}><div className="lbl">Category</div><div className="pill-row">{GOAL_CATS.map(c=><button key={c} className={`pill${gcat===c?" active":""}`} onClick={()=>setGcat(c)}>{c}</button>)}</div></div>
            <div style={{marginBottom:12}}><div className="lbl">Target Date</div><input type="date" value={gdate} onChange={e=>setGdate(e.target.value)}/></div>
            <button className="btn-primary" style={{background:"linear-gradient(135deg,var(--gold),#6b4900)",color:"#fff"}} onClick={submitG}>🎯 Set Goal</button>
          </div>
        </>)}

        {/* ── Squad Goal (Q6) ── */}
        {mode==="groupGoal"&&(
          <div className="card card-hl">
            <div style={{fontFamily:"var(--font-display)",fontSize:20,color:"var(--accent3)",marginBottom:4}}>🎯👥 Squad Goal</div>
            <div style={{fontSize:11,color:"var(--text2)",marginBottom:14}}>
              Set one shared goal for multiple players at once. Every selected player sees it in their own Goals screen with a 👥 Squad badge.
            </div>
            <div style={{marginBottom:10}}><div className="lbl">Goal Title</div>
              <input value={ggTitle} onChange={e=>setGgTitle(e.target.value)} placeholder="e.g. Improve batting strike rate to 80+"/>
            </div>
            <div style={{marginBottom:10}}><div className="lbl">Category</div>
              <div className="pill-row">{GOAL_CATS.map(c=><button key={c} className={`pill${ggCat===c?" active":""}`} onClick={()=>setGgCat(c)}>{c}</button>)}</div>
            </div>
            <div style={{marginBottom:14}}><div className="lbl">Target Date</div>
              <input type="date" value={ggDate} onChange={e=>setGgDate(e.target.value)}/>
            </div>
            <div className="lbl" style={{marginBottom:6}}>Select Players</div>
            <div style={{display:"flex",gap:6,marginBottom:8}}>
              <button className="btn-ghost btn-sm" onClick={()=>setGgSel(new Set(students.map(s=>s.id)))}>Select All</button>
              <button className="btn-ghost btn-sm" onClick={()=>setGgSel(new Set())}>Clear</button>
              {ggSel.size>0&&<span style={{fontSize:11,color:"var(--accent3)",fontWeight:700,alignSelf:"center"}}>{ggSel.size} selected</span>}
            </div>
            {[...students].sort((a,b)=>a.name.localeCompare(b.name)).map(s=>(
              <div key={s.id} onClick={()=>toggleGgSel(s.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",borderRadius:8,marginBottom:5,cursor:"pointer",background:ggSel.has(s.id)?"#dcfce7":"var(--surface2)",border:`1.5px solid ${ggSel.has(s.id)?"var(--accent3)":"var(--border)"}`,transition:"all .15s"}}>
                <div style={{width:20,height:20,borderRadius:4,border:`2px solid ${ggSel.has(s.id)?"var(--accent3)":"var(--text3)"}`,background:ggSel.has(s.id)?"var(--accent3)":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:12,color:"#fff"}}>{ggSel.has(s.id)?"✓":""}</div>
                <div className="avatar" style={{width:28,height:28,background:"linear-gradient(135deg,var(--accent3),#0a4424)",fontSize:10,color:"#fff",fontFamily:"var(--font-display)",flexShrink:0}}>{s.avatar}</div>
                <div style={{flex:1}}><div style={{fontSize:13,fontWeight:600}}>{s.name}</div><div style={{fontSize:10,color:"var(--text3)"}}>{s.cricketClass}</div></div>
              </div>
            ))}
            <button className="btn-primary" style={{marginTop:14,background:"linear-gradient(135deg,var(--accent3),#0a5c2a)"}} onClick={submitGroupGoal}>
              🎯 Set Squad Goal {ggSel.size>0?`(${ggSel.size} players)`:""}
            </button>
          </div>
        )}

        {/* ── Squad Goal Log — all goals sorted by target date ── */}
        {mode==="goalLog"&&(()=>{
          const goalPool=isAdmin?(allOrgStudents||students):students;
          // Collect all goals across all players, attach player info
          const allGoals=[];
          goalPool.forEach(stu=>{
            (db.goals[stu.id]||[]).forEach(g=>{
              allGoals.push({...g,studentId:stu.id,studentName:stu.name,studentAvatar:stu.avatar,coachId:stu.coachId});
            });
          });
          const active=allGoals.filter(g=>g.status!=="completed").sort((a,b)=>new Date(a.targetDate)-new Date(b.targetDate));
          const done=allGoals.filter(g=>g.status==="completed").sort((a,b)=>new Date(b.targetDate)-new Date(a.targetDate));
          const today=new Date(); today.setHours(0,0,0,0);
          const daysLeft=d=>{const t=new Date(d);t.setHours(0,0,0,0);return Math.ceil((t-today)/(1000*60*60*24));};
          const urgencyColor=d=>{const n=daysLeft(d);return n<=14?"var(--danger)":n<=30?"var(--accent2)":"var(--accent3)";};
          const urgencyLabel=d=>{const n=daysLeft(d);return n<0?`${Math.abs(n)}d overdue`:n===0?"Due today":`${n}d left`;};
          if(allGoals.length===0) return(
            <div className="empty"><div className="empty-icon">🎯</div><div>No goals set yet</div></div>
          );
          return(
            <div>
              <div style={{fontSize:11,color:"var(--text2)",marginBottom:10,lineHeight:1.5}}>
                All squad goals sorted by target date — active first, completed at bottom.
              </div>
              {active.map(g=>(
                <div key={g.id+"_"+g.studentId} className="card" style={{marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:700,color:"var(--text)",marginBottom:4,lineHeight:1.3}}>{g.title}</div>
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:5,flexWrap:"wrap"}}>
                        <div className="avatar" style={{width:20,height:20,fontSize:8,background:"linear-gradient(135deg,var(--accent),#003d5c)",flexShrink:0,fontFamily:"var(--font-display)"}}>{g.studentAvatar}</div>
                        <span style={{fontSize:11,color:"var(--text2)",fontWeight:600}}>{g.studentName}</span>
                        {isAdmin&&g.coachId&&<span style={{fontSize:10,color:"var(--text3)"}}>· {(allOrgCoaches||[]).find(c=>c.id===g.coachId)?.name||""}</span>}
                      </div>
                      <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:6}}>
                        <span style={{fontSize:10,padding:"2px 7px",borderRadius:99,background:CAT_COLOR[g.category]+"18",color:CAT_COLOR[g.category],border:`1px solid ${CAT_COLOR[g.category]}44`,fontWeight:600}}>{g.category}</span>
                        {g.isGroupGoal&&<span style={{fontSize:10,padding:"2px 7px",borderRadius:99,background:"#eef2ff",color:"#4338ca",border:"1px solid #c7d2fe",fontWeight:600}}>👥 Squad</span>}
                        {g.createdBy==="coach"&&!g.isGroupGoal&&<span style={{fontSize:10,padding:"2px 7px",borderRadius:99,background:"var(--surface2)",color:"var(--text3)",border:"1px solid var(--border)",fontWeight:600}}>Coach set</span>}
                      </div>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <div style={{flex:1,height:4,background:"var(--surface3)",borderRadius:99,overflow:"hidden"}}>
                          <div style={{height:"100%",borderRadius:99,background:CAT_COLOR[g.category],width:`${g.progress}%`}}/>
                        </div>
                        <span style={{fontSize:10,color:"var(--text3)",minWidth:28}}>{g.progress}%</span>
                      </div>
                    </div>
                    <div style={{textAlign:"right",flexShrink:0}}>
                      <div style={{fontSize:12,fontWeight:700,color:"var(--text)"}}>{g.targetDate?g.targetDate.slice(5).replace("-","/"):""}</div>
                      {g.targetDate&&<div style={{fontSize:10,fontWeight:700,color:urgencyColor(g.targetDate),marginTop:2}}>{urgencyLabel(g.targetDate)}</div>}
                    </div>
                  </div>
                </div>
              ))}
              {done.length>0&&(
                <>
                  <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:1,margin:"16px 0 8px"}}>🏆 Completed ({done.length})</div>
                  {done.map(g=>(
                    <div key={g.id+"_"+g.studentId} className="card" style={{marginBottom:6,opacity:.7}}>
                      <div style={{display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:16,flexShrink:0}}>✅</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:600,color:"var(--text)"}}>{g.title}</div>
                          <div style={{fontSize:10,color:"var(--text3)"}}>{g.studentName} · {g.category}{g.isGroupGoal?" · 👥 Squad":""}</div>
                        </div>
                        {g.targetDate&&<div style={{fontSize:10,color:"var(--text3)",flexShrink:0}}>{g.targetDate.slice(5).replace("-","/")}</div>}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          );
        })()}

        {/* ── Reports mode — generate reports for any player on demand ── */}
        {mode==="reports"&&(
          <div>
            <div style={{fontSize:12,color:"var(--text2)",marginBottom:12,lineHeight:1.6}}>
              Generate a PDF progress report for any player at any time. Reports are saved to the player's profile and also visible to the player in their own login.
            </div>
            {(isAdmin?allOrgStudents:students).filter(s=>s.status!=="inactive").map(s=>{
              const sAssessments=(db.assessments[s.id]||[]);
              const sSessions=(db.sessions[s.id]||[]);
              const sReports=(s.reportHistory||[]);
              const sCoach=(allOrgCoaches||[]).find(c=>c.id===s.coachId);
              return(
                <div key={s.id} className="card" style={{marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div className="avatar" style={{width:38,height:38,fontSize:13,background:"linear-gradient(135deg,var(--accent),#003d5c)"}}>{s.avatar}</div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:13}}>{s.name}</div>
                      <div style={{fontSize:11,color:"var(--text3)"}}>
                        {sSessions.length} session{sSessions.length!==1?"s":""} · {sAssessments.length} assessment{sAssessments.length!==1?"s":""}
                        {sReports.length>0?` · 📄 ${sReports.length} report${sReports.length!==1?"s":""}`:""}</div>
                      {sCoach&&isAdmin&&<div style={{fontSize:10,color:"var(--text3)"}}>👨‍🏫 {sCoach.name}</div>}
                    </div>
                    <button
                      className={sAssessments.length>0?"btn-primary btn-sm":"btn-ghost btn-sm"}
                      style={sAssessments.length>0?{background:"linear-gradient(135deg,var(--accent3),#0a5c2a)",minWidth:80}:{minWidth:80,opacity:0.5}}
                      disabled={sAssessments.length===0}
                      onClick={()=>onGenerateReport&&onGenerateReport(s.id)}>
                      📄 Generate
                    </button>
                  </div>
                  {sReports.length>0&&(
                    <div style={{marginTop:8,paddingTop:8,borderTop:"1px solid var(--border)"}}>
                      <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:1,marginBottom:4}}>Past Reports</div>
                      {[...sReports].reverse().map((r,i)=>(
                        <div key={r.id||i} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 0",
                          borderBottom:i<sReports.length-1?"1px solid var(--surface3)":"none"}}>
                          <span style={{fontSize:14,flexShrink:0}}>📄</span>
                          <div style={{flex:1}}>
                            <div style={{fontSize:12,fontWeight:600}}>{r.label||`Report — ${dateStr(r.generatedAt)}`}</div>
                            <div style={{fontSize:10,color:"var(--text3)"}}>{r.sessionCount} sessions · {r.assessmentCount} assessments{r.overallScore?` · Overall: ${r.overallScore}/5`:""}</div>
                          </div>
                          <button className="btn-ghost btn-sm" style={{fontSize:10,color:"var(--accent)",flexShrink:0}}
                            onClick={()=>onGenerateReport&&onGenerateReport(s.id,r)}>View</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {sAssessments.length===0&&(
                    <div style={{marginTop:6,fontSize:11,color:"var(--text3)",fontStyle:"italic"}}>Complete at least one assessment to generate a report</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}

function CoachSettings({user,db,onLib,onManageCoaches,squadVisibility="private",onUpdateSquadVisibility,onChangePassword,onTransferSquad,myCamps,activeCampId,onEnterCamp,onExitCamp,onCreateCamp,onOpenCampRoster,onUpdateCampCoaches}){
  const isAdmin=user.role==="admin";
  const [showTransfer,setShowTransfer]=useState(false);
  const [manageCoachesCampId,setManageCoachesCampId]=useState(null);
  const [showChangePw,setShowChangePw]=useState(false);
  const [currentPw,setCurrentPw]=useState("");
  const [newPw1,setNewPw1]=useState("");
  const [newPw2,setNewPw2]=useState("");
  const [pwErr,setPwErr]=useState("");

  const handleChangePw=()=>{
    setPwErr("");
    const coach=db?.coaches?.find(c=>c.id===user.id);
    if(!coach){setPwErr("Account not found");return;}
    if(coach.password!==currentPw){setPwErr("Current password is incorrect");return;}
    if(newPw1.length<6){setPwErr("New password must be at least 6 characters");return;}
    if(newPw1!==newPw2){setPwErr("New passwords do not match");return;}
    onChangePassword&&onChangePassword(newPw1);
    setCurrentPw("");setNewPw1("");setNewPw2("");
    setShowChangePw(false);
  };
  // For non-admin coaches: their own students + other active coaches in the org
  const myStudents=!isAdmin
    ? (db?.students||[]).filter(s=>s.coachId===user.id&&s.status!=="inactive")
    : [];
  const otherCoaches=!isAdmin
    ? (db?.coaches||[]).filter(c=>c.orgId===user.orgId&&c.id!==user.id&&c.role!=="admin"&&c.status!=="inactive")
    : [];
  return(
    <div>
      <div style={{padding:"0 8px"}}>
        {/* Profile card */}
        <div className="card card-hl" style={{display:"flex",alignItems:"center",gap:14,marginBottom:14}}>
          <div className="avatar" style={{width:56,height:56,background:isAdmin?"linear-gradient(135deg,#ffd700,#a07d00)":"linear-gradient(135deg,var(--accent),#005577)",fontSize:20,color:"#000",fontFamily:"var(--font-display)"}}>{user.avatar}</div>
          <div style={{flex:1}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:20}}>{user.name}</div>
            <div style={{fontSize:12,color:"var(--text3)",marginTop:1}}>{user.email}</div>
            {user.schoolName&&<div style={{fontSize:11,color:"var(--text2)",marginTop:2}}>🏫 {user.schoolName}</div>}
            <div style={{marginTop:6,display:"flex",gap:5,flexWrap:"wrap"}}>
              {isAdmin
                ?<span className="badge" style={{background:"#fef9e7",color:"var(--gold)",border:"1.5px solid var(--gold)"}}>👑 School Admin</span>
                :<span className="badge" style={{background:"#e0f0fa",color:"var(--accent)",border:"1.5px solid var(--accent)"}}>🎓 Coach</span>
              }
            </div>
          </div>
        </div>

        {/* Camp Mode */}
        <div className="card" style={{marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
            <span style={{fontSize:18}}>⛺</span>
            <div style={{fontWeight:700,fontSize:14}}>Camp mode</div>
          </div>
          <div style={{fontSize:12,color:"var(--text2)",marginBottom:10,lineHeight:1.5}}>Run a time-bound camp with its own roster, separate from your regular squad.</div>
          {(myCamps||[]).filter(c=>c.status==="active").map(camp=>{
            const orgCoaches=(db?.coaches||[]).filter(c=>c.orgId===user.orgId&&c.role!=="admin"&&c.status!=="inactive");
            const isManaging=manageCoachesCampId===camp.id;
            return(
            <div key={camp.id} style={{marginBottom:8}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 10px",background:activeCampId===camp.id?"#fffbeb":"#e0f0fa",border:`1.5px solid ${activeCampId===camp.id?"#fde68a":"#b8dced"}`,borderRadius:isManaging?"8px 8px 0 0":8}}>
                <div style={{flex:1,minWidth:0,cursor:"pointer"}} onClick={()=>onOpenCampRoster&&onOpenCampRoster(camp.id)}>
                  <div style={{fontSize:12,fontWeight:700,color:activeCampId===camp.id?"#92400e":"var(--accent)"}}>{camp.name}</div>
                  <div style={{fontSize:11,color:"var(--text3)"}}>{(camp.rosterStudentIds||[]).length+(camp.guestPlayerIds||[]).length} players · {dateStr(camp.startDate)} – {dateStr(camp.endDate)}</div>
                </div>
                {activeCampId===camp.id
                  ? <button onClick={onExitCamp} style={{fontSize:11,fontWeight:700,padding:"6px 10px",borderRadius:8,background:"transparent",color:"#92400e",border:"1.5px solid #92400e",cursor:"pointer",flexShrink:0,marginRight:isAdmin?6:0}}>Exit</button>
                  : <button onClick={()=>onEnterCamp(camp.id)} style={{fontSize:11,fontWeight:700,padding:"6px 10px",borderRadius:8,background:"var(--accent)",color:"#fff",border:"none",cursor:"pointer",flexShrink:0,marginRight:isAdmin?6:0}}>Enter</button>
                }
                {isAdmin&&(
                  <button onClick={()=>setManageCoachesCampId(isManaging?null:camp.id)} style={{fontSize:11,fontWeight:700,padding:"6px 10px",borderRadius:8,background:"transparent",color:"var(--text2)",border:"1.5px solid var(--border)",cursor:"pointer",flexShrink:0}}>{isManaging?"Done":"Coaches"}</button>
                )}
              </div>
              {isManaging&&(
                <div style={{padding:"10px",border:"1.5px solid #b8dced",borderTop:"none",borderRadius:"0 0 8px 8px",background:"var(--surface)"}}>
                  <div style={{fontSize:11,color:"var(--text3)",marginBottom:8}}>Coaches assigned to run this camp:</div>
                  {orgCoaches.map(c=>{
                    const assigned=(camp.coachIds||[]).includes(c.id);
                    return(
                      <div key={c.id} onClick={()=>{
                        const next=assigned?(camp.coachIds||[]).filter(id=>id!==c.id):[...(camp.coachIds||[]),c.id];
                        onUpdateCampCoaches&&onUpdateCampCoaches(camp.id,next);
                      }} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 8px",borderRadius:6,cursor:"pointer",marginBottom:4,background:assigned?"#e0f0fa":"transparent"}}>
                        <div className="avatar" style={{width:22,height:22,fontSize:9,background:"linear-gradient(135deg,var(--accent),#005577)"}}>{c.avatar}</div>
                        <div style={{flex:1,fontSize:12,fontWeight:600}}>{c.name}</div>
                        {assigned&&<span style={{color:"var(--accent)"}}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );})}
          {(myCamps||[]).filter(c=>c.status==="closed").map(camp=>(
            <div key={camp.id} onClick={()=>onOpenCampRoster&&onOpenCampRoster(camp.id)} style={{fontSize:11,color:"var(--text3)",padding:"6px 10px",cursor:"pointer"}}>📁 {camp.name} — closed</div>
          ))}
          <button className="btn-secondary" style={{width:"100%",marginTop:4}} onClick={onCreateCamp}>Create New Camp</button>
        </div>

        {/* Admin-only: Manage Coaches */}
        {isAdmin&&(
          <button onClick={onManageCoaches} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px",background:"linear-gradient(135deg,#ffd70012,#ffd70008)",border:"1.5px solid #ffd70040",borderRadius:"var(--radius)",color:"var(--text)",cursor:"pointer",marginBottom:14,textAlign:"left"}}>
            <div style={{fontSize:28,flexShrink:0}}>👨‍🏫</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:14,color:"var(--gold)"}}>Manage Coaches</div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Add coaches, view team, deactivate accounts</div>
            </div>
            <div style={{fontSize:18,color:"var(--gold)"}}>›</div>
          </button>
        )}

        {/* Admin-only: Squad Visibility */}
        {isAdmin&&(
          <div style={{marginBottom:14,padding:"14px",background:"linear-gradient(135deg,#f0f4ff,#e8edf8)",border:"1.5px solid #94a3b8",borderRadius:"var(--radius)"}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
              <div style={{fontSize:24}}>🌐</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:14,color:"var(--navy,#0B1F3A)"}}>Squad Visibility</div>
                <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                  {squadVisibility==="shared"
                    ? "All coaches can see and log sessions for all players"
                    : "Each coach sees their own assigned players only"}
                </div>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
              <button
                onClick={()=>onUpdateSquadVisibility("private")}
                style={{padding:"10px 8px",borderRadius:8,fontWeight:700,fontSize:12,
                  background:squadVisibility==="private"?"var(--accent)":"var(--surface,#fff)",
                  color:squadVisibility==="private"?"#fff":"var(--text3)",
                  border:`1.5px solid ${squadVisibility==="private"?"var(--accent)":"var(--border)"}`,
                  cursor:"pointer"}}>
                🔒 Private<br/>
                <span style={{fontWeight:400,fontSize:10}}>Own squad only</span>
              </button>
              <button
                onClick={()=>onUpdateSquadVisibility("shared")}
                style={{padding:"10px 8px",borderRadius:8,fontWeight:700,fontSize:12,
                  background:squadVisibility==="shared"?"var(--accent)":"var(--surface,#fff)",
                  color:squadVisibility==="shared"?"#fff":"var(--text3)",
                  border:`1.5px solid ${squadVisibility==="shared"?"var(--accent)":"var(--border)"}`,
                  cursor:"pointer"}}>
                🌐 Shared<br/>
                <span style={{fontWeight:400,fontSize:10}}>All coaches, all players</span>
              </button>
            </div>
          </div>
        )}

        {/* Coach-only: Transfer My Squad */}
        {!isAdmin&&myStudents.length>0&&otherCoaches.length>0&&(
          <div style={{marginBottom:14}}>
            <button
              onClick={()=>setShowTransfer(s=>!s)}
              style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px",
                background:"linear-gradient(135deg,#f0f4ff,#e8edf8)",
                border:`1.5px solid ${showTransfer?"var(--accent)":"#94a3b8"}`,
                borderRadius:"var(--radius)",color:"var(--text)",cursor:"pointer",textAlign:"left"}}>
              <div style={{fontSize:28,flexShrink:0}}>🔄</div>
              <div style={{flex:1}}>
                <div style={{fontWeight:700,fontSize:14,color:"var(--accent)"}}>Transfer My Squad</div>
                <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                  Move players from your squad to another coach
                </div>
              </div>
              <div style={{fontSize:18,color:"var(--accent)"}}>{showTransfer?"▾":"›"}</div>
            </button>
            {showTransfer&&(
              <div style={{marginTop:8}}>
                <MassTransferPanel
                  students={myStudents}
                  otherCoaches={otherCoaches}
                  onTransfer={(sids,toCoachId)=>{
                    onTransferSquad&&onTransferSquad(sids,toCoachId);
                    setShowTransfer(false);
                  }}
                  showNotif={()=>{}}
                />
              </div>
            )}
          </div>
        )}

        {/* Change Password (all coaches and admin) */}
        <div style={{marginBottom:14}}>
          <button
            onClick={()=>{setShowChangePw(s=>!s);setPwErr("");setCurrentPw("");setNewPw1("");setNewPw2("");}}
            style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px",
              background:"linear-gradient(135deg,#f8f0ff,#f0e8ff)",
              border:`1.5px solid ${showChangePw?"#7c3aed":"#c4b5fd"}`,
              borderRadius:"var(--radius)",color:"var(--text)",cursor:"pointer",textAlign:"left"}}>
            <div style={{fontSize:28,flexShrink:0}}>🔑</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:14,color:"#7c3aed"}}>Change Password</div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Update your own login password</div>
            </div>
            <div style={{fontSize:18,color:"#7c3aed"}}>{showChangePw?"▾":"›"}</div>
          </button>
          {showChangePw&&(
            <div style={{padding:"14px",background:"#faf5ff",border:"1.5px solid #c4b5fd",
              borderTop:"none",borderRadius:"0 0 var(--radius) var(--radius)"}}>
              <div style={{marginBottom:10}}>
                <div className="lbl">Current Password</div>
                <input type="password" value={currentPw} onChange={e=>setCurrentPw(e.target.value)}
                  placeholder="Enter your current password"
                  style={{border:"1.5px solid #c4b5fd"}}/>
              </div>
              <div style={{marginBottom:10}}>
                <div className="lbl">New Password (min 6 characters)</div>
                <input type="password" value={newPw1} onChange={e=>setNewPw1(e.target.value)}
                  placeholder="Choose a new password"
                  style={{border:"1.5px solid #c4b5fd"}}/>
              </div>
              <div style={{marginBottom:12}}>
                <div className="lbl">Confirm New Password</div>
                <input type="password" value={newPw2} onChange={e=>setNewPw2(e.target.value)}
                  placeholder="Repeat new password"
                  onKeyDown={e=>e.key==="Enter"&&handleChangePw()}
                  style={{border:"1.5px solid #c4b5fd"}}/>
              </div>
              {pwErr&&(
                <div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",
                  borderRadius:8,padding:"8px 12px",fontSize:12,color:"var(--danger)",marginBottom:10}}>
                  {pwErr}
                </div>
              )}
              <div style={{display:"flex",gap:8}}>
                <button
                  onClick={()=>{setShowChangePw(false);setPwErr("");}}
                  style={{flex:1,padding:"10px",borderRadius:8,fontWeight:700,fontSize:13,
                    background:"transparent",border:"1.5px solid var(--border)",cursor:"pointer"}}>
                  Cancel
                </button>
                <button
                  onClick={handleChangePw}
                  style={{flex:2,padding:"10px",borderRadius:8,fontWeight:700,fontSize:13,
                    background:"#7c3aed",color:"#fff",border:"none",cursor:"pointer"}}>
                  🔑 Update Password
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Skill Library */}
        <button onClick={onLib} style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px",background:"linear-gradient(135deg,var(--surface2),var(--surface3))",border:"2px solid var(--accent)",borderRadius:"var(--radius)",color:"var(--text)",cursor:"pointer",marginBottom:14,textAlign:"left"}}>
          <div style={{fontSize:28,flexShrink:0}}>📚</div>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:14,color:"var(--accent)"}}>Skill Library</div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>Manage skills for all 4 class levels · 5 categories</div>
          </div>
          <div style={{fontSize:18,color:"var(--accent)"}}>›</div>
        </button>

        {/* Help & User Guide */}
        <button onClick={openGuide}
          style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px",background:"linear-gradient(135deg,#f0fdf4,#e8f5e9)",border:"1.5px solid var(--accent3)",borderRadius:"var(--radius)",color:"var(--text)",cursor:"pointer",marginBottom:14,textAlign:"left",boxSizing:"border-box"}}>
          <div style={{fontSize:28,flexShrink:0}}>📖</div>
          <div style={{flex:1}}>
            <div style={{fontWeight:700,fontSize:14,color:"var(--accent3)"}}>Help &amp; User Guide</div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>How to use CrickitCoach · Coaches, Admin &amp; Players</div>
          </div>
          <div style={{fontSize:18,color:"var(--accent3)"}}>›</div>
        </button>


        <div style={{textAlign:"center",fontSize:10,color:"var(--text4)",marginBottom:14}}>CrickitCoach v4.997 · ArcGain LLC</div>
        <button className="btn-danger" style={{width:"100%",marginBottom:6}} onClick={()=>window.location.reload()}>Sign Out</button>

      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STUDENT SCREENS
// ═══════════════════════════════════════════════════════════════════════════════
function StudentHome({user,db}){
  const assessments=(db.assessments[user.id]||[]).sort((a,b)=>new Date(a.date)-new Date(b.date));
  const goals=db.goals[user.id]||[];
  const sessions=(db.sessions[user.id]||[]).sort((a,b)=>new Date(b.date)-new Date(a.date));
  const lt=assessments[assessments.length-1];
  const coach=db.coaches.find(c=>c.id===user.coachId);
  const dg=goals.filter(g=>g.status==="completed").length;
  return(
    <div>
      <div className="screen-hdr">
        {coach?.schoolName&&<div style={{fontSize:11,color:"var(--accent)",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:2}}>🏫 {coach.schoolName}</div>}
        <div style={{fontSize:10,color:"var(--text2)",fontWeight:700,letterSpacing:2,textTransform:"uppercase",marginBottom:3}}>Welcome,</div>
        <div style={{fontFamily:"var(--font-display)",fontSize:28,letterSpacing:1,marginBottom:2,color:"var(--text)",fontWeight:900}}>{user.name}</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:4}}>
          <ClassBadge cls={user.cricketClass}/>
          {(user.tags||[]).map(t=><TagChip key={t} tagId={t}/>)}
        </div>
        {coach&&<div style={{fontSize:11,color:"var(--text3)"}}>👨‍🏫 {coach.name}</div>}
      </div>
      <div style={{padding:"0 8px"}}>
        {lt?(
          <div className="card card-hl" style={{display:"flex",alignItems:"center",gap:14,marginBottom:10}}>
            <ScoreRing value={lt.overall||overallFromAssessment(lt)} size={72} label="Overall"/>
            <div style={{flex:1}}>
              <div style={{fontFamily:"var(--font-display)",fontSize:18,marginBottom:2}}>Current Rating</div>
              <div style={{fontSize:12,color:"var(--text2)",marginBottom:6}}>{RATING_LABEL(lt.overall||overallFromAssessment(lt))} · Week {lt.week}</div>
              <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                {CATEGORIES.map(cat=>{const avg=catAvgFromAssessment(lt,cat);if(!avg)return null;return<div key={cat} style={{textAlign:"center",background:"var(--surface3)",borderRadius:6,padding:"4px 6px"}}><div style={{fontSize:8}}>{CAT_ICON[cat]}</div><div style={{fontFamily:"var(--font-mono)",fontWeight:700,fontSize:12,color:CAT_COLOR[cat]}}>{avg}</div></div>;})}
              </div>
            </div>
          </div>
        ):(
          <div className="card" style={{textAlign:"center",padding:20}}><div style={{fontSize:28,marginBottom:6}}>🏏</div><div style={{fontWeight:700,color:"var(--text2)"}}>No assessment yet</div><div style={{fontSize:12,color:"var(--text3)",marginTop:3}}>Your coach will assess you soon!</div></div>
        )}
        <div className="stat-row">
          <div className="stat-box"><div className="stat-val">{assessments.length}</div><div className="stat-lbl">Assessed</div></div>
          <div className="stat-box"><div className="stat-val" style={{color:"var(--gold)"}}>{dg}</div><div className="stat-lbl">Goals Hit</div></div>
          <div className="stat-box"><div className="stat-val" style={{color:"var(--accent2)"}}>{goals.filter(g=>g.status==="active").length}</div><div className="stat-lbl">Active</div></div>
        </div>
        {sessions.length>0&&(<>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
            <div className="sec-title" style={{margin:0}}>Recent Sessions</div>
            <span style={{fontSize:11,color:"var(--text3)"}}>{sessions.length} total</span>
          </div>
          {sessions.slice(0,3).map((se,i)=>(
            <div key={se.id||i} className="card" style={{marginBottom:6,padding:"10px 12px",borderColor:se.isGroupSession?"var(--accent2)":"var(--border)",borderWidth:se.isGroupSession?2:1.5}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                <div style={{flex:1}}>
                  <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:3,flexWrap:"wrap"}}>
                    <span style={{fontWeight:700,fontSize:13}}>{se.type} Session</span>
                    {se.isGroupSession&&<span className="badge" style={{background:"#fff0ea",color:"var(--accent2)",border:"1.5px solid var(--accent2)",fontSize:9}}>👥 Group</span>}
                  </div>
                  <div style={{fontSize:11,color:"var(--text3)",marginBottom:4}}>{se.duration} min · {dateStr(se.date)}</div>
                  <div style={{fontSize:12,color:"var(--text2)",fontStyle:"italic"}}>"{se.coachNote}"</div>
                </div>
                <div style={{fontSize:22,marginLeft:8}}>{se.type==="Batting"?"🏏":se.type==="Bowling"?"⚾":se.type==="Fielding"?"🧤":se.type==="Match"?"🏆":se.type==="Fitness"?"💪":"📋"}</div>
              </div>
            </div>
          ))}
          {sessions.length>3&&(
            <div style={{textAlign:"center",fontSize:11,color:"var(--accent)",fontWeight:600,padding:"4px 0 10px"}}>
              + {sessions.length-3} more sessions — see Sessions tab 📋
            </div>
          )}
        </>)}
        {lt?.notes&&<><div className="sec-title">Coach Notes</div>
          <div className="card" style={{borderColor:"var(--accent)"}}><div style={{fontSize:12,color:"var(--text2)",fontStyle:"italic",lineHeight:1.5}}>"{lt.notes}"</div><div style={{fontSize:10,color:"var(--text3)",marginTop:6}}>— {coach?.name||"Coach"} · {dateStr(lt.date)}</div></div>
        </>}
      </div>
    </div>
  );
}

function StudentProgress({user,db}){
  const [view,setView]=useState("Batting");
  const assessments=(db.assessments[user.id]||[]).sort((a,b)=>new Date(a.date)-new Date(b.date));
  const lt=assessments[assessments.length-1];
  const lib=getOrgLibrary(db,user.orgId);
  if(!lt) return <div><div className="screen-hdr"><div className="sec-title">My Progress</div></div><div className="empty"><div className="empty-icon">📊</div><div>No assessments yet</div></div></div>;

  const sks=lt.skillSnapshot?.[view]||(lib[lt.cricketClass||user.cricketClass]?.[view]||[]).filter(s=>s.active);
  const sc=lt.scores?.[view]||{};
  const impr=assessments.length>=2?( (catAvgFromAssessment(assessments[assessments.length-1],view)-catAvgFromAssessment(assessments[0],view)).toFixed(1) ):null;
  const progressData=assessments.filter(a=>a.scores?.[view]).map(a=>({y:catAvgFromAssessment(a,view),label:`W${a.week}`}));

  return(
    <div>
      <div className="screen-hdr">
        <div className="sec-title">My Progress</div>
        <div className="scroll-x" style={{padding:"0 0 6px"}}>
          {CATEGORIES.map(cat=><button key={cat} className={`pill${view===cat?" active":""}`} onClick={()=>setView(cat)} style={{color:view===cat?"#000":CAT_COLOR[cat],borderColor:CAT_COLOR[cat]+"60"}}>{CAT_ICON[cat]} {cat}</button>)}
        </div>
      </div>
      <div style={{padding:"0 8px"}}>
        <div className="card card-hl" style={{display:"flex",alignItems:"center",gap:14,marginBottom:10}}>
          <ScoreRing value={catAvgFromAssessment(lt,view)} size={72} label={view}/>
          <div style={{flex:1}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:18,color:CAT_COLOR[view]}}>{RATING_LABEL(catAvgFromAssessment(lt,view))}</div>
            <div style={{fontSize:11,color:"var(--text3)"}}>Current · {lt.cricketClass||user.cricketClass}</div>
            {impr!==null&&<div style={{marginTop:6,fontSize:13,fontWeight:700,color:parseFloat(impr)>=0?"var(--accent3)":"var(--danger)"}}>{parseFloat(impr)>=0?"▲ +":"▼ "}{impr} over program</div>}
          </div>
        </div>
        {progressData.length>=2&&<div className="card" style={{marginBottom:10}}><div style={{fontSize:10,color:"var(--text2)",textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>Progress Over Time</div><MiniLine data={progressData} color={CAT_COLOR[view]}/></div>}
        {sks.length>0&&<div className="card" style={{marginBottom:10}}><div style={{fontWeight:700,fontSize:11,color:CAT_COLOR[view],textTransform:"uppercase",letterSpacing:.5,marginBottom:8}}>{view} Radar</div><RadarChart scores={sc} skills={sks} color={CAT_COLOR[view]}/></div>}
        <div className="card">
          <div style={{fontSize:10,color:"var(--text2)",textTransform:"uppercase",letterSpacing:.5,marginBottom:10}}>Skill Detail</div>
          {sks.map(sk=>(
            <div key={sk.id} style={{marginBottom:10}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}><div style={{fontSize:12,color:"var(--text2)"}}>{sk.label}</div><div style={{fontFamily:"var(--font-mono)",fontSize:12,color:CAT_COLOR[view],fontWeight:700}}>{sc[sk.id]||0}/5</div></div>
              <div className="pb-track"><div className="pb-fill" style={{width:`${((sc[sk.id]||0)/5)*100}%`,background:RATING_COLOR(sc[sk.id]||0)}}/></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StudentGoals({user,db,updGoal,addGoal,addCoachingNote,showNotif,onViewed}){
  useEffect(()=>{ if(onViewed) onViewed(); },[]);
  const goals=db.goals[user.id]||[];
  const coachingNotes=(db.coachingNotes?.[user.id]||[]).slice().reverse();
  const [adding,setAdding]=useState(false);
  const [addingNote,setAddingNote]=useState(false);
  const [title,setTitle]=useState(""); const [cat,setCat]=useState("Batting"); const [date,setDate]=useState("");
  const [noteText,setNoteText]=useState("");
  const active=goals.filter(g=>g.status==="active"), done=goals.filter(g=>g.status==="completed");
  const submit=()=>{if(!title.trim()||!date){showNotif("Fill all fields","error");return;}addGoal(user.id,{title,category:cat,targetDate:date,status:"active",createdBy:"student",progress:0});setTitle("");setDate("");setAdding(false);};
  const submitNote=()=>{
    if(!noteText.trim()){showNotif("Write a note first","error");return;}
    addCoachingNote(user.id,noteText);
    setNoteText("");setAddingNote(false);
    showNotif("Note sent to your coach ✅");
  };
  return(
    <div>
      <div className="screen-hdr">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div className="sec-title" style={{margin:0}}>Goals & Notes</div>
          <button className="btn-primary btn-sm" style={{minWidth:90}} onClick={()=>setAdding(!adding)}>{adding?"Cancel":"+ New Goal"}</button>
        </div>
        <div style={{display:"flex",gap:10}}>
          <div className="stat-box" style={{flex:1}}><div className="stat-val" style={{color:"var(--accent2)",fontSize:22}}>{active.length}</div><div className="stat-lbl">Active</div></div>
          <div className="stat-box" style={{flex:1}}><div className="stat-val" style={{color:"var(--accent3)",fontSize:22}}>{done.length}</div><div className="stat-lbl">Done</div></div>
          <div className="stat-box" style={{flex:1}}><div className="stat-val" style={{color:"var(--gold)",fontSize:22}}>{goals.length}</div><div className="stat-lbl">Total</div></div>
        </div>
      </div>
      <div style={{padding:"0 8px"}}>

        {/* New goal form */}
        {adding&&<div className="card card-hl" style={{marginBottom:12}}>
          <div style={{fontFamily:"var(--font-display)",fontSize:18,color:"var(--gold)",marginBottom:12}}>🎯 New Goal</div>
          <div style={{marginBottom:10}}><div className="lbl">What do you want to achieve?</div><input value={title} onChange={e=>setTitle(e.target.value)} placeholder="e.g. Score 30 in a net session"/></div>
          <div style={{marginBottom:10}}><div className="lbl">Category</div><div className="pill-row">{GOAL_CATS.map(c=><button key={c} className={`pill${cat===c?" active":""}`} onClick={()=>setCat(c)}>{c}</button>)}</div></div>
          <div style={{marginBottom:12}}><div className="lbl">Target Date</div><input type="date" value={date} onChange={e=>setDate(e.target.value)}/></div>
          <button className="btn-primary" style={{background:"linear-gradient(135deg,var(--gold),#a07d00)",color:"#000"}} onClick={submit}>Set Goal 🎯</button>
        </div>}

        {/* Active goals */}
        {active.length===0&&!adding&&<div className="empty"><div className="empty-icon">🎯</div><div>No active goals yet</div></div>}
        {active.length>0&&<div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:1,marginBottom:8}}>Active</div>}
        {active.map(g=><GoalCard key={g.id} goal={g} canEdit={g.createdBy==="student"} onUpdate={(gid,v)=>updGoal(user.id,gid,v)}/>)}
        {done.length>0&&<><div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:1,margin:"16px 0 8px"}}>🏆 Completed</div>{done.map(g=><GoalCard key={g.id} goal={g} canEdit={false} onUpdate={()=>{}}/>)}</>}

        {/* ── Notes for My Coach ─────────────────────────────────────────── */}
        <div className="divider" style={{margin:"20px 0 14px"}}/>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:16}}>💬</span>
            <div style={{fontWeight:700,fontSize:13,color:"#a855f7"}}>Notes for My Coach</div>
          </div>
          <button className="btn-ghost btn-sm" style={{borderColor:"#a855f740",color:"#a855f7"}}
            onClick={()=>setAddingNote(!addingNote)}>{addingNote?"Cancel":"+ Add Note"}</button>
        </div>
        <div style={{fontSize:11,color:"var(--text3)",marginBottom:10,lineHeight:1.5}}>
          Tell your coach what you want to work on, any questions you have, or anything you'd like to focus on next session. Your coach sees these in your profile.
        </div>

        {addingNote&&(
          <div className="card card-hl" style={{marginBottom:12,borderColor:"#a855f740"}}>
            <div style={{fontFamily:"var(--font-display)",fontSize:18,color:"#a855f7",marginBottom:10}}>💬 New Note</div>
            <textarea value={noteText} onChange={e=>setNoteText(e.target.value)}
              placeholder="e.g. I'd like to work on my bowling run-up next session…"
              style={{marginBottom:12,minHeight:80}}/>
            <button className="btn-primary" style={{background:"linear-gradient(135deg,#a855f7,#6d28d9)",color:"#fff"}} onClick={submitNote}>
              Send to Coach 💬
            </button>
          </div>
        )}

        {coachingNotes.length===0&&!addingNote&&(
          <div style={{textAlign:"center",padding:"20px 0",color:"var(--text3)",fontSize:12}}>
            No notes sent yet — tap <strong>+ Add Note</strong> to send a message to your coach
          </div>
        )}
        {coachingNotes.map(n=>(
          <div key={n.id} className="card" style={{borderColor:"#a855f728",background:"#a855f708",marginBottom:6}}>
            <div style={{fontSize:13,color:"var(--text)",lineHeight:1.5,marginBottom:4}}>"{n.text}"</div>
            <div style={{fontSize:10,color:"var(--text3)"}}>{dateStr(n.date)}</div>
          </div>
        ))}

      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STUDENT SESSIONS SCREEN
// Full chronological session log for the student — individual + group sessions
// Collapsible cards, newest first, with group session badge and shared notes
// ═══════════════════════════════════════════════════════════════════════════════
function StudentSessions({user,db,onViewed}){
  const sessions=(db.sessions[user.id]||[]).sort((a,b)=>new Date(b.date)-new Date(a.date));
  const [expanded,setExpanded]=useState({});
  const [viewTab,setViewTab]=useState("sessions"); // "sessions" | "reports"
  const [showReportOverlay,setShowReportOverlay]=useState(null); // snapshot or null
  const toggleExp=id=>setExpanded(e=>({...e,[id]:!e[id]}));
  useEffect(()=>{ if(onViewed) onViewed(); },[]);

  const coach=db.coaches.find(c=>c.id===user.coachId);
  const studentRecord=db.students.find(s=>s.id===user.id)||user;
  const reportHistory=(studentRecord.reportHistory||[]).slice().reverse(); // newest first

  const sessTypeIcon=t=>t==="Batting"?"🏏":t==="Bowling"?"⚾":t==="Fielding"?"🧤":t==="Match"?"🏆":t==="Fitness"?"💪":"📋";

  // Stat summaries
  const totalMins=sessions.reduce((acc,s)=>acc+(s.duration||0),0);
  const groupCount=sessions.filter(s=>s.isGroupSession).length;
  const indivCount=sessions.length-groupCount;

  // Student views report overlay (read-only — print only, no save)
  if(showReportOverlay){
    return(
      <ProgressReportOverlay
        student={studentRecord}
        db={db}
        onClose={()=>setShowReportOverlay(null)}
        saveReportSnapshot={null}
        showNotif={()=>{}}
        readOnly={true}
      />
    );
  }

  if(sessions.length===0) return(
    <div>
      <div className="screen-hdr">
        <div className="sec-title">My Sessions</div>
      </div>
      <div className="empty">
        <div className="empty-icon">📋</div>
        <div style={{fontWeight:700,marginBottom:6}}>No sessions recorded yet</div>
        <div style={{fontSize:12}}>Your coach logs sessions after each training</div>
      </div>
    </div>
  );

  return(
    <div>
      <div className="screen-hdr">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div className="sec-title" style={{margin:0}}>My Activity</div>
          {reportHistory.length>0&&(
            <span style={{fontSize:11,color:"var(--accent3)",fontWeight:700}}>
              📄 {reportHistory.length} report{reportHistory.length!==1?"s":""}
            </span>
          )}
        </div>
        {/* Tab switcher */}
        <div className="scroll-x" style={{marginBottom:10,gap:4}}>
          <button className={`pill${viewTab==="sessions"?" active":""}`} onClick={()=>setViewTab("sessions")}>
            📋 Sessions ({sessions.length})
          </button>
          <button className={`pill${viewTab==="reports"?" active":""}`} onClick={()=>setViewTab("reports")}
            style={reportHistory.length>0?{borderColor:"var(--accent3)",color:viewTab==="reports"?"#fff":"var(--accent3)"}:{}}>
            📄 Reports ({reportHistory.length})
          </button>
        </div>
        {/* Stats row — sessions only */}
        {viewTab==="sessions"&&(<>
          <div style={{display:"flex",gap:6,marginBottom:8}}>
            <div className="stat-box" style={{flex:1}}>
              <div className="stat-val" style={{fontSize:22}}>{sessions.length}</div>
              <div className="stat-lbl">Total</div>
            </div>
            <div className="stat-box" style={{flex:1}}>
              <div className="stat-val" style={{fontSize:22,color:"var(--accent2)"}}>{groupCount}</div>
              <div className="stat-lbl">Group</div>
            </div>
            <div className="stat-box" style={{flex:1}}>
              <div className="stat-val" style={{fontSize:22,color:"var(--accent3)"}}>{Math.round(totalMins/60)}h</div>
              <div className="stat-lbl">Total Time</div>
            </div>
          </div>
          {coach&&<div style={{fontSize:11,color:"var(--text3)",marginBottom:4}}>👨‍🏫 Coach: {coach.name}</div>}
        </>)}
        <div className="divider" style={{margin:"8px 0 0"}}/>
      </div>

      <div style={{padding:"0 8px"}}>

        {/* ── Sessions tab ── */}
        {viewTab==="sessions"&&(
          sessions.length===0
            ?<div className="empty"><div className="empty-icon">📋</div><div style={{fontWeight:700,marginBottom:6}}>No sessions recorded yet</div><div style={{fontSize:12}}>Your coach logs sessions after each training</div></div>
            :sessions.map((se,i)=>{
              const isGroup=!!se.isGroupSession;
              const showExpanded= i<3 ? expanded[se.id||i]!==false : !!expanded[se.id||i];
              return(
                <div key={se.id||i} className="card" style={{marginBottom:8,borderColor:isGroup?"var(--accent2)":"var(--border)",borderWidth:isGroup?2:1.5}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer"}} onClick={()=>toggleExp(se.id||i)}>
                    <div style={{width:40,height:40,borderRadius:10,flexShrink:0,
                      background:isGroup?"linear-gradient(135deg,var(--accent2),#922c00)":"linear-gradient(135deg,var(--accent),#003d5c)",
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:20}}>{sessTypeIcon(se.type)}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:5,flexWrap:"wrap",marginBottom:2}}>
                        <span style={{fontWeight:700,fontSize:13,color:"var(--text)"}}>{se.type} Session</span>
                        {isGroup&&<span className="badge" style={{background:"#fff0ea",color:"var(--accent2)",border:"1.5px solid var(--accent2)",fontSize:9}}>👥 Group</span>}
                      </div>
                      <div style={{fontSize:11,color:"var(--text3)"}}>{dateStr(se.date)} · {se.duration} min</div>
                    </div>
                    <span style={{fontSize:13,color:"var(--text3)",flexShrink:0,marginTop:2}}>{showExpanded?"▾":"▸"}</span>
                  </div>
                  {showExpanded&&(
                    <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid var(--border)"}}>
                      <div style={{fontSize:12,color:"var(--text)",fontStyle:"italic",lineHeight:1.6}}>"{se.coachNote}"</div>
                      {isGroup&&<div style={{marginTop:8,fontSize:11,color:"var(--accent2)",fontWeight:600}}>👥 This was a group training session shared with your squad</div>}
                    </div>
                  )}
                </div>
              );
            })
        )}

        {/* ── Reports tab ── */}
        {viewTab==="reports"&&(
          reportHistory.length===0?(
            <div className="empty">
              <div className="empty-icon">📄</div>
              <div style={{fontWeight:700,marginBottom:6}}>No reports yet</div>
              <div style={{fontSize:12}}>Your coach will generate progress reports which will appear here</div>
            </div>
          ):(
            <>
              <div style={{fontSize:11,color:"var(--text3)",marginBottom:10,lineHeight:1.5}}>
                Progress reports generated by your coach. Tap any report to view and print.
              </div>
              {reportHistory.map((r,i)=>(
                <div key={r.id||i} className="card" style={{marginBottom:8,borderColor:"var(--accent3)",borderWidth:1.5,cursor:"pointer"}}
                  onClick={()=>setShowReportOverlay(r)}>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <div style={{width:44,height:44,borderRadius:10,flexShrink:0,
                      background:"linear-gradient(135deg,var(--accent3),#0a5c2a)",
                      display:"flex",alignItems:"center",justifyContent:"center",fontSize:22}}>📄</div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:13,color:"var(--text)"}}>{r.label||`Progress Report`}</div>
                      <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                        {dateStr(r.generatedAt)}
                      </div>
                      <div style={{fontSize:11,color:"var(--text2)",marginTop:2}}>
                        {r.sessionCount} sessions · {r.assessmentCount} assessments
                        {r.overallScore?` · Overall: ${r.overallScore}/5`:""}
                      </div>
                    </div>
                    <div style={{fontSize:18,color:"var(--accent3)"}}>›</div>
                  </div>
                </div>
              ))}
            </>
          )
        )}

      </div>
    </div>
  );
}

function StudentSkillTips({user,db}){
  const cls=user.cricketClass;
  const lib=getOrgLibrary(db,user.orgId);
  const sNotes=db.skillNotes?.[user.id]||{};
  const updatedAt=sNotes._updatedAt;
  const coachId=sNotes._coachId||user.coachId;
  const coach=db.coaches.find(c=>c.id===coachId);
  const coachName=coach?coach.name:"your coach";
  const [histExpanded,setHistExpanded]=useState({});
  // Helper: get notes array (handles legacy string migration)
  const getNotes=(cat,skillId)=>{
    const raw=(sNotes[cat]||{})[skillId];
    if(!raw) return [];
    if(Array.isArray(raw)) return raw;
    return [{note:raw,date:sNotes._updatedAt||"",coachId:sNotes._coachId||""}];
  };
  const catsWithNotes=CATEGORIES.filter(cat=>{
    const catSkills=(lib[cls]?.[cat]||[]).filter(sk=>sk.active);
    return catSkills.some(sk=>getNotes(cat,sk.id).length>0);
  });
  return(
    <div style={{padding:"0 8px"}}>
      {catsWithNotes.length===0?(
        <div className="empty" style={{marginTop:40}}>
          <div className="empty-icon">💡</div>
          <div style={{fontWeight:700,marginBottom:4}}>No coaching tips yet</div>
          <div style={{fontSize:12,color:"var(--text3)"}}>Your coach will add tips here after your sessions</div>
        </div>
      ):(
        <>
          <div style={{background:"#f0f9ff",border:"1.5px solid #bae0fd",borderRadius:10,padding:"10px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:22}}>💡</span>
            <div>
              <div style={{fontWeight:700,fontSize:13,color:"#0369a1"}}>Tips from {coachName}</div>
              {updatedAt&&<div style={{fontSize:11,color:"#0369a1",opacity:.8,marginTop:1}}>Updated {dateStr(updatedAt)}</div>}
            </div>
          </div>
          {CATEGORIES.map(cat=>{
            const catSkills=(lib[cls]?.[cat]||[]).filter(sk=>sk.active);
            const skillsWithNotes=catSkills.filter(sk=>getNotes(cat,sk.id).length>0);
            if(!skillsWithNotes.length) return null;
            return(
              <div key={cat} className="card" style={{marginBottom:10,padding:0,overflow:"hidden"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,padding:"9px 12px",borderBottom:"1px solid var(--border)",background:"var(--surface)"}}>
                  <span style={{fontSize:15}}>{CAT_ICON[cat]}</span>
                  <span style={{fontWeight:700,fontSize:12,color:CAT_COLOR[cat],textTransform:"uppercase",letterSpacing:.5}}>{cat}</span>
                  <span style={{fontSize:10,color:"var(--text3)",marginLeft:"auto"}}>{skillsWithNotes.length} tip{skillsWithNotes.length!==1?"s":""}</span>
                </div>
                {skillsWithNotes.map(sk=>{
                  const notes=getNotes(cat,sk.id);
                  const latest=notes[0];
                  const history=notes.slice(1);
                  const hKey=cat+"__"+sk.id;
                  return(
                    <div key={sk.id} style={{padding:"10px 12px",borderBottom:"1px solid var(--border)"}}>
                      <div style={{fontSize:12,fontWeight:700,color:"var(--text)",marginBottom:5}}>{sk.label}</div>
                      {/* Latest note — prominent */}
                      <div style={{fontSize:12,color:"var(--text2)",lineHeight:1.6,padding:"7px 10px",background:"var(--surface2)",borderRadius:8,borderLeft:"3px solid "+CAT_COLOR[cat],marginBottom:3}}>
                        {latest.note}
                      </div>
                      <div style={{fontSize:10,color:"var(--text3)",marginBottom:history.length>0?4:0}}>{latest.date?dateStr(latest.date):""}</div>
                      {/* Collapsible history */}
                      {history.length>0&&(
                        <div>
                          <button
                            onClick={()=>setHistExpanded(h=>({...h,[hKey]:!h[hKey]}))}
                            style={{background:"none",border:"none",padding:"2px 0",fontSize:11,color:"var(--text3)",cursor:"pointer",display:"flex",alignItems:"center",gap:4}}>
                            <span style={{fontSize:10}}>{histExpanded[hKey]?"▾":"▸"}</span>
                            {history.length} earlier tip{history.length!==1?"s":""}
                          </button>
                          {histExpanded[hKey]&&history.map((n,i)=>(
                            <div key={i} style={{marginTop:4,padding:"6px 10px",borderRadius:8,background:"var(--surface2)",border:"1px solid var(--border)",opacity:.65}}>
                              <div style={{fontSize:11,color:"var(--text2)",lineHeight:1.5}}>{n.note}</div>
                              <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>{n.date?dateStr(n.date):""}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
          {CATEGORIES.filter(cat=>{
            const catSkills=(lib[cls]?.[cat]||[]).filter(sk=>sk.active);
            return catSkills.length>0&&!catSkills.some(sk=>getNotes(cat,sk.id).length>0);
          }).length>0&&(
            <div style={{fontSize:11,color:"var(--text3)",textAlign:"center",padding:"8px 0"}}>
              More tips coming for other skill areas
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StudentSettings({user,db,onSwitchPlayer,onChangePassword}){
  const coach=db.coaches.find(c=>c.id===user.coachId);
  const [showChangePw,setShowChangePw]=useState(false);
  const [currentPw,setCurrentPw]=useState("");
  const [newPw1,setNewPw1]=useState("");
  const [newPw2,setNewPw2]=useState("");
  const [pwErr,setPwErr]=useState("");
  const [pwDone,setPwDone]=useState(false);

  const handleChangePw=()=>{
    setPwErr("");
    const student=db?.students?.find(s=>s.id===user.id);
    if(!student){setPwErr("Account not found");return;}
    if(student.password!==currentPw){setPwErr("Current password is incorrect");return;}
    if(newPw1.length<6){setPwErr("New password must be at least 6 characters");return;}
    if(newPw1!==newPw2){setPwErr("New passwords do not match");return;}
    onChangePassword&&onChangePassword(newPw1);
    setCurrentPw("");setNewPw1("");setNewPw2("");
    setShowChangePw(false);setPwDone(true);
  };

  // Check if this player has siblings (for Switch Player)
  const siblings=(db?.students||[]).filter(s=>s.id!==user.id&&(user.linkedPlayerIds||[]).includes(s.id));

  return(
    <div>
      <div style={{padding:"0 8px"}}>
        <div className="card card-hl" style={{display:"flex",alignItems:"center",gap:14,marginBottom:12}}>
          <div className="avatar" style={{width:56,height:56,background:"linear-gradient(135deg,var(--accent),#005577)",fontSize:20,color:"#000",fontFamily:"var(--font-display)"}}>{user.avatar}</div>
          <div>
            <div style={{fontFamily:"var(--font-display)",fontSize:20}}>{user.name}</div>
            <div style={{fontSize:12,color:"var(--text3)",marginTop:1}}>{user.email}</div>
            <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:5}}>
              <ClassBadge cls={user.cricketClass}/>
              {(user.tags||[]).map(t=><TagChip key={t} tagId={t}/>)}
            </div>
          </div>
        </div>
        {coach&&<div className="card" style={{marginBottom:10}}>
          <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:.5,marginBottom:6}}>My Coach</div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            <div className="avatar" style={{width:36,height:36,background:"linear-gradient(135deg,var(--accent2),#8a3000)",fontSize:12,color:"#fff",fontFamily:"var(--font-display)"}}>{coach.avatar}</div>
            <div><div style={{fontWeight:700,fontSize:13}}>{coach.name}</div><div style={{fontSize:11,color:"var(--text3)"}}>{coach.team}</div></div>
          </div>
        </div>}

        {/* Change Password */}
        <div style={{marginBottom:14}}>
          <button
            onClick={()=>{setShowChangePw(s=>!s);setPwErr("");setPwDone(false);setCurrentPw("");setNewPw1("");setNewPw2("");}}
            style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px",
              background:"linear-gradient(135deg,#f8f0ff,#f0e8ff)",
              border:`1.5px solid ${showChangePw?"#7c3aed":"#c4b5fd"}`,
              borderRadius:"var(--radius)",color:"var(--text)",cursor:"pointer",textAlign:"left"}}>
            <div style={{fontSize:28,flexShrink:0}}>🔑</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:14,color:"#7c3aed"}}>Change Password</div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{pwDone?"Password updated successfully ✅":"Update your login password"}</div>
            </div>
            <div style={{fontSize:18,color:"#7c3aed"}}>{showChangePw?"▾":"›"}</div>
          </button>
          {showChangePw&&(
            <div style={{padding:"14px",background:"#faf5ff",border:"1.5px solid #c4b5fd",
              borderTop:"none",borderRadius:"0 0 var(--radius) var(--radius)"}}>
              <div style={{marginBottom:10}}>
                <div className="lbl">Current Password</div>
                <input type="password" value={currentPw} onChange={e=>setCurrentPw(e.target.value)}
                  placeholder="Enter your current password"
                  style={{border:"1.5px solid #c4b5fd"}}/>
              </div>
              <div style={{marginBottom:10}}>
                <div className="lbl">New Password (min 6 characters)</div>
                <input type="password" value={newPw1} onChange={e=>setNewPw1(e.target.value)}
                  placeholder="Choose a new password"
                  style={{border:"1.5px solid #c4b5fd"}}/>
              </div>
              <div style={{marginBottom:12}}>
                <div className="lbl">Confirm New Password</div>
                <input type="password" value={newPw2} onChange={e=>setNewPw2(e.target.value)}
                  placeholder="Repeat new password"
                  onKeyDown={e=>e.key==="Enter"&&handleChangePw()}
                  style={{border:"1.5px solid #c4b5fd"}}/>
              </div>
              {pwErr&&(
                <div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",
                  borderRadius:8,padding:"8px 12px",fontSize:12,color:"var(--danger)",marginBottom:10}}>
                  {pwErr}
                </div>
              )}
              <div style={{display:"flex",gap:8}}>
                <button
                  onClick={()=>{setShowChangePw(false);setPwErr("");}}
                  style={{flex:1,padding:"10px",borderRadius:8,fontWeight:700,fontSize:13,
                    background:"transparent",border:"1.5px solid var(--border)",cursor:"pointer"}}>
                  Cancel
                </button>
                <button
                  onClick={handleChangePw}
                  style={{flex:2,padding:"10px",borderRadius:8,fontWeight:700,fontSize:13,
                    background:"linear-gradient(135deg,#7c3aed,#5b21b6)",color:"#fff",
                    border:"none",cursor:"pointer"}}>
                  Update Password
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Switch Player (siblings) */}
        {siblings.length>0&&(
          <button
            onClick={()=>onSwitchPlayer&&onSwitchPlayer(null)}
            style={{width:"100%",display:"flex",alignItems:"center",gap:12,padding:"14px",
              background:"linear-gradient(135deg,#f0f4ff,#e8edf8)",
              border:"1.5px solid #94a3b8",
              borderRadius:"var(--radius)",color:"var(--text)",cursor:"pointer",marginBottom:14,textAlign:"left"}}>
            <div style={{fontSize:28,flexShrink:0}}>🔄</div>
            <div style={{flex:1}}>
              <div style={{fontWeight:700,fontSize:14,color:"var(--accent)"}}>Switch Player</div>
              <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>View another player on this account</div>
            </div>
            <div style={{fontSize:18,color:"var(--accent)"}}>›</div>
          </button>
        )}

        <button className="btn-danger" style={{width:"100%",marginBottom:6}} onClick={()=>window.location.reload()}>Sign Out</button>
        <div style={{textAlign:"center",fontSize:10,color:"var(--text3)",marginBottom:20}}>CrickitCoach v4.997 · © 2026 ArcGain LLC</div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// FORGOT PASSWORD SCREEN  (self-service for coaches and students)
// ═══════════════════════════════════════════════════════════════════════════════
function ForgotPasswordScreen({db,onBack,onPasswordReset}){
  const [step,setStep]=useState("find");   // "find" | "reset" | "done"
  const [email,setEmail]=useState("");
  const [newPw,setNewPw]=useState("");
  const [confirm,setConfirm]=useState("");
  const [found,setFound]=useState(null);   // {type:"coach"|"student", record}
  const [err,setErr]=useState("");

  const findAccount=()=>{
    setErr("");
    if(!email.trim()||!email.includes("@")){setErr("Enter a valid email address");return;}
    const em=email.trim().toLowerCase();
    const coach=(db?.coaches||[]).find(c=>c.email===em);
    if(coach){setFound({type:"coach",record:coach});setStep("reset");return;}
    const student=(db?.students||[]).find(s=>s.email===em);
    if(student){setFound({type:"student",record:student});setStep("reset");return;}
    setErr("No account found with that email address.");
  };

  const resetPassword=()=>{
    setErr("");
    if(newPw.length<6){setErr("Password must be at least 6 characters");return;}
    if(newPw!==confirm){setErr("Passwords do not match");return;}
    onPasswordReset(found.type,found.record.id,newPw);
    setStep("done");
  };

  return(
    <div style={{height:"100svh",display:"flex",flexDirection:"column",background:"var(--bg)",maxWidth:440,margin:"0 auto"}}>
      <div style={{padding:"32px 22px 20px",textAlign:"center",background:"linear-gradient(180deg,#1a3a5c,var(--bg))"}}>
        <div style={{fontSize:40,marginBottom:6}}>🔑</div>
        <div style={{fontFamily:"var(--font-display)",fontSize:30,letterSpacing:2}}>RESET PASSWORD</div>
        <div style={{fontSize:12,color:"var(--text3)",marginTop:4}}>CrickitCoach · by ArcGain LLC</div>
      </div>

      <div style={{flex:1,overflow:"auto",padding:"0 22px 32px"}}>

        {step==="find"&&(<>
          <div style={{marginBottom:6,fontSize:13,color:"var(--text2)",lineHeight:1.6}}>
            Enter the email address you use to sign in. We'll find your account and let you set a new password.
          </div>
          <div style={{marginBottom:14,marginTop:14}}>
            <div className="lbl">Email Address</div>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
              placeholder="your@email.com" onKeyDown={e=>e.key==="Enter"&&findAccount()}/>
          </div>
          {err&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"9px 12px",fontSize:12,color:"var(--danger)",marginBottom:14}}>{err}</div>}
          <button className="btn-primary" onClick={findAccount} style={{marginBottom:10}}>Find My Account</button>
          <button className="btn-secondary" onClick={onBack}>← Back to Sign In</button>
        </>)}

        {step==="reset"&&found&&(<>
          <div style={{padding:"14px",background:"var(--surface2)",borderRadius:12,marginBottom:18,marginTop:8}}>
            <div style={{fontSize:11,color:"var(--text3)",textTransform:"uppercase",letterSpacing:.5,marginBottom:4}}>Account Found</div>
            <div style={{fontWeight:700,fontSize:15}}>{found.record.name}</div>
            <div style={{fontSize:12,color:"var(--text2)"}}>{found.record.email}</div>
            <div style={{marginTop:6}}>
              <span className="badge" style={{background:"#e0f0fa",color:"var(--accent)",border:"1.5px solid var(--accent)"}}>
                {found.type==="coach"?(found.record.role==="admin"?"👑 School Admin":"🎓 Coach"):"🏏 Player"}
              </span>
            </div>
          </div>
          <div style={{marginBottom:12}}>
            <div className="lbl">New Password (min 6 characters)</div>
            <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="Choose a new password"/>
          </div>
          <div style={{marginBottom:16}}>
            <div className="lbl">Confirm New Password</div>
            <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)} placeholder="Repeat new password"
              onKeyDown={e=>e.key==="Enter"&&resetPassword()}/>
          </div>
          {err&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"9px 12px",fontSize:12,color:"var(--danger)",marginBottom:14}}>{err}</div>}
          <button className="btn-primary" onClick={resetPassword} style={{marginBottom:10}}>🔑 Set New Password</button>
          <button className="btn-secondary" onClick={()=>{setStep("find");setFound(null);setErr("");}}>← Try Different Email</button>
        </>)}

        {step==="done"&&(<>
          <div style={{textAlign:"center",padding:"30px 0"}}>
            <div style={{fontSize:52,marginBottom:12}}>✅</div>
            <div style={{fontFamily:"var(--font-display)",fontSize:24,marginBottom:8}}>Password Updated</div>
            <div style={{fontSize:13,color:"var(--text2)",marginBottom:24,lineHeight:1.6}}>
              Your password has been changed. You can now sign in with your new password.
            </div>
          </div>
          <button className="btn-primary" onClick={onBack}>← Go to Sign In</button>
        </>)}

      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SUPERADMIN DASHBOARD  (ArcGain LLC owner only — never shown to schools)
// ═══════════════════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════════════════
// BILLING EDITOR MODAL — SuperAdmin edits one school's billing plan
// ═══════════════════════════════════════════════════════════════════════════════
const PLAN_CONFIG={
  trial:     {label:"Trial",      icon:"⏳", color:"#d97706", bg:"#fef9c3"},
  free:      {label:"Free",       icon:"🎁", color:"#0891b2", bg:"#e0f2fe"},
  discounted:{label:"Discounted", icon:"🏷️", color:"#7c3aed", bg:"#ede9fe"},
  paid:      {label:"Paid",       icon:"✅", color:"#15803d", bg:"#dcfce7"},
  suspended: {label:"Suspended",  icon:"🚫", color:"#dc2626", bg:"#fee2e2"},
};

function BillingEditorModal({billing,onSave,onClose}){
  const [plan,setPlan]=useState(billing.plan||"trial");
  const [monthlyRate,setMonthlyRate]=useState(String(billing.monthlyRate||99));
  const [effectiveRate,setEffectiveRate]=useState(String(billing.effectiveRate||0));
  const [discountPct,setDiscountPct]=useState(String(billing.discountPercent||100));
  const [reason,setReason]=useState(billing.discountReason||"");
  const [trialEnd,setTrialEnd]=useState(billing.trialEndDate||"");
  const [autoExpire,setAutoExpire]=useState(billing.autoExpire!==false);
  const [revertTo,setRevertTo]=useState(billing.revertToPlan||"paid");
  const [notes,setNotes]=useState(billing.notes||"");
  const [err,setErr]=useState("");

  // Auto-calculate effectiveRate when discountPct changes
  const onDiscountChange=v=>{
    setDiscountPct(v);
    const mr=parseFloat(monthlyRate)||0;
    const dp=parseFloat(v)||0;
    setEffectiveRate(String(Math.round(mr*(1-dp/100))));
  };
  // Auto-calculate discountPct when effectiveRate changes
  const onEffectiveChange=v=>{
    setEffectiveRate(v);
    const mr=parseFloat(monthlyRate)||0;
    const er=parseFloat(v)||0;
    if(mr>0) setDiscountPct(String(Math.round((1-er/mr)*100)));
  };

  const submit=()=>{
    setErr("");
    if(!reason.trim()&&plan!=="paid"&&plan!=="suspended"){setErr("Reason is required for non-paid plans");return;}
    if(plan==="trial"&&!trialEnd){setErr("Trial end date is required");return;}
    onSave({
      plan,
      monthlyRate:parseFloat(monthlyRate)||99,
      effectiveRate:parseFloat(effectiveRate)||0,
      discountPercent:parseFloat(discountPct)||0,
      discountReason:reason.trim(),
      trialEndDate:trialEnd||null,
      autoExpire,
      revertToPlan:revertTo,
      notes:notes.trim(),
      updatedBy:"superadmin",
    });
  };

  const pc=PLAN_CONFIG[plan]||PLAN_CONFIG.paid;
  const mr=parseFloat(monthlyRate)||0;
  const er=parseFloat(effectiveRate)||0;
  const saving=mr-er;

  return(
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="sheet" style={{maxHeight:"96%",overflowY:"auto"}}>
        <div className="handle"/>
        <div style={{fontFamily:"var(--font-display)",fontSize:22,marginBottom:4}}>💰 Edit Billing Plan</div>
        <div style={{fontSize:12,color:"var(--text3)",marginBottom:16}}>{billing.schoolName||billing.orgId}</div>

        {/* Plan selector */}
        <div className="lbl" style={{marginBottom:8}}>Subscription Plan</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,marginBottom:16}}>
          {Object.entries(PLAN_CONFIG).map(([key,cfg])=>(
            <button key={key} onClick={()=>setPlan(key)} style={{
              padding:"10px 6px",borderRadius:10,fontWeight:700,fontSize:12,cursor:"pointer",textAlign:"center",
              background:plan===key?cfg.bg:"var(--surface2)",
              color:plan===key?cfg.color:"var(--text2)",
              border:`2px solid ${plan===key?cfg.color:"var(--border)"}`,
            }}>
              <div style={{fontSize:18,marginBottom:3}}>{cfg.icon}</div>
              {cfg.label}
            </button>
          ))}
        </div>

        {/* Rate fields */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8,marginBottom:12}}>
          <div>
            <div className="lbl">Standard rate ($/mo)</div>
            <input type="number" value={monthlyRate} onChange={e=>setMonthlyRate(e.target.value)} min="0" step="1"/>
          </div>
          <div>
            <div className="lbl">Discount %</div>
            <input type="number" value={discountPct} onChange={e=>onDiscountChange(e.target.value)} min="0" max="100" step="1"/>
          </div>
          <div>
            <div className="lbl">Effective rate ($/mo)</div>
            <input type="number" value={effectiveRate} onChange={e=>onEffectiveChange(e.target.value)} min="0" step="1"/>
          </div>
        </div>

        {/* Saving summary */}
        {saving>0&&(
          <div style={{padding:"8px 12px",background:"#dcfce7",borderRadius:8,border:"1px solid #86efac",fontSize:12,color:"#15803d",fontWeight:600,marginBottom:12}}>
            💚 School saves ${saving}/mo ({discountPct}% off standard rate)
          </div>
        )}
        {er===0&&plan!=="suspended"&&(
          <div style={{padding:"8px 12px",background:"#e0f2fe",borderRadius:8,border:"1px solid #7dd3fc",fontSize:12,color:"#0369a1",fontWeight:600,marginBottom:12}}>
            🎁 School pays $0/mo — fully free access
          </div>
        )}

        {/* Reason */}
        <div style={{marginBottom:12}}>
          <div className="lbl">Reason / description {plan!=="paid"&&plan!=="suspended"?"*":""}</div>
          <input value={reason} onChange={e=>setReason(e.target.value)}
            placeholder="e.g. Launch partner — free for 6 months"/>
        </div>

        {/* Trial dates */}
        {plan==="trial"&&(
          <div style={{marginBottom:12,padding:"12px 14px",background:"#fef9c3",borderRadius:10,border:"1.5px solid #fbbf24"}}>
            <div style={{fontSize:12,fontWeight:700,color:"#92400e",marginBottom:10}}>⏳ Trial Settings</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:10}}>
              <div>
                <div className="lbl">Trial end date *</div>
                <input type="date" value={trialEnd} onChange={e=>setTrialEnd(e.target.value)}/>
              </div>
              <div>
                <div className="lbl">After trial, revert to</div>
                <select value={revertTo} onChange={e=>setRevertTo(e.target.value)}>
                  <option value="paid">Paid plan</option>
                  <option value="discounted">Discounted plan</option>
                  <option value="suspended">Suspend access</option>
                  <option value="free">Keep free</option>
                </select>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <input type="checkbox" id="autoexp" checked={autoExpire} onChange={e=>setAutoExpire(e.target.checked)}
                style={{width:16,height:16,accentColor:"#d97706"}}/>
              <label htmlFor="autoexp" style={{fontSize:12,color:"var(--text2)"}}>
                Auto-expire — automatically change plan when trial ends
              </label>
            </div>
          </div>
        )}

        {/* Internal notes */}
        <div style={{marginBottom:16}}>
          <div className="lbl">Internal notes (not visible to school)</div>
          <textarea value={notes} onChange={e=>setNotes(e.target.value)}
            placeholder="e.g. Agreed at San Diego cricket club meeting April 2026 — review in September"
            style={{width:"100%",minHeight:64,borderRadius:8,border:"1.5px solid var(--border)",
              background:"var(--surface2)",color:"var(--text)",fontSize:12,padding:"8px 10px",resize:"vertical"}}/>
        </div>

        {err&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"9px 12px",fontSize:12,color:"var(--danger)",marginBottom:12}}>{err}</div>}

        <div style={{display:"flex",gap:8}}>
          <button className="btn-secondary" style={{flex:1}} onClick={onClose}>Cancel</button>
          <button className="btn-primary" style={{flex:2,background:"linear-gradient(135deg,#1a3a5c,#0369a1)"}} onClick={submit}>
            💾 Save Billing Plan
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BILLING TAB — full billing management inside SuperAdmin
// ═══════════════════════════════════════════════════════════════════════════════
function BillingTab({orgs,showNotif}){
  const [billings,setBillings]=useState({});    // orgId → billing record
  const [loading,setLoading]=useState(true);
  const [editTarget,setEditTarget]=useState(null); // billing record being edited
  const [saving,setSaving]=useState(false);

  useEffect(()=>{
    (async()=>{
      setLoading(true);
      const all=await fetchBillingAll();
      const map={};
      all.forEach(b=>{ if(b.orgId) map[b.orgId]=b; });
      // Fill in any orgs with no billing record yet with a placeholder
      orgs.forEach(org=>{
        if(!map[org.orgId]) map[org.orgId]={
          orgId:org.orgId, schoolName:org.schoolName,
          plan:"trial", monthlyRate:99, effectiveRate:0,
          discountPercent:100, discountReason:"Free first year — CrickitCoach launch offer",
          trialEndDate:(()=>{const d=new Date();d.setFullYear(d.getFullYear()+1);return d.toISOString().split('T')[0];})(), autoExpire:true, revertToPlan:"paid",
          notes:"", daysRemaining:null,
        };
      });
      setBillings(map);
      setLoading(false);
    })();
  },[]);

  const handleSave=async(orgId,data)=>{
    setSaving(true);
    const ok=await saveBilling(orgId,data);
    if(ok){
      setBillings(b=>({...b,[orgId]:{...b[orgId],...data,orgId}}));
      showNotif("Billing plan updated ✅");
      setEditTarget(null);
    } else {
      showNotif("Failed to save — check API connection","error");
    }
    setSaving(false);
  };

  // Summary stats
  const bArr=Object.values(billings);
  const totalMRR=bArr.reduce((acc,b)=>acc+(b.effectiveRate||0),0);
  const potentialMRR=bArr.reduce((acc,b)=>acc+(b.monthlyRate||0),0);
  const onTrial=bArr.filter(b=>b.plan==="trial").length;
  const onFree=bArr.filter(b=>b.plan==="free").length;
  const onDiscount=bArr.filter(b=>b.plan==="discounted").length;
  const onPaid=bArr.filter(b=>b.plan==="paid").length;
  const suspended=bArr.filter(b=>b.plan==="suspended").length;

  if(loading) return(
    <div style={{padding:20,textAlign:"center",color:"var(--text3)"}}>
      <div style={{fontSize:24,marginBottom:8}}>💰</div>
      Loading billing data…
    </div>
  );

  return(
    <div>
      {/* MRR Summary */}
      <div style={{padding:"12px 14px",background:"linear-gradient(135deg,#1a3a5c,#0F2744)",borderRadius:12,marginBottom:14}}>
        <div style={{fontSize:10,color:"rgba(255,255,255,0.6)",textTransform:"uppercase",letterSpacing:1,marginBottom:6}}>Monthly Recurring Revenue</div>
        <div style={{display:"flex",alignItems:"flex-end",gap:8,marginBottom:8}}>
          <div style={{fontFamily:"var(--font-display)",fontSize:36,color:"#4ade80",fontWeight:900,lineHeight:1}}>
            ${totalMRR.toLocaleString()}
          </div>
          <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginBottom:4}}>
            / mo actual
          </div>
        </div>
        <div style={{fontSize:11,color:"rgba(255,255,255,0.5)"}}>
          ${potentialMRR.toLocaleString()}/mo at full rate · ${(potentialMRR-totalMRR).toLocaleString()}/mo in discounts/trials
        </div>
        <div style={{display:"flex",gap:8,marginTop:10,flexWrap:"wrap"}}>
          {[["✅",onPaid,"Paid","#4ade80"],["⏳",onTrial,"Trial","#fbbf24"],
            ["🏷️",onDiscount,"Discounted","#a78bfa"],["🎁",onFree,"Free","#67e8f9"],
            ["🚫",suspended,"Suspended","#f87171"]
          ].filter(([,n])=>n>0).map(([icon,n,lbl,color])=>(
            <div key={lbl} style={{background:"rgba(255,255,255,0.08)",borderRadius:8,padding:"4px 10px",display:"flex",alignItems:"center",gap:4}}>
              <span style={{fontSize:12}}>{icon}</span>
              <span style={{fontSize:12,color,fontWeight:700}}>{n}</span>
              <span style={{fontSize:10,color:"rgba(255,255,255,0.5)"}}>{lbl}</span>
            </div>
          ))}
        </div>
      </div>

      {/* School billing cards */}
      {orgs.length===0&&<div className="empty"><div className="empty-icon">🏫</div><div>No schools registered yet</div></div>}
      {orgs.map(org=>{
        const b=billings[org.orgId];
        if(!b) return null;
        const pc=PLAN_CONFIG[b.plan]||PLAN_CONFIG.paid;
        const isExpiringSoon=b.plan==="trial"&&b.daysRemaining!==null&&b.daysRemaining<=7;

        return(
          <div key={org.orgId} className="card" style={{marginBottom:10,
            borderColor:isExpiringSoon?"#d97706":b.plan==="suspended"?"var(--danger)":"var(--border)",
            borderWidth:isExpiringSoon||b.plan==="suspended"?2:1.5}}>

            {/* School header row */}
            <div style={{display:"flex",alignItems:"flex-start",gap:10,marginBottom:10}}>
              <span style={{fontSize:22,flexShrink:0}}>{pc.icon}</span>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap",marginBottom:2}}>
                  <span style={{fontWeight:700,fontSize:14}}>{org.schoolName}</span>
                  <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:99,
                    background:pc.bg,color:pc.color,border:`1px solid ${pc.color}40`}}>
                    {pc.label}
                  </span>
                </div>
                <div style={{fontSize:11,color:"var(--text3)"}}>
                  {org.coaches.length} coach{org.coaches.length!==1?"es":""} · {org.students.length} student{org.students.length!==1?"s":""}
                  {org.admin&&` · Admin: ${org.admin.email}`}
                </div>
              </div>
              <button className="btn-ghost btn-sm" style={{color:"var(--accent)",borderColor:"var(--accent)40",flexShrink:0}}
                onClick={()=>setEditTarget(b)}>✏️ Edit</button>
            </div>

            {/* Billing details row */}
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:b.notes||b.discountReason?8:0}}>
              <div style={{flex:1,minWidth:120,padding:"8px 10px",background:"var(--surface2)",borderRadius:8,textAlign:"center"}}>
                <div style={{fontSize:20,fontWeight:900,color:"#4ade80",fontFamily:"monospace"}}>
                  ${b.effectiveRate||0}<span style={{fontSize:11,color:"var(--text3)"}}>/mo</span>
                </div>
                <div style={{fontSize:9,color:"var(--text3)",textTransform:"uppercase",letterSpacing:.5}}>Charges</div>
              </div>
              {(b.discountPercent||0)>0&&(
                <div style={{flex:1,minWidth:100,padding:"8px 10px",background:"var(--surface2)",borderRadius:8,textAlign:"center"}}>
                  <div style={{fontSize:20,fontWeight:900,color:"#a78bfa",fontFamily:"monospace"}}>{b.discountPercent}%</div>
                  <div style={{fontSize:9,color:"var(--text3)",textTransform:"uppercase",letterSpacing:.5}}>Discount</div>
                </div>
              )}
              {b.plan==="trial"&&b.trialEndDate&&(
                <div style={{flex:1,minWidth:110,padding:"8px 10px",
                  background:isExpiringSoon?"#fef9c3":"var(--surface2)",borderRadius:8,textAlign:"center",
                  border:isExpiringSoon?"1.5px solid #fbbf24":"none"}}>
                  <div style={{fontSize:20,fontWeight:900,
                    color:isExpiringSoon?"#d97706":"var(--text)",fontFamily:"monospace"}}>
                    {b.daysRemaining!==null?b.daysRemaining:"—"}
                  </div>
                  <div style={{fontSize:9,color:"var(--text3)",textTransform:"uppercase",letterSpacing:.5}}>
                    {isExpiringSoon?"⚠️ Days left":"Days left"}
                  </div>
                </div>
              )}
              <div style={{flex:1,minWidth:100,padding:"8px 10px",background:"var(--surface2)",borderRadius:8,textAlign:"center"}}>
                <div style={{fontSize:14,fontWeight:700,color:"var(--text2)",fontFamily:"monospace"}}>${b.monthlyRate||99}</div>
                <div style={{fontSize:9,color:"var(--text3)",textTransform:"uppercase",letterSpacing:.5}}>Standard</div>
              </div>
            </div>

            {/* Reason / notes */}
            {b.discountReason&&b.plan!=="paid"&&b.plan!=="suspended"&&(
              <div style={{fontSize:11,color:"var(--text2)",fontStyle:"italic",marginBottom:4}}>
                "{b.discountReason}"
              </div>
            )}
            {b.notes&&(
              <div style={{fontSize:10,color:"var(--text3)",lineHeight:1.5}}>
                📝 {b.notes}
              </div>
            )}
            {b.plan==="trial"&&b.trialEndDate&&(
              <div style={{fontSize:10,color:isExpiringSoon?"#d97706":"var(--text3)",marginTop:4,fontWeight:isExpiringSoon?700:400}}>
                {isExpiringSoon?"⚠️ ":""}Trial ends {b.trialEndDate} → reverts to {b.revertToPlan||"paid"} plan
                {b.autoExpire?"":" (manual)"}
              </div>
            )}
          </div>
        );
      })}

      {/* Edit modal */}
      {editTarget&&(
        <BillingEditorModal
          billing={editTarget}
          onClose={()=>setEditTarget(null)}
          onSave={(data)=>handleSave(editTarget.orgId,data)}
        />
      )}
    </div>
  );
}

function SuperAdminDashboard({db,onResetAdminPassword,onBack,showNotif,onSuspendSchool,onReinstateSchool}){
  const [saTab,setSaTab]=useState("schools"); // "schools" | "billing"
  const [resetTarget,setResetTarget]=useState(null);
  const [newPw,setNewPw]=useState("");
  const [err,setErr]=useState("");
  const [expandedOrg,setExpandedOrg]=useState({});
  const [suspendTarget,setSuspendTarget]=useState(null);
  const [reinstateTarget,setReinstateTarget]=useState(null);
  const [suspendNote,setSuspendNote]=useState("");
  const [statusOverride,setStatusOverride]=useState({});

  // Group coaches and students by orgId — also load school-level status
  const orgs=[];
  const seen=new Set();
  (db?.coaches||[]).forEach(c=>{
    if(!seen.has(c.orgId)){
      seen.add(c.orgId);
      const orgCoaches=(db.coaches||[]).filter(x=>x.orgId===c.orgId);
      const orgStudents=(db.students||[]).filter(x=>x.orgId===c.orgId);
      const admin=orgCoaches.find(x=>x.role==="admin")||orgCoaches[0];
      orgs.push({orgId:c.orgId,schoolName:admin?.schoolName||c.orgId,admin,coaches:orgCoaches,students:orgStudents});
    }
  });

  // Fetch live status from each org's DynamoDB record
  const [orgStatuses,setOrgStatuses]=useState({});
  useEffect(()=>{
    if(!onSuspendSchool) return; // SuperAdmin only
    orgs.forEach(async org=>{
      try{
        const {loadDbByOrgId}=window._crickitHelpers||{};
        // We use the already-loaded db for status since it's the logged-in school's data
        // For other schools, status comes from statusOverride or defaults to "active"
      }catch(e){}
    });
  },[]);

  const getStatus=(orgId)=>statusOverride[orgId]||"active";

  const doReset=()=>{
    setErr("");
    if(newPw.length<6){setErr("Password must be at least 6 characters");return;}
    onResetAdminPassword(resetTarget.id,newPw);
    showNotif(`Password reset for ${resetTarget.name} ✅`);
    setResetTarget(null);setNewPw("");
  };

  const doSuspend=()=>{
    if(!suspendTarget) return;
    onSuspendSchool(suspendTarget.orgId, suspendNote.trim()||"Suspended by SuperAdmin");
    setStatusOverride(o=>({...o,[suspendTarget.orgId]:"suspended"}));
    setSuspendTarget(null);setSuspendNote("");
  };

  const doReinstate=()=>{
    if(!reinstateTarget) return;
    onReinstateSchool(reinstateTarget.orgId);
    setStatusOverride(o=>({...o,[reinstateTarget.orgId]:"active"}));
    setReinstateTarget(null);
  };

  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
      {/* Header */}
      <div style={{padding:"14px 16px 10px",flexShrink:0,background:"linear-gradient(180deg,#1a3a5c,var(--bg))"}}>
        <button onClick={onBack} style={{background:"transparent",color:"var(--accent)",fontSize:13,fontWeight:700,padding:0,marginBottom:10}}>← Sign Out</button>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:4}}>
          <span style={{fontSize:28}}>🛡️</span>
          <div>
            <div style={{fontFamily:"var(--font-display)",fontSize:26,letterSpacing:1}}>SuperAdmin</div>
            <div style={{fontSize:11,color:"var(--text3)"}}>ArcGain LLC · Platform Overview</div>
          </div>
        </div>
        <div style={{display:"flex",gap:10,marginTop:6}}>
          <div className="stat-box" style={{flex:1}}><div className="stat-val">{orgs.length}</div><div className="stat-lbl">Schools</div></div>
          <div className="stat-box" style={{flex:1}}><div className="stat-val">{(db?.coaches||[]).length}</div><div className="stat-lbl">Coaches</div></div>
          <div className="stat-box" style={{flex:1}}><div className="stat-val">{(db?.students||[]).length}</div><div className="stat-lbl">Students</div></div>
        </div>
        {/* Tab switcher */}
        <div className="scroll-x" style={{marginTop:10,gap:4}}>
          <button className={`pill${saTab==="schools"?" active":""}`} onClick={()=>setSaTab("schools")}>🏫 Schools</button>
          <button className={`pill${saTab==="billing"?" active":""}`} onClick={()=>setSaTab("billing")}
            style={saTab==="billing"?{}:{borderColor:"#4ade8060",color:"#4ade80"}}>💰 Billing & Plans</button>
        </div>
        <div className="divider" style={{margin:"10px 0 0"}}/>
      </div>

      <div className="screen" style={{padding:"10px 14px"}}>
        {/* Billing tab */}
        {saTab==="billing"&&<BillingTab orgs={orgs} showNotif={showNotif}/>}
        {/* Schools tab */}
        {saTab==="schools"&&orgs.length===0&&(
          <div className="empty"><div className="empty-icon">🏫</div><div>No schools registered yet</div></div>
        )}
        {saTab==="schools"&&orgs.map(org=>{
          const expanded=expandedOrg[org.orgId]!==false;
          return(
            <div key={org.orgId} style={{marginBottom:12}}>
              {/* School header card */}
              {(()=>{
                const status=getStatus(org.orgId);
                const isSuspended=status==="suspended";
                return(
                  <div className="card" style={{borderColor:isSuspended?"var(--danger)":"var(--accent)",marginBottom:4,
                    background:isSuspended?"#fef2f2":"var(--surface)",cursor:"pointer"}}
                    onClick={()=>setExpandedOrg(e=>({...e,[org.orgId]:!expanded}))}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:24}}>{isSuspended?"🚫":"🏫"}</span>
                      <div style={{flex:1}}>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexWrap:"wrap"}}>
                          <span style={{fontWeight:700,fontSize:14}}>{org.schoolName}</span>
                          {isSuspended
                            ?<span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:99,background:"#fef2f2",color:"var(--danger)",border:"1px solid var(--danger)"}}>● Suspended</span>
                            :<span style={{fontSize:10,fontWeight:700,padding:"2px 7px",borderRadius:99,background:"#dcfce7",color:"var(--accent3)",border:"1px solid var(--accent3)"}}>● Active</span>
                          }
                        </div>
                        <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>
                          {org.coaches.length} coach{org.coaches.length!==1?"es":""} · {org.students.length} student{org.students.length!==1?"s":""}
                        </div>
                        {org.admin&&<div style={{fontSize:11,color:"var(--text2)",marginTop:1}}>Admin: {org.admin.name} · {org.admin.email}</div>}
                      </div>
                      <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:5}}>
                        <span style={{fontSize:12,color:"var(--text3)"}}>{expanded?"▾":"▸"}</span>
                        {org.admin&&(
                          <button className="btn-ghost btn-sm" style={{fontSize:10,color:"var(--warn)",borderColor:"var(--warn)40",whiteSpace:"nowrap"}}
                            onClick={e=>{e.stopPropagation();setResetTarget(org.admin);setNewPw("");setErr("");}}>
                            🔑 Reset PW
                          </button>
                        )}
                        {isSuspended
                          ?<button className="btn-ghost btn-sm" style={{fontSize:10,color:"var(--accent3)",borderColor:"var(--accent3)40",whiteSpace:"nowrap"}}
                              onClick={e=>{e.stopPropagation();setReinstateTarget(org);}}>
                              ✅ Reinstate
                            </button>
                          :<button className="btn-ghost btn-sm" style={{fontSize:10,color:"var(--danger)",borderColor:"var(--danger)40",whiteSpace:"nowrap"}}
                              onClick={e=>{e.stopPropagation();setSuspendTarget(org);setSuspendNote("");}}>
                              🚫 Suspend
                            </button>
                        }
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Expanded: coaches + students */}
              {expanded&&(
                <div style={{marginLeft:8}}>
                  {/* Coaches */}
                  <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:.5,margin:"6px 0 4px"}}>Coaches</div>
                  {org.coaches.map(c=>(
                    <div key={c.id} style={{display:"flex",alignItems:"center",gap:8,padding:"7px 10px",background:"var(--surface2)",borderRadius:8,marginBottom:4}}>
                      <div className="avatar" style={{width:30,height:30,background:c.role==="admin"?"linear-gradient(135deg,#b8860b,#8b6914)":"linear-gradient(135deg,var(--accent),#005577)",fontSize:11,color:"#fff",fontFamily:"var(--font-display)",flexShrink:0}}>{c.avatar}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:12,fontWeight:700}}>{c.name}</div>
                        <div style={{fontSize:10,color:"var(--text3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.email}</div>
                      </div>
                      <span className="badge" style={{background:c.role==="admin"?"#b8860b20":"var(--accent)20",color:c.role==="admin"?"var(--gold)":"var(--accent)",border:`1px solid ${c.role==="admin"?"#b8860b40":"var(--accent)40"}`,flexShrink:0,fontSize:9}}>
                        {c.role==="admin"?"👑 Admin":"🎓 Coach"}
                      </span>
                    </div>
                  ))}

                  {/* Students */}
                  {org.students.length>0&&(<>
                    <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:.5,margin:"8px 0 4px"}}>Students ({org.students.length})</div>
                    {org.students.map(s=>(
                      <div key={s.id} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",background:"var(--surface2)",borderRadius:8,marginBottom:3}}>
                        <div className="avatar" style={{width:26,height:26,background:"linear-gradient(135deg,var(--accent3),#005730)",fontSize:10,color:"#fff",fontFamily:"var(--font-display)",flexShrink:0}}>{s.avatar}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:600}}>{s.name}</div>
                          <div style={{fontSize:10,color:"var(--text3)"}}>{s.cricketClass} · Coach: {(db.coaches||[]).find(c=>c.id===s.coachId)?.name||"—"}</div>
                        </div>
                        <ClassBadge cls={s.cricketClass} size={8}/>
                      </div>
                    ))}
                  </>)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Reset Password Modal */}
      {resetTarget&&(
        <div className="overlay" onClick={e=>{if(e.target===e.currentTarget){setResetTarget(null);}}}>
          <div className="sheet">
            <div className="handle"/>
            <div style={{fontFamily:"var(--font-display)",fontSize:22,color:"var(--warn)",marginBottom:4}}>🔑 Reset Admin Password</div>
            <div style={{fontSize:12,color:"var(--text2)",marginBottom:16,lineHeight:1.6}}>
              Resetting password for <strong>{resetTarget.name}</strong> ({resetTarget.email}) at <strong>{resetTarget.schoolName}</strong>.
            </div>
            <div style={{marginBottom:12}}>
              <div className="lbl">New Password (min 6 characters)</div>
              <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)} placeholder="New password for school admin"/>
            </div>
            {err&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"9px 12px",fontSize:12,color:"var(--danger)",marginBottom:12}}>{err}</div>}
            <div style={{display:"flex",gap:8}}>
              <button className="btn-secondary" style={{flex:1}} onClick={()=>{setResetTarget(null);setErr("");}}>Cancel</button>
              <button className="btn-primary" style={{flex:2,background:"linear-gradient(135deg,var(--warn),#b34700)"}} onClick={doReset}>Reset Password</button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend School Modal */}
      {suspendTarget&&(
        <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)setSuspendTarget(null);}}>
          <div className="sheet">
            <div className="handle"/>
            <div style={{fontFamily:"var(--font-display)",fontSize:22,color:"var(--danger)",marginBottom:4}}>🚫 Suspend School</div>
            <div style={{fontSize:12,color:"var(--text2)",marginBottom:16,lineHeight:1.6}}>
              Suspending <strong>{suspendTarget.schoolName}</strong>. All coaches and students will be locked out immediately. No data will be deleted.
            </div>
            <div style={{marginBottom:16}}>
              <div className="lbl">Reason (optional — for internal reference)</div>
              <input value={suspendNote} onChange={e=>setSuspendNote(e.target.value)} placeholder="e.g. Non-payment — invoice overdue 30 days"/>
            </div>
            <div style={{padding:"10px 12px",background:"#fef2f2",borderRadius:8,fontSize:12,color:"var(--danger)",marginBottom:16,lineHeight:1.5}}>
              ⚠️ Coaches and students at this school will see: "This school's account has been suspended. Please contact support@crickitcoach.com."
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn-secondary" style={{flex:1}} onClick={()=>setSuspendTarget(null)}>Cancel</button>
              <button className="btn-primary" style={{flex:2,background:"linear-gradient(135deg,var(--danger),#7f1d1d)"}} onClick={doSuspend}>🚫 Suspend School</button>
            </div>
          </div>
        </div>
      )}

      {/* Reinstate School Modal */}
      {reinstateTarget&&(
        <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)setReinstateTarget(null);}}>
          <div className="sheet">
            <div className="handle"/>
            <div style={{fontFamily:"var(--font-display)",fontSize:22,color:"var(--accent3)",marginBottom:4}}>✅ Reinstate School</div>
            <div style={{fontSize:12,color:"var(--text2)",marginBottom:20,lineHeight:1.6}}>
              Reinstating <strong>{reinstateTarget.schoolName}</strong>. All coaches and students will be able to log in immediately.
            </div>
            <div style={{display:"flex",gap:8}}>
              <button className="btn-secondary" style={{flex:1}} onClick={()=>setReinstateTarget(null)}>Cancel</button>
              <button className="btn-primary" style={{flex:2,background:"linear-gradient(135deg,var(--accent3),#0a5c2a)"}} onClick={doReinstate}>✅ Reinstate School</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════════
function RegisterSchoolScreen({onRegister,onBack}){
  const [schoolName,setSchoolName]=useState("");
  const [adminName,setAdminName]=useState("");
  const [email,setEmail]=useState("");
  const [pw,setPw]=useState("");
  const [pw2,setPw2]=useState("");
  const [err,setErr]=useState("");
  const [loading,setLoading]=useState(false);

  const submit=async()=>{
    setErr("");
    if(!schoolName.trim()) { setErr("School name is required"); return; }
    if(!adminName.trim())  { setErr("Your name is required"); return; }
    if(!email.trim()||!email.includes("@")) { setErr("Valid email address required"); return; }
    if(pw.length<8)        { setErr("Password must be at least 8 characters"); return; }
    if(pw!==pw2)           { setErr("Passwords do not match"); return; }
    setLoading(true);
    await new Promise(r=>setTimeout(r,400));
    onRegister({ schoolName:schoolName.trim(), adminName:adminName.trim(), email:email.trim().toLowerCase(), password:pw });
    setLoading(false);
  };

  return(
    <div style={{height:"100svh",display:"flex",flexDirection:"column",background:"var(--bg)",overflow:"hidden",maxWidth:440,margin:"0 auto"}}>
      {/* Header */}
      <div style={{flex:"0 0 auto",padding:"32px 22px 24px",textAlign:"center",background:"linear-gradient(180deg,#1a3a5c,var(--bg))"}}>
        <div style={{position:"absolute",inset:0,backgroundImage:"radial-gradient(circle at 50% 0%,rgba(0,212,255,0.07),transparent 65%)",pointerEvents:"none"}}/>
        <div style={{fontSize:44,marginBottom:4,lineHeight:1}}>🏏</div>
        <div style={{fontFamily:"var(--font-display)",fontSize:36,letterSpacing:3,lineHeight:1}}>CrickitCoach</div>
        <div style={{fontSize:13,color:"var(--accent)",fontWeight:700,marginTop:8,letterSpacing:1}}>CREATE YOUR SCHOOL</div>
        <div style={{fontSize:11,color:"var(--text3)",marginTop:4}}>Free for the first year · Takes 2 minutes</div>
      </div>

      {/* Form */}
      <div style={{flex:1,overflow:"auto",padding:"0 22px 32px"}}>

        <div style={{marginBottom:12}}>
          <div className="lbl">School / Club Name *</div>
          <input value={schoolName} onChange={e=>setSchoolName(e.target.value)} placeholder="e.g. Lincoln Cricket Academy"/>
        </div>
        <div style={{marginBottom:12}}>
          <div className="lbl">Your Name (School Admin) *</div>
          <input value={adminName} onChange={e=>setAdminName(e.target.value)} placeholder="e.g. John Smith"/>
        </div>
        <div style={{marginBottom:12}}>
          <div className="lbl">Email Address *</div>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com"/>
        </div>
        <div style={{marginBottom:12}}>
          <div className="lbl">Password * (min 8 characters)</div>
          <input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="Choose a strong password"/>
        </div>
        <div style={{marginBottom:18}}>
          <div className="lbl">Confirm Password *</div>
          <input type="password" value={pw2} onChange={e=>setPw2(e.target.value)} placeholder="Repeat password"
            onKeyDown={e=>e.key==="Enter"&&submit()}/>
        </div>

        {err&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"9px 12px",fontSize:12,color:"var(--danger)",marginBottom:14}}>{err}</div>}

        <button className="btn-primary" onClick={submit} disabled={loading} style={{marginBottom:10}}>
          {loading?"Creating your school…":"🏏 Create School Account"}
        </button>
        <button className="btn-secondary" onClick={onBack}>← Back to Sign In</button>

        <div style={{marginTop:18,padding:"12px 14px",background:"var(--surface2)",borderRadius:10,fontSize:11,color:"var(--text3)",lineHeight:1.7}}>
          <strong style={{color:"var(--text2)"}}>What happens next:</strong><br/>
          ① Your school account is created instantly<br/>
          ② You sign in as School Admin<br/>
          ③ Add coaches from your Settings tab<br/>
          ④ Each coach adds their own students
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MANAGE COACHES SCREEN  (School Admin only)
// ═══════════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════════
// MASS TRANSFER PANEL — select multiple students and move to another coach
// ═══════════════════════════════════════════════════════════════════════════════
function MassTransferPanel({students,otherCoaches,onTransfer,showNotif}){
  const [selected,setSelected]=useState([]);
  const [toCoachId,setToCoachId]=useState(otherCoaches[0]?.id||"");
  const [confirmed,setConfirmed]=useState(false);

  const sorted=[...students].sort((a,b)=>(a.age||99)-(b.age||99));
  const allSelected=selected.length===sorted.length;

  const toggleOne=id=>setSelected(s=>s.includes(id)?s.filter(x=>x!==id):[...s,id]);
  const toggleAll=()=>setSelected(allSelected?[]:sorted.map(s=>s.id));

  const handleTransfer=()=>{
    if(!selected.length){showNotif("Select at least one player","error");return;}
    if(!toCoachId){showNotif("Select a destination coach","error");return;}
    onTransfer(selected,toCoachId);
    setSelected([]);
    setConfirmed(false);
  };

  return(
    <div style={{marginBottom:16,padding:"14px",background:"#f0f4ff",
      border:"2px solid var(--accent)",borderRadius:12}}>
      <div style={{fontSize:12,fontWeight:800,color:"var(--accent)",
        textTransform:"uppercase",letterSpacing:.5,marginBottom:10}}>
        🔄 Transfer Players to Another Coach
      </div>

      {/* Destination coach selector */}
      <div style={{marginBottom:10}}>
        <div className="lbl" style={{marginBottom:4}}>Transfer to</div>
        <select value={toCoachId} onChange={e=>setToCoachId(e.target.value)}
          style={{border:"1.5px solid var(--accent)"}}>
          {otherCoaches.map(c=>(
            <option key={c.id} value={c.id}>{c.name}{c.team?` — ${c.team}`:""}</option>
          ))}
        </select>
      </div>

      {/* Select all / none row */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
        marginBottom:6}}>
        <div className="lbl" style={{marginBottom:0}}>
          Select Players ({selected.length}/{sorted.length} selected)
        </div>
        <button onClick={toggleAll}
          style={{fontSize:11,fontWeight:700,color:"var(--accent)",
            background:"transparent",border:"none",cursor:"pointer",padding:"2px 4px"}}>
          {allSelected?"Deselect All":"Select All"}
        </button>
      </div>

      {/* Student checklist — sorted A→Z */}
      <div style={{maxHeight:220,overflowY:"auto",borderRadius:8,
        border:"1px solid var(--border)",background:"var(--surface,#fff)",marginBottom:10}}>
        {sorted.map((s,i)=>{
          const checked=selected.includes(s.id);
          return(
            <div key={s.id}
              onClick={()=>toggleOne(s.id)}
              style={{display:"flex",alignItems:"center",gap:10,
                padding:"9px 12px",cursor:"pointer",
                borderBottom:i<sorted.length-1?"1px solid var(--border-lt,#f0f0f0)":"none",
                background:checked?"#eff6ff":"transparent"}}>
              {/* Checkbox */}
              <div style={{width:18,height:18,borderRadius:4,flexShrink:0,
                background:checked?"var(--accent)":"transparent",
                border:`2px solid ${checked?"var(--accent)":"var(--border)"}`,
                display:"flex",alignItems:"center",justifyContent:"center"}}>
                {checked&&<span style={{color:"#fff",fontSize:11,fontWeight:900,lineHeight:1}}>✓</span>}
              </div>
              {/* Avatar */}
              <div className="avatar" style={{width:30,height:30,flexShrink:0,
                background:"linear-gradient(135deg,var(--accent),#005577)",
                fontSize:11,color:"#000",fontFamily:"var(--font-display)"}}>
                {s.avatar}
              </div>
              {/* Name + class */}
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:700,fontSize:13,
                  color:checked?"var(--accent)":"var(--text)"}}>{s.name}</div>
                <div style={{fontSize:10,color:"var(--text3)"}}>{s.cricketClass}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirm + transfer button */}
      {selected.length>0&&(
        !confirmed
        ?<button
            onClick={()=>setConfirmed(true)}
            style={{width:"100%",padding:"11px",borderRadius:8,fontWeight:700,
              fontSize:13,background:"var(--accent)",color:"#fff",border:"none",cursor:"pointer"}}>
            Transfer {selected.length} Player{selected.length!==1?"s":""}  →
          </button>
        :<div>
            <div style={{fontSize:12,color:"var(--text2)",marginBottom:8,padding:"8px 10px",
              background:"#fef9e7",borderRadius:8,border:"1px solid var(--gold)",lineHeight:1.5}}>
              ⚠️ Transfer <strong>{selected.length}</strong> player{selected.length!==1?"s":""} to{" "}
              <strong>{otherCoaches.find(c=>c.id===toCoachId)?.name||"selected coach"}</strong>?
              This cannot be undone from here.
            </div>
            <div style={{display:"flex",gap:8}}>
              <button onClick={()=>setConfirmed(false)}
                style={{flex:1,padding:"10px",borderRadius:8,fontWeight:700,fontSize:13,
                  background:"transparent",border:"1.5px solid var(--border)",cursor:"pointer"}}>
                Cancel
              </button>
              <button onClick={handleTransfer}
                style={{flex:2,padding:"10px",borderRadius:8,fontWeight:700,fontSize:13,
                  background:"var(--accent)",color:"#fff",border:"none",cursor:"pointer"}}>
                ✅ Confirm Transfer
              </button>
            </div>
          </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT COACH MODAL — admin can edit name, squad name, reset password
// Also shows which students are assigned and allows reassignment
// ═══════════════════════════════════════════════════════════════════════════════
function EditCoachModal({coach,db,onSave,onClose,onTransfer,showNotif}){
  const [name,setName]=useState(coach.name||"");
  const [team,setTeam]=useState(coach.team||"");
  const [newPw,setNewPw]=useState("");
  const [changePw,setChangePw]=useState(false);
  const [err,setErr]=useState("");

  // Students currently assigned to this coach
  const myStudents=db.students.filter(s=>s.coachId===coach.id&&s.status!=="inactive");
  // Other active coaches in the org (for reassignment)
  const otherCoaches=db.coaches.filter(c=>c.orgId===coach.orgId&&c.id!==coach.id&&c.status!=="inactive"&&c.role!=="admin");

  const submit=()=>{
    setErr("");
    if(!name.trim()){setErr("Name is required");return;}
    if(changePw&&newPw.length>0&&newPw.length<6){setErr("Password must be at least 6 characters");return;}
    onSave(coach.id, name.trim(), team.trim(), changePw&&newPw?newPw:null);
  };

  return(
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="sheet" style={{maxHeight:"92%",overflowY:"auto"}}>
        <div className="handle"/>
        <div style={{fontFamily:"var(--font-display)",fontSize:22,marginBottom:4}}>✏️ Edit Coach</div>
        <div style={{fontSize:12,color:"var(--text3)",marginBottom:16}}>Update coach details for {coach.name}</div>

        {/* Name */}
        <div style={{marginBottom:12}}>
          <div className="lbl">Full Name *</div>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="Coach full name"/>
        </div>

        {/* Squad / Team name */}
        <div style={{marginBottom:16}}>
          <div className="lbl">Squad / Team Name</div>
          <input value={team} onChange={e=>setTeam(e.target.value)} placeholder="e.g. Under-14 Dragons"/>
          <div style={{fontSize:11,color:"var(--text3)",marginTop:4}}>This name appears on the coach's home screen</div>
        </div>

        {/* Password reset */}
        <div style={{marginBottom:16,padding:"12px 14px",background:"var(--surface2)",borderRadius:10,border:"1px solid var(--border)"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:changePw?10:0}}>
            <div style={{flex:1,fontSize:13,fontWeight:600}}>Reset Password</div>
            <button className="btn-ghost btn-sm" onClick={()=>{setChangePw(!changePw);setNewPw("");}}
              style={{color:changePw?"var(--danger)":"var(--accent)",borderColor:changePw?"var(--danger)":"var(--accent)"}}>
              {changePw?"Cancel":"Change"}
            </button>
          </div>
          {changePw&&(
            <input type="password" value={newPw} onChange={e=>setNewPw(e.target.value)}
              placeholder="New password (min 6 characters)"/>
          )}
        </div>

        {/* ── Mass Transfer ─────────────────────────────────────────────────── */}
        {myStudents.length>0&&otherCoaches.length>0&&(
          <MassTransferPanel
            students={myStudents}
            otherCoaches={otherCoaches}
            onTransfer={(sids,toCoachId)=>{onTransfer&&onTransfer(sids,toCoachId);}}
            showNotif={showNotif}
          />
        )}
        {myStudents.length>0&&otherCoaches.length===0&&(
          <div style={{marginBottom:14,padding:"12px 14px",background:"var(--surface2)",
            borderRadius:10,border:"1px solid var(--border)"}}>
            <div className="lbl" style={{marginBottom:4}}>Students Assigned ({myStudents.length})</div>
            <div style={{fontSize:11,color:"var(--text3)"}}>
              Add another coach to enable student transfers.
            </div>
          </div>
        )}

        {err&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"9px 12px",fontSize:12,color:"var(--danger)",marginBottom:12}}>{err}</div>}

        <div style={{display:"flex",gap:8,marginTop:4}}>
          <button className="btn-secondary" style={{flex:1}} onClick={onClose}>Cancel</button>
          <button className="btn-primary" style={{flex:2}} onClick={submit}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MANAGE COACHES SCREEN  (School Admin only) — with full edit capability
// ═══════════════════════════════════════════════════════════════════════════════
function ManageCoachesScreen({db,user,onAddCoach,onDeactivateCoach,onReactivateCoach,onUpdCoach,onReassignStudents,onBack,showNotif}){
  const [showAdd,setShowAdd]=useState(false);
  const [editingCoach,setEditingCoach]=useState(null);
  const orgCoaches=db.coaches.filter(c=>c.orgId===user.orgId);
  const active=orgCoaches.filter(c=>c.status!=="inactive");
  const inactive=orgCoaches.filter(c=>c.status==="inactive");

  return(
    <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}} className="slide-in">
      <div style={{padding:"14px 16px 0",flexShrink:0}}>
        <button onClick={onBack} style={{background:"transparent",color:"var(--accent)",fontSize:13,fontWeight:700,padding:0,marginBottom:12}}>← Settings</button>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
          <div>
            <div style={{fontFamily:"var(--font-display)",fontSize:26,letterSpacing:1}}>Manage Coaches</div>
            <div style={{fontSize:11,color:"var(--text3)",marginTop:2}}>{active.length} active coach{active.length!==1?"es":""} · {user.schoolName||"Your School"}</div>
          </div>
          <button className="btn-primary btn-sm" style={{minWidth:90}} onClick={()=>setShowAdd(true)}>+ Add Coach</button>
        </div>
        <div className="divider" style={{margin:"10px 0 0"}}/>
      </div>

      <div className="screen" style={{padding:"10px 14px"}}>

        {/* Active coaches */}
        {active.length===0&&(
          <div className="empty"><div className="empty-icon">👨‍🏫</div><div>No coaches yet</div><div style={{fontSize:12,marginTop:6}}>Add your first coach to get started</div></div>
        )}
        {active.map(c=>{
          const squadSize=db.students.filter(s=>s.coachId===c.id&&s.status!=="inactive").length;
          return(
            <div key={c.id} className="card" style={{marginBottom:8,cursor:"pointer"}}
              onClick={()=>c.role!=="admin"&&setEditingCoach(c)}>
              <div style={{display:"flex",alignItems:"center",gap:12}}>
                <div className="avatar" style={{width:42,height:42,
                  background:`linear-gradient(135deg,${c.role==="admin"?"#ffd700,#a07d00":"var(--accent),#005577"})`,
                  fontSize:14,color:"#000",fontFamily:"var(--font-display)"}}>{c.avatar}</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:700,fontSize:13}}>{c.name}</div>
                  <div style={{fontSize:11,color:"var(--text3)"}}>{c.email}</div>
                  {c.team&&<div style={{fontSize:11,color:"var(--text2)",marginTop:2}}>🏏 {c.team}</div>}
                  <div style={{fontSize:10,color:"var(--text3)",marginTop:2}}>{squadSize} student{squadSize!==1?"s":""} assigned</div>
                </div>
                <div style={{display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                  {c.role==="admin"
                    ?<span className="badge" style={{background:"#fef9e7",color:"var(--gold)",border:"1.5px solid var(--gold)"}}>👑 Admin</span>
                    :<span className="badge" style={{background:"#e0f0fa",color:"var(--accent)",border:"1.5px solid var(--accent)"}}>🎓 Coach</span>
                  }
                  {c.role!=="admin"&&(
                    <div style={{display:"flex",gap:4}}>
                      <button className="btn-ghost btn-sm" style={{fontSize:10,color:"var(--accent)",borderColor:"var(--accent)40"}}
                        onClick={e=>{e.stopPropagation();setEditingCoach(c);}}>✏️ Edit</button>
                      {c.id!==user.id&&(
                        <button className="btn-ghost btn-sm" style={{fontSize:10,color:"var(--danger)",borderColor:"var(--danger)40"}}
                          onClick={e=>{e.stopPropagation();onDeactivateCoach(c.id);showNotif(`${c.name} deactivated`);}}>Deactivate</button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Inactive coaches */}
        {inactive.length>0&&(
          <>
            <div style={{fontSize:10,color:"var(--text3)",textTransform:"uppercase",letterSpacing:1,margin:"16px 0 8px"}}>Inactive Coaches</div>
            {inactive.map(c=>(
              <div key={c.id} className="card" style={{opacity:0.55,marginBottom:8}}>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <div className="avatar" style={{width:42,height:42,background:"linear-gradient(135deg,#444,#222)",fontSize:14,color:"#666",fontFamily:"var(--font-display)"}}>{c.avatar}</div>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:700,fontSize:13}}>{c.name}</div>
                    <div style={{fontSize:11,color:"var(--text3)"}}>{c.email}</div>
                  </div>
                  <button className="btn-secondary btn-sm" style={{borderColor:"var(--accent3)40",color:"var(--accent3)"}}
                    onClick={()=>{onReactivateCoach(c.id);showNotif(`${c.name} reactivated ✅`);}}>Reactivate</button>
                </div>
              </div>
            ))}
          </>
        )}

        <div style={{marginTop:14,padding:"10px 12px",background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:10,fontSize:11,color:"var(--text3)",lineHeight:1.7}}>
          ℹ️ Tap any coach card to edit their name, squad name, or reset their password. To move students between coaches, open the student profile from the School tab and use the Reassign option.
        </div>
      </div>

      {/* Add Coach Modal */}
      {showAdd&&(
        <AddCoachModal
          orgId={user.orgId}
          schoolName={user.schoolName}
          onClose={()=>setShowAdd(false)}
          onSave={data=>{ onAddCoach(data); setShowAdd(false); showNotif(`${data.name} added as coach ✅`); }}
        />
      )}

      {/* Edit Coach Modal */}
      {editingCoach&&(
        <EditCoachModal
          coach={editingCoach}
          db={db}
          onClose={()=>setEditingCoach(null)}
          onSave={(cid,name,team,pw)=>{
            onUpdCoach(cid,name,team,pw);
            setEditingCoach(null);
          }}
          onTransfer={(sids,toCoachId)=>{
            sids.forEach(sid=>onReassignStudents(sid,undefined,undefined,undefined,toCoachId));
            const toCoach=db.coaches.find(c=>c.id===toCoachId);
            showNotif(`${sids.length} player${sids.length!==1?"s":""} transferred to ${toCoach?.name||"new coach"} ✅`);
          }}
          showNotif={showNotif}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ADD COACH MODAL  (admin only)
// ═══════════════════════════════════════════════════════════════════════════════
function AddCoachModal({orgId,schoolName,onSave,onClose}){
  const [name,setName]=useState("");
  const [email,setEmail]=useState("");
  const [pw,setPw]=useState("");
  const [team,setTeam]=useState("");
  const [err,setErr]=useState("");

  const submit=()=>{
    setErr("");
    if(!name.trim())                         { setErr("Name is required"); return; }
    if(!email.trim()||!email.includes("@")) { setErr("Valid email required"); return; }
    if(pw.length<6)                          { setErr("Password must be 6+ characters"); return; }
    onSave({ name:name.trim(), email:email.trim().toLowerCase(), password:pw, team:team.trim(), orgId, role:"coach" });
  };

  return(
    <div className="overlay" onClick={e=>{if(e.target===e.currentTarget)onClose();}}>
      <div className="sheet" style={{maxHeight:"88%",overflowY:"auto"}}>
        <div className="handle"/>
        <div style={{fontFamily:"var(--font-display)",fontSize:24,color:"var(--accent)",marginBottom:4}}>➕ Add Coach</div>
        <div style={{fontSize:12,color:"var(--text3)",marginBottom:18}}>
          The coach will log in with their email and password. They can then add students to their squad.
        </div>

        <div style={{marginBottom:12}}><div className="lbl">Full Name *</div>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Sarah Miles"/></div>

        <div style={{marginBottom:12}}><div className="lbl">Email Address *</div>
          <input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="coach@school.edu"/></div>

        <div style={{marginBottom:12}}><div className="lbl">Password * (min 6 characters)</div>
          <input type="password" value={pw} onChange={e=>setPw(e.target.value)} placeholder="Temporary password — coach can change later"/></div>

        <div style={{marginBottom:18}}><div className="lbl">Team / Squad Name (optional)</div>
          <input value={team} onChange={e=>setTeam(e.target.value)} placeholder="e.g. Under-14 Dragons"/></div>

        {err&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"9px 12px",fontSize:12,color:"var(--danger)",marginBottom:14}}>{err}</div>}

        <div style={{display:"flex",gap:8}}>
          <button className="btn-secondary" style={{flex:1}} onClick={onClose}>Cancel</button>
          <button className="btn-primary" style={{flex:2}} onClick={submit}>✅ Add Coach</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LOGIN
// ═══════════════════════════════════════════════════════════════════════════════
// ── Opens the User Guide in a new window without any external URL ────────────
const GUIDE_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1"/>
<title>CrickitCoach — Getting Started Guide</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
:root{--navy:#0B1F3A;--blue:#0369A1;--blue-lt:#EFF6FF;--green:#15803D;--green-lt:#F0FDF4;--amber:#D97706;--amber-lt:#FFFBEB;--border:#E2E8F0;--bg:#F8FAFC;--white:#fff;--text:#0F172A;--text2:#334155;--text3:#64748B;--text4:#94A3B8}
html{scroll-behavior:smooth}
body{background:var(--bg);color:var(--text);font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;font-size:16px;line-height:1.6}
a{color:var(--blue)}
.topbar{background:var(--navy);padding:0 20px;height:56px;display:flex;align-items:center;justify-content:space-between;position:-webkit-sticky;position:sticky;top:0;z-index:100}
.tb-logo{font-size:18px;font-weight:800;color:#fff;letter-spacing:-.3px}
.tb-logo em{color:#F59E0B;font-style:normal}
.tb-link{font-size:13px;font-weight:600;color:rgba(255,255,255,.7);background:rgba(255,255,255,.1);border:1px solid rgba(255,255,255,.2);padding:6px 14px;border-radius:20px;text-decoration:none;white-space:nowrap}
.tb-link:hover{color:#fff;background:rgba(255,255,255,.15)}
.hero{background:linear-gradient(135deg,#0B1F3A 0%,#1a4a7a 100%);padding:48px 20px 40px;text-align:center}
.hero-icon{font-size:52px;margin-bottom:16px}
.hero h1{font-size:clamp(26px,6vw,38px);font-weight:800;color:#fff;margin-bottom:10px;line-height:1.2}
.hero h1 span{color:#F59E0B}
.hero-sub{font-size:clamp(15px,3vw,17px);color:rgba(255,255,255,.7);max-width:480px;margin:0 auto 28px;line-height:1.6}
.hero-who{display:inline-flex;gap:12px;flex-wrap:wrap;justify-content:center}
.who-pill{background:rgba(255,255,255,.12);color:rgba(255,255,255,.9);border:1px solid rgba(255,255,255,.2);padding:8px 18px;border-radius:99px;font-size:14px;font-weight:600}
.tabs{background:#fff;border-bottom:2px solid var(--border);position:-webkit-sticky;position:sticky;top:56px;z-index:90;display:flex;overflow-x:auto;scrollbar-width:none;-webkit-overflow-scrolling:touch}
.tabs::-webkit-scrollbar{display:none}
.tab-radio{display:none}
.tab{flex:1;min-width:110px;padding:14px 8px;border:none;background:transparent;font-size:14px;font-weight:700;color:var(--text3);cursor:pointer;border-bottom:3px solid transparent;transition:all .2s;white-space:nowrap;text-align:center;touch-action:manipulation;-webkit-tap-highlight-color:transparent;min-height:48px;display:block;user-select:none}
.tab:hover{color:var(--text)}
#tab-coach:checked ~ .tabs label[for="tab-coach"],
#tab-admin:checked ~ .tabs label[for="tab-admin"],
#tab-player:checked ~ .tabs label[for="tab-player"]{color:var(--blue);border-bottom-color:var(--blue)}
/* Fallback: all labels default to inactive look, checked one gets active */
.tabs label{border-bottom:3px solid transparent}
.tab.on{color:var(--blue);border-bottom-color:var(--blue)}
.tab:hover:not(.on){color:var(--text)}
.page{display:none;padding-bottom:60px}
/* Radio-driven page visibility */
.page{display:none}
#tab-coach:checked ~ #pg-coach,
#tab-admin:checked ~ #pg-admin,
#tab-player:checked ~ #pg-player{display:block !important}
.section{max-width:800px;margin:0 auto;padding:40px 20px 0}
.section+.section{padding-top:32px}
.phone-row{display:flex;gap:24px;align-items:flex-start;background:#fff;border:1.5px solid var(--border);border-radius:16px;padding:24px;margin-bottom:20px;box-shadow:0 2px 8px rgba(0,0,0,.05)}
.phone-row.flip{flex-direction:row-reverse}
.ph{display:flex;flex-direction:column;align-items:center;flex-shrink:0}
.ph-lbl{font-size:10px;font-weight:700;letter-spacing:.5px;text-transform:uppercase;color:var(--text4);text-align:center;margin-top:8px}
.pr-text{flex:1}
.pr-text h3{font-size:16px;font-weight:800;color:var(--navy);margin-bottom:8px}
.pr-text p{font-size:14px;color:var(--text2);line-height:1.65;margin-bottom:8px}
.pr-text p:last-child{margin-bottom:0}
.pr-text strong{color:var(--navy)}
.step-card{background:#fff;border:1.5px solid var(--border);border-radius:16px;padding:24px;margin-bottom:20px;display:flex;gap:20px;align-items:flex-start;box-shadow:0 2px 8px rgba(0,0,0,.05)}
.step-num{width:36px;height:36px;background:var(--blue);color:#fff;border-radius:50%;font-size:16px;font-weight:900;display:flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:2px}
.step-body h3{font-size:16px;font-weight:800;color:var(--navy);margin-bottom:6px}
.step-body p{font-size:14px;color:var(--text2);line-height:1.65;margin-bottom:0}
.step-body p+p{margin-top:8px}
.step-body strong{color:var(--navy)}
.tip{background:var(--green-lt);border:1.5px solid #86EFAC;border-radius:10px;padding:12px 16px;font-size:13px;color:#166534;margin-top:12px;line-height:1.55}
.tip::before{content:"💡 "}
.note{background:var(--blue-lt);border:1.5px solid #BFDBFE;border-radius:10px;padding:12px 16px;font-size:13px;color:#1E40AF;margin-top:12px;line-height:1.55}
.note::before{content:"ℹ️  "}
.warn{background:var(--amber-lt);border:1.5px solid #FDE68A;border-radius:10px;padding:12px 16px;font-size:13px;color:#92400E;margin-top:12px;line-height:1.55}
.warn::before{content:"⚠️  "}
.sh{font-size:22px;font-weight:800;color:var(--navy);margin-bottom:6px}
.sh-sub{font-size:14px;color:var(--text3);margin-bottom:24px}
.divider{height:1px;background:var(--border);margin:32px 0}
.ql{list-style:none;counter-reset:s;margin:12px 0 0}
.ql li{counter-increment:s;padding:8px 0 8px 36px;position:relative;border-bottom:1px solid var(--border);font-size:14px;color:var(--text2);line-height:1.55}
.ql li:last-child{border-bottom:none}
.ql li::before{content:counter(s);position:absolute;left:0;top:9px;width:22px;height:22px;background:var(--blue);color:#fff;border-radius:50%;font-size:10px;font-weight:800;display:flex;align-items:center;justify-content:center;text-align:center;line-height:22px}
.rt{width:100%;border-collapse:collapse;margin-top:12px;font-size:13px}
.rt th{background:var(--navy);color:#fff;padding:9px 12px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.8px}
.rt td{padding:9px 12px;border-bottom:1px solid var(--border);color:var(--text2)}
.rt tr:last-child td{border-bottom:none}
.rt tr:nth-child(even) td{background:#f8fafc}
.gallery{background:var(--navy);padding:32px 20px;margin-top:40px}
.gallery h3{font-size:11px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:rgba(255,255,255,.35);margin-bottom:20px;text-align:center}
.gal-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;max-width:800px;margin:0 auto}
.gal-cell p{font-size:10px;font-weight:600;letter-spacing:.4px;text-transform:uppercase;color:rgba(255,255,255,.3);text-align:center;margin-top:6px}
footer{background:var(--navy);padding:28px 20px;text-align:center}
footer .fw{font-size:17px;font-weight:800;color:#fff;margin-bottom:6px}
footer .fw em{color:#F59E0B;font-style:normal}
footer a{color:rgba(255,255,255,.65);font-size:13px;display:block;margin-bottom:4px}
footer .copy{font-size:11px;color:rgba(255,255,255,.25);margin-top:16px}
@media(max-width:560px){
  .phone-row,.phone-row.flip{flex-direction:column;gap:16px}
  .ph{align-items:center;width:100%}
  .gal-grid{grid-template-columns:repeat(3,1fr)}
  .tab{min-width:90px;font-size:13px}
}
@media(max-width:380px){.gal-grid{grid-template-columns:repeat(2,1fr)}}
</style>
</head>
<body>

<div class="topbar">
  <div class="tb-logo">Crickit<em>Coach</em></div>
  <a class="tb-link" href="https://www.crickitcoach.com" target="_blank">Open App →</a>
</div>

<div class="hero">
  <div class="hero-icon">🏏</div>
  <h1>Welcome to<br/><span>CrickitCoach</span></h1>
  <p class="hero-sub">Your cricket school's coaching app — track sessions, skills and progress all in one place.</p>
  <div class="hero-who">
    <span class="who-pill">🎓 For Coaches</span>
    <span class="who-pill">🏫 For School Admins</span>
    <span class="who-pill">👨‍👩‍👧 For Players &amp; Parents</span>
  </div>
</div>

<!-- Tab radio inputs — must precede .tabs and .page elements as siblings -->
<input type="radio" class="tab-radio" name="tab" id="tab-coach" checked/>
<input type="radio" class="tab-radio" name="tab" id="tab-admin"/>
<input type="radio" class="tab-radio" name="tab" id="tab-player"/>
<div class="tabs">
  <label for="tab-coach" class="tab">🎓 Coaches</label>
  <label for="tab-admin" class="tab">🏫 School Admin</label>
  <label for="tab-player" class="tab">🏏 Players &amp; Parents</label>
</div>

<!-- COACH -->
<div class="page" id="pg-coach">
<div class="section">
  <div class="sh">Hey Coach! 👋</div>
  <div class="sh-sub">Here's everything you need to get up and running with CrickitCoach.</div>

  <div class="phone-row">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="white"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="white"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Logo area -->
  <rect x="0" y="32" width="280" height="160" fill="#0f2744"/>
  <rect x="0" y="32" width="280" height="160" fill="url(#grad1)"/>
  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f2744"/><stop offset="1" stop-color="#1a4a7a"/></linearGradient></defs>
  <!-- Starburst lines -->
  <line x1="140" y1="112" x2="260" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="237" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="177" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="104" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="44" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="21" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="43" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="103" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="176" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="236" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/>
  <!-- Logo text -->
  <text x="140" y="96" font-family="Arial" font-size="30" font-weight="900" fill="white" text-anchor="middle">Crickit</text>
  <text x="140" y="128" font-family="Arial" font-size="30" font-weight="900" fill="#F59E0B" text-anchor="middle">Coach</text>
  <!-- Cricket bat icon centred -->
  <text x="140" y="162" font-family="Arial" font-size="26" text-anchor="middle">🏏</text>

  <!-- Toggle -->
  <rect x="14" y="202" width="252" height="38" rx="10" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <rect x="16" y="204" width="124" height="34" rx="8" fill="#0369a1"/>
  <text x="78" y="226" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">🎓 Coach / Admin</text>
  <text x="202" y="226" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">🏏 Player</text>

  <!-- Email field -->
  <text x="14" y="258" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">📧 EMAIL ADDRESS</text>
  <rect x="14" y="264" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="287" font-family="Arial" font-size="12" fill="#94a3b8">coach@academy.com</text>

  <!-- Password field -->
  <text x="14" y="318" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">🔒 PASSWORD</text>
  <rect x="14" y="324" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="347" font-family="Arial" font-size="16" fill="#94a3b8" letter-spacing="4">••••••••</text>

  <!-- Forgot -->
  <text x="266" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1" text-anchor="end">Forgot password?</text>

  <!-- Sign In button -->
  <rect x="14" y="388" width="252" height="42" rx="10" fill="#0369a1"/>
  <text x="140" y="414" font-family="Arial" font-size="13" font-weight="700" fill="white" text-anchor="middle">Sign In as Coach / Admin</text>

  <!-- User guide link -->
  <text x="140" y="446" font-family="Arial" font-size="11" font-weight="600" fill="#0369a1" text-anchor="middle">📖 User Guide — How it works</text>

  <!-- Register -->
  <line x1="14" y1="460" x2="266" y2="460" stroke="#e2e8f0" stroke-width="1"/>
  <text x="140" y="478" font-family="Arial" font-size="11" fill="#475569" text-anchor="middle">New to CrickitCoach?</text>
  <rect x="14" y="486" width="252" height="36" rx="8" fill="transparent" stroke="#0369a1" stroke-width="1.5"/>
  <text x="140" y="509" font-family="Arial" font-size="12" font-weight="700" fill="#0369a1" text-anchor="middle">🏫 Create New School Account</text>

  <text x="140" y="540" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="middle">support@crickitcoach.com · © 2026 ArcGain LLC</text>
</svg><div class="ph-lbl">Login Screen</div></div>
    <div class="pr-text">
      <h3>Signing In</h3>
      <p>Go to <strong>crickitcoach.com</strong> on your phone. Select <strong>Coach / Admin</strong> and enter your email and password.</p>
      <p>Your school admin will have sent you your login details.</p>
      <div class="tip">Add to your iPhone home screen: Share → Add to Home Screen. Opens full screen like a native app.</div>
    </div>
  </div>

  <div class="phone-row flip">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744"></text>
  <!-- School name + greeting -->
  <text x="14" y="54" font-family="Arial" font-size="9" font-weight="700" fill="#0369a1" letter-spacing="1">🏫 DEMO CRICKET ACADEMY</text>
  <text x="14" y="66" font-family="Arial" font-size="9" font-weight="700" fill="#475569" letter-spacing="2">GOOD SESSION,</text>
  <text x="14" y="86" font-family="Arial" font-size="22" font-weight="900" fill="#0f2744">ALEX JOHNSON</text>
  <text x="14" y="100" font-family="Arial" font-size="11" fill="#475569">Demo Cricket Academy</text>

  <!-- Stats row -->
  <rect x="8" y="108" width="82" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="49" y="132" font-family="Arial" font-size="20" font-weight="900" fill="#0369a1" text-anchor="middle">18</text><text x="49" y="148" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">PLAYERS</text>
  <rect x="99" y="108" width="82" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="140" y="132" font-family="Arial" font-size="20" font-weight="900" fill="#15803d" text-anchor="middle">3.4</text><text x="140" y="148" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">AVG SCORE</text>
  <rect x="190" y="108" width="82" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="231" y="132" font-family="Arial" font-size="20" font-weight="900" fill="#d97706" text-anchor="middle">42</text><text x="231" y="148" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">ASSESSMENTS</text>

  <!-- Classes heading -->
  <text x="14" y="182" font-family="Arial" font-size="14" font-weight="900" fill="#0f2744">CLASSES</text>
  <text x="266" y="182" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="end">Tap a class to view players</text>

  <!-- Class tiles -->
  <rect x="8" y="188" width="126" height="60" rx="12" fill="white" stroke="#0284c740" stroke-width="1.5"/> <text x="20" y="207" font-family="Arial" font-size="8" font-weight="700" fill="#0284c7">🌱 INTRODUCTORY</text><text x="124" y="225" font-family="Arial" font-size="24" font-weight="900" fill="#0284c7" text-anchor="end">6</text><text x="20" y="240" font-family="Arial" font-size="9" fill="#475569">6 players</text><text x="120" y="240" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="end">View →</text>
  <rect x="142" y="188" width="130" height="60" rx="12" fill="white" stroke="#15803d40" stroke-width="1.5"/> <text x="154" y="207" font-family="Arial" font-size="8" font-weight="700" fill="#15803d">⚡ FOUNDATIONAL</text><text x="262" y="225" font-family="Arial" font-size="24" font-weight="900" fill="#15803d" text-anchor="end">8</text><text x="154" y="240" font-family="Arial" font-size="9" fill="#475569">8 players</text><text x="258" y="240" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="end">View →</text>
  <rect x="8" y="254" width="126" height="60" rx="12" fill="white" stroke="#d9770640" stroke-width="1.5"/> <text x="20" y="273" font-family="Arial" font-size="8" font-weight="700" fill="#d97706">🔥 INTERMEDIATE</text><text x="124" y="291" font-family="Arial" font-size="24" font-weight="900" fill="#d97706" text-anchor="end">3</text><text x="20" y="306" font-family="Arial" font-size="9" fill="#475569">3 players</text><text x="120" y="306" font-family="Arial" font-size="9" font-weight="700" fill="#d97706" text-anchor="end">View →</text>
  <rect x="142" y="254" width="130" height="60" rx="12" fill="white" stroke="#dc262640" stroke-width="1.5"/> <text x="154" y="273" font-family="Arial" font-size="8" font-weight="700" fill="#dc2626">🏆 ADVANCED</text><text x="262" y="291" font-family="Arial" font-size="24" font-weight="900" fill="#dc2626" text-anchor="end">1</text><text x="154" y="306" font-family="Arial" font-size="9" fill="#475569">1 player</text><text x="258" y="306" font-family="Arial" font-size="9" font-weight="700" fill="#dc2626" text-anchor="end">View →</text>

  <!-- Recent Players -->
  <text x="14" y="336" font-family="Arial" font-size="14" font-weight="900" fill="#0f2744">RECENT PLAYERS</text>
  <!-- Player rows -->
  <rect x="8" y="342" width="264" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <circle cx="38" cy="368" r="18" fill="#15803d"/><text x="38" y="372" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">SJ</text> <text x="68" y="362" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Sam Johnson</text><rect x="68" y="368" width="88.96" height="17" rx="8" fill="#15803d22"/><text x="112.47999999999999" y="378" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="middle">⚡ Foundational</text><rect x="164.95999999999998" y="368" width="62.98" height="17" rx="8" fill="transparent" stroke="#0284c7" stroke-width="1.2"/><text x="196.45" y="378" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="middle">Batter-RH</text><rect x="8" y="400" width="264" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <circle cx="38" cy="426" r="18" fill="#0284c7"/><text x="38" y="430" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">PT</text> <text x="68" y="420" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Priya Thakur</text><rect x="68" y="426" width="88.96" height="17" rx="8" fill="#0284c722"/><text x="112.47999999999999" y="436" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="middle">🌱 Introductory</text><rect x="164.95999999999998" y="426" width="83.86" height="17" rx="8" fill="transparent" stroke="#f97316" stroke-width="1.2"/><text x="206.89" y="436" font-family="Arial" font-size="9" font-weight="700" fill="#f97316" text-anchor="middle">Bowler-PaceRH</text><rect x="8" y="458" width="264" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <circle cx="38" cy="484" r="18" fill="#15803d"/><text x="38" y="488" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">MC</text> <text x="68" y="478" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Marcus Chen</text><rect x="68" y="484" width="88.96" height="17" rx="8" fill="#15803d22"/><text x="112.47999999999999" y="494" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="middle">⚡ Foundational</text><rect x="164.95999999999998" y="484" width="68.19999999999999" height="17" rx="8" fill="transparent" stroke="#22c55e" stroke-width="1.2"/><text x="199.05999999999997" y="494" font-family="Arial" font-size="9" font-weight="700" fill="#22c55e" text-anchor="middle">AllRounder</text>
  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">HOME</text><rect x="14" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">ASSESS</text><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><div class="ph-lbl">Your Dashboard</div></div>
    <div class="pr-text">
      <h3>Your Dashboard</h3>
      <p>After signing in you'll see your squad dashboard — total players, average score, and your class tiles. Tap any class to see the players in that group.</p>
    </div>
  </div>

  <div class="divider"></div>
  <div class="sh" style="font-size:18px">Adding Your Players</div>
</div>

<div class="section">
  <div class="phone-row">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Back link -->
  <text x="14" y="50" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1">← Squad</text>
  <text x="14" y="72" font-family="Arial" font-size="20" font-weight="900" fill="#0369a1">➕ Add New Player</text>
  <text x="14" y="88" font-family="Arial" font-size="11" fill="#475569">Player can log in with their email and password</text>
  <line x1="0" y1="96" x2="280" y2="96" stroke="#e2e8f0" stroke-width="1"/>

  <!-- Fields -->
  <text x="14" y="116" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">FULL NAME *</text>
  <rect x="14" y="120" width="252" height="34" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="141" font-family="Arial" font-size="12" fill="#94a3b8">e.g. Sam Williams</text>

  <text x="14" y="166" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">EMAIL ADDRESS *</text>
  <rect x="14" y="170" width="252" height="34" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="191" font-family="Arial" font-size="12" fill="#94a3b8">player@school.edu</text>

  <text x="14" y="216" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">PASSWORD * (MIN 6 CHARACTERS)</text>
  <rect x="14" y="220" width="252" height="34" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="241" font-family="Arial" font-size="14" fill="#94a3b8" letter-spacing="3">••••••••</text>

  <text x="14" y="266" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">ASSIGN TO COACH *</text>
  <rect x="14" y="270" width="252" height="34" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="291" font-family="Arial" font-size="12" fill="#0a0f1a">Alex Johnson — Head Coach</text>
  <text x="256" y="291" font-family="Arial" font-size="14" fill="#475569" text-anchor="middle">⌄</text>

  <text x="14" y="316" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">CRICKET CLASS LEVEL</text>
  <rect x="14" y="320" width="123" height="32" rx="8" fill="#0284c7"/>
  <text x="75" y="340" font-family="Arial" font-size="11" font-weight="700" fill="white" text-anchor="middle">🌱 Introductory</text>
  <rect x="141" y="320" width="123" height="32" rx="8" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="202" y="340" font-family="Arial" font-size="11" font-weight="700" fill="#475569" text-anchor="middle">⚡ Foundational</text>
  <rect x="14" y="358" width="123" height="32" rx="8" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="75" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#475569" text-anchor="middle">🔥 Intermediate</text>
  <rect x="141" y="358" width="123" height="32" rx="8" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="202" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#475569" text-anchor="middle">🏆 Advanced</text>

  <!-- Buttons -->
  <rect x="14" y="408" width="82" height="38" rx="8" fill="white" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="55" y="432" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">Cancel</text>
  <rect x="100" y="408" width="164" height="38" rx="8" fill="#0369a1"/>
  <text x="182" y="432" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">✅ Add Player</text>

  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">SQUAD</text><rect x="70" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">ASSESS</text><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><div class="ph-lbl">Add New Player</div></div>
    <div class="pr-text">
      <h3>Adding a Player</h3>
      <p>Tap the <strong>Squad tab</strong> then <strong>+ Add Player</strong>. Fill in their name, email and a temporary password.</p>
      <ol class="ql">
        <li>Squad tab → <strong>+ Add Player</strong></li>
        <li>Name, email &amp; temporary password</li>
        <li>Select their Cricket Class Level</li>
        <li>Add Specialisation Tags if you know their role</li>
        <li>Tap <strong>✅ Add Player</strong></li>
      </ol>
      <div class="tip">Give the player their email and password — they can log in straight away to see their record.</div>
    </div>
  </div>

  <div class="divider"></div>
  <div class="sh" style="font-size:18px">Logging Sessions</div>
</div>

<div class="section">
  <div class="phone-row flip">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744">Input Tools</text>
  <!-- Tool icons row -->
  <circle cx="40" cy="82" r="22" fill="#0369a1" stroke="none" stroke-width="1.5"/><text x="40" y="79" font-family="Arial" font-size="14" text-anchor="middle">📋</text><text x="40" y="93" font-family="Arial" font-size="7" font-weight="700" fill="white" text-anchor="middle">Session</text><circle cx="92" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="92" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="92" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Group</text><circle cx="144" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="144" y="79" font-family="Arial" font-size="14" text-anchor="middle">📅</text><text x="144" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Sess. Log</text><circle cx="196" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="196" y="79" font-family="Arial" font-size="14" text-anchor="middle">🎯</text><text x="196" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Goal</text><circle cx="248" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="248" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="248" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Squad</text>

  <line x1="0" y1="112" x2="280" y2="112" stroke="#e2e8f0" stroke-width="1"/>

  <!-- Session form -->
  <rect x="8" y="118" width="264" height="260" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="140" font-family="Arial" font-size="11" font-weight="900" fill="#0369a1">📋 LOG SESSION</text>

  <text x="22" y="162" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">PLAYER</text>
  <rect x="22" y="166" width="236" height="30" rx="6" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.2"/>
  <text x="32" y="185" font-family="Arial" font-size="11" fill="#0a0f1a">Sam Johnson — Foundational</text>
  <text x="252" y="185" font-family="Arial" font-size="12" fill="#475569" text-anchor="end">⌄</text>

  <text x="22" y="210" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">TYPE</text>
  <rect x="22" y="214" width="40" height="24" rx="12" fill="#0369a1" stroke="#0369a1" stroke-width="1.2"/><text x="42" y="230" font-family="Arial" font-size="9" font-weight="700" fill="white" text-anchor="middle">Batting</text><rect x="66" y="214" width="40" height="24" rx="12" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="86" y="230" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Bowl</text><rect x="110" y="214" width="40" height="24" rx="12" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="130" y="230" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Field</text><rect x="154" y="214" width="40" height="24" rx="12" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="174" y="230" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Match</text><rect x="198" y="214" width="40" height="24" rx="12" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="218" y="230" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Fit</text>

  <text x="22" y="254" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">DURATION</text>
  <rect x="22" y="258" width="236" height="30" rx="6" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.2"/>
  <text x="32" y="277" font-family="Arial" font-size="11" fill="#0a0f1a">60 min</text>

  <text x="22" y="302" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">NOTES</text>
  <rect x="22" y="306" width="236" height="52" rx="6" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.2"/>
  <text x="32" y="326" font-family="Arial" font-size="11" fill="#94a3b8">What did you work on?</text>

  <rect x="22" y="366" width="236" height="36" rx="8" fill="#0369a1"/>
  <text x="140" y="389" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">📋 Log Session</text>

  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">ASSESS</text><rect x="182" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><div class="ph-lbl">Log a Session</div></div>
    <div class="pr-text">
      <h3>After Every Session</h3>
      <p>Tap the <strong>Assess tab</strong> after training and log what you worked on. Takes less than a minute.</p>
      <ol class="ql">
        <li>Assess tab → <strong>Session</strong></li>
        <li>Pick the player and session type</li>
        <li>Set duration and write your notes</li>
        <li>Tap <strong>Log Session</strong></li>
      </ol>
      <div class="note">Session types: Batting · Bowling · Fielding · Match · Fitness · General</div>
    </div>
  </div>

  <div class="phone-row">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744">Input Tools</text>
  <circle cx="40" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="40" y="79" font-family="Arial" font-size="14" text-anchor="middle">📋</text><text x="40" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Session</text><circle cx="92" cy="82" r="22" fill="#0369a1" stroke="none" stroke-width="1.5"/><text x="92" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="92" y="93" font-family="Arial" font-size="7" font-weight="700" fill="white" text-anchor="middle">Group</text><circle cx="144" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="144" y="79" font-family="Arial" font-size="14" text-anchor="middle">📅</text><text x="144" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Sess Log</text><circle cx="196" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="196" y="79" font-family="Arial" font-size="14" text-anchor="middle">🎯</text><text x="196" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Goal</text><circle cx="248" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="248" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="248" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Squad</text>
  <line x1="0" y1="112" x2="280" y2="112" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="8" y="118" width="264" height="322" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="140" font-family="Arial" font-size="11" font-weight="900" fill="#0369a1">👥 GROUP SESSION</text>
  <text x="22" y="154" font-family="Arial" font-size="9" fill="#475569" style="font-style:italic">Select players, enter shared notes. Same session logged for each.</text>

  <text x="22" y="174" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">SESSION TYPE</text>
  <rect x="22" y="178" width="42" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="43" y="193" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Batting</text><rect x="68" y="178" width="42" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="89" y="193" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Bowl</text><rect x="114" y="178" width="42" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="135" y="193" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Field</text><rect x="160" y="178" width="42" height="22" rx="11" fill="#d97706" stroke="#d97706" stroke-width="1.2"/><text x="181" y="193" font-family="Arial" font-size="9" font-weight="700" fill="white" text-anchor="middle">Match</text>

  <text x="22" y="216" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">SELECT PLAYERS</text>
  <rect x="22" y="220" width="70" height="22" rx="6" fill="#0369a1"/><text x="57" y="235" font-family="Arial" font-size="10" font-weight="700" fill="white" text-anchor="middle">Select All</text>
  <rect x="100" y="220" width="50" height="22" rx="6" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="125" y="235" font-family="Arial" font-size="10" fill="#475569" text-anchor="middle">Clear</text>
  <rect x="22" y="248" width="236" height="32" rx="6" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="28" y="256" width="16" height="16" rx="3" fill="#15803d22" stroke="#15803d" stroke-width="1.5"/>
  <text x="36" y="268" font-family="Arial" font-size="10" font-weight="900" fill="#15803d" text-anchor="middle">✓</text>
  <circle cx="66" cy="264" r="12" fill="#15803d"/><text x="66" y="268" font-family="Arial" font-size="8" font-weight="900" fill="white" text-anchor="middle">SJ</text>
  <text x="84" y="262" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Sam Johnson</text>
  <text x="84" y="275" font-family="Arial" font-size="9" fill="#475569">Foundational</text>
  <rect x="22" y="284" width="236" height="32" rx="6" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="28" y="292" width="16" height="16" rx="3" fill="#0284c722" stroke="#0284c7" stroke-width="1.5"/>
  <text x="36" y="304" font-family="Arial" font-size="10" font-weight="900" fill="#0284c7" text-anchor="middle">✓</text>
  <circle cx="66" cy="300" r="12" fill="#0284c7"/><text x="66" y="304" font-family="Arial" font-size="8" font-weight="900" fill="white" text-anchor="middle">PT</text>
  <text x="84" y="298" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Priya Thakur</text>
  <text x="84" y="311" font-family="Arial" font-size="9" fill="#475569">Introductory</text>
  <rect x="22" y="320" width="236" height="32" rx="6" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="28" y="328" width="16" height="16" rx="3" fill="#15803d22" stroke="#15803d" stroke-width="1.5"/>
  <text x="36" y="340" font-family="Arial" font-size="10" font-weight="900" fill="#15803d" text-anchor="middle">✓</text>
  <circle cx="66" cy="336" r="12" fill="#15803d"/><text x="66" y="340" font-family="Arial" font-size="8" font-weight="900" fill="white" text-anchor="middle">MC</text>
  <text x="84" y="334" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Marcus Chen</text>
  <text x="84" y="347" font-family="Arial" font-size="9" fill="#475569">Foundational</text>
  <rect x="22" y="356" width="236" height="32" rx="6" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="28" y="364" width="16" height="16" rx="3" fill="#d9770622" stroke="#d97706" stroke-width="1.5"/>
  <text x="36" y="376" font-family="Arial" font-size="10" font-weight="900" fill="#d97706" text-anchor="middle">✓</text>
  <circle cx="66" cy="372" r="12" fill="#d97706"/><text x="66" y="376" font-family="Arial" font-size="8" font-weight="900" fill="white" text-anchor="middle">AK</text>
  <text x="84" y="370" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Aisha Khan</text>
  <text x="84" y="383" font-family="Arial" font-size="9" fill="#475569">Intermediate</text>
  <rect x="22" y="392" width="236" height="32" rx="6" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="28" y="400" width="16" height="16" rx="3" fill="#0284c722" stroke="#0284c7" stroke-width="1.5"/>
  <text x="36" y="412" font-family="Arial" font-size="10" font-weight="900" fill="#0284c7" text-anchor="middle">✓</text>
  <circle cx="66" cy="408" r="12" fill="#0284c7"/><text x="66" y="412" font-family="Arial" font-size="8" font-weight="900" fill="white" text-anchor="middle">TW</text>
  <text x="84" y="406" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Tom Williams</text>
  <text x="84" y="419" font-family="Arial" font-size="9" fill="#475569">Introductory</text>
  <rect x="22" y="434" width="236" height="36" rx="8" fill="#0369a1"/>
  <text x="140" y="457" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">Log Group Session</text>
  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">ASSESS</text><rect x="182" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><div class="ph-lbl">Group Session</div></div>
    <div class="pr-text">
      <h3>Running a Group Session?</h3>
      <p>Use <strong>Group Session</strong> to log one session for multiple players at once. Select who attended, write shared notes, and tap Log — done for everyone in one go.</p>
      <div class="tip">Great for net sessions, fielding drills or match days where the same session applies to the whole squad.</div>
    </div>
  </div>

  <div class="divider"></div>
  <div class="sh" style="font-size:18px">Assessing Skills</div>
</div>

<div class="section">
  <div class="phone-row flip">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="white"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="white"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Logo area -->
  <rect x="0" y="32" width="280" height="160" fill="#0f2744"/>
  <rect x="0" y="32" width="280" height="160" fill="url(#grad1)"/>
  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f2744"/><stop offset="1" stop-color="#1a4a7a"/></linearGradient></defs>
  <!-- Starburst lines -->
  <line x1="140" y1="112" x2="260" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="237" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="177" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="104" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="44" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="21" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="43" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="103" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="176" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="236" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/>
  <!-- Logo text -->
  <text x="140" y="96" font-family="Arial" font-size="30" font-weight="900" fill="white" text-anchor="middle">Crickit</text>
  <text x="140" y="128" font-family="Arial" font-size="30" font-weight="900" fill="#F59E0B" text-anchor="middle">Coach</text>
  <!-- Cricket bat icon centred -->
  <text x="140" y="162" font-family="Arial" font-size="26" text-anchor="middle">🏏</text>

  <!-- Toggle -->
  <rect x="14" y="202" width="252" height="38" rx="10" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <rect x="16" y="204" width="124" height="34" rx="8" fill="#0369a1"/>
  <text x="78" y="226" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">🎓 Coach / Admin</text>
  <text x="202" y="226" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">🏏 Player</text>

  <!-- Email field -->
  <text x="14" y="258" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">📧 EMAIL ADDRESS</text>
  <rect x="14" y="264" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="287" font-family="Arial" font-size="12" fill="#94a3b8">coach@academy.com</text>

  <!-- Password field -->
  <text x="14" y="318" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">🔒 PASSWORD</text>
  <rect x="14" y="324" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="347" font-family="Arial" font-size="16" fill="#94a3b8" letter-spacing="4">••••••••</text>

  <!-- Forgot -->
  <text x="266" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1" text-anchor="end">Forgot password?</text>

  <!-- Sign In button -->
  <rect x="14" y="388" width="252" height="42" rx="10" fill="#0369a1"/>
  <text x="140" y="414" font-family="Arial" font-size="13" font-weight="700" fill="white" text-anchor="middle">Sign In as Coach / Admin</text>

  <!-- User guide link -->
  <text x="140" y="446" font-family="Arial" font-size="11" font-weight="600" fill="#0369a1" text-anchor="middle">📖 User Guide — How it works</text>

  <!-- Register -->
  <line x1="14" y1="460" x2="266" y2="460" stroke="#e2e8f0" stroke-width="1"/>
  <text x="140" y="478" font-family="Arial" font-size="11" fill="#475569" text-anchor="middle">New to CrickitCoach?</text>
  <rect x="14" y="486" width="252" height="36" rx="8" fill="transparent" stroke="#0369a1" stroke-width="1.5"/>
  <text x="140" y="509" font-family="Arial" font-size="12" font-weight="700" fill="#0369a1" text-anchor="middle">🏫 Create New School Account</text>

  <text x="140" y="540" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="middle">support@crickitcoach.com · © 2026 ArcGain LLC</text>
</svg><div class="ph-lbl">Skill Assessment</div></div>
    <div class="pr-text">
      <h3>Running an Assessment</h3>
      <p>Rate a player's skills from 1 to 5 across five categories. Each assessment builds up a history you can share with parents.</p>
      <ol class="ql">
        <li>Tap a player to open their profile</li>
        <li>Tap <strong>+ New Assessment</strong></li>
        <li>Work through each category tab</li>
        <li>Rate each skill with the slider</li>
        <li>Tap <strong>Save Assessment</strong></li>
      </ol>
      <table class="rt" style="margin-top:14px">
        <thead><tr><th>Score</th><th>What it means</th></tr></thead>
        <tbody>
          <tr><td><strong>5 — Elite</strong></td><td>Exceptional. Ready to move up.</td></tr>
          <tr><td><strong>4 — Proficient</strong></td><td>Performing well and consistently.</td></tr>
          <tr><td><strong>3 — Competent</strong></td><td>Meeting expectations for this level.</td></tr>
          <tr><td><strong>2 — Developing</strong></td><td>Improving but still needs work.</td></tr>
          <tr><td><strong>1 — Beginner</strong></td><td>Just starting out with this skill.</td></tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="divider"></div>
  <div class="sh" style="font-size:18px">Setting Goals</div>
</div>

<div class="section">
  <div class="phone-row">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744">Input Tools</text>
  <circle cx="40" cy="82" r="22" fill="#e8edf5"/><text x="40" y="79" font-family="Arial" font-size="14" text-anchor="middle">📋</text><text x="40" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Session</text><circle cx="92" cy="82" r="22" fill="#e8edf5"/><text x="92" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="92" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Group</text><circle cx="144" cy="82" r="22" fill="#e8edf5"/><text x="144" y="79" font-family="Arial" font-size="14" text-anchor="middle">📅</text><text x="144" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Sess Log</text><circle cx="196" cy="82" r="22" fill="#e8edf5"/><text x="196" y="79" font-family="Arial" font-size="14" text-anchor="middle">🎯</text><text x="196" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Goal</text><circle cx="248" cy="82" r="22" fill="#0369a1"/><text x="248" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="248" y="93" font-family="Arial" font-size="7" font-weight="700" fill="white" text-anchor="middle">Squad</text>
  <line x1="0" y1="112" x2="280" y2="112" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="8" y="118" width="264" height="332" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="140" font-family="Arial" font-size="11" font-weight="900" fill="#15803d">🎯 👥 SQUAD GOAL</text>
  <text x="22" y="154" font-family="Arial" font-size="9" fill="#475569">Set one shared goal for multiple players at once.</text>

  <text x="22" y="174" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">GOAL TITLE</text>
  <rect x="22" y="178" width="236" height="30" rx="6" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.2"/>
  <text x="32" y="197" font-family="Arial" font-size="11" fill="#94a3b8">e.g. Improve batting strike rate to 80+</text>

  <text x="22" y="222" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">CATEGORY</text>
  <rect x="22" y="226" width="46" height="22" rx="11" fill="#0369a1" stroke="#0369a1" stroke-width="1.2"/><text x="45" y="241" font-family="Arial" font-size="9" font-weight="700" fill="white" text-anchor="middle">Batting</text><rect x="72" y="226" width="46" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="95" y="241" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Bowling</text><rect x="122" y="226" width="46" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="145" y="241" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Field</text><rect x="172" y="226" width="46" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="195" y="241" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Mental</text><rect x="222" y="226" width="46" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="245" y="241" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Phys</text>

  <text x="22" y="264" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">TARGET DATE</text>
  <rect x="22" y="268" width="236" height="30" rx="6" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.2"/>
  <text x="32" y="287" font-family="Arial" font-size="11" fill="#94a3b8">Select date…</text>

  <text x="22" y="312" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">SELECT PLAYERS</text>
  <rect x="22" y="318" width="236" height="28" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <rect x="28" y="322" width="14" height="14" rx="3" fill="#15803d22" stroke="#15803d" stroke-width="1.2"/> <circle cx="58" cy="332" r="11" fill="#15803d"/><text x="58" y="336" font-family="Arial" font-size="7" font-weight="900" fill="white" text-anchor="middle">SJ</text> <text x="76" y="336" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Sam Johnson</text><text x="76" y="346" font-family="Arial" font-size="9" fill="#475569">Foundational</text><rect x="22" y="352" width="236" height="28" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <rect x="28" y="356" width="14" height="14" rx="3" fill="#0284c722" stroke="#0284c7" stroke-width="1.2"/> <circle cx="58" cy="366" r="11" fill="#0284c7"/><text x="58" y="370" font-family="Arial" font-size="7" font-weight="900" fill="white" text-anchor="middle">PT</text> <text x="76" y="370" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Priya Thakur</text><text x="76" y="380" font-family="Arial" font-size="9" fill="#475569">Introductory</text><rect x="22" y="386" width="236" height="28" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <rect x="28" y="390" width="14" height="14" rx="3" fill="#15803d22" stroke="#15803d" stroke-width="1.2"/> <circle cx="58" cy="400" r="11" fill="#15803d"/><text x="58" y="404" font-family="Arial" font-size="7" font-weight="900" fill="white" text-anchor="middle">MC</text> <text x="76" y="404" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Marcus Chen</text><text x="76" y="414" font-family="Arial" font-size="9" fill="#475569">Foundational</text>

  <rect x="22" y="428" width="236" height="36" rx="8" fill="#15803d"/>
  <text x="140" y="451" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">Add Squad Goal</text>
  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">ASSESS</text><rect x="182" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><div class="ph-lbl">Squad Goals</div></div>
    <div class="pr-text">
      <h3>Setting Goals</h3>
      <p>Set individual goals from any player's profile, or use <strong>Squad Goal</strong> to set the same goal for your whole squad at once — great for term objectives.</p>
      <p>Each goal has a progress percentage you can update as the player improves. At 100% it marks as Completed automatically.</p>
      <div class="tip">Players and parents can see their goals when they log in — a great way to keep them motivated between sessions.</div>
    </div>
  </div>

  <div class="divider"></div>
  <div class="sh" style="font-size:18px">Progress Reports</div>
</div>

<div class="section">
  <div class="phone-row flip">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="white"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="white"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Logo area -->
  <rect x="0" y="32" width="280" height="160" fill="#0f2744"/>
  <rect x="0" y="32" width="280" height="160" fill="url(#grad1)"/>
  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f2744"/><stop offset="1" stop-color="#1a4a7a"/></linearGradient></defs>
  <!-- Starburst lines -->
  <line x1="140" y1="112" x2="260" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="237" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="177" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="104" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="44" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="21" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="43" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="103" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="176" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="236" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/>
  <!-- Logo text -->
  <text x="140" y="96" font-family="Arial" font-size="30" font-weight="900" fill="white" text-anchor="middle">Crickit</text>
  <text x="140" y="128" font-family="Arial" font-size="30" font-weight="900" fill="#F59E0B" text-anchor="middle">Coach</text>
  <!-- Cricket bat icon centred -->
  <text x="140" y="162" font-family="Arial" font-size="26" text-anchor="middle">🏏</text>

  <!-- Toggle -->
  <rect x="14" y="202" width="252" height="38" rx="10" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <rect x="16" y="204" width="124" height="34" rx="8" fill="#0369a1"/>
  <text x="78" y="226" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">🎓 Coach / Admin</text>
  <text x="202" y="226" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">🏏 Player</text>

  <!-- Email field -->
  <text x="14" y="258" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">📧 EMAIL ADDRESS</text>
  <rect x="14" y="264" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="287" font-family="Arial" font-size="12" fill="#94a3b8">coach@academy.com</text>

  <!-- Password field -->
  <text x="14" y="318" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">🔒 PASSWORD</text>
  <rect x="14" y="324" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="347" font-family="Arial" font-size="16" fill="#94a3b8" letter-spacing="4">••••••••</text>

  <!-- Forgot -->
  <text x="266" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1" text-anchor="end">Forgot password?</text>

  <!-- Sign In button -->
  <rect x="14" y="388" width="252" height="42" rx="10" fill="#0369a1"/>
  <text x="140" y="414" font-family="Arial" font-size="13" font-weight="700" fill="white" text-anchor="middle">Sign In as Coach / Admin</text>

  <!-- User guide link -->
  <text x="140" y="446" font-family="Arial" font-size="11" font-weight="600" fill="#0369a1" text-anchor="middle">📖 User Guide — How it works</text>

  <!-- Register -->
  <line x1="14" y1="460" x2="266" y2="460" stroke="#e2e8f0" stroke-width="1"/>
  <text x="140" y="478" font-family="Arial" font-size="11" fill="#475569" text-anchor="middle">New to CrickitCoach?</text>
  <rect x="14" y="486" width="252" height="36" rx="8" fill="transparent" stroke="#0369a1" stroke-width="1.5"/>
  <text x="140" y="509" font-family="Arial" font-size="12" font-weight="700" fill="#0369a1" text-anchor="middle">🏫 Create New School Account</text>

  <text x="140" y="540" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="middle">support@crickitcoach.com · © 2026 ArcGain LLC</text>
</svg><div class="ph-lbl">Progress Report</div></div>
    <div class="pr-text">
      <h3>Generating a Report</h3>
      <p>Generate a full progress report for any player — skill scores, goals, sessions, coaching notes and overall rating — ready to share with parents as a PDF.</p>
      <ol class="ql">
        <li>Open the player's profile</li>
        <li>Tap the <strong>Reports tab</strong> (📄)</li>
        <li>Tap <strong>Generate New Report</strong></li>
        <li>Tap <strong>Save Report</strong> to share with the student, then <strong>Download PDF</strong> to save to your device</li>
      </ol>
      <div class="note">Reports are saved permanently — you can always go back and see exactly what the report said on the day you generated it.</div>
    </div>
  </div>
</div>
</div>

<!-- ADMIN -->
<div class="page" id="pg-admin">
<div class="section">
  <div class="sh">School Admin Guide 🏫</div>
  <div class="sh-sub">You set up and manage the whole school — coaches, players, and settings.</div>

  <div class="step-card">
    <div class="step-num">1</div>
    <div class="step-body">
      <h3>Register Your School</h3>
      <p>Go to <strong>crickitcoach.com</strong> and tap <strong>Create New School Account</strong>. Enter your school name, your name, email and password. You're in straight away — takes about 2 minutes.</p>
      <div class="tip">Your first year is completely free — no credit card needed.</div>
    </div>
  </div>

  <div class="step-card">
    <div class="step-num">2</div>
    <div class="step-body">
      <h3>Add Your Coaches</h3>
      <p>Before players can be added, you need at least one coach. Go to <strong>Settings → Manage Coaches → + Add Coach</strong>. Give them a name, email and temporary password — they can log in immediately.</p>
      <div class="warn">The Admin account is for running the school — not for coaching. Even if you're also a coach, create a separate Coach account for your coaching work.</div>
    </div>
  </div>

  <div class="phone-row">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744">School View</text>
  <!-- Stats -->
  <rect x="8" y="82" width="82" height="50" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="49" y="106" font-family="Arial" font-size="18" font-weight="900" fill="#0369a1" text-anchor="middle">18</text><text x="49" y="120" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">PLAYERS</text>
  <rect x="99" y="82" width="82" height="50" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="140" y="106" font-family="Arial" font-size="18" font-weight="900" fill="#15803d" text-anchor="middle">3.4</text><text x="140" y="120" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">AVG SCORE</text>
  <rect x="190" y="82" width="82" height="50" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="231" y="106" font-family="Arial" font-size="18" font-weight="900" fill="#d97706" text-anchor="middle">2</text><text x="231" y="120" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">COACHES</text>

  <text x="14" y="152" font-family="Arial" font-size="12" font-weight="900" fill="#0f2744">BY CLASS</text>
  <text x="14" y="170" font-family="Arial" font-size="10" font-weight="700" fill="#0284c7">🌱 Introductory</text><rect x="14" y="174" width="220" height="11" rx="5" fill="#e2e8f0"/><rect x="14" y="174" width="72" height="11" rx="5" fill="#0284c7"/><text x="240" y="183" font-family="Arial" font-size="11" font-weight="900" fill="#0284c7">6</text><text x="14" y="206" font-family="Arial" font-size="10" font-weight="700" fill="#15803d">⚡ Foundational</text><rect x="14" y="210" width="220" height="11" rx="5" fill="#e2e8f0"/><rect x="14" y="210" width="96" height="11" rx="5" fill="#15803d"/><text x="240" y="219" font-family="Arial" font-size="11" font-weight="900" fill="#15803d">8</text><text x="14" y="242" font-family="Arial" font-size="10" font-weight="700" fill="#d97706">🔥 Intermediate</text><rect x="14" y="246" width="220" height="11" rx="5" fill="#e2e8f0"/><rect x="14" y="246" width="37" height="11" rx="5" fill="#d97706"/><text x="240" y="255" font-family="Arial" font-size="11" font-weight="900" fill="#d97706">3</text><text x="14" y="278" font-family="Arial" font-size="10" font-weight="700" fill="#dc2626">🏆 Advanced</text><rect x="14" y="282" width="220" height="11" rx="5" fill="#e2e8f0"/><rect x="14" y="282" width="13" height="11" rx="5" fill="#dc2626"/><text x="240" y="291" font-family="Arial" font-size="11" font-weight="900" fill="#dc2626">1</text>

  <text x="14" y="304" font-family="Arial" font-size="12" font-weight="900" fill="#0f2744">BY COACH</text>
  <rect x="8" y="310" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <circle cx="36" cy="337" r="18" fill="#0369a1"/><text x="36" y="341" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">AJ</text> <text x="64" y="330" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Alex Johnson — Head Coach</text><text x="64" y="346" font-family="Arial" font-size="10" fill="#475569">10 players · Foundational avg</text><text x="262" y="340" font-family="Arial" font-size="18" fill="#0369a1" text-anchor="middle">›</text><rect x="8" y="372" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <circle cx="36" cy="399" r="18" fill="#15803d"/><text x="36" y="403" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">SR</text> <text x="64" y="392" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Sara Reed — Junior Coach</text><text x="64" y="408" font-family="Arial" font-size="10" fill="#475569">8 players · Introductory avg</text><text x="262" y="402" font-family="Arial" font-size="18" fill="#0369a1" text-anchor="middle">›</text>
  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">SCHOOL</text><rect x="126" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="196" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">ASSESS</text><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><div class="ph-lbl">School Overview</div></div>
    <div class="pr-text">
      <h3>Seeing the Whole School</h3>
      <p>The <strong>School tab</strong> gives you a full picture — player counts per class level, which coach has who, and overall school performance.</p>
      <p>Tap any coach card to see their player list. Tap any class tile to see all players at that level across every coach.</p>
    </div>
  </div>

  <div class="step-card">
    <div class="step-num">3</div>
    <div class="step-body">
      <h3>Moving Players Between Coaches</h3>
      <p>Only the Admin can reassign players. Open any player's profile, tap <strong>Edit</strong>, use the <strong>Reassign Coach</strong> dropdown, and tap Save. Done instantly.</p>
      <div class="note">If a coach leaves, their players stay in the system. Reassign them to another coach so their records are maintained.</div>
    </div>
  </div>

  <div class="phone-row flip">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744">Squad</text>
  <!-- Add Player button -->
  <rect x="162" y="40" width="104" height="28" rx="8" fill="#0369a1"/>
  <text x="214" y="59" font-family="Arial" font-size="11" font-weight="700" fill="white" text-anchor="middle">+ Add Player</text>
  <!-- Filters -->
  <rect x="0" y="76" width="280" height="30" fill="#f8fafc"/>
  <rect x="8" y="81" width="48" height="20" rx="10" fill="#0369a1" stroke="#0369a1" stroke-width="1.2"/><text x="32" y="95" font-family="Arial" font-size="9" font-weight="700" fill="white" text-anchor="middle">All</text><rect x="62" y="81" width="48" height="20" rx="10" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="86" y="95" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">🌱 Intro</text><rect x="116" y="81" width="48" height="20" rx="10" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="140" y="95" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">⚡ Found</text><rect x="170" y="81" width="48" height="20" rx="10" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="194" y="95" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">🔥 Inter</text>
  <line x1="0" y1="106" x2="280" y2="106" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="8" y="112" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <circle cx="36" cy="139" r="18" fill="#15803d"/><text x="36" y="143" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">SJ</text>
  <text x="64" y="131" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Sam Johnson</text>
  <rect x="64" y="136" width="88.96" height="17" rx="8" fill="#15803d22"/><text x="108.47999999999999" y="146" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="middle">⚡ Foundational</text>
  <rect x="156.95999999999998" y="136" width="62.98" height="17" rx="8" fill="transparent" stroke="#0284c7" stroke-width="1.2"/><text x="188.45" y="146" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="middle">Batter-RH</text><circle cx="254" cy="139" r="18" fill="none" stroke="#0369a1" stroke-width="2"/><text x="254" y="144" font-family="Arial" font-size="11" font-weight="900" fill="#0369a1" text-anchor="middle">3.8</text>
  <rect x="8" y="174" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <circle cx="36" cy="201" r="18" fill="#0284c7"/><text x="36" y="205" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">PT</text>
  <text x="64" y="193" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Priya Thakur</text>
  <rect x="64" y="198" width="88.96" height="17" rx="8" fill="#0284c722"/><text x="108.47999999999999" y="208" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="middle">🌱 Introductory</text>
  <rect x="156.95999999999998" y="198" width="83.86" height="17" rx="8" fill="transparent" stroke="#f97316" stroke-width="1.2"/><text x="198.89" y="208" font-family="Arial" font-size="9" font-weight="700" fill="#f97316" text-anchor="middle">Bowler-PaceRH</text>
  <rect x="8" y="236" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <circle cx="36" cy="263" r="18" fill="#15803d"/><text x="36" y="267" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">MC</text>
  <text x="64" y="255" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Marcus Chen</text>
  <rect x="64" y="260" width="88.96" height="17" rx="8" fill="#15803d22"/><text x="108.47999999999999" y="270" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="middle">⚡ Foundational</text>
  <rect x="156.95999999999998" y="260" width="68.19999999999999" height="17" rx="8" fill="transparent" stroke="#22c55e" stroke-width="1.2"/><text x="191.05999999999997" y="270" font-family="Arial" font-size="9" font-weight="700" fill="#22c55e" text-anchor="middle">AllRounder</text><circle cx="254" cy="263" r="18" fill="none" stroke="#0369a1" stroke-width="2"/><text x="254" y="268" font-family="Arial" font-size="11" font-weight="900" fill="#0369a1" text-anchor="middle">4.2</text>
  <rect x="8" y="298" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <circle cx="36" cy="325" r="18" fill="#d97706"/><text x="36" y="329" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">AK</text>
  <text x="64" y="317" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Aisha Khan</text>
  <rect x="64" y="322" width="88.96" height="17" rx="8" fill="#d9770622"/><text x="108.47999999999999" y="332" font-family="Arial" font-size="9" font-weight="700" fill="#d97706" text-anchor="middle">🔥 Intermediate</text>
  <rect x="156.95999999999998" y="322" width="62.98" height="17" rx="8" fill="transparent" stroke="#0ea5e9" stroke-width="1.2"/><text x="188.45" y="332" font-family="Arial" font-size="9" font-weight="700" fill="#0ea5e9" text-anchor="middle">Batter-LH</text><circle cx="254" cy="325" r="18" fill="none" stroke="#0369a1" stroke-width="2"/><text x="254" y="330" font-family="Arial" font-size="11" font-weight="900" fill="#0369a1" text-anchor="middle">3.1</text>
  <rect x="8" y="360" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <circle cx="36" cy="387" r="18" fill="#0284c7"/><text x="36" y="391" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">TW</text>
  <text x="64" y="379" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Tom Williams</text>
  <rect x="64" y="384" width="88.96" height="17" rx="8" fill="#0284c722"/><text x="108.47999999999999" y="394" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="middle">🌱 Introductory</text>
  <rect x="156.95999999999998" y="384" width="78.63999999999999" height="17" rx="8" fill="transparent" stroke="#a855f7" stroke-width="1.2"/><text x="196.27999999999997" y="394" font-family="Arial" font-size="9" font-weight="700" fill="#a855f7" text-anchor="middle">Wicketkeeper</text><rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">SQUAD</text><rect x="70" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">ASSESS</text><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><div class="ph-lbl">Squad Tab</div></div>
    <div class="pr-text">
      <h3>Viewing Any Squad</h3>
      <p>As Admin you can see all players across every coach. The Squad tab shows players with their class badges, role tags and latest scores. Tap any player to open their full profile.</p>
    </div>
  </div>
</div>
</div>

<!-- PLAYER/PARENT -->
<div class="page" id="pg-player">
<div class="section">
  <div class="sh">For Players &amp; Parents 👨‍👩‍👧</div>
  <div class="sh-sub">Here's how to get the most out of CrickitCoach as a player or parent.</div>

  <div class="phone-row">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="white"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="white"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Logo area -->
  <rect x="0" y="32" width="280" height="160" fill="#0f2744"/>
  <rect x="0" y="32" width="280" height="160" fill="url(#grad1)"/>
  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f2744"/><stop offset="1" stop-color="#1a4a7a"/></linearGradient></defs>
  <!-- Starburst lines -->
  <line x1="140" y1="112" x2="260" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="237" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="177" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="104" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="44" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="21" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="43" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="103" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="176" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="236" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/>
  <!-- Logo text -->
  <text x="140" y="96" font-family="Arial" font-size="30" font-weight="900" fill="white" text-anchor="middle">Crickit</text>
  <text x="140" y="128" font-family="Arial" font-size="30" font-weight="900" fill="#F59E0B" text-anchor="middle">Coach</text>
  <!-- Cricket bat icon centred -->
  <text x="140" y="162" font-family="Arial" font-size="26" text-anchor="middle">🏏</text>

  <!-- Toggle -->
  <rect x="14" y="202" width="252" height="38" rx="10" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <rect x="16" y="204" width="124" height="34" rx="8" fill="#0369a1"/>
  <text x="78" y="226" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">🎓 Coach / Admin</text>
  <text x="202" y="226" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">🏏 Player</text>

  <!-- Email field -->
  <text x="14" y="258" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">📧 EMAIL ADDRESS</text>
  <rect x="14" y="264" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="287" font-family="Arial" font-size="12" fill="#94a3b8">coach@academy.com</text>

  <!-- Password field -->
  <text x="14" y="318" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">🔒 PASSWORD</text>
  <rect x="14" y="324" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="347" font-family="Arial" font-size="16" fill="#94a3b8" letter-spacing="4">••••••••</text>

  <!-- Forgot -->
  <text x="266" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1" text-anchor="end">Forgot password?</text>

  <!-- Sign In button -->
  <rect x="14" y="388" width="252" height="42" rx="10" fill="#0369a1"/>
  <text x="140" y="414" font-family="Arial" font-size="13" font-weight="700" fill="white" text-anchor="middle">Sign In as Coach / Admin</text>

  <!-- User guide link -->
  <text x="140" y="446" font-family="Arial" font-size="11" font-weight="600" fill="#0369a1" text-anchor="middle">📖 User Guide — How it works</text>

  <!-- Register -->
  <line x1="14" y1="460" x2="266" y2="460" stroke="#e2e8f0" stroke-width="1"/>
  <text x="140" y="478" font-family="Arial" font-size="11" fill="#475569" text-anchor="middle">New to CrickitCoach?</text>
  <rect x="14" y="486" width="252" height="36" rx="8" fill="transparent" stroke="#0369a1" stroke-width="1.5"/>
  <text x="140" y="509" font-family="Arial" font-size="12" font-weight="700" fill="#0369a1" text-anchor="middle">🏫 Create New School Account</text>

  <text x="140" y="540" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="middle">support@crickitcoach.com · © 2026 ArcGain LLC</text>
</svg><div class="ph-lbl">Player Login</div></div>
    <div class="pr-text">
      <h3>Getting Started</h3>
      <p>Your coach sets up your account and gives you an email and temporary password. Select <strong>Player</strong> on the toggle — not Coach / Admin.</p>
      <ol class="ql">
        <li>Go to <strong>crickitcoach.com</strong></li>
        <li>Tap <strong>Player</strong> on the toggle</li>
        <li>Enter your email and password</li>
        <li>Tap <strong>Sign In as Player</strong></li>
      </ol>
      <div class="tip">Add the app to your home screen so it's easy to find. You can change your password any time from Settings.</div>
    </div>
  </div>

  <div class="step-card">
    <div class="step-num">👀</div>
    <div class="step-body">
      <h3>What You Can See</h3>
      <p>Your five tabs: <strong>Home</strong> (overview) · <strong>Progress</strong> (skill charts) · <strong>Goals</strong> (your targets) · <strong>Sessions</strong> (training history &amp; reports) · <strong>Settings</strong></p>
      <p>Everything is read-only — your coach enters the data, you view it.</p>
    </div>
  </div>

  <div class="phone-row">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="white"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="white"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Logo area -->
  <rect x="0" y="32" width="280" height="160" fill="#0f2744"/>
  <rect x="0" y="32" width="280" height="160" fill="url(#grad1)"/>
  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f2744"/><stop offset="1" stop-color="#1a4a7a"/></linearGradient></defs>
  <!-- Starburst lines -->
  <line x1="140" y1="112" x2="260" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="237" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="177" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="104" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="44" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="21" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="43" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="103" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="176" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="236" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/>
  <!-- Logo text -->
  <text x="140" y="96" font-family="Arial" font-size="30" font-weight="900" fill="white" text-anchor="middle">Crickit</text>
  <text x="140" y="128" font-family="Arial" font-size="30" font-weight="900" fill="#F59E0B" text-anchor="middle">Coach</text>
  <!-- Cricket bat icon centred -->
  <text x="140" y="162" font-family="Arial" font-size="26" text-anchor="middle">🏏</text>

  <!-- Toggle -->
  <rect x="14" y="202" width="252" height="38" rx="10" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <rect x="16" y="204" width="124" height="34" rx="8" fill="#0369a1"/>
  <text x="78" y="226" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">🎓 Coach / Admin</text>
  <text x="202" y="226" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">🏏 Player</text>

  <!-- Email field -->
  <text x="14" y="258" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">📧 EMAIL ADDRESS</text>
  <rect x="14" y="264" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="287" font-family="Arial" font-size="12" fill="#94a3b8">coach@academy.com</text>

  <!-- Password field -->
  <text x="14" y="318" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">🔒 PASSWORD</text>
  <rect x="14" y="324" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="347" font-family="Arial" font-size="16" fill="#94a3b8" letter-spacing="4">••••••••</text>

  <!-- Forgot -->
  <text x="266" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1" text-anchor="end">Forgot password?</text>

  <!-- Sign In button -->
  <rect x="14" y="388" width="252" height="42" rx="10" fill="#0369a1"/>
  <text x="140" y="414" font-family="Arial" font-size="13" font-weight="700" fill="white" text-anchor="middle">Sign In as Coach / Admin</text>

  <!-- User guide link -->
  <text x="140" y="446" font-family="Arial" font-size="11" font-weight="600" fill="#0369a1" text-anchor="middle">📖 User Guide — How it works</text>

  <!-- Register -->
  <line x1="14" y1="460" x2="266" y2="460" stroke="#e2e8f0" stroke-width="1"/>
  <text x="140" y="478" font-family="Arial" font-size="11" fill="#475569" text-anchor="middle">New to CrickitCoach?</text>
  <rect x="14" y="486" width="252" height="36" rx="8" fill="transparent" stroke="#0369a1" stroke-width="1.5"/>
  <text x="140" y="509" font-family="Arial" font-size="12" font-weight="700" fill="#0369a1" text-anchor="middle">🏫 Create New School Account</text>

  <text x="140" y="540" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="middle">support@crickitcoach.com · © 2026 ArcGain LLC</text>
</svg><div class="ph-lbl">Your Progress</div></div>
    <div class="pr-text">
      <h3>Tracking Your Progress</h3>
      <p>The <strong>Progress tab</strong> shows your skill scores across five areas — Batting, Bowling, Fielding, Mental and Physical — and how much you've improved since your first assessment.</p>
    </div>
  </div>

  <div class="phone-row flip">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="white"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="white"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Logo area -->
  <rect x="0" y="32" width="280" height="160" fill="#0f2744"/>
  <rect x="0" y="32" width="280" height="160" fill="url(#grad1)"/>
  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f2744"/><stop offset="1" stop-color="#1a4a7a"/></linearGradient></defs>
  <!-- Starburst lines -->
  <line x1="140" y1="112" x2="260" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="237" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="177" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="104" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="44" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="21" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="43" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="103" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="176" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="236" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/>
  <!-- Logo text -->
  <text x="140" y="96" font-family="Arial" font-size="30" font-weight="900" fill="white" text-anchor="middle">Crickit</text>
  <text x="140" y="128" font-family="Arial" font-size="30" font-weight="900" fill="#F59E0B" text-anchor="middle">Coach</text>
  <!-- Cricket bat icon centred -->
  <text x="140" y="162" font-family="Arial" font-size="26" text-anchor="middle">🏏</text>

  <!-- Toggle -->
  <rect x="14" y="202" width="252" height="38" rx="10" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <rect x="16" y="204" width="124" height="34" rx="8" fill="#0369a1"/>
  <text x="78" y="226" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">🎓 Coach / Admin</text>
  <text x="202" y="226" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">🏏 Player</text>

  <!-- Email field -->
  <text x="14" y="258" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">📧 EMAIL ADDRESS</text>
  <rect x="14" y="264" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="287" font-family="Arial" font-size="12" fill="#94a3b8">coach@academy.com</text>

  <!-- Password field -->
  <text x="14" y="318" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">🔒 PASSWORD</text>
  <rect x="14" y="324" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="347" font-family="Arial" font-size="16" fill="#94a3b8" letter-spacing="4">••••••••</text>

  <!-- Forgot -->
  <text x="266" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1" text-anchor="end">Forgot password?</text>

  <!-- Sign In button -->
  <rect x="14" y="388" width="252" height="42" rx="10" fill="#0369a1"/>
  <text x="140" y="414" font-family="Arial" font-size="13" font-weight="700" fill="white" text-anchor="middle">Sign In as Coach / Admin</text>

  <!-- User guide link -->
  <text x="140" y="446" font-family="Arial" font-size="11" font-weight="600" fill="#0369a1" text-anchor="middle">📖 User Guide — How it works</text>

  <!-- Register -->
  <line x1="14" y1="460" x2="266" y2="460" stroke="#e2e8f0" stroke-width="1"/>
  <text x="140" y="478" font-family="Arial" font-size="11" fill="#475569" text-anchor="middle">New to CrickitCoach?</text>
  <rect x="14" y="486" width="252" height="36" rx="8" fill="transparent" stroke="#0369a1" stroke-width="1.5"/>
  <text x="140" y="509" font-family="Arial" font-size="12" font-weight="700" fill="#0369a1" text-anchor="middle">🏫 Create New School Account</text>

  <text x="140" y="540" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="middle">support@crickitcoach.com · © 2026 ArcGain LLC</text>
</svg><div class="ph-lbl">Progress Report</div></div>
    <div class="pr-text">
      <h3>Progress Reports &amp; Parents</h3>
      <p>Your coach generates progress reports visible in the <strong>Sessions → Reports</strong> tab. Each report is a full snapshot of your skills, goals and sessions at that point in time.</p>
      <p><strong>Parents</strong> — save any report as a PDF by tapping the report → print icon → Save as PDF.</p>
      <div class="note">Coaching notes are private between you and your coach — parents and other players can't see them.</div>
    </div>
  </div>

  <div class="phone-row">
    <div class="ph"><svg style="width:130px;border-radius:20px;box-shadow:0 8px 24px rgba(0,0,0,.18);border:5px solid #0B1F3A;display:block;background:#0B1F3A;overflow:hidden;flex-shrink:0" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/>
  <text x="14" y="50" font-family="Arial" font-size="10" font-weight="700" fill="#0369a1">← Sam Johnson</text>
  <!-- Sub tabs -->
  <rect x="0" y="56" width="280" height="32" fill="#f8fafc"/>
  <rect x="8" y="60" width="80" height="24" rx="12" fill="#0369a1"/>
  <text x="48" y="76" font-family="Arial" font-size="10" font-weight="700" fill="white" text-anchor="middle">📋 Sessions</text>
  <rect x="96" y="60" width="70" height="24" rx="12" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/>
  <text x="131" y="76" font-family="Arial" font-size="10" font-weight="700" fill="#475569" text-anchor="middle">📄 Reports</text>
  <line x1="0" y1="88" x2="280" y2="88" stroke="#e2e8f0" stroke-width="1"/>
  <text x="14" y="106" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Sessions · Most recent first</text>
  <text x="266" y="106" font-family="Arial" font-size="10" fill="#94a3b8" text-anchor="end">12 total</text>
  <rect x="8" y="114" width="264" height="76" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="130" font-family="Arial" font-size="9" fill="#94a3b8">Today</text>
  <rect x="22" y="136" width="63" height="16" rx="8" fill="#0369a122"/>
  <text x="53.5" y="148" font-family="Arial" font-size="9" font-weight="700" fill="#0369a1" text-anchor="middle">Batting</text>
  <text x="93" y="148" font-family="Arial" font-size="9" fill="#94a3b8">· 60 min</text>
  <text x="22" y="166" font-family="Arial" font-size="10" fill="#1e293b">Worked on driving through covers. Good session.</text>
  <line x1="22" y1="180" x2="258" y2="180" stroke="#f0f4f8" stroke-width="1"/>
  <rect x="8" y="196" width="264" height="76" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="212" font-family="Arial" font-size="9" fill="#94a3b8">2 days ago</text>
  <rect x="22" y="218" width="49" height="16" rx="8" fill="#dc262622"/>
  <text x="46.5" y="230" font-family="Arial" font-size="9" font-weight="700" fill="#dc2626" text-anchor="middle">Match</text>
  <text x="79" y="230" font-family="Arial" font-size="9" fill="#94a3b8">· 90 min</text>
  <text x="22" y="248" font-family="Arial" font-size="10" fill="#1e293b">Strong batting performance. Needs to work on run…</text>
  <line x1="22" y1="262" x2="258" y2="262" stroke="#f0f4f8" stroke-width="1"/>
  <rect x="8" y="278" width="264" height="76" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="294" font-family="Arial" font-size="9" fill="#94a3b8">5 days ago</text>
  <rect x="22" y="300" width="70" height="16" rx="8" fill="#15803d22"/>
  <text x="57.0" y="312" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="middle">Fielding</text>
  <text x="100" y="312" font-family="Arial" font-size="9" fill="#94a3b8">· 45 min</text>
  <text x="22" y="330" font-family="Arial" font-size="10" fill="#1e293b">Ground fielding drills and catching practice.</text>
  <line x1="22" y1="344" x2="258" y2="344" stroke="#f0f4f8" stroke-width="1"/>
  <rect x="8" y="360" width="264" height="76" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="376" font-family="Arial" font-size="9" fill="#94a3b8">1 week ago</text>
  <rect x="22" y="382" width="63" height="16" rx="8" fill="#d9770622"/>
  <text x="53.5" y="394" font-family="Arial" font-size="9" font-weight="700" fill="#d97706" text-anchor="middle">Bowling</text>
  <text x="93" y="394" font-family="Arial" font-size="9" fill="#94a3b8">· 60 min</text>
  <text x="22" y="412" font-family="Arial" font-size="10" fill="#1e293b">Pace bowling action and follow-through work.</text>
  <line x1="22" y1="426" x2="258" y2="426" stroke="#f0f4f8" stroke-width="1"/><rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">ASSESS</text><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><div class="ph-lbl">Session History</div></div>
    <div class="pr-text">
      <h3>Your Session History</h3>
      <p>Every session your coach logs appears in the <strong>Sessions tab</strong> — date, type, duration and the notes they wrote about your work.</p>
      <div class="tip">Swipe across to the Reports sub-tab inside Sessions to find all your progress reports.</div>
    </div>
  </div>
</div>
</div>

<div class="gallery">
  <h3>All Screens at a Glance</h3>
  <div class="gal-grid">
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="white"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="white"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Logo area -->
  <rect x="0" y="32" width="280" height="160" fill="#0f2744"/>
  <rect x="0" y="32" width="280" height="160" fill="url(#grad1)"/>
  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f2744"/><stop offset="1" stop-color="#1a4a7a"/></linearGradient></defs>
  <!-- Starburst lines -->
  <line x1="140" y1="112" x2="260" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="237" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="177" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="104" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="44" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="21" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="43" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="103" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="176" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="236" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/>
  <!-- Logo text -->
  <text x="140" y="96" font-family="Arial" font-size="30" font-weight="900" fill="white" text-anchor="middle">Crickit</text>
  <text x="140" y="128" font-family="Arial" font-size="30" font-weight="900" fill="#F59E0B" text-anchor="middle">Coach</text>
  <!-- Cricket bat icon centred -->
  <text x="140" y="162" font-family="Arial" font-size="26" text-anchor="middle">🏏</text>

  <!-- Toggle -->
  <rect x="14" y="202" width="252" height="38" rx="10" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <rect x="16" y="204" width="124" height="34" rx="8" fill="#0369a1"/>
  <text x="78" y="226" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">🎓 Coach / Admin</text>
  <text x="202" y="226" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">🏏 Player</text>

  <!-- Email field -->
  <text x="14" y="258" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">📧 EMAIL ADDRESS</text>
  <rect x="14" y="264" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="287" font-family="Arial" font-size="12" fill="#94a3b8">coach@academy.com</text>

  <!-- Password field -->
  <text x="14" y="318" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">🔒 PASSWORD</text>
  <rect x="14" y="324" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="347" font-family="Arial" font-size="16" fill="#94a3b8" letter-spacing="4">••••••••</text>

  <!-- Forgot -->
  <text x="266" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1" text-anchor="end">Forgot password?</text>

  <!-- Sign In button -->
  <rect x="14" y="388" width="252" height="42" rx="10" fill="#0369a1"/>
  <text x="140" y="414" font-family="Arial" font-size="13" font-weight="700" fill="white" text-anchor="middle">Sign In as Coach / Admin</text>

  <!-- User guide link -->
  <text x="140" y="446" font-family="Arial" font-size="11" font-weight="600" fill="#0369a1" text-anchor="middle">📖 User Guide — How it works</text>

  <!-- Register -->
  <line x1="14" y1="460" x2="266" y2="460" stroke="#e2e8f0" stroke-width="1"/>
  <text x="140" y="478" font-family="Arial" font-size="11" fill="#475569" text-anchor="middle">New to CrickitCoach?</text>
  <rect x="14" y="486" width="252" height="36" rx="8" fill="transparent" stroke="#0369a1" stroke-width="1.5"/>
  <text x="140" y="509" font-family="Arial" font-size="12" font-weight="700" fill="#0369a1" text-anchor="middle">🏫 Create New School Account</text>

  <text x="140" y="540" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="middle">support@crickitcoach.com · © 2026 ArcGain LLC</text>
</svg><p>Login</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744"></text>
  <!-- School name + greeting -->
  <text x="14" y="54" font-family="Arial" font-size="9" font-weight="700" fill="#0369a1" letter-spacing="1">🏫 DEMO CRICKET ACADEMY</text>
  <text x="14" y="66" font-family="Arial" font-size="9" font-weight="700" fill="#475569" letter-spacing="2">GOOD SESSION,</text>
  <text x="14" y="86" font-family="Arial" font-size="22" font-weight="900" fill="#0f2744">ALEX JOHNSON</text>
  <text x="14" y="100" font-family="Arial" font-size="11" fill="#475569">Demo Cricket Academy</text>

  <!-- Stats row -->
  <rect x="8" y="108" width="82" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="49" y="132" font-family="Arial" font-size="20" font-weight="900" fill="#0369a1" text-anchor="middle">18</text><text x="49" y="148" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">PLAYERS</text>
  <rect x="99" y="108" width="82" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="140" y="132" font-family="Arial" font-size="20" font-weight="900" fill="#15803d" text-anchor="middle">3.4</text><text x="140" y="148" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">AVG SCORE</text>
  <rect x="190" y="108" width="82" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="231" y="132" font-family="Arial" font-size="20" font-weight="900" fill="#d97706" text-anchor="middle">42</text><text x="231" y="148" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">ASSESSMENTS</text>

  <!-- Classes heading -->
  <text x="14" y="182" font-family="Arial" font-size="14" font-weight="900" fill="#0f2744">CLASSES</text>
  <text x="266" y="182" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="end">Tap a class to view players</text>

  <!-- Class tiles -->
  <rect x="8" y="188" width="126" height="60" rx="12" fill="white" stroke="#0284c740" stroke-width="1.5"/> <text x="20" y="207" font-family="Arial" font-size="8" font-weight="700" fill="#0284c7">🌱 INTRODUCTORY</text><text x="124" y="225" font-family="Arial" font-size="24" font-weight="900" fill="#0284c7" text-anchor="end">6</text><text x="20" y="240" font-family="Arial" font-size="9" fill="#475569">6 players</text><text x="120" y="240" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="end">View →</text>
  <rect x="142" y="188" width="130" height="60" rx="12" fill="white" stroke="#15803d40" stroke-width="1.5"/> <text x="154" y="207" font-family="Arial" font-size="8" font-weight="700" fill="#15803d">⚡ FOUNDATIONAL</text><text x="262" y="225" font-family="Arial" font-size="24" font-weight="900" fill="#15803d" text-anchor="end">8</text><text x="154" y="240" font-family="Arial" font-size="9" fill="#475569">8 players</text><text x="258" y="240" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="end">View →</text>
  <rect x="8" y="254" width="126" height="60" rx="12" fill="white" stroke="#d9770640" stroke-width="1.5"/> <text x="20" y="273" font-family="Arial" font-size="8" font-weight="700" fill="#d97706">🔥 INTERMEDIATE</text><text x="124" y="291" font-family="Arial" font-size="24" font-weight="900" fill="#d97706" text-anchor="end">3</text><text x="20" y="306" font-family="Arial" font-size="9" fill="#475569">3 players</text><text x="120" y="306" font-family="Arial" font-size="9" font-weight="700" fill="#d97706" text-anchor="end">View →</text>
  <rect x="142" y="254" width="130" height="60" rx="12" fill="white" stroke="#dc262640" stroke-width="1.5"/> <text x="154" y="273" font-family="Arial" font-size="8" font-weight="700" fill="#dc2626">🏆 ADVANCED</text><text x="262" y="291" font-family="Arial" font-size="24" font-weight="900" fill="#dc2626" text-anchor="end">1</text><text x="154" y="306" font-family="Arial" font-size="9" fill="#475569">1 player</text><text x="258" y="306" font-family="Arial" font-size="9" font-weight="700" fill="#dc2626" text-anchor="end">View →</text>

  <!-- Recent Players -->
  <text x="14" y="336" font-family="Arial" font-size="14" font-weight="900" fill="#0f2744">RECENT PLAYERS</text>
  <!-- Player rows -->
  <rect x="8" y="342" width="264" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <circle cx="38" cy="368" r="18" fill="#15803d"/><text x="38" y="372" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">SJ</text> <text x="68" y="362" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Sam Johnson</text><rect x="68" y="368" width="88.96" height="17" rx="8" fill="#15803d22"/><text x="112.47999999999999" y="378" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="middle">⚡ Foundational</text><rect x="164.95999999999998" y="368" width="62.98" height="17" rx="8" fill="transparent" stroke="#0284c7" stroke-width="1.2"/><text x="196.45" y="378" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="middle">Batter-RH</text><rect x="8" y="400" width="264" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <circle cx="38" cy="426" r="18" fill="#0284c7"/><text x="38" y="430" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">PT</text> <text x="68" y="420" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Priya Thakur</text><rect x="68" y="426" width="88.96" height="17" rx="8" fill="#0284c722"/><text x="112.47999999999999" y="436" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="middle">🌱 Introductory</text><rect x="164.95999999999998" y="426" width="83.86" height="17" rx="8" fill="transparent" stroke="#f97316" stroke-width="1.2"/><text x="206.89" y="436" font-family="Arial" font-size="9" font-weight="700" fill="#f97316" text-anchor="middle">Bowler-PaceRH</text><rect x="8" y="458" width="264" height="52" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <circle cx="38" cy="484" r="18" fill="#15803d"/><text x="38" y="488" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">MC</text> <text x="68" y="478" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Marcus Chen</text><rect x="68" y="484" width="88.96" height="17" rx="8" fill="#15803d22"/><text x="112.47999999999999" y="494" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="middle">⚡ Foundational</text><rect x="164.95999999999998" y="484" width="68.19999999999999" height="17" rx="8" fill="transparent" stroke="#22c55e" stroke-width="1.2"/><text x="199.05999999999997" y="494" font-family="Arial" font-size="9" font-weight="700" fill="#22c55e" text-anchor="middle">AllRounder</text>
  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">HOME</text><rect x="14" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">ASSESS</text><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><p>Dashboard</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744">Squad</text>
  <!-- Add Player button -->
  <rect x="162" y="40" width="104" height="28" rx="8" fill="#0369a1"/>
  <text x="214" y="59" font-family="Arial" font-size="11" font-weight="700" fill="white" text-anchor="middle">+ Add Player</text>
  <!-- Filters -->
  <rect x="0" y="76" width="280" height="30" fill="#f8fafc"/>
  <rect x="8" y="81" width="48" height="20" rx="10" fill="#0369a1" stroke="#0369a1" stroke-width="1.2"/><text x="32" y="95" font-family="Arial" font-size="9" font-weight="700" fill="white" text-anchor="middle">All</text><rect x="62" y="81" width="48" height="20" rx="10" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="86" y="95" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">🌱 Intro</text><rect x="116" y="81" width="48" height="20" rx="10" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="140" y="95" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">⚡ Found</text><rect x="170" y="81" width="48" height="20" rx="10" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="194" y="95" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">🔥 Inter</text>
  <line x1="0" y1="106" x2="280" y2="106" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="8" y="112" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <circle cx="36" cy="139" r="18" fill="#15803d"/><text x="36" y="143" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">SJ</text>
  <text x="64" y="131" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Sam Johnson</text>
  <rect x="64" y="136" width="88.96" height="17" rx="8" fill="#15803d22"/><text x="108.47999999999999" y="146" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="middle">⚡ Foundational</text>
  <rect x="156.95999999999998" y="136" width="62.98" height="17" rx="8" fill="transparent" stroke="#0284c7" stroke-width="1.2"/><text x="188.45" y="146" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="middle">Batter-RH</text><circle cx="254" cy="139" r="18" fill="none" stroke="#0369a1" stroke-width="2"/><text x="254" y="144" font-family="Arial" font-size="11" font-weight="900" fill="#0369a1" text-anchor="middle">3.8</text>
  <rect x="8" y="174" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <circle cx="36" cy="201" r="18" fill="#0284c7"/><text x="36" y="205" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">PT</text>
  <text x="64" y="193" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Priya Thakur</text>
  <rect x="64" y="198" width="88.96" height="17" rx="8" fill="#0284c722"/><text x="108.47999999999999" y="208" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="middle">🌱 Introductory</text>
  <rect x="156.95999999999998" y="198" width="83.86" height="17" rx="8" fill="transparent" stroke="#f97316" stroke-width="1.2"/><text x="198.89" y="208" font-family="Arial" font-size="9" font-weight="700" fill="#f97316" text-anchor="middle">Bowler-PaceRH</text>
  <rect x="8" y="236" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <circle cx="36" cy="263" r="18" fill="#15803d"/><text x="36" y="267" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">MC</text>
  <text x="64" y="255" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Marcus Chen</text>
  <rect x="64" y="260" width="88.96" height="17" rx="8" fill="#15803d22"/><text x="108.47999999999999" y="270" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="middle">⚡ Foundational</text>
  <rect x="156.95999999999998" y="260" width="68.19999999999999" height="17" rx="8" fill="transparent" stroke="#22c55e" stroke-width="1.2"/><text x="191.05999999999997" y="270" font-family="Arial" font-size="9" font-weight="700" fill="#22c55e" text-anchor="middle">AllRounder</text><circle cx="254" cy="263" r="18" fill="none" stroke="#0369a1" stroke-width="2"/><text x="254" y="268" font-family="Arial" font-size="11" font-weight="900" fill="#0369a1" text-anchor="middle">4.2</text>
  <rect x="8" y="298" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <circle cx="36" cy="325" r="18" fill="#d97706"/><text x="36" y="329" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">AK</text>
  <text x="64" y="317" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Aisha Khan</text>
  <rect x="64" y="322" width="88.96" height="17" rx="8" fill="#d9770622"/><text x="108.47999999999999" y="332" font-family="Arial" font-size="9" font-weight="700" fill="#d97706" text-anchor="middle">🔥 Intermediate</text>
  <rect x="156.95999999999998" y="322" width="62.98" height="17" rx="8" fill="transparent" stroke="#0ea5e9" stroke-width="1.2"/><text x="188.45" y="332" font-family="Arial" font-size="9" font-weight="700" fill="#0ea5e9" text-anchor="middle">Batter-LH</text><circle cx="254" cy="325" r="18" fill="none" stroke="#0369a1" stroke-width="2"/><text x="254" y="330" font-family="Arial" font-size="11" font-weight="900" fill="#0369a1" text-anchor="middle">3.1</text>
  <rect x="8" y="360" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <circle cx="36" cy="387" r="18" fill="#0284c7"/><text x="36" y="391" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">TW</text>
  <text x="64" y="379" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Tom Williams</text>
  <rect x="64" y="384" width="88.96" height="17" rx="8" fill="#0284c722"/><text x="108.47999999999999" y="394" font-family="Arial" font-size="9" font-weight="700" fill="#0284c7" text-anchor="middle">🌱 Introductory</text>
  <rect x="156.95999999999998" y="384" width="78.63999999999999" height="17" rx="8" fill="transparent" stroke="#a855f7" stroke-width="1.2"/><text x="196.27999999999997" y="394" font-family="Arial" font-size="9" font-weight="700" fill="#a855f7" text-anchor="middle">Wicketkeeper</text><rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">SQUAD</text><rect x="70" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">ASSESS</text><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><p>Squad</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744">School View</text>
  <!-- Stats -->
  <rect x="8" y="82" width="82" height="50" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="49" y="106" font-family="Arial" font-size="18" font-weight="900" fill="#0369a1" text-anchor="middle">18</text><text x="49" y="120" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">PLAYERS</text>
  <rect x="99" y="82" width="82" height="50" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="140" y="106" font-family="Arial" font-size="18" font-weight="900" fill="#15803d" text-anchor="middle">3.4</text><text x="140" y="120" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">AVG SCORE</text>
  <rect x="190" y="82" width="82" height="50" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <text x="231" y="106" font-family="Arial" font-size="18" font-weight="900" fill="#d97706" text-anchor="middle">2</text><text x="231" y="120" font-family="Arial" font-size="8" font-weight="700" fill="#475569" text-anchor="middle">COACHES</text>

  <text x="14" y="152" font-family="Arial" font-size="12" font-weight="900" fill="#0f2744">BY CLASS</text>
  <text x="14" y="170" font-family="Arial" font-size="10" font-weight="700" fill="#0284c7">🌱 Introductory</text><rect x="14" y="174" width="220" height="11" rx="5" fill="#e2e8f0"/><rect x="14" y="174" width="72" height="11" rx="5" fill="#0284c7"/><text x="240" y="183" font-family="Arial" font-size="11" font-weight="900" fill="#0284c7">6</text><text x="14" y="206" font-family="Arial" font-size="10" font-weight="700" fill="#15803d">⚡ Foundational</text><rect x="14" y="210" width="220" height="11" rx="5" fill="#e2e8f0"/><rect x="14" y="210" width="96" height="11" rx="5" fill="#15803d"/><text x="240" y="219" font-family="Arial" font-size="11" font-weight="900" fill="#15803d">8</text><text x="14" y="242" font-family="Arial" font-size="10" font-weight="700" fill="#d97706">🔥 Intermediate</text><rect x="14" y="246" width="220" height="11" rx="5" fill="#e2e8f0"/><rect x="14" y="246" width="37" height="11" rx="5" fill="#d97706"/><text x="240" y="255" font-family="Arial" font-size="11" font-weight="900" fill="#d97706">3</text><text x="14" y="278" font-family="Arial" font-size="10" font-weight="700" fill="#dc2626">🏆 Advanced</text><rect x="14" y="282" width="220" height="11" rx="5" fill="#e2e8f0"/><rect x="14" y="282" width="13" height="11" rx="5" fill="#dc2626"/><text x="240" y="291" font-family="Arial" font-size="11" font-weight="900" fill="#dc2626">1</text>

  <text x="14" y="304" font-family="Arial" font-size="12" font-weight="900" fill="#0f2744">BY COACH</text>
  <rect x="8" y="310" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <circle cx="36" cy="337" r="18" fill="#0369a1"/><text x="36" y="341" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">AJ</text> <text x="64" y="330" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Alex Johnson — Head Coach</text><text x="64" y="346" font-family="Arial" font-size="10" fill="#475569">10 players · Foundational avg</text><text x="262" y="340" font-family="Arial" font-size="18" fill="#0369a1" text-anchor="middle">›</text><rect x="8" y="372" width="264" height="54" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <circle cx="36" cy="399" r="18" fill="#15803d"/><text x="36" y="403" font-family="Arial" font-size="12" font-weight="900" fill="white" text-anchor="middle">SR</text> <text x="64" y="392" font-family="Arial" font-size="12" font-weight="700" fill="#0f2744">Sara Reed — Junior Coach</text><text x="64" y="408" font-family="Arial" font-size="10" fill="#475569">8 players · Introductory avg</text><text x="262" y="402" font-family="Arial" font-size="18" fill="#0369a1" text-anchor="middle">›</text>
  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">SCHOOL</text><rect x="126" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="196" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">ASSESS</text><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><p>School</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Back link -->
  <text x="14" y="50" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1">← Squad</text>
  <text x="14" y="72" font-family="Arial" font-size="20" font-weight="900" fill="#0369a1">➕ Add New Player</text>
  <text x="14" y="88" font-family="Arial" font-size="11" fill="#475569">Player can log in with their email and password</text>
  <line x1="0" y1="96" x2="280" y2="96" stroke="#e2e8f0" stroke-width="1"/>

  <!-- Fields -->
  <text x="14" y="116" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">FULL NAME *</text>
  <rect x="14" y="120" width="252" height="34" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="141" font-family="Arial" font-size="12" fill="#94a3b8">e.g. Sam Williams</text>

  <text x="14" y="166" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">EMAIL ADDRESS *</text>
  <rect x="14" y="170" width="252" height="34" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="191" font-family="Arial" font-size="12" fill="#94a3b8">player@school.edu</text>

  <text x="14" y="216" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">PASSWORD * (MIN 6 CHARACTERS)</text>
  <rect x="14" y="220" width="252" height="34" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="241" font-family="Arial" font-size="14" fill="#94a3b8" letter-spacing="3">••••••••</text>

  <text x="14" y="266" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">ASSIGN TO COACH *</text>
  <rect x="14" y="270" width="252" height="34" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="291" font-family="Arial" font-size="12" fill="#0a0f1a">Alex Johnson — Head Coach</text>
  <text x="256" y="291" font-family="Arial" font-size="14" fill="#475569" text-anchor="middle">⌄</text>

  <text x="14" y="316" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">CRICKET CLASS LEVEL</text>
  <rect x="14" y="320" width="123" height="32" rx="8" fill="#0284c7"/>
  <text x="75" y="340" font-family="Arial" font-size="11" font-weight="700" fill="white" text-anchor="middle">🌱 Introductory</text>
  <rect x="141" y="320" width="123" height="32" rx="8" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="202" y="340" font-family="Arial" font-size="11" font-weight="700" fill="#475569" text-anchor="middle">⚡ Foundational</text>
  <rect x="14" y="358" width="123" height="32" rx="8" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="75" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#475569" text-anchor="middle">🔥 Intermediate</text>
  <rect x="141" y="358" width="123" height="32" rx="8" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="202" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#475569" text-anchor="middle">🏆 Advanced</text>

  <!-- Buttons -->
  <rect x="14" y="408" width="82" height="38" rx="8" fill="white" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="55" y="432" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">Cancel</text>
  <rect x="100" y="408" width="164" height="38" rx="8" fill="#0369a1"/>
  <text x="182" y="432" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">✅ Add Player</text>

  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">SQUAD</text><rect x="70" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">ASSESS</text><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><p>Add Player</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744">Input Tools</text>
  <!-- Tool icons row -->
  <circle cx="40" cy="82" r="22" fill="#0369a1" stroke="none" stroke-width="1.5"/><text x="40" y="79" font-family="Arial" font-size="14" text-anchor="middle">📋</text><text x="40" y="93" font-family="Arial" font-size="7" font-weight="700" fill="white" text-anchor="middle">Session</text><circle cx="92" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="92" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="92" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Group</text><circle cx="144" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="144" y="79" font-family="Arial" font-size="14" text-anchor="middle">📅</text><text x="144" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Sess. Log</text><circle cx="196" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="196" y="79" font-family="Arial" font-size="14" text-anchor="middle">🎯</text><text x="196" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Goal</text><circle cx="248" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="248" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="248" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Squad</text>

  <line x1="0" y1="112" x2="280" y2="112" stroke="#e2e8f0" stroke-width="1"/>

  <!-- Session form -->
  <rect x="8" y="118" width="264" height="260" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="140" font-family="Arial" font-size="11" font-weight="900" fill="#0369a1">📋 LOG SESSION</text>

  <text x="22" y="162" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">PLAYER</text>
  <rect x="22" y="166" width="236" height="30" rx="6" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.2"/>
  <text x="32" y="185" font-family="Arial" font-size="11" fill="#0a0f1a">Sam Johnson — Foundational</text>
  <text x="252" y="185" font-family="Arial" font-size="12" fill="#475569" text-anchor="end">⌄</text>

  <text x="22" y="210" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">TYPE</text>
  <rect x="22" y="214" width="40" height="24" rx="12" fill="#0369a1" stroke="#0369a1" stroke-width="1.2"/><text x="42" y="230" font-family="Arial" font-size="9" font-weight="700" fill="white" text-anchor="middle">Batting</text><rect x="66" y="214" width="40" height="24" rx="12" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="86" y="230" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Bowl</text><rect x="110" y="214" width="40" height="24" rx="12" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="130" y="230" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Field</text><rect x="154" y="214" width="40" height="24" rx="12" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="174" y="230" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Match</text><rect x="198" y="214" width="40" height="24" rx="12" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="218" y="230" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Fit</text>

  <text x="22" y="254" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">DURATION</text>
  <rect x="22" y="258" width="236" height="30" rx="6" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.2"/>
  <text x="32" y="277" font-family="Arial" font-size="11" fill="#0a0f1a">60 min</text>

  <text x="22" y="302" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">NOTES</text>
  <rect x="22" y="306" width="236" height="52" rx="6" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.2"/>
  <text x="32" y="326" font-family="Arial" font-size="11" fill="#94a3b8">What did you work on?</text>

  <rect x="22" y="366" width="236" height="36" rx="8" fill="#0369a1"/>
  <text x="140" y="389" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">📋 Log Session</text>

  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">ASSESS</text><rect x="182" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><p>Session</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744">Input Tools</text>
  <circle cx="40" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="40" y="79" font-family="Arial" font-size="14" text-anchor="middle">📋</text><text x="40" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Session</text><circle cx="92" cy="82" r="22" fill="#0369a1" stroke="none" stroke-width="1.5"/><text x="92" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="92" y="93" font-family="Arial" font-size="7" font-weight="700" fill="white" text-anchor="middle">Group</text><circle cx="144" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="144" y="79" font-family="Arial" font-size="14" text-anchor="middle">📅</text><text x="144" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Sess Log</text><circle cx="196" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="196" y="79" font-family="Arial" font-size="14" text-anchor="middle">🎯</text><text x="196" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Goal</text><circle cx="248" cy="82" r="22" fill="#e8edf5" stroke="#e2e8f0" stroke-width="1.5"/><text x="248" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="248" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Squad</text>
  <line x1="0" y1="112" x2="280" y2="112" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="8" y="118" width="264" height="322" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="140" font-family="Arial" font-size="11" font-weight="900" fill="#0369a1">👥 GROUP SESSION</text>
  <text x="22" y="154" font-family="Arial" font-size="9" fill="#475569" style="font-style:italic">Select players, enter shared notes. Same session logged for each.</text>

  <text x="22" y="174" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">SESSION TYPE</text>
  <rect x="22" y="178" width="42" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="43" y="193" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Batting</text><rect x="68" y="178" width="42" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="89" y="193" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Bowl</text><rect x="114" y="178" width="42" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="135" y="193" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Field</text><rect x="160" y="178" width="42" height="22" rx="11" fill="#d97706" stroke="#d97706" stroke-width="1.2"/><text x="181" y="193" font-family="Arial" font-size="9" font-weight="700" fill="white" text-anchor="middle">Match</text>

  <text x="22" y="216" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">SELECT PLAYERS</text>
  <rect x="22" y="220" width="70" height="22" rx="6" fill="#0369a1"/><text x="57" y="235" font-family="Arial" font-size="10" font-weight="700" fill="white" text-anchor="middle">Select All</text>
  <rect x="100" y="220" width="50" height="22" rx="6" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="125" y="235" font-family="Arial" font-size="10" fill="#475569" text-anchor="middle">Clear</text>
  <rect x="22" y="248" width="236" height="32" rx="6" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="28" y="256" width="16" height="16" rx="3" fill="#15803d22" stroke="#15803d" stroke-width="1.5"/>
  <text x="36" y="268" font-family="Arial" font-size="10" font-weight="900" fill="#15803d" text-anchor="middle">✓</text>
  <circle cx="66" cy="264" r="12" fill="#15803d"/><text x="66" y="268" font-family="Arial" font-size="8" font-weight="900" fill="white" text-anchor="middle">SJ</text>
  <text x="84" y="262" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Sam Johnson</text>
  <text x="84" y="275" font-family="Arial" font-size="9" fill="#475569">Foundational</text>
  <rect x="22" y="284" width="236" height="32" rx="6" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="28" y="292" width="16" height="16" rx="3" fill="#0284c722" stroke="#0284c7" stroke-width="1.5"/>
  <text x="36" y="304" font-family="Arial" font-size="10" font-weight="900" fill="#0284c7" text-anchor="middle">✓</text>
  <circle cx="66" cy="300" r="12" fill="#0284c7"/><text x="66" y="304" font-family="Arial" font-size="8" font-weight="900" fill="white" text-anchor="middle">PT</text>
  <text x="84" y="298" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Priya Thakur</text>
  <text x="84" y="311" font-family="Arial" font-size="9" fill="#475569">Introductory</text>
  <rect x="22" y="320" width="236" height="32" rx="6" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="28" y="328" width="16" height="16" rx="3" fill="#15803d22" stroke="#15803d" stroke-width="1.5"/>
  <text x="36" y="340" font-family="Arial" font-size="10" font-weight="900" fill="#15803d" text-anchor="middle">✓</text>
  <circle cx="66" cy="336" r="12" fill="#15803d"/><text x="66" y="340" font-family="Arial" font-size="8" font-weight="900" fill="white" text-anchor="middle">MC</text>
  <text x="84" y="334" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Marcus Chen</text>
  <text x="84" y="347" font-family="Arial" font-size="9" fill="#475569">Foundational</text>
  <rect x="22" y="356" width="236" height="32" rx="6" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="28" y="364" width="16" height="16" rx="3" fill="#d9770622" stroke="#d97706" stroke-width="1.5"/>
  <text x="36" y="376" font-family="Arial" font-size="10" font-weight="900" fill="#d97706" text-anchor="middle">✓</text>
  <circle cx="66" cy="372" r="12" fill="#d97706"/><text x="66" y="376" font-family="Arial" font-size="8" font-weight="900" fill="white" text-anchor="middle">AK</text>
  <text x="84" y="370" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Aisha Khan</text>
  <text x="84" y="383" font-family="Arial" font-size="9" fill="#475569">Intermediate</text>
  <rect x="22" y="392" width="236" height="32" rx="6" fill="#f8fafc" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="28" y="400" width="16" height="16" rx="3" fill="#0284c722" stroke="#0284c7" stroke-width="1.5"/>
  <text x="36" y="412" font-family="Arial" font-size="10" font-weight="900" fill="#0284c7" text-anchor="middle">✓</text>
  <circle cx="66" cy="408" r="12" fill="#0284c7"/><text x="66" y="412" font-family="Arial" font-size="8" font-weight="900" fill="white" text-anchor="middle">TW</text>
  <text x="84" y="406" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Tom Williams</text>
  <text x="84" y="419" font-family="Arial" font-size="9" fill="#475569">Introductory</text>
  <rect x="22" y="434" width="236" height="36" rx="8" fill="#0369a1"/>
  <text x="140" y="457" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">Log Group Session</text>
  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">ASSESS</text><rect x="182" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><p>Group Session</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/><text x="14" y="61" font-family="Arial" font-size="18" font-weight="900" fill="#0f2744">Input Tools</text>
  <circle cx="40" cy="82" r="22" fill="#e8edf5"/><text x="40" y="79" font-family="Arial" font-size="14" text-anchor="middle">📋</text><text x="40" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Session</text><circle cx="92" cy="82" r="22" fill="#e8edf5"/><text x="92" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="92" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Group</text><circle cx="144" cy="82" r="22" fill="#e8edf5"/><text x="144" y="79" font-family="Arial" font-size="14" text-anchor="middle">📅</text><text x="144" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Sess Log</text><circle cx="196" cy="82" r="22" fill="#e8edf5"/><text x="196" y="79" font-family="Arial" font-size="14" text-anchor="middle">🎯</text><text x="196" y="93" font-family="Arial" font-size="7" font-weight="700" fill="#475569" text-anchor="middle">Goal</text><circle cx="248" cy="82" r="22" fill="#0369a1"/><text x="248" y="79" font-family="Arial" font-size="14" text-anchor="middle">👥</text><text x="248" y="93" font-family="Arial" font-size="7" font-weight="700" fill="white" text-anchor="middle">Squad</text>
  <line x1="0" y1="112" x2="280" y2="112" stroke="#e2e8f0" stroke-width="1"/>
  <rect x="8" y="118" width="264" height="332" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="140" font-family="Arial" font-size="11" font-weight="900" fill="#15803d">🎯 👥 SQUAD GOAL</text>
  <text x="22" y="154" font-family="Arial" font-size="9" fill="#475569">Set one shared goal for multiple players at once.</text>

  <text x="22" y="174" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">GOAL TITLE</text>
  <rect x="22" y="178" width="236" height="30" rx="6" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.2"/>
  <text x="32" y="197" font-family="Arial" font-size="11" fill="#94a3b8">e.g. Improve batting strike rate to 80+</text>

  <text x="22" y="222" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">CATEGORY</text>
  <rect x="22" y="226" width="46" height="22" rx="11" fill="#0369a1" stroke="#0369a1" stroke-width="1.2"/><text x="45" y="241" font-family="Arial" font-size="9" font-weight="700" fill="white" text-anchor="middle">Batting</text><rect x="72" y="226" width="46" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="95" y="241" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Bowling</text><rect x="122" y="226" width="46" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="145" y="241" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Field</text><rect x="172" y="226" width="46" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="195" y="241" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Mental</text><rect x="222" y="226" width="46" height="22" rx="11" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/><text x="245" y="241" font-family="Arial" font-size="9" font-weight="700" fill="#475569" text-anchor="middle">Phys</text>

  <text x="22" y="264" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">TARGET DATE</text>
  <rect x="22" y="268" width="236" height="30" rx="6" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.2"/>
  <text x="32" y="287" font-family="Arial" font-size="11" fill="#94a3b8">Select date…</text>

  <text x="22" y="312" font-family="Arial" font-size="9" font-weight="800" fill="#0a0f1a" letter-spacing="0.5">SELECT PLAYERS</text>
  <rect x="22" y="318" width="236" height="28" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <rect x="28" y="322" width="14" height="14" rx="3" fill="#15803d22" stroke="#15803d" stroke-width="1.2"/> <circle cx="58" cy="332" r="11" fill="#15803d"/><text x="58" y="336" font-family="Arial" font-size="7" font-weight="900" fill="white" text-anchor="middle">SJ</text> <text x="76" y="336" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Sam Johnson</text><text x="76" y="346" font-family="Arial" font-size="9" fill="#475569">Foundational</text><rect x="22" y="352" width="236" height="28" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <rect x="28" y="356" width="14" height="14" rx="3" fill="#0284c722" stroke="#0284c7" stroke-width="1.2"/> <circle cx="58" cy="366" r="11" fill="#0284c7"/><text x="58" y="370" font-family="Arial" font-size="7" font-weight="900" fill="white" text-anchor="middle">PT</text> <text x="76" y="370" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Priya Thakur</text><text x="76" y="380" font-family="Arial" font-size="9" fill="#475569">Introductory</text><rect x="22" y="386" width="236" height="28" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/> <rect x="28" y="390" width="14" height="14" rx="3" fill="#15803d22" stroke="#15803d" stroke-width="1.2"/> <circle cx="58" cy="400" r="11" fill="#15803d"/><text x="58" y="404" font-family="Arial" font-size="7" font-weight="900" fill="white" text-anchor="middle">MC</text> <text x="76" y="404" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Marcus Chen</text><text x="76" y="414" font-family="Arial" font-size="9" fill="#475569">Foundational</text>

  <rect x="22" y="428" width="236" height="36" rx="8" fill="#15803d"/>
  <text x="140" y="451" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">Add Squad Goal</text>
  <rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#0369a1" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#0369a1" text-anchor="middle">ASSESS</text><rect x="182" y="497" width="28" height="3" rx="1.5" fill="#0369a1"/><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><p>Goals</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="white"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="white"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Logo area -->
  <rect x="0" y="32" width="280" height="160" fill="#0f2744"/>
  <rect x="0" y="32" width="280" height="160" fill="url(#grad1)"/>
  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f2744"/><stop offset="1" stop-color="#1a4a7a"/></linearGradient></defs>
  <!-- Starburst lines -->
  <line x1="140" y1="112" x2="260" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="237" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="177" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="104" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="44" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="21" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="43" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="103" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="176" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="236" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/>
  <!-- Logo text -->
  <text x="140" y="96" font-family="Arial" font-size="30" font-weight="900" fill="white" text-anchor="middle">Crickit</text>
  <text x="140" y="128" font-family="Arial" font-size="30" font-weight="900" fill="#F59E0B" text-anchor="middle">Coach</text>
  <!-- Cricket bat icon centred -->
  <text x="140" y="162" font-family="Arial" font-size="26" text-anchor="middle">🏏</text>

  <!-- Toggle -->
  <rect x="14" y="202" width="252" height="38" rx="10" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <rect x="16" y="204" width="124" height="34" rx="8" fill="#0369a1"/>
  <text x="78" y="226" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">🎓 Coach / Admin</text>
  <text x="202" y="226" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">🏏 Player</text>

  <!-- Email field -->
  <text x="14" y="258" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">📧 EMAIL ADDRESS</text>
  <rect x="14" y="264" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="287" font-family="Arial" font-size="12" fill="#94a3b8">coach@academy.com</text>

  <!-- Password field -->
  <text x="14" y="318" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">🔒 PASSWORD</text>
  <rect x="14" y="324" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="347" font-family="Arial" font-size="16" fill="#94a3b8" letter-spacing="4">••••••••</text>

  <!-- Forgot -->
  <text x="266" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1" text-anchor="end">Forgot password?</text>

  <!-- Sign In button -->
  <rect x="14" y="388" width="252" height="42" rx="10" fill="#0369a1"/>
  <text x="140" y="414" font-family="Arial" font-size="13" font-weight="700" fill="white" text-anchor="middle">Sign In as Coach / Admin</text>

  <!-- User guide link -->
  <text x="140" y="446" font-family="Arial" font-size="11" font-weight="600" fill="#0369a1" text-anchor="middle">📖 User Guide — How it works</text>

  <!-- Register -->
  <line x1="14" y1="460" x2="266" y2="460" stroke="#e2e8f0" stroke-width="1"/>
  <text x="140" y="478" font-family="Arial" font-size="11" fill="#475569" text-anchor="middle">New to CrickitCoach?</text>
  <rect x="14" y="486" width="252" height="36" rx="8" fill="transparent" stroke="#0369a1" stroke-width="1.5"/>
  <text x="140" y="509" font-family="Arial" font-size="12" font-weight="700" fill="#0369a1" text-anchor="middle">🏫 Create New School Account</text>

  <text x="140" y="540" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="middle">support@crickitcoach.com · © 2026 ArcGain LLC</text>
</svg><p>Assessment</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="white"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="white"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Logo area -->
  <rect x="0" y="32" width="280" height="160" fill="#0f2744"/>
  <rect x="0" y="32" width="280" height="160" fill="url(#grad1)"/>
  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f2744"/><stop offset="1" stop-color="#1a4a7a"/></linearGradient></defs>
  <!-- Starburst lines -->
  <line x1="140" y1="112" x2="260" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="237" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="177" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="104" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="44" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="21" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="43" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="103" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="176" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="236" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/>
  <!-- Logo text -->
  <text x="140" y="96" font-family="Arial" font-size="30" font-weight="900" fill="white" text-anchor="middle">Crickit</text>
  <text x="140" y="128" font-family="Arial" font-size="30" font-weight="900" fill="#F59E0B" text-anchor="middle">Coach</text>
  <!-- Cricket bat icon centred -->
  <text x="140" y="162" font-family="Arial" font-size="26" text-anchor="middle">🏏</text>

  <!-- Toggle -->
  <rect x="14" y="202" width="252" height="38" rx="10" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <rect x="16" y="204" width="124" height="34" rx="8" fill="#0369a1"/>
  <text x="78" y="226" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">🎓 Coach / Admin</text>
  <text x="202" y="226" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">🏏 Player</text>

  <!-- Email field -->
  <text x="14" y="258" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">📧 EMAIL ADDRESS</text>
  <rect x="14" y="264" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="287" font-family="Arial" font-size="12" fill="#94a3b8">coach@academy.com</text>

  <!-- Password field -->
  <text x="14" y="318" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">🔒 PASSWORD</text>
  <rect x="14" y="324" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="347" font-family="Arial" font-size="16" fill="#94a3b8" letter-spacing="4">••••••••</text>

  <!-- Forgot -->
  <text x="266" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1" text-anchor="end">Forgot password?</text>

  <!-- Sign In button -->
  <rect x="14" y="388" width="252" height="42" rx="10" fill="#0369a1"/>
  <text x="140" y="414" font-family="Arial" font-size="13" font-weight="700" fill="white" text-anchor="middle">Sign In as Coach / Admin</text>

  <!-- User guide link -->
  <text x="140" y="446" font-family="Arial" font-size="11" font-weight="600" fill="#0369a1" text-anchor="middle">📖 User Guide — How it works</text>

  <!-- Register -->
  <line x1="14" y1="460" x2="266" y2="460" stroke="#e2e8f0" stroke-width="1"/>
  <text x="140" y="478" font-family="Arial" font-size="11" fill="#475569" text-anchor="middle">New to CrickitCoach?</text>
  <rect x="14" y="486" width="252" height="36" rx="8" fill="transparent" stroke="#0369a1" stroke-width="1.5"/>
  <text x="140" y="509" font-family="Arial" font-size="12" font-weight="700" fill="#0369a1" text-anchor="middle">🏫 Create New School Account</text>

  <text x="140" y="540" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="middle">support@crickitcoach.com · © 2026 ArcGain LLC</text>
</svg><p>Progress</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="white"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="white"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <!-- Logo area -->
  <rect x="0" y="32" width="280" height="160" fill="#0f2744"/>
  <rect x="0" y="32" width="280" height="160" fill="url(#grad1)"/>
  <defs><linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0f2744"/><stop offset="1" stop-color="#1a4a7a"/></linearGradient></defs>
  <!-- Starburst lines -->
  <line x1="140" y1="112" x2="260" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="237" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="177" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="104" y2="226" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="44" y2="182" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="21" y2="112" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="43" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="103" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="176" y2="-2" stroke="rgba(255,255,255,0.06)" stroke-width="2"/><line x1="140" y1="112" x2="236" y2="42" stroke="rgba(255,255,255,0.06)" stroke-width="2"/>
  <!-- Logo text -->
  <text x="140" y="96" font-family="Arial" font-size="30" font-weight="900" fill="white" text-anchor="middle">Crickit</text>
  <text x="140" y="128" font-family="Arial" font-size="30" font-weight="900" fill="#F59E0B" text-anchor="middle">Coach</text>
  <!-- Cricket bat icon centred -->
  <text x="140" y="162" font-family="Arial" font-size="26" text-anchor="middle">🏏</text>

  <!-- Toggle -->
  <rect x="14" y="202" width="252" height="38" rx="10" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <rect x="16" y="204" width="124" height="34" rx="8" fill="#0369a1"/>
  <text x="78" y="226" font-family="Arial" font-size="12" font-weight="700" fill="white" text-anchor="middle">🎓 Coach / Admin</text>
  <text x="202" y="226" font-family="Arial" font-size="12" font-weight="700" fill="#475569" text-anchor="middle">🏏 Player</text>

  <!-- Email field -->
  <text x="14" y="258" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">📧 EMAIL ADDRESS</text>
  <rect x="14" y="264" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="287" font-family="Arial" font-size="12" fill="#94a3b8">coach@academy.com</text>

  <!-- Password field -->
  <text x="14" y="318" font-family="Arial" font-size="10" font-weight="800" fill="#0a0f1a">🔒 PASSWORD</text>
  <rect x="14" y="324" width="252" height="38" rx="8" fill="#e8edf5" stroke="#94a3b8" stroke-width="1.5"/>
  <text x="24" y="347" font-family="Arial" font-size="16" fill="#94a3b8" letter-spacing="4">••••••••</text>

  <!-- Forgot -->
  <text x="266" y="378" font-family="Arial" font-size="11" font-weight="700" fill="#0369a1" text-anchor="end">Forgot password?</text>

  <!-- Sign In button -->
  <rect x="14" y="388" width="252" height="42" rx="10" fill="#0369a1"/>
  <text x="140" y="414" font-family="Arial" font-size="13" font-weight="700" fill="white" text-anchor="middle">Sign In as Coach / Admin</text>

  <!-- User guide link -->
  <text x="140" y="446" font-family="Arial" font-size="11" font-weight="600" fill="#0369a1" text-anchor="middle">📖 User Guide — How it works</text>

  <!-- Register -->
  <line x1="14" y1="460" x2="266" y2="460" stroke="#e2e8f0" stroke-width="1"/>
  <text x="140" y="478" font-family="Arial" font-size="11" fill="#475569" text-anchor="middle">New to CrickitCoach?</text>
  <rect x="14" y="486" width="252" height="36" rx="8" fill="transparent" stroke="#0369a1" stroke-width="1.5"/>
  <text x="140" y="509" font-family="Arial" font-size="12" font-weight="700" fill="#0369a1" text-anchor="middle">🏫 Create New School Account</text>

  <text x="140" y="540" font-family="Arial" font-size="9" fill="#94a3b8" text-anchor="middle">support@crickitcoach.com · © 2026 ArcGain LLC</text>
</svg><p>Report</p></div>
    <div class="gal-cell"><svg style="width:100%;border-radius:14px;border:3px solid rgba(255,255,255,.12);display:block;overflow:hidden" viewBox="0 0 280 560" xmlns="http://www.w3.org/2000/svg">
  <rect width="280" height="560" fill="#f0f4f8"/>
  <!-- status bar -->
  <rect width="280" height="32" fill="#f0f4f8"/>
  <text x="14" y="22" font-family="Arial" font-size="12" font-weight="700" fill="#0a0f1a">9:41</text>
  <rect x="224" y="10" width="18" height="10" rx="2" fill="none" stroke="#0a0f1a" stroke-width="1.5"/>
  <rect x="225" y="11" width="13" height="8" rx="1" fill="#0a0f1a"/>
  <text x="246" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">▲</text>
  <text x="260" y="21" font-family="Arial" font-size="10" fill="#0a0f1a">●</text>
  
  <rect x="0" y="32" width="280" height="44" fill="white"/>
  <text x="14" y="50" font-family="Arial" font-size="10" font-weight="700" fill="#0369a1">← Sam Johnson</text>
  <!-- Sub tabs -->
  <rect x="0" y="56" width="280" height="32" fill="#f8fafc"/>
  <rect x="8" y="60" width="80" height="24" rx="12" fill="#0369a1"/>
  <text x="48" y="76" font-family="Arial" font-size="10" font-weight="700" fill="white" text-anchor="middle">📋 Sessions</text>
  <rect x="96" y="60" width="70" height="24" rx="12" fill="transparent" stroke="#e2e8f0" stroke-width="1.2"/>
  <text x="131" y="76" font-family="Arial" font-size="10" font-weight="700" fill="#475569" text-anchor="middle">📄 Reports</text>
  <line x1="0" y1="88" x2="280" y2="88" stroke="#e2e8f0" stroke-width="1"/>
  <text x="14" y="106" font-family="Arial" font-size="11" font-weight="700" fill="#0f2744">Sessions · Most recent first</text>
  <text x="266" y="106" font-family="Arial" font-size="10" fill="#94a3b8" text-anchor="end">12 total</text>
  <rect x="8" y="114" width="264" height="76" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="130" font-family="Arial" font-size="9" fill="#94a3b8">Today</text>
  <rect x="22" y="136" width="63" height="16" rx="8" fill="#0369a122"/>
  <text x="53.5" y="148" font-family="Arial" font-size="9" font-weight="700" fill="#0369a1" text-anchor="middle">Batting</text>
  <text x="93" y="148" font-family="Arial" font-size="9" fill="#94a3b8">· 60 min</text>
  <text x="22" y="166" font-family="Arial" font-size="10" fill="#1e293b">Worked on driving through covers. Good session.</text>
  <line x1="22" y1="180" x2="258" y2="180" stroke="#f0f4f8" stroke-width="1"/>
  <rect x="8" y="196" width="264" height="76" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="212" font-family="Arial" font-size="9" fill="#94a3b8">2 days ago</text>
  <rect x="22" y="218" width="49" height="16" rx="8" fill="#dc262622"/>
  <text x="46.5" y="230" font-family="Arial" font-size="9" font-weight="700" fill="#dc2626" text-anchor="middle">Match</text>
  <text x="79" y="230" font-family="Arial" font-size="9" fill="#94a3b8">· 90 min</text>
  <text x="22" y="248" font-family="Arial" font-size="10" fill="#1e293b">Strong batting performance. Needs to work on run…</text>
  <line x1="22" y1="262" x2="258" y2="262" stroke="#f0f4f8" stroke-width="1"/>
  <rect x="8" y="278" width="264" height="76" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="294" font-family="Arial" font-size="9" fill="#94a3b8">5 days ago</text>
  <rect x="22" y="300" width="70" height="16" rx="8" fill="#15803d22"/>
  <text x="57.0" y="312" font-family="Arial" font-size="9" font-weight="700" fill="#15803d" text-anchor="middle">Fielding</text>
  <text x="100" y="312" font-family="Arial" font-size="9" fill="#94a3b8">· 45 min</text>
  <text x="22" y="330" font-family="Arial" font-size="10" fill="#1e293b">Ground fielding drills and catching practice.</text>
  <line x1="22" y1="344" x2="258" y2="344" stroke="#f0f4f8" stroke-width="1"/>
  <rect x="8" y="360" width="264" height="76" rx="12" fill="white" stroke="#e2e8f0" stroke-width="1.5"/>
  <text x="22" y="376" font-family="Arial" font-size="9" fill="#94a3b8">1 week ago</text>
  <rect x="22" y="382" width="63" height="16" rx="8" fill="#d9770622"/>
  <text x="53.5" y="394" font-family="Arial" font-size="9" font-weight="700" fill="#d97706" text-anchor="middle">Bowling</text>
  <text x="93" y="394" font-family="Arial" font-size="9" fill="#94a3b8">· 60 min</text>
  <text x="22" y="412" font-family="Arial" font-size="10" fill="#1e293b">Pace bowling action and follow-through work.</text>
  <line x1="22" y1="426" x2="258" y2="426" stroke="#f0f4f8" stroke-width="1"/><rect x="0" y="498" width="280" height="62" fill="white"/><line x1="0" y1="498" x2="280" y2="498" stroke="#e2e8f0" stroke-width="1"/><text x="28" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏠</text><text x="28" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">HOME</text><text x="84" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">👥</text><text x="84" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SQUAD</text><text x="140" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">🏫</text><text x="140" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SCHOOL</text><text x="196" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">📊</text><text x="196" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">ASSESS</text><text x="252" y="520" font-family="Arial" font-size="16" fill="#94a3b8" text-anchor="middle">⚙️</text><text x="252" y="536" font-family="Arial" font-size="7" font-weight="700" fill="#94a3b8" text-anchor="middle">SET</text>
</svg><p>History</p></div>
  </div>
</div>

<footer>
  <div class="fw">Crickit<em>Coach</em></div>
  <a href="https://www.crickitcoach.com" target="_blank">www.crickitcoach.com</a>
  <a href="mailto:support@crickitcoach.com">support@crickitcoach.com</a>
  <div class="copy">© 2026 ArcGain LLC · All rights reserved</div>
</footer>

<script>
/* Scroll to tabs on label click — non-critical enhancement, won't block iOS */
document.querySelectorAll('.tab').forEach(function(lbl){
  lbl.addEventListener('click',function(){
    var nav=document.querySelector('.tabs');
    if(nav){
      var top=nav.getBoundingClientRect().top+window.scrollY-56;
      window.scrollTo({top:top,behavior:'smooth'});
    }
  },{passive:true});
});
<\/script>
</body>
</html>`;

function openGuide(){
  const w = window.open('','_blank');
  if(w){
    w.document.open();
    w.document.write(GUIDE_HTML);
    w.document.close();
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// PLAYER SELECT SCREEN — shown after login when a parent has multiple children
// ═══════════════════════════════════════════════════════════════════════════════
function PlayerSelectScreen({players,schoolName,onSelect,onLogout}){
  return(
    <div style={{height:"100svh",overflowY:"auto",background:"var(--bg)",maxWidth:440,margin:"0 auto",
      padding:"env(safe-area-inset-top,0px) 0 0"}}>
      {/* Header */}
      <div style={{background:"linear-gradient(160deg,#0B1F3A 0%,#0d2a4a 100%)",padding:"36px 24px 28px",textAlign:"center"}}>
        <div style={{fontFamily:"var(--font-display)",fontSize:32,color:"#fff",letterSpacing:1}}>
          CRICKIT<span style={{color:"#F59E0B"}}>COACH</span>
        </div>
        <div style={{fontSize:12,color:"rgba(255,255,255,0.5)",marginTop:6,letterSpacing:1,textTransform:"uppercase"}}>
          {schoolName||"Youth Cricket Coaching"}
        </div>
      </div>
      <div style={{padding:"28px 20px"}}>
        <div style={{fontSize:22,fontWeight:800,color:"var(--navy,#0B1F3A)",marginBottom:6}}>
          Welcome back! 👋
        </div>
        <div style={{fontSize:14,color:"var(--text3)",marginBottom:24,lineHeight:1.6}}>
          Select which player you would like to view:
        </div>
        {players.map(p=>(
          <button key={p.id} onClick={()=>onSelect(p)}
            style={{width:"100%",display:"flex",alignItems:"center",gap:14,
              padding:"16px",background:"#fff",borderRadius:14,marginBottom:12,
              border:"1.5px solid var(--border)",cursor:"pointer",textAlign:"left",
              boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
            <div style={{width:48,height:48,borderRadius:"50%",flexShrink:0,
              background:"linear-gradient(135deg,var(--accent,#0369a1),#003d5c)",
              display:"flex",alignItems:"center",justifyContent:"center",
              fontSize:18,fontWeight:900,color:"#fff",fontFamily:"var(--font-display)"}}>
              {p.avatar}
            </div>
            <div style={{flex:1}}>
              <div style={{fontWeight:800,fontSize:16,color:"var(--navy,#0B1F3A)"}}>{p.name}</div>
              <div style={{fontSize:12,color:"var(--text3)",marginTop:2}}>
                {p.cricketClass||"Cricket Player"}
                {p.age?` · Age ${p.age}`:""}
              </div>
            </div>
            <div style={{fontSize:20,color:"var(--accent,#0369a1)"}}>›</div>
          </button>
        ))}
        <button onClick={onLogout}
          style={{width:"100%",marginTop:8,padding:"12px",background:"transparent",
            border:"1.5px solid var(--border)",borderRadius:10,fontSize:13,
            color:"var(--text3)",cursor:"pointer",fontWeight:600}}>
          Sign out
        </button>
      </div>
    </div>
  );
}

function LoginScreen({onLogin,onShowRegister,onForgotPassword,loginMode,setLoginMode}){
  const [email,setEmail]=useState(""); const [pw,setPw]=useState(""); const [err,setErr]=useState(""); const [loading,setLoading]=useState(false);
  const handle=async()=>{
    setErr("");setLoading(true);
    const result=await onLogin(email.trim(),pw);
    if(result==="inactive") setErr("This account has been deactivated. Please contact your school admin.");
    else if(result==="suspended") setErr("This school's account has been suspended. Please contact support@crickitcoach.com to resolve.");
    else if(result==="trial_expired") setErr("Your free trial has ended. Please contact support@crickitcoach.com to activate a subscription.");
    else if(!result) setErr("Incorrect email or password. Please try again.");
    setLoading(false);
  };
  return(
    <div style={{height:"100svh",paddingTop:"env(safe-area-inset-top,0px)",overflowY:"auto",overflowX:"hidden",WebkitOverflowScrolling:"touch",background:"var(--bg)",maxWidth:440,margin:"0 auto"}}>

      {/* ── Header: navy gradient, wordmark, bat icon ── */}
      <div style={{background:"linear-gradient(160deg,#0B1F3A 0%,#0d2a4a 55%,#112244 100%)",padding:"44px 24px 36px",textAlign:"center"}}>
        {/* Wordmark */}
        <div style={{fontFamily:"var(--font-display)",fontSize:38,letterSpacing:2,lineHeight:1,marginBottom:4}}>
          <span style={{color:"#ffffff"}}>CRICKIT</span>
          <span style={{color:"#F59E0B"}}>COACH</span>
        </div>
        {/* Tagline */}
        <div style={{fontSize:11,fontWeight:700,letterSpacing:2,textTransform:"uppercase",color:"rgba(255,255,255,0.45)",marginBottom:18}}>
          Youth Cricket Coaching Platform
        </div>
        {/* Cricket bat — single, centred */}
        <div style={{fontSize:32,lineHeight:1}}>🏏</div>
      </div>

      <div style={{padding:"18px 22px 48px"}}>
        {/* Coach / Player toggle */}
        <div style={{display:"flex",background:"var(--surface2)",borderRadius:"var(--radius-sm)",padding:3,marginBottom:20,border:"2px solid var(--border)",marginTop:4}}>
          {["coach","student"].map(m=><button key={m} onClick={()=>{setLoginMode(m);setErr("");}} style={{flex:1,padding:"10px 0",borderRadius:8,fontWeight:700,fontSize:13,textTransform:"capitalize",background:loginMode===m?"var(--accent)":"transparent",color:loginMode===m?"#fff":"var(--text2)"}}>{m==="coach"?"🎓 Coach / Admin":"🏏 Player"}</button>)}
        </div>

        {/* Email — bold label */}
        <div style={{marginBottom:14}}>
          <div style={{fontSize:13,fontWeight:800,color:"var(--text)",textTransform:"uppercase",letterSpacing:.7,marginBottom:7}}>📧 Email Address</div>
          <input type="email" placeholder="your@email.com" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handle()}
            style={{fontSize:15,fontWeight:500,color:"var(--text)",background:"var(--surface)",border:"2px solid var(--border)"}}/>
        </div>

        {/* Password — bold label */}
        <div style={{marginBottom:6}}>
          <div style={{fontSize:13,fontWeight:800,color:"var(--text)",textTransform:"uppercase",letterSpacing:.7,marginBottom:7}}>🔒 Password</div>
          <input type="password" placeholder="••••••••" value={pw} onChange={e=>setPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handle()}
            style={{fontSize:15,fontWeight:500,color:"var(--text)",background:"var(--surface)",border:"2px solid var(--border)"}}/>
        </div>

        <div style={{textAlign:"right",marginBottom:16}}>
          <button onClick={onForgotPassword} style={{background:"none",border:"none",color:"var(--accent)",fontSize:13,fontWeight:700,cursor:"pointer",padding:"2px 0"}}>
            Forgot password?
          </button>
        </div>

        {err&&<div style={{background:"#fef2f2",border:"2px solid var(--danger)",borderRadius:8,padding:"10px 14px",fontSize:13,fontWeight:600,color:"var(--danger)",marginBottom:14}}>{err}</div>}

        <button className="btn-primary" onClick={handle} disabled={loading} style={{marginBottom:14,fontSize:15,padding:"14px 20px"}}>
          {loading?"Signing in…":`Sign In as ${loginMode==="coach"?"Coach / Admin":"Player"}`}
        </button>

        {/* User Guide link */}
        <div style={{textAlign:"center",marginBottom:16}}>
          <button onClick={openGuide}
            style={{fontSize:13,color:"var(--accent)",fontWeight:600,textDecoration:"none",display:"inline-flex",alignItems:"center",gap:6,background:"none",border:"none",cursor:"pointer",padding:0}}>
            📖 User Guide — How it works
          </button>
        </div>

        {loginMode==="coach"&&(
          <div style={{borderTop:"2px solid var(--border)",paddingTop:16,marginTop:4}}>
            <div style={{textAlign:"center",fontSize:13,color:"var(--text2)",fontWeight:600,marginBottom:10}}>
              New to CrickitCoach? Set up your school account.
            </div>
            <button className="btn-secondary" onClick={onShowRegister}
              style={{width:"100%",borderColor:"var(--accent)",borderWidth:2,color:"var(--accent)",fontWeight:700,fontSize:13}}>
              🏫 Create New School Account
            </button>
          </div>
        )}

        <div style={{marginTop:16,textAlign:"center",fontSize:11,color:"var(--text3)",fontWeight:500}}>
          support@crickitcoach.com · © 2026 ArcGain LLC
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT APP
// ═══════════════════════════════════════════════════════════════════════════════
function CampBanner({camp,onExit}){
  if(!camp) return null;
  const today=new Date(); today.setHours(0,0,0,0);
  const start=new Date(camp.startDate); const end=new Date(camp.endDate);
  const totalDays=Math.max(1,Math.round((end-start)/(1000*60*60*24))+1);
  const dayNum=Math.min(totalDays,Math.max(1,Math.round((today-start)/(1000*60*60*24))+1));
  return(
    <div style={{background:"#fffbeb",border:"1.5px solid #fde68a",borderRadius:10,padding:"8px 12px",margin:"0 8px 10px",display:"flex",alignItems:"center",gap:8}}>
      <span style={{fontSize:16,flexShrink:0}}>⛺</span>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:12,fontWeight:700,color:"#92400e"}}>Camp mode active</div>
        <div style={{fontSize:11,color:"#78350f",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{camp.name} · day {dayNum} of {totalDays}</div>
      </div>
      <button onClick={onExit} style={{flexShrink:0,fontSize:11,fontWeight:700,padding:"5px 10px",borderRadius:8,background:"transparent",color:"#92400e",border:"1.5px solid #92400e",cursor:"pointer"}}>Exit</button>
    </div>
  );
}

function CreateCampScreen({coaches,orgId,onClose,onSave}){
  const [name,setName]=useState("");
  const [startDate,setStartDate]=useState(new Date().toISOString().split("T")[0]);
  const [endDate,setEndDate]=useState("");
  const [selectedCoaches,setSelectedCoaches]=useState([]);
  const [guestCoachName,setGuestCoachName]=useState("");
  const [err,setErr]=useState("");
  const toggleCoach=id=>setSelectedCoaches(s=>s.includes(id)?s.filter(x=>x!==id):[...s,id]);
  const handleSave=()=>{
    setErr("");
    if(!name.trim()){setErr("Camp name is required");return;}
    if(!endDate){setErr("End date is required");return;}
    if(selectedCoaches.length===0){setErr("Select at least one coach to run the camp");return;}
    onSave({name,startDate,endDate,coachIds:selectedCoaches,guestCoachName,orgId});
  };
  return(
    <div id="app-shell">
      <div className="top-bar"><button className="back-btn" onClick={onClose}>← Cancel</button><div className="top-bar-title">New Camp</div><div style={{width:40}}/></div>
      <div className="screen" style={{padding:"12px 16px"}}>
        <div style={{marginBottom:12}}>
          <div className="lbl">Camp Name *</div>
          <input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Summer High Performance Camp"/>
        </div>
        <div style={{display:"flex",gap:10,marginBottom:12}}>
          <div style={{flex:1}}>
            <div className="lbl">Start Date</div>
            <input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)}/>
          </div>
          <div style={{flex:1}}>
            <div className="lbl">End Date *</div>
            <input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} min={startDate}/>
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <div className="lbl">Surf Riders coaching staff running this camp *</div>
          {coaches.map(c=>(
            <div key={c.id} onClick={()=>toggleCoach(c.id)} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",border:`1.5px solid ${selectedCoaches.includes(c.id)?"var(--accent)":"var(--border)"}`,borderRadius:8,marginBottom:6,cursor:"pointer",background:selectedCoaches.includes(c.id)?"#e0f0fa":"transparent"}}>
              <div className="avatar" style={{width:28,height:28,fontSize:10,background:"linear-gradient(135deg,var(--accent),#005577)"}}>{c.avatar}</div>
              <div style={{flex:1,fontSize:13,fontWeight:600}}>{c.name}</div>
              {selectedCoaches.includes(c.id)&&<span style={{color:"var(--accent)"}}>✓</span>}
            </div>
          ))}
        </div>
        <div style={{marginBottom:16}}>
          <div className="lbl">Guest coach name (optional — display only, no login)</div>
          <input value={guestCoachName} onChange={e=>setGuestCoachName(e.target.value)} placeholder="e.g. Coach Ricky Ponting"/>
        </div>
        {err&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"8px 12px",fontSize:12,color:"var(--danger)",marginBottom:12}}>{err}</div>}
        <button className="btn-primary" style={{width:"100%"}} onClick={handleSave}>Create Camp</button>
      </div>
    </div>
  );
}

function CampRosterScreen({camp,db,orgId,squadStudents,onAddExisting,onAddGuest,onRemove,onClose,onCloseCamp,onExport,showNotif}){
  const [showAddExisting,setShowAddExisting]=useState(false);
  const [pickerSelected,setPickerSelected]=useState(new Set());
  const [showAddGuest,setShowAddGuest]=useState(false);
  const [guestName,setGuestName]=useState("");
  const [guestEmail,setGuestEmail]=useState("");
  const [guestPw,setGuestPw]=useState("");
  const [guestAge,setGuestAge]=useState("");
  const [guestCls,setGuestCls]=useState("Introductory");
  const [guestErr,setGuestErr]=useState("");
  const [confirmClose,setConfirmClose]=useState(false);

  const rosterExisting=(camp.rosterStudentIds||[]).map(id=>db.students.find(s=>s.id===id)).filter(Boolean);
  const rosterGuests=(camp.guestPlayerIds||[]).map(id=>db.students.find(s=>s.id===id)).filter(Boolean);
  const availableToAdd=squadStudents.filter(s=>!(camp.rosterStudentIds||[]).includes(s.id));

  const saveGuest=()=>{
    setGuestErr("");
    if(!guestName.trim()){setGuestErr("Name is required");return;}
    if(!guestEmail.trim()||!guestEmail.includes("@")){setGuestErr("Valid email required");return;}
    if(guestPw.length<6){setGuestErr("Password must be at least 6 characters");return;}
    onAddGuest({name:guestName,email:guestEmail,password:guestPw,age:parseInt(guestAge)||undefined,cricketClass:guestCls,orgId});
    setGuestName("");setGuestEmail("");setGuestPw("");setGuestAge("");setShowAddGuest(false);
  };

  if(showAddExisting) return(
    <div id="app-shell">
      <div className="top-bar"><button className="back-btn" onClick={()=>setShowAddExisting(false)}>← Back</button><div className="top-bar-title">Add From Squad</div><div style={{width:40}}/></div>
      <div className="screen" style={{padding:"12px 16px"}}>
        {[...availableToAdd].sort((a,b)=>a.name.localeCompare(b.name)).map(s=>(
          <div key={s.id} onClick={()=>setPickerSelected(p=>{const n=new Set(p);n.has(s.id)?n.delete(s.id):n.add(s.id);return n;})}
            style={{display:"flex",alignItems:"center",gap:10,padding:"8px 10px",border:`1.5px solid ${pickerSelected.has(s.id)?"var(--accent)":"var(--border)"}`,borderRadius:8,marginBottom:6,cursor:"pointer",background:pickerSelected.has(s.id)?"#e0f0fa":"transparent"}}>
            <div className="avatar" style={{width:28,height:28,fontSize:10,background:"linear-gradient(135deg,var(--accent),#005577)"}}>{s.avatar}</div>
            <div style={{flex:1,fontSize:13,fontWeight:600}}>{s.name}</div>
            {pickerSelected.has(s.id)&&<span style={{color:"var(--accent)"}}>✓</span>}
          </div>
        ))}
        {availableToAdd.length===0&&<div className="empty"><div className="empty-icon">👥</div><div>All squad players already in camp</div></div>}
      </div>
      <div style={{padding:"12px 16px"}}>
        <button className="btn-primary" style={{width:"100%"}} disabled={pickerSelected.size===0} onClick={()=>{onAddExisting([...pickerSelected]);setPickerSelected(new Set());setShowAddExisting(false);}}>
          Add {pickerSelected.size||""} Player{pickerSelected.size!==1?"s":""} to Camp
        </button>
      </div>
    </div>
  );

  if(showAddGuest) return(
    <div id="app-shell">
      <div className="top-bar"><button className="back-btn" onClick={()=>setShowAddGuest(false)}>← Back</button><div className="top-bar-title">Add Guest Player</div><div style={{width:40}}/></div>
      <div className="screen" style={{padding:"12px 16px"}}>
        <div style={{fontSize:12,color:"var(--text2)",marginBottom:12,lineHeight:1.5}}>Guest players get a full login so their parents can track progress during and after camp.</div>
        <div style={{marginBottom:10}}><div className="lbl">Name *</div><input value={guestName} onChange={e=>setGuestName(e.target.value)} placeholder="Player full name"/></div>
        <div style={{marginBottom:10}}><div className="lbl">Email *</div><input type="email" value={guestEmail} onChange={e=>setGuestEmail(e.target.value)} placeholder="player@email.com"/></div>
        <div style={{marginBottom:10}}><div className="lbl">Temporary Password *</div><input type="password" value={guestPw} onChange={e=>setGuestPw(e.target.value)} placeholder="Min 6 characters"/></div>
        <div style={{display:"flex",gap:10,marginBottom:10}}>
          <div style={{flex:1}}><div className="lbl">Age</div><input type="number" value={guestAge} onChange={e=>setGuestAge(e.target.value)} placeholder="Age"/></div>
          <div style={{flex:1}}>
            <div className="lbl">Class</div>
            <select value={guestCls} onChange={e=>setGuestCls(e.target.value)}>
              {CLASSES.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        {guestErr&&<div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:8,padding:"8px 12px",fontSize:12,color:"var(--danger)",marginBottom:12}}>{guestErr}</div>}
        <button className="btn-primary" style={{width:"100%"}} onClick={saveGuest}>Add Guest Player</button>
      </div>
    </div>
  );

  return(
    <div id="app-shell">
      <div className="top-bar"><button className="back-btn" onClick={onClose}>← Back</button><div className="top-bar-title">{camp.name}</div><div style={{width:40}}/></div>
      <div className="screen" style={{padding:"12px 16px"}}>
        <div style={{fontSize:11,color:"var(--text3)",marginBottom:12}}>{dateStr(camp.startDate)} – {dateStr(camp.endDate)} · {camp.status==="closed"?"Closed":"Active"}</div>

        <div className="lbl">From your squad ({rosterExisting.length})</div>
        {rosterExisting.map(s=>(
          <div key={s.id} className="card" style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",marginBottom:6}}>
            <div className="avatar" style={{width:28,height:28,fontSize:10,background:"linear-gradient(135deg,var(--accent),#005577)"}}>{s.avatar}</div>
            <div style={{flex:1,fontSize:13,fontWeight:600}}>{s.name}</div>
            {camp.status!=="closed"&&<button onClick={()=>onRemove(s.id)} style={{background:"none",border:"none",color:"var(--text3)",cursor:"pointer",fontSize:14}}>✕</button>}
          </div>
        ))}
        {camp.status!=="closed"&&<button className="btn-secondary" style={{width:"100%",marginBottom:16}} onClick={()=>setShowAddExisting(true)}>Add More From Squad</button>}

        <div className="lbl">Guest players — external academies ({rosterGuests.length})</div>
        {rosterGuests.map(s=>(
          <div key={s.id} className="card" style={{display:"flex",alignItems:"center",gap:8,padding:"8px 10px",marginBottom:6}}>
            <div className="avatar" style={{width:28,height:28,fontSize:10,background:"linear-gradient(135deg,#7c3aed,#5b21b6)",color:"#fff"}}>{s.avatar}</div>
            <div style={{flex:1}}>
              <div style={{fontSize:13,fontWeight:600}}>{s.name}</div>
              <div style={{fontSize:10,color:"var(--text3)"}}>Guest · {s.status==="inactive"?"login retained":"active"}</div>
            </div>
            <span style={{fontSize:10,padding:"2px 8px",borderRadius:99,background:"#eef2ff",color:"#4338ca",border:"1px solid #c7d2fe",fontWeight:600}}>Guest</span>
          </div>
        ))}
        {camp.status!=="closed"&&<button className="btn-secondary" style={{width:"100%",marginBottom:16}} onClick={()=>setShowAddGuest(true)}>Add Guest Player</button>}

        <button className="btn-secondary" style={{width:"100%",marginBottom:8}} onClick={onExport}>📄 Export Roster to CSV</button>

        {camp.status!=="closed"&&(
          confirmClose?(
            <div style={{background:"#fef2f2",border:"1.5px solid var(--danger)",borderRadius:10,padding:12,marginTop:8}}>
              <div style={{fontSize:12,color:"var(--danger)",fontWeight:700,marginBottom:8}}>Close this camp? A final report will be generated for every player. Guest players will be set inactive but keep login access.</div>
              <div style={{display:"flex",gap:8}}>
                <button className="btn-secondary" style={{flex:1}} onClick={()=>setConfirmClose(false)}>Cancel</button>
                <button style={{flex:1,background:"var(--danger)",color:"#fff",border:"none",borderRadius:8,fontWeight:700,cursor:"pointer"}} onClick={()=>{onCloseCamp();setConfirmClose(false);}}>Close Camp</button>
              </div>
            </div>
          ):(
            <button style={{width:"100%",background:"transparent",color:"var(--danger)",border:"1.5px solid var(--danger)",borderRadius:8,padding:"10px",fontWeight:700,cursor:"pointer"}} onClick={()=>setConfirmClose(true)}>Close Camp</button>
          )
        )}
      </div>
    </div>
  );
}

export default function App(){
  const [db,setDb]=useState(null);
  const [dbReady,setDbReady]=useState(false);
  const [user,setUser]=useState(null);
  const [userType,setUserType]=useState(null);
  const [playerSelectData,setPlayerSelectData]=useState(null); // {players, schoolDb, orgId} when parent has multiple children
  const [tab,setTab]=useState("home");
  const [subView,setSubView]=useState(null);
  const [showLib,setShowLib]=useState(false);
  const [showManageCoaches,setShowManageCoaches]=useState(false);
  const [showRegister,setShowRegister]=useState(false);
  const [activeCampId,setActiveCampId]=useState(null);
  const [showCreateCamp,setShowCreateCamp]=useState(false);
  const [showCampRoster,setShowCampRoster]=useState(null); // campId when open
  const [showForgotPw,setShowForgotPw]=useState(false);
  const [showSuperAdmin,setShowSuperAdmin]=useState(false);
  const [notif,setNotif]=useState(null);
  const [loginMode,setLoginMode]=useState("coach");
  const [activeOrgId,setActiveOrgId]=useState(getStoredOrgId); // orgId of currently loaded school
  // Ref keeps latest activeOrgId accessible inside useEffect without stale closure
  const activeOrgIdRef=useRef(getStoredOrgId());
  const [showReport,setShowReport]=useState(null); // sid when report overlay is open
  const [showReportSnapshot,setShowReportSnapshot]=useState(null); // snapshot when viewing historical report

  // ── Load from persistent storage on mount ─────────────────────────────────
  useEffect(()=>{
    (async()=>{
      const stored=await loadFromStorage();
      setDb(stored ? migrateDb(stored) : null); // null = no school registered yet
      setDbReady(true);
    })();
  },[]);

  // ── Keep activeOrgId ref in sync ────────────────────────────────────────────
  useEffect(()=>{ activeOrgIdRef.current=activeOrgId; },[activeOrgId]);

  // ── Auto-save db whenever it changes ─────────────────────────────────────
  // Uses activeOrgIdRef.current (not activeOrgId state) to avoid stale closure —
  // the dependency array only has [db,dbReady] but the ref always has the latest orgId.
  useEffect(()=>{
    if(db&&dbReady&&db.coaches&&db.coaches.length>0){
      const orgId=activeOrgIdRef.current;
      if(orgId) saveToStorage(db, orgId);
      else console.warn("CricKit: activeOrgId not set — save skipped");
    }
  },[db,dbReady]);

  const showNotif=useCallback((msg,type="success")=>{
    setNotif({msg,type});setTimeout(()=>setNotif(null),3000);
  },[]);

  const upDb=fn=>setDb(p=>{
    // Deep-clone the mutable sub-tables so concurrent writes never stomp each other.
    // Shallow spread on sessions/goals means multiple rapid calls all start from the
    // same stale reference — the last write wins and earlier writes are lost.
    const next={
      ...p,
      sessions:    Object.fromEntries(Object.entries(p.sessions||{}).map(([k,v])=>[k,[...v]])),
      goals:       Object.fromEntries(Object.entries(p.goals||{}).map(([k,v])=>[k,[...v]])),
      assessments: Object.fromEntries(Object.entries(p.assessments||{}).map(([k,v])=>[k,[...v]])),
      coaches:     [...(p.coaches||[])],
      students:    [...(p.students||[])],
    };
    fn(next);
    return next;
  });

  // ── Auth ──────────────────────────────────────────────────────────────────
  // ── Login: two-step for cross-device support ─────────────────────────────────
  // Step 1 (if API configured): look up orgId by email from DynamoDB email index.
  //         Load that school's full data, then validate password client-side.
  // Step 2 (fallback): search local db (works offline / no API configured).
  // This means ANY device can log in with just email + password — no orgId needed.
  const login=async(email,pw)=>{
    const em=email.trim().toLowerCase();

    // SuperAdmin check
    if(em===SUPERADMIN_EMAIL&&pw===SUPERADMIN_PASSWORD){
      setShowSuperAdmin(true);return "superadmin";
    }

    // ── Try API-based login (cross-device) ────────────────────────────────────
    if(API_URL&&API_SECRET){
      try{
        const lookup=await lookupEmailOrgId(em);
        if(lookup&&lookup.orgId){
          const schoolDb=await loadDbByOrgId(lookup.orgId);
          if(schoolDb){
            // Check school suspension or expired trial BEFORE validating password
            if(schoolDb.schoolStatus==="suspended") return "suspended";

            // Validate password in the fetched school data
            const c=schoolDb.coaches.find(c=>c.email===em&&c.password===pw);
            if(c){
              if(c.status==="inactive") return "inactive";
              setDb(schoolDb);
              setActiveOrgId(lookup.orgId);
              setStoredOrgId(lookup.orgId);
              activeOrgIdRef.current=lookup.orgId;
              setUser(c);setUserType("coach");
              return true;
            }
            const s=schoolDb.students.find(s=>s.email===em&&s.password===pw);
            if(s){
              if(s.status==="inactive") return "inactive";
              setDb(schoolDb);
              setActiveOrgId(lookup.orgId);
              setStoredOrgId(lookup.orgId);
              activeOrgIdRef.current=lookup.orgId;
              // Record last login — fire and forget
              recordStudentLogin(lookup.orgId, s.id);
              // Find siblings — via linkedPlayerIds OR same email (fallback for unlinked siblings)
              const linkedIds=s.linkedPlayerIds||[];
              const sameEmailSiblings=schoolDb.students.filter(x=>
                x.id!==s.id&&x.email===em&&x.status!=="inactive"
              );
              const allSiblingIds=[...new Set([...linkedIds,...sameEmailSiblings.map(x=>x.id)])];
              const linked=allSiblingIds
                .map(id=>schoolDb.students.find(x=>x.id===id))
                .filter(Boolean)
                .filter(x=>x.status!=="inactive");
              if(linked.length>0){
                setPlayerSelectData({players:[s,...linked],schoolDb,orgId:lookup.orgId});
              } else {
                setUser(s);setUserType("student");
              }
              return true;
            }
            // Email found in DynamoDB but password wrong — don't fall through to local
            return false;
          }
        }
        // Email not in DynamoDB at all — fall through to local db search
      }catch(e){
        console.warn("API login failed, trying local db:", e.message);
      }
    }

    // ── Fallback: search local db (offline / API not configured) ─────────────
    if(!db) return false;
    if(db.schoolStatus==="suspended") return "suspended";
    const c=db.coaches.find(c=>c.email===em&&c.password===pw);
    if(c){
      if(c.status==="inactive") return "inactive";
      // Set activeOrgId from the user record so saveToStorage can reach DynamoDB
      if(c.orgId){ setActiveOrgId(c.orgId); setStoredOrgId(c.orgId); activeOrgIdRef.current=c.orgId; }
      setUser(c);setUserType("coach");return true;
    }
    const s=db.students.find(s=>s.email===em&&s.password===pw);
    if(s){
      if(s.status==="inactive") return "inactive";
      if(s.orgId){ setActiveOrgId(s.orgId); setStoredOrgId(s.orgId); activeOrgIdRef.current=s.orgId; }
      // Record last login — fire and forget
      recordStudentLogin(s.orgId, s.id);
      // Find siblings — via linkedPlayerIds OR same email (fallback for unlinked siblings)
      const linkedIds=s.linkedPlayerIds||[];
      const sameEmailSiblings=db.students.filter(x=>
        x.id!==s.id&&x.email===em&&x.status!=="inactive"
      );
      const allSiblingIds=[...new Set([...linkedIds,...sameEmailSiblings.map(x=>x.id)])];
      const linked=allSiblingIds
        .map(id=>db.students.find(x=>x.id===id))
        .filter(Boolean)
        .filter(x=>x.status!=="inactive");
      if(linked.length>0){
        setPlayerSelectData({players:[s,...linked],schoolDb:db,orgId:s.orgId});
      } else {
        setUser(s);setUserType("student");
      }
      return true;
    }
    return false;
  };

  // ── Register new school ───────────────────────────────────────────────────
  const registerSchool=({schoolName,adminName,email,password})=>{
    const orgId = "org_" + Date.now();
    const adminId = "c_" + Date.now();
    const parts = adminName.trim().split(" ");
    const avatar = parts.length>=2
      ? (parts[0][0]+parts[parts.length-1][0]).toUpperCase()
      : adminName.substring(0,2).toUpperCase();

    const newDb = {
      coaches: [{
        id: adminId, name: adminName.trim(), email: email.toLowerCase(),
        password, avatar, role:"admin", orgId, schoolName: schoolName.trim(),
        team: "", joinDate: new Date().toISOString().split("T")[0], status:"active"
      }],
      students: [],
      skillLibraries: { [orgId]: JSON.parse(JSON.stringify(DEFAULT_SKILL_LIBRARY)) },
      assessments: {}, goals: {}, sessions: {}, coachingNotes: {},
      schoolStatus: "active",
      schoolStatusNote: "",
      schoolStatusUpdated: "",
    };
    setDb(newDb);
    // Track the new orgId so saves go to the right DynamoDB record
    setActiveOrgId(orgId);
    setStoredOrgId(orgId);
    activeOrgIdRef.current=orgId;
    // Log in immediately as the new admin
    const admin = newDb.coaches[0];
    setUser(admin);
    setUserType("coach");
    setShowRegister(false);
    showNotif(`Welcome to CrickitCoach, ${adminName.split(" ")[0]}! 🎉 Your school has 1 year of free access.`);
  };

  // ── DB mutations ──────────────────────────────────────────────────────────
  // SuperAdmin: suspend or reinstate a school
  const suspendSchool=(note="")=>{
    upDb(d=>{
      d.schoolStatus="suspended";
      d.schoolStatusNote=note||"Suspended by SuperAdmin";
      d.schoolStatusUpdated=new Date().toISOString().split("T")[0];
    });
  };
  const reinstateSchool=()=>{
    upDb(d=>{
      d.schoolStatus="active";
      d.schoolStatusNote="";
      d.schoolStatusUpdated=new Date().toISOString().split("T")[0];
    });
  };
  const addGoal=(sid,goal)=>upDb(d=>{d.goals={...d.goals,[sid]:[...(d.goals[sid]||[]),{id:"g"+Date.now(),...goal,createdAt:new Date().toISOString()}]};});
  // Group goal — write same goal to multiple students simultaneously
  const addGroupGoal=(sids,goal)=>{
    const ts=Date.now();
    upDb(d=>{
      const patch={...d.goals};
      sids.forEach((sid,i)=>{
        patch[sid]=[...(patch[sid]||[]),{id:`g${ts}_${i}_${sid}`,...goal,isGroupGoal:true}];
      });
      d.goals=patch;
    });
  };
  const updGoal=(sid,gid,progress)=>upDb(d=>{d.goals={...d.goals,[sid]:(d.goals[sid]||[]).map(g=>g.id===gid?{...g,progress,status:progress>=100?"completed":"active"}:g)};});
  const addAssessment=(sid,a)=>upDb(d=>{d.assessments={...d.assessments,[sid]:[...(d.assessments[sid]||[]),{id:"a"+Date.now(),...a}]};showNotif("Assessment saved! ✅");});
  // Edit notes on a specific past assessment (coach corrects/adds notes)
  const updAssessmentNotes=(sid,aid,notes)=>upDb(d=>{
    d.assessments={...d.assessments,[sid]:(d.assessments[sid]||[]).map(a=>a.id===aid?{...a,notes}:a)};
  });
  // addSession: log session and notify
  const addSession=(sid,sess)=>{
    upDb(d=>{d.sessions={...d.sessions,[sid]:[...(d.sessions[sid]||[]),{id:"ss"+Date.now(),...sess,coachId:user?.id,loggedAt:new Date().toISOString()}]};});
    showNotif("Session logged! 🏏");
  };
  const deleteSession=(sid,sessionId)=>{
    upDb(d=>{d.sessions={...d.sessions,[sid]:(d.sessions[sid]||[]).filter(se=>se.id!==sessionId)};});
    showNotif("Session removed ✅");
  };
  const updateLib=(orgId,newLib)=>upDb(d=>{d.skillLibraries={...d.skillLibraries,[orgId]:newLib};});
  const updStudent=(sid,cls,tags,naSkills,newCoachId,newName,newEmail,newPassword,newAge,transferToCoachId,siblingLinkId)=>{
    upDb(d=>{
      d.students=d.students.map(s=>s.id===sid
        ?{...s,
          cricketClass:cls, tags, naSkills:naSkills||[],
          ...(newCoachId!==undefined ? {coachId:newCoachId} : {}),
          ...(newName     ? {name:newName.trim(),
              avatar:(()=>{const p=newName.trim().split(" ");return p.length>=2?(p[0][0]+p[p.length-1][0]).toUpperCase():newName.substring(0,2).toUpperCase();})()}
                          : {}),
          ...(newEmail    ? {email:newEmail.trim().toLowerCase()} : {}),
          ...(newPassword ? {password:newPassword}                : {}),
          ...(newAge      ? {age:newAge}                           : {}),
          // Add sibling link if requested
          ...(siblingLinkId&&!(s.linkedPlayerIds||[]).includes(siblingLinkId)
            ? {linkedPlayerIds:[...(s.linkedPlayerIds||[]),siblingLinkId]}
            : {}),
         }
        :s);
      // Bidirectional sibling link — also update the other student
      if(siblingLinkId){
        d.students=d.students.map(s=>s.id===siblingLinkId&&!(s.linkedPlayerIds||[]).includes(sid)
          ?{...s,linkedPlayerIds:[...(s.linkedPlayerIds||[]),sid],
            // Sync password so either password works for the parent login
            ...(newPassword?{password:newPassword}:{})
          }
          :s
        );
      }
    });
    if(user?.id===sid){
      setUser(u=>({...u,cricketClass:cls,tags,naSkills:naSkills||[],
        ...(newName?{name:newName.trim()}:{}),
        ...(newEmail?{email:newEmail.trim().toLowerCase()}:{}),
        ...(newAge?{age:newAge}:{}),
      }));
    }
    if(transferToCoachId){
      const fromCoach=db?.coaches?.find(c=>c.id===user?.id);
      const toCoach=db?.coaches?.find(c=>c.id===transferToCoachId);
      const noteText=`Transferred from ${fromCoach?.name||"Coach"} to ${toCoach?.name||"New Coach"} on ${new Date().toLocaleDateString()}`;
      upDb(d=>{
        d.coachingNotes={...d.coachingNotes,[sid]:[...(d.coachingNotes?.[sid]||[]),
          {id:"n"+Date.now(),text:noteText,date:new Date().toISOString().split("T")[0],createdBy:"system"}
        ]};
      });
    }
  };
  // Coach changes their own password from Settings
  const changeOwnPassword=(newPassword)=>{
    if(!newPassword||newPassword.length<6) return;
    upDb(d=>{
      d.coaches=d.coaches.map(c=>c.id===user?.id?{...c,password:newPassword}:c);
    });
    showNotif("Password updated ✅");
  };

  const changeStudentPassword=(newPassword)=>{
    if(!newPassword||newPassword.length<6) return;
    upDb(d=>{
      // Update this student's password
      d.students=d.students.map(s=>s.id===user?.id?{...s,password:newPassword}:s);
      // Sync to linked siblings so parent login still works with either password
      const linkedIds=user?.linkedPlayerIds||[];
      const sameEmailIds=d.students.filter(x=>x.id!==user?.id&&x.email===user?.email&&x.status!=="inactive").map(x=>x.id);
      const allSiblingIds=[...new Set([...linkedIds,...sameEmailIds])];
      if(allSiblingIds.length>0){
        d.students=d.students.map(s=>allSiblingIds.includes(s.id)?{...s,password:newPassword}:s);
      }
    });
    showNotif("Password updated ✅");
  };

  // Update coach name, team, and optionally password
  const updCoach=(cid,name,team,newPassword)=>{
    upDb(d=>{
      d.coaches=d.coaches.map(c=>c.id===cid
        ?{...c,name:name.trim(),team:team.trim(),
           ...(newPassword?{password:newPassword}:{}),
           // Regenerate avatar from new name
           avatar:(()=>{const p=name.trim().split(" ");return p.length>=2?(p[0][0]+p[p.length-1][0]).toUpperCase():name.substring(0,2).toUpperCase();})()
          }
        :c);
    });
    if(user?.id===cid) setUser(u=>({...u,name:name.trim(),team:team.trim()}));
    showNotif("Coach updated ✅");
  };
  // Update squad visibility setting
  const updateSquadVisibility=(mode)=>{
    upDb(d=>{ d.squadVisibility=mode; });
    showNotif(mode==="shared"?"Squad visibility set to Shared — all coaches see all players 🌐":"Squad visibility set to Private — coaches see own squad only 🔒");
  };

  // Save a report snapshot to the student's reportHistory array
  const saveReportSnapshot=(sid,snapshot)=>{
    upDb(d=>{
      d.students=d.students.map(s=>s.id===sid
        ?{...s,reportHistory:[...(s.reportHistory||[]),snapshot]}
        :s);
    });
  };
  const deactivateStudent=(sid,reason)=>upDb(d=>{
    d.students=d.students.map(s=>s.id===sid
      ?{...s,status:"inactive",statusReason:reason||"",statusDate:new Date().toISOString().split("T")[0]}
      :s);
  });
  const reactivateStudent=(sid)=>upDb(d=>{
    d.students=d.students.map(s=>s.id===sid
      ?{...s,status:"active",statusReason:"",statusDate:null}
      :s);
  });
  const addCoachingNote=(sid,text)=>upDb(d=>{
    const note={id:"n"+Date.now(),text:text.trim(),date:new Date().toISOString().split("T")[0],createdBy:"student"};
    d.coachingNotes={...d.coachingNotes,[sid]:[...(d.coachingNotes?.[sid]||[]),note]};
  });
  const deleteCoachingNote=(sid,nid)=>upDb(d=>{
    d.coachingNotes={...d.coachingNotes,[sid]:(d.coachingNotes?.[sid]||[]).filter(n=>n.id!==nid)};
  });
  const saveSkillNote=(sid,cat,skillId,note)=>upDb(d=>{
    if(!d.skillNotes) d.skillNotes={};
    if(!d.skillNotes[sid]) d.skillNotes[sid]={};
    if(!d.skillNotes[sid][cat]) d.skillNotes[sid][cat]={};
    // Migrate legacy string to array
    const existing=d.skillNotes[sid][cat][skillId];
    const arr=Array.isArray(existing)?existing:
      (existing&&existing.trim()?[{note:existing,date:d.skillNotes[sid]._updatedAt||new Date().toISOString().split("T")[0],coachId:d.skillNotes[sid]._coachId||sid}]:[]);
    // Prepend new entry
    d.skillNotes[sid][cat][skillId]=[{note:note.trim(),date:new Date().toISOString().split("T")[0],coachId:sid},...arr];
    d.skillNotes[sid]._updatedAt=new Date().toISOString().split("T")[0];
    d.skillNotes[sid]._coachId=user?.id;
  });
  const markCoachViewed=(sid)=>upDb(d=>{
    if(!d.coachLastViewedAt) d.coachLastViewedAt={};
    d.coachLastViewedAt[sid]=new Date().toISOString();
  });
  const markStudentViewed=(sid,section)=>upDb(d=>{
    if(!d.studentLastViewedAt) d.studentLastViewedAt={};
    if(!d.studentLastViewedAt[sid]) d.studentLastViewedAt[sid]={};
    d.studentLastViewedAt[sid][section]=new Date().toISOString();
  });
  const addStudent=(data)=>{
    const parts = data.name.trim().split(" ");
    const avatar = parts.length>=2
      ? (parts[0][0]+parts[parts.length-1][0]).toUpperCase()
      : data.name.substring(0,2).toUpperCase();
    const sid = "s" + Date.now();
    upDb(d=>{
      d.students = [...d.students, {
        id: sid, name: data.name, email: data.email,
        password: data.password, avatar, status:"active", naSkills:[], reportHistory:[],
        age: data.age, cricketClass: data.cricketClass,
        tags: data.tags, coachId: data.coachId,
        orgId: data.orgId, joinDate: new Date().toISOString().split("T")[0],
        linkedPlayerIds: data.linkToPlayerId ? [data.linkToPlayerId] : [],
      }];
      d.assessments    = {...d.assessments,    [sid]:[]};
      d.goals          = {...d.goals,          [sid]:[]};
      d.sessions       = {...d.sessions,       [sid]:[]};
      d.coachingNotes  = {...(d.coachingNotes||{}), [sid]:[]};
      // Bidirectional: add new player to existing sibling's linkedPlayerIds
      if(data.linkToPlayerId){
        d.students = d.students.map(s=>s.id===data.linkToPlayerId
          ? {...s, linkedPlayerIds:[...(s.linkedPlayerIds||[]), sid]}
          : s
        );
      }
    });
    showNotif(data.linkToPlayerId
      ? `${data.name} linked as sibling ✅ — shared login enabled`
      : `${data.name} added to squad ✅`
    );
  };
  // ── Password reset (self-service and SuperAdmin) ─────────────────────────
  // ── Camp Mode — create camp, build roster, add guests, close camp ─────────
  const createCamp=(data)=>{
    const cid="camp"+Date.now();
    upDb(d=>{
      if(!d.camps) d.camps={};
      d.camps[cid]={
        id:cid, name:data.name.trim(),
        startDate:data.startDate, endDate:data.endDate,
        status:"active",
        coachIds:data.coachIds||[],
        guestCoachName:data.guestCoachName||"",
        rosterStudentIds:[],
        guestPlayerIds:[],
        createdAt:new Date().toISOString(),
      };
    });
    showNotif(`${data.name} created ✅`);
    return cid;
  };
  const addExistingToCampRoster=(campId,studentIds)=>upDb(d=>{
    const camp=d.camps?.[campId]; if(!camp) return;
    const merged=[...new Set([...(camp.rosterStudentIds||[]),...studentIds])];
    d.camps={...d.camps,[campId]:{...camp,rosterStudentIds:merged}};
  });
  const removeFromCampRoster=(campId,studentId)=>upDb(d=>{
    const camp=d.camps?.[campId]; if(!camp) return;
    d.camps={...d.camps,[campId]:{...camp,
      rosterStudentIds:(camp.rosterStudentIds||[]).filter(id=>id!==studentId),
      guestPlayerIds:(camp.guestPlayerIds||[]).filter(id=>id!==studentId),
    }};
  });
  const addGuestToCamp=(campId,guestData)=>{
    const camp=db?.camps?.[campId];
    const sid="guest"+Date.now();
    const parts=guestData.name.trim().split(" ");
    const avatar=parts.length>=2?(parts[0][0]+parts[parts.length-1][0]).toUpperCase():guestData.name.substring(0,2).toUpperCase();
    upDb(d=>{
      d.students=[...d.students,{
        id:sid, name:guestData.name, email:guestData.email.trim().toLowerCase(),
        password:guestData.password, avatar, status:"active", naSkills:[], reportHistory:[],
        age:guestData.age, cricketClass:guestData.cricketClass,
        tags:[], coachId:camp?.coachIds?.[0]||"",
        orgId:guestData.orgId,
        isGuest:true, campId:campId,
        joinDate:new Date().toISOString().split("T")[0],
        linkedPlayerIds:[],
      }];
      d.assessments={...d.assessments,[sid]:[]};
      d.goals={...d.goals,[sid]:[]};
      d.sessions={...d.sessions,[sid]:[]};
      d.coachingNotes={...(d.coachingNotes||{}),[sid]:[]};
      const c=d.camps?.[campId]; if(c){
        d.camps={...d.camps,[campId]:{...c,guestPlayerIds:[...(c.guestPlayerIds||[]),sid]}};
      }
    });
    showNotif(`${guestData.name} added as guest ✅`);
  };
  const updateCampCoaches=(campId,coachIds)=>upDb(d=>{
    const camp=d.camps?.[campId]; if(!camp) return;
    d.camps={...d.camps,[campId]:{...camp,coachIds}};
  });
  const closeCamp=(campId)=>upDb(d=>{
    const camp=d.camps?.[campId]; if(!camp) return;
    const rosterIds=[...(camp.rosterStudentIds||[]),...(camp.guestPlayerIds||[])];
    // Auto-generate a final report snapshot for every player in the camp
    d.students=d.students.map(s=>{
      if(!rosterIds.includes(s.id)) return s;
      const snapshot=buildReportSnapshot(s,d);
      const withReport={...s,reportHistory:[...(s.reportHistory||[]),snapshot]};
      // Guest players go inactive but keep login access indefinitely
      if((camp.guestPlayerIds||[]).includes(s.id)){
        return {...withReport,status:"inactive",statusReason:"Camp ended — login access retained",statusDate:new Date().toISOString().split("T")[0]};
      }
      return withReport;
    });
    d.camps={...d.camps,[campId]:{...camp,status:"closed",closedAt:new Date().toISOString()}};
  });
  const exportCampCSV=(campId)=>{
    const camp=db?.camps?.[campId]; if(!camp) return;
    const rosterIds=[...(camp.rosterStudentIds||[]),...(camp.guestPlayerIds||[])];
    const rows=[["Name","Type","Class","Sessions Logged","Latest Assessment Score","Email"]];
    rosterIds.forEach(sid=>{
      const s=db.students.find(x=>x.id===sid); if(!s) return;
      const sessCount=(db.sessions?.[sid]||[]).length;
      const assess=(db.assessments?.[sid]||[]).slice(-1)[0];
      const score=assess?(assess.overall||overallFromAssessment(assess)):"";
      rows.push([s.name,s.isGuest?"Guest":"Squad",s.cricketClass,sessCount,score,s.email]);
    });
    const csv=rows.map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob=new Blob([csv],{type:"text/csv"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");
    a.href=url; a.download=`${camp.name.replace(/[^a-z0-9]/gi,"_")}_roster.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resetPassword=(type,id,newPw)=>{
    upDb(d=>{
      if(type==="coach") d.coaches=d.coaches.map(c=>c.id===id?{...c,password:newPw}:c);
      if(type==="student") d.students=d.students.map(s=>s.id===id?{...s,password:newPw}:s);
    });
  };

  // ── Group session — log same session to multiple students ─────────────────
  // FIX v3.11: build patch object first — avoids ID collision + spread-overwrite bug
  const addGroupSession=(sids,sess)=>{
    const ts=Date.now();
    upDb(d=>{
      const patch={...d.sessions};
      sids.forEach((sid,i)=>{
        patch[sid]=[...(patch[sid]||[]),{id:`ss${ts}_${i}_${sid}`,...sess}];
      });
      d.sessions=patch;
    });
  };

    const addCoach=(data)=>{
    const parts = data.name.trim().split(" ");
    const avatar = parts.length>=2
      ? (parts[0][0]+parts[parts.length-1][0]).toUpperCase()
      : data.name.substring(0,2).toUpperCase();
    const cid = "c_" + Date.now();
    upDb(d=>{
      d.coaches = [...d.coaches, {
        id: cid, name: data.name, email: data.email,
        password: data.password, avatar, role:"coach", status:"active",
        orgId: data.orgId, team: data.team||"",
        schoolName: user.schoolName||"",
        joinDate: new Date().toISOString().split("T")[0]
      }];
    });
  };
  const deactivateCoach=(cid)=>upDb(d=>{
    d.coaches=d.coaches.map(c=>c.id===cid?{...c,status:"inactive"}:c);
  });
  const reactivateCoach=(cid)=>upDb(d=>{
    d.coaches=d.coaches.map(c=>c.id===cid?{...c,status:"active"}:c);
  });

  // ── Derived data ──────────────────────────────────────────────────────────
  const getSA=useCallback(sid=>db?(db.assessments[sid]||[]).sort((a,b)=>new Date(a.date)-new Date(b.date)):[]  ,[db]);
  const getSG=useCallback(sid=>db?db.goals[sid]||[]:[],[db]);
  const getSS=useCallback(sid=>db?(db.sessions[sid]||[]).sort((a,b)=>new Date(b.date)-new Date(a.date)):[], [db]);
  const getCS=useCallback(cid=>db?db.students.filter(s=>s.coachId===cid&&s.status!=="inactive"&&!s.isGuest):[], [db]);
  const getAllCS=useCallback(cid=>db?db.students.filter(s=>s.coachId===cid&&!s.isGuest):[], [db]);

  // ── Loading state ─────────────────────────────────────────────────────────
  if(!dbReady) return(
    <>
      <style>{CSS}</style>
      <div id="app-shell" style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100svh",flexDirection:"column",gap:16}}>
        <div style={{fontSize:48}}>🏏</div>
        <div style={{fontFamily:"var(--font-display)",fontSize:32,letterSpacing:2,color:"var(--accent)"}}>CrickitCoach</div>
        <div style={{fontSize:13,color:"var(--text3)"}}>Loading…</div>
      </div>
    </>
  );

  // ── SuperAdmin dashboard ──────────────────────────────────────────────────
  if(showSuperAdmin) return(
    <>
      <style>{CSS}</style>
      <div id="app-shell">
        {notif&&<div className={`notif${notif.type==="error"?" notif-err":""}`}>{notif.msg}</div>}
        <SuperAdminDashboard
          db={db}
          onResetAdminPassword={(id,pw)=>resetPassword("coach",id,pw)}
          onSuspendSchool={(orgId,note)=>{
            // Load that school's db, suspend it, and save back via API
            (async()=>{
              const schoolDb=await loadDbByOrgId(orgId);
              if(schoolDb){
                schoolDb.schoolStatus="suspended";
                schoolDb.schoolStatusNote=note||"Suspended by SuperAdmin";
                schoolDb.schoolStatusUpdated=new Date().toISOString().split("T")[0];
                await saveToStorage(schoolDb, orgId);
                showNotif(`School suspended ✅`);
              }
            })();
          }}
          onReinstateSchool={(orgId)=>{
            (async()=>{
              const schoolDb=await loadDbByOrgId(orgId);
              if(schoolDb){
                schoolDb.schoolStatus="active";
                schoolDb.schoolStatusNote="";
                schoolDb.schoolStatusUpdated=new Date().toISOString().split("T")[0];
                await saveToStorage(schoolDb, orgId);
                showNotif(`School reinstated ✅`);
              }
            })();
          }}
          onBack={()=>setShowSuperAdmin(false)}
          showNotif={showNotif}
        />
      </div>
    </>
  );

  // ── Forgot password screen ────────────────────────────────────────────────
  if(showForgotPw) return(
    <>
      <style>{CSS}</style>
      <ForgotPasswordScreen
        db={db}
        onBack={()=>setShowForgotPw(false)}
        onPasswordReset={(type,id,pw)=>{resetPassword(type,id,pw);}}
      />
    </>
  );

  // ── Register school screen ─────────────────────────────────────────────────
  if(showRegister) return(
    <>
      <style>{CSS}</style>
      <RegisterSchoolScreen onRegister={registerSchool} onBack={()=>setShowRegister(false)}/>
    </>
  );

  // ── Player selector — shown when parent has multiple children ────────────
  if(playerSelectData) return(
    <>
      <style>{CSS}</style>
      <PlayerSelectScreen
        players={playerSelectData.players}
        schoolName={playerSelectData.schoolDb?.schoolName||playerSelectData.schoolDb?.coaches?.[0]?.schoolName||""}
        onSelect={p=>{
          setDb(playerSelectData.schoolDb);
          setUser(p);
          setUserType("student");
          setPlayerSelectData(null);
        }}
        onLogout={()=>{setPlayerSelectData(null);setUser(null);}}
      />
    </>
  );

  // ── Login screen (shown when no db yet OR user not logged in) ─────────────
  if(!user) return(
    <>
      <style>{CSS}</style>
      <LoginScreen
        onLogin={login}
        onShowRegister={()=>setShowRegister(true)}
        onForgotPassword={()=>setShowForgotPw(true)}
        loginMode={loginMode}
        setLoginMode={setLoginMode}
      />
    </>
  );

  const orgId = user.orgId || "org1";
  // Admin is not a coach — they should see all org students, not an empty personal squad
  const isAdmin = userType==="coach" && user.role==="admin";
  // Shared squad: all coaches see all school students (set by admin in Settings)
  const isShared = db.squadVisibility === "shared";
  const students = (isAdmin || isShared)
    ? db.students.filter(s=>s.orgId===orgId&&s.status!=="inactive"&&!s.isGuest)
    : getCS(user.id);
  const allStudentsIncInactive = (isAdmin || isShared)
    ? db.students.filter(s=>s.orgId===orgId&&!s.isGuest)
    : getAllCS(user.id);

  // ── Camp Mode — scoped roster (existing squad players + guest players) ────
  // db is always scoped to a single school, so all of db.camps already belongs to this org.
  // Admin sees every camp; a regular coach only sees camps they're assigned to run.
  const myCamps = (userType==="coach"&&user.role==="admin")
    ? Object.values(db.camps||{})
    : Object.values(db.camps||{}).filter(c=>(c.coachIds||[]).includes(user.id));
  const activeCamp = activeCampId ? (db.camps||{})[activeCampId] : null;
  const campStudents = activeCamp
    ? [...(activeCamp.rosterStudentIds||[]),...(activeCamp.guestPlayerIds||[])]
        .map(sid=>db.students.find(s=>s.id===sid)).filter(Boolean).filter(s=>s.status!=="inactive")
    : null;

  // ── Skill Library overlay ─────────────────────────────────────────────────
  if(showLib&&userType==="coach") return(
    <>
      <style>{CSS}</style>
      <div id="app-shell"><SkillLibraryManager db={db} orgId={orgId} updateLib={updateLib} onBack={()=>setShowLib(false)} showNotif={showNotif}/></div>
    </>
  );

  // ── Create Camp overlay ────────────────────────────────────────────────────
  if(showCreateCamp&&userType==="coach") return(
    <>
      <style>{CSS}</style>
      <CreateCampScreen
        coaches={db.coaches.filter(c=>c.orgId===orgId&&c.role!=="admin"&&c.status!=="inactive")}
        orgId={orgId}
        onClose={()=>setShowCreateCamp(false)}
        onSave={data=>{const cid=createCamp(data);setShowCreateCamp(false);setShowCampRoster(cid);}}
      />
    </>
  );

  // ── Camp Roster overlay ────────────────────────────────────────────────────
  if(showCampRoster&&userType==="coach"){
    const camp=(db.camps||{})[showCampRoster];
    if(camp) return(
      <>
        <style>{CSS}</style>
        {notif&&<div className={`notif${notif.type==="error"?" notif-err":""}`}>{notif.msg}</div>}
        <CampRosterScreen
          camp={camp} db={db} orgId={orgId}
          squadStudents={db.students.filter(s=>s.orgId===orgId&&s.status!=="inactive"&&!s.isGuest)}
          onAddExisting={ids=>addExistingToCampRoster(showCampRoster,ids)}
          onAddGuest={data=>addGuestToCamp(showCampRoster,data)}
          onRemove={sid=>removeFromCampRoster(showCampRoster,sid)}
          onClose={()=>setShowCampRoster(null)}
          onCloseCamp={()=>{closeCamp(showCampRoster);showNotif("Camp closed — final reports ready ✅");setShowCampRoster(null);}}
          onExport={()=>exportCampCSV(showCampRoster)}
          showNotif={showNotif}
        />
      </>
    );
  }

  // ── Manage Coaches overlay (admin only) ───────────────────────────────────
  if(showManageCoaches&&userType==="coach"&&user.role==="admin") return(
    <>
      <style>{CSS}</style>
      <div id="app-shell">
        {notif&&<div className={`notif${notif.type==="error"?" notif-err":""}`}>{notif.msg}</div>}
        <ManageCoachesScreen
          db={db} user={user}
          onAddCoach={addCoach}
          onDeactivateCoach={deactivateCoach}
          onReactivateCoach={reactivateCoach}
          onUpdCoach={updCoach}
          onReassignStudents={updStudent}
          onBack={()=>setShowManageCoaches(false)}
          showNotif={showNotif}
        />
      </div>
    </>
  );

  // ── Report overlay — MUST be checked before subView, or subView guard blocks it ──
  if(showReport&&userType==="coach"){
    const rStu=db.students.find(s=>s.id===showReport);
    if(rStu) return(
      <>
        <style>{CSS}</style>
        <div id="app-shell">
          <ProgressReportOverlay
            student={rStu}
            db={db}
            onClose={()=>{setShowReport(null);setShowReportSnapshot(null);}}
            saveReportSnapshot={saveReportSnapshot}
            showNotif={showNotif}
            viewSnapshot={showReportSnapshot}
          />
        </div>
      </>
    );
    setShowReport(null); // student not found — clear and fall through
  }

  // ── Coach sub-view (student profile / assessment) ─────────────────────────
  if(subView&&userType==="coach") return(
    <>
      <style>{CSS}</style>
      <div id="app-shell">
        {notif&&<div className={`notif${notif.type==="error"?" notif-err":""}`}>{notif.msg}</div>}
        <CoachSubView subView={{...subView,coachId:user.id}} setSubView={v=>{setSubView(v);if(!v)setTab(subView.fromTab||"squad");}} db={db}
          addAssessment={addAssessment} addGoal={addGoal} addSession={addSession} deleteSession={deleteSession} updGoal={updGoal} updStudent={updStudent}
          updAssessmentNotes={updAssessmentNotes} isAdmin={isAdmin}
          deactivateStudent={deactivateStudent} reactivateStudent={reactivateStudent}
          deleteCoachingNote={deleteCoachingNote} saveSkillNote={saveSkillNote} markCoachViewed={markCoachViewed} showNotif={showNotif}
          onGenerateReport={(sid,snap)=>{setShowReport(sid);setShowReportSnapshot(snap||null);}} saveReportSnapshot={saveReportSnapshot}/>
      </div>
    </>
  );

  // ── Main app ──────────────────────────────────────────────────────────────
  const allOrgStudents = db.students.filter(s=>s.orgId===orgId&&s.status!=="inactive");
  const allOrgCoaches  = db.coaches.filter(c=>c.orgId===orgId);

  const coachTabs=[["home","🏠","Home"],["squad","👥","Squad"],["school","🏫","School"],["assess","📊","Assess"],["settings","⚙️","Settings"]];

  // Student unread counts — new coach activity since student last viewed each section
  const studentUnread=(()=>{
    if(userType!=="student"||!user||!db) return {sessions:0,goals:0};
    const sid=user.id;
    const lastLogin=user.lastLoginAt?new Date(user.lastLoginAt):null;
    const svAt=db.studentLastViewedAt?.[sid]||{};
    const lastViewedSessions=svAt.sessions?new Date(svAt.sessions):lastLogin;
    const lastViewedGoals=svAt.goals?new Date(svAt.goals):lastLogin;
    const newSessions=lastViewedSessions
      ?(db.sessions?.[sid]||[]).filter(s=>s.coachId&&s.loggedAt&&new Date(s.loggedAt)>lastViewedSessions).length
      :0;
    const newGoals=lastViewedGoals
      ?(db.goals?.[sid]||[]).filter(g=>g.createdBy!=="student"&&g.createdAt&&new Date(g.createdAt)>lastViewedGoals).length
      :0;
    return {sessions:newSessions,goals:newGoals};
  })();

  const studentTabs=[["home","🏠","Home"],["progress","📈","Progress"],["goals","🎯","Goals"],["sessions","📋","Sessions"],["tips","💡","Coach Tips"],["settings","⚙️","Settings"]];

  // Tab title lookup for top bar
  const tabTitles={home:"Home",squad:"Squad",school:"School",assess:"Assess",settings:"Settings",progress:"Progress",goals:"Goals",sessions:"Sessions"};
  const schoolName=db?.schoolName||db?.coaches?.find(c=>c.id===user?.id)?.schoolName||"";

  return(
    <>
      <style>{CSS}</style>
      <div id="app-shell">
        {notif&&<div className={`notif${notif.type==="error"?" notif-err":""}`}>{notif.msg}</div>}

        {/* ── Fixed top bar ── */}
        <div className="top-bar">
          <div className="top-bar-school">🏫 {schoolName||"CrickitCoach"}</div>
          <div className="top-bar-tab">{tabTitles[tab]||""}</div>
        </div>

        <div className="screen">
          {userType==="coach"&&(<>
            {tab==="home"&&<CoachHome user={user} students={students} db={db} getSA={getSA} setSubView={setSubView} activeTab={tab}/>}
            {activeCamp&&(tab==="squad"||tab==="assess")&&<CampBanner camp={activeCamp} onExit={()=>setActiveCampId(null)}/>}
            {tab==="squad"&&<CoachSquad students={activeCamp?campStudents:students} allStudents={activeCamp?campStudents:allStudentsIncInactive} db={db} getSA={getSA} setSubView={setSubView} onAddStudent={addStudent} allCoaches={allOrgCoaches} orgId={orgId}/>}
            {tab==="school"&&<SchoolView allStudents={allOrgStudents} allCoaches={allOrgCoaches} db={db} setSubView={setSubView}/>}
            {tab==="assess"&&<CoachAssessTab students={activeCamp?campStudents:students} allOrgStudents={activeCamp?campStudents:allOrgStudents} allOrgCoaches={allOrgCoaches} db={db} addSession={addSession} addGroupSession={addGroupSession} addGoal={addGoal} addGroupGoal={addGroupGoal} showNotif={showNotif} isAdmin={isAdmin} isShared={isShared} onGenerateReport={(sid,snap)=>{setShowReport(sid);setShowReportSnapshot(snap||null);}}/>}
            {tab==="settings"&&<CoachSettings user={user} db={db} onLib={()=>setShowLib(true)} onManageCoaches={()=>setShowManageCoaches(true)} squadVisibility={db.squadVisibility||"private"} onUpdateSquadVisibility={updateSquadVisibility} onChangePassword={changeOwnPassword}
              myCamps={myCamps} activeCampId={activeCampId} onEnterCamp={setActiveCampId} onExitCamp={()=>setActiveCampId(null)} onCreateCamp={()=>setShowCreateCamp(true)} onOpenCampRoster={setShowCampRoster} onUpdateCampCoaches={updateCampCoaches}
              onTransferSquad={(sids,toCoachId)=>{
              sids.forEach(sid=>updStudent(sid,undefined,undefined,undefined,toCoachId));
              const toCoach=db.coaches.find(c=>c.id===toCoachId);
              showNotif(`${sids.length} player${sids.length!==1?"s":""} transferred to ${toCoach?.name||"new coach"} ✅`);
            }}/>}
          </>)}
          {userType==="student"&&(<>
            {tab==="home"&&<StudentHome user={user} db={db}/>}
            {tab==="progress"&&<StudentProgress user={user} db={db}/>}
            {tab==="goals"&&<StudentGoals user={user} db={db} updGoal={updGoal} addGoal={addGoal} addCoachingNote={addCoachingNote} showNotif={showNotif} onViewed={()=>markStudentViewed(user.id,"goals")}/>}
            {tab==="sessions"&&<StudentSessions user={user} db={db} onViewed={()=>markStudentViewed(user.id,"sessions")}/>}
            {tab==="tips"&&<StudentSkillTips user={user} db={db}/>}
            {tab==="settings"&&<StudentSettings user={user} db={db} onSwitchPlayer={()=>{
              // Re-build sibling list from current db and show player selector
              const linkedIds=user.linkedPlayerIds||[];
              const sameEmailSiblings=(db.students||[]).filter(x=>x.id!==user.id&&x.email===user.email&&x.status!=="inactive");
              const allSiblingIds=[...new Set([...linkedIds,...sameEmailSiblings.map(x=>x.id)])];
              const siblings=allSiblingIds.map(id=>(db.students||[]).find(x=>x.id===id)).filter(Boolean).filter(x=>x.status!=="inactive");
              if(siblings.length>0){
                setPlayerSelectData({players:[user,...siblings],schoolDb:db,orgId:user.orgId});
                setUser(null);
              }
            }} onChangePassword={changeStudentPassword}/>}
          </>)}
        </div>
        <nav className="tab-bar">
          {(userType==="coach"?coachTabs:studentTabs).map(([t,ic,lb])=>{
            const unread=userType==="student"?(t==="sessions"?studentUnread.sessions:t==="goals"?studentUnread.goals:0):0;
            return(
              <button key={t} className={`tab-item${tab===t?" active":""}`} onClick={()=>setTab(t)}>
                <span className="tab-icon" style={{position:"relative",display:"inline-block"}}>
                  {ic}
                  {unread>0&&<span style={{position:"absolute",top:-4,right:-6,minWidth:14,height:14,borderRadius:99,fontSize:8,fontWeight:800,background:"var(--danger)",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",padding:"0 3px",lineHeight:1,boxSizing:"border-box"}}>{unread}</span>}
                </span>
                <span className="tab-label">{lb}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </>
  );
}
